// SDK20260129
/***
Spot SDK Overview
======================

## Who should read this document?

This is a technical document, **intended for users with Javascript, HTML and CSS experience**.

It's aim is to explain how to use the Spot SDK to integrate Spot functionality on a Shopify store.

**If you're non-technical** or you're looking to install Spot on a free theme on Shopify, or a relatively standard theme, it is recommended that you **use the configurable Spot "section"
installed via the Spot control panel**. It works by default with at least the following themes:

* Simple
* Minimal
* Brooklyn
* Narrative
* Supply
* Venture
* Boundless
* Debut
* Dawn

## What exactly is the Spot SDK?

The **Spot SDK** is a [single javascript file](/docs/spot.js) ([minified](/docs/spot.min.js)), which provides all spot functionality in an easy-to-digest package.
The SDK consists of three separate classes, in a layered architecture, each [defined below](#core-classes).

You should **not** link to these files directly from your website; please download a copy, and upload to Shopify's CDN.

### What aspects of a website does it handle interact with/handle?

The Spot SDK interacts with a merchant's collection page, and search page, and to a very minor extent, the merchant's product pages.

### What does the SDK *not* provide out of the box?

The Spot SDK provides quite a lot. However, sometimes custom features that are specific to a merchant's site do need to be built. Spot's SDK does not provide any of the following out of the box:

* An entirely new theme.
* A new homepage design.
* A completely custom product tile that integrates perfectly with an existing custom theme. This should be provided, or the default should be at least styled by the integrator.
* A completely custom instant search bar that integrates perfectly with an existing custom theme. This should be provided, or the default should be at least styled by the integrator.
* A quick buy modal on the collection listing page. This is up to the integrator to provide.
* The ability to upload, edit or delete color swatch images, or the ability to display swatches to the product display page, or the collections page. This is up to the interator.
* The ability to provide a system where alternate images to show on products on hover. This is up to the integrator. By default, we will show the most appropriate image for a product or variant.
* Automatic translation of untranslated products. (Though it does work with Shopify's multi-language data, if it is present).
* The ability to provide solutions to unelicited requirements.

### Core Classes

There are three core classes in Spot's SDK. A high-level sample implementation (`SpotDefault`), a low-level state tracker (`SpotDOM`), and a means of sending requests to the HTTPS endpoint (`SpotAPI`).

#### [SpotDefault](#spotdefault-documentation)

This class contains a **sample high-level implementation** that uses the two below classes. It implements these features:

* Faceting Pane
* Recommendations Panel
* Pagination Selector
* Infinite Scroll
* Search-as-you-Type
* Product Result Listing
* Spell Correction Display

This is called directly by the Spot section (`spot.liquid`), that works out-of-the-box with all free themes on Shopify.
It also holds general settings from Spot's control panel, which inlunece how the underlying classes operates.

#### [SpotDOM](#spotdom-documentation)

This class contains a **higher-level API**, which keeps track of things like page state, and facet composition, allowing you to easily build things like a
pagination mechanism, a sorting dropdown, a faceting panel, a search bar, that sort of thing. *You normally won't need to use this directly.*

#### [SpotAPI](#spotapi-documentation)

This class contains the **low-level API**. It lets you construct queries, send them to Spot, and get a JSON representation of the objects you want back. This
is the bare minimum you'll need to create a Spot implementation on a website. *You normally won't need to use this directly.*

## Theme Section (spot.liquid)

`spot.liquid` is a sample invocation of `SpotDefault`. You can retrieve the `spot.liquid` section from [spot's setup section](/setup). This is how most themes
will interact with the Spot SDK.

## Conventions

### Casing

By default, the SpotSDK will *always* use **camelCase** when dealing with **variables, methods**, and most other data. It will use **TitleCase** when dealing with **class names**.

### No Modification

In an ideal world, **there should be no modification of `spot.js`**, as new versions of the file are pushed out regulary, and merging them can be onerous. If you
find you need to modify `spot.js` during what you would consider to be fairly normal operations, please contact us, and let us know what you're doing! We'd
be happy to add it to the API if it doesn't exist, or give clarification about how to perform a particular operation.

The generally accepted way, if you need to modify the underlying Spot classes, is to override the individual methods, and call the original methods, while
modifying their output. As a brief example, if you wanted to modify how the system generates queries in general, you'd place inside the `init` parameter call
in `SpotDefault.init`, something like the following:

```js
  spotDOM.addQueryTransform([function(query) {
    return query.available(true);
  }]);
```

## Are there are sample implementations of Spot?

You can see https://merchman-filters.myshopify.com for a sample implementation of the system, or look at [SpotDefault](#spotdefault-documentation) below.

## Where can I go to get help?

You can contact spot@moddapps.com to get more infromation, or visit our SDK guide at https://spot.moddapps.com/docs, which will have a few more examples.

If you're internal to Modd, or to Diff, you can feel free to reach out to the `#spot` channel, on Slack.

## Found a bug?

If you've found a bug, please tell us at the address above! If you do file a bug report, please include the following information.

1. Your .myshopify.com URL.
2. The time at which the bug ocurred.
3. Whether or not the bug is consistently repeatable.

**If your bug is with the Spot API itself**, meaning that you are not getting the expected result from the search service, please also include:

1. The full request you're sending. (Including all Request Headers)
2. The full response you're receiving. (Including all Response Headers)

If you have a browser debugger active, this information can usually be found under the "Network" tab.

**If your bug is with the Spot SDK**, please ensure that you haven't modified `spot.js` (or if you have, that none of the modifications have any effect on the bug at hand). In this case, be sure to include the following information.

1. A link to preview the Shopify theme on the store where you're seeing the issue.
2. Any actions to take that will reproduce the problem.

## [Endpoint Details](#spot-endpoint-documentation)

In addition to this SDK, there are also a few details on the actual endpoint itself, that is directly queriable in HTTP, should you be in an environment that does not support JavaScript.

***/

/***

SpotAPI Documentation
=====================

## Overview

### What is this class?

This is the low-level query API for Spot. What this means is, this is how you talk to Spot directly, without any intervening modules.

### When should I use it?

If you're trying to implement a collections page with various filters, you should not use this directly. You should use [SpotDOM](#spotdom-documentation) which
provides a lot of time-saving functionality, built over top of this. If you need/want to talk to the shopify product index directly, this is what
you should use.

### How do I use it?

The first step to using this module is to instantiate it, as an object, with new. Like below:

    var spotAPI = new SpotAPI({{ shop.metafields.esafilters.clusters | json }});

You can treat this as a global object; you really only ever need to make one of these. The object keeps track of some global options
(stuff like which filters server to point at, whether to use JSONP, GET, or POST, etc..), you'd like to use for all your queries.
It has sensible defaults, so you don't really need to customize anything unless you really want to; it should just work.

Once you've made the object, you can start making queries. A query is also an object. To start a query, type:

    var query = spotAPI.s();

This will instantiate a query object. Queries record most of the stuff you want to communicate to filters, like what collection you'd
like to look up, what colors of things you want to see, the price range, the pagination, etc.., etc... To specify these things, you make
method calls on the query object, like you would a normal javascript object. There are a bunch of different fields to choose from; and you
can choose exactly what you want to query, by passing desired paramters to the query object. There are a number examples in the querying
section below.

The query object keeps track of all operations you perform on it. As you're constructing your query, no remote calls to the server have been made yet,
you're just assembling your request in javascript. Once you've finished querying things, and are ready to finish up, call e(), on the object.

    query.e()

This will make a query to the server. This function returns a jQuery-style deferred (you can also pass in a success function to it).
What this means is that you can then make things happen depending on what the result of the query is. So, instead of just calling e()
(which does nothing), you can tell it to do things after we get our data back from Spot.

    query.e().done(function(products) {
      console.log(products);
    });

This will dump all the products that matched your query to the console. You can do whatever you like, though, build HTML, amend the page,
whatever you like.

In summary, as an example:

    new SpotAPI({{ shop.metafields.esafilters.clusters | json }}).s().product_type("T-Shirt").e().done(function(products) {
      console.log(products);
    });

Will output a list of all products that have the product type of T-Shirt to the console.

## Querying Basics

### Filtering

Say for example, you want to get all products, that have the vendor "My Vendor" in the store. After instanting the query, you do:

    query = query.vendor("My Vendor");

or

    query = query.vendor("==", "My Vendor");

The operator can be changed to any of the following: ==, !=, ^, in, not_in.


This will add the requirement that you only want red products to the query. You can also chain queries together. Let's say you've made
the call above, and now you decide you also want to restrict things to those products under 100$, and only hats. So, you'd then do:

    query = query.price("<", 100).product_type("Hat");

The list of full properties that can be queried is as follows: "id", "price", "grams", "compare_at_price", "inventory_quantity", "priority", "title", "option1", "option2", "option3", "product_type", "vendor","tags", "sku", "product_metafield", "variant_metafield", "option", "available".

Metafields and options work a bit differently, as you have to specify the name of the option, or the namespace and key of the metafield , like so:

    query = query.option("Color", "==", "Red")

    query = query.product_metafield("myNamspace", "myKey", "<", 100)

In addition to the normal operators, you can also perform and and or queries:

    query = spotAPI.s().and(query1, query2)

    query = spotAPI.s().or(query1, query2)

### Searching

Searching works much like regular filtering; you can also narrow down *any* resultset with free-text searches.

    query = query.search("Red Dresses")

    query = query.in_search("Red Dresses")

The difference between the two of these is that the top one will affect sort order (ranking by relevancy), whereas the second will act purely as a filter.

Almost always, you'll want to use the first one.

#### Spell Checking

By default you can also have your results spelling-corrected.

    query = query.search("Red Drasses")

This will likely automatically return "Red Dresses", if that exists in your catalog. The rules of the spell check are also returned under the `spellCheck` key in the options
arugment of the `done` function. Allowing you to easily construct "Did you mean?" dialogs.

This behaviour can be turned off by using `.autoCorrect(false)`.

You can also just use `.spellCheck(true).autoCorrect(false)` to get the results of the spellcheck, if you want to manage how that works yourself, and not have the user's search
be autoCorrected to a most likely correct search.

### Collections

You can also restrict searches to occur inside a particular collection with one of two operators.

    query = query.collection("my-collection-handle")

    query = query.in_collection("my-collection-handle")

The difference here is that collection should be used whenever you're on a particular collection's page, or if you need to operate in the context of that collection. It lets you do things like sort by the sort order of that collection, and a couple of other things. in_collection should be used when you need to use it as part of a query; or if you're creating collection unions.

### Sorting

#### Default Sort Orders

Sorting can be used in various ways. The normal Shopify sorting strings that exist on the default front-end of Shopify can be passed to the query object to change the sort order
of the object. As an example:

    query = query.sort("alpha-asc")

You can also, more intuitively pass an object to this function, and specify the sort direction independent of the sort field, like so:

    query = query.sort({ "asc": "alpha"})

#### Collection Sort Order

Using the sort order "manual" will sort the collection via the collection's specified manual ordering. Please note that this *only* applies when the query has a collection associated with it.

    query = query.sort("manual")

#### Search Sort Order

Using the sort order `"search"` will sort the resultset based on the most relevant results for a given search term. Please note that this *only* applies when a search term has been provided with `.search`

    query = query.search("My Search Terms")

The above query will automatically set the sort order to `"search"` if their has been no previous sort order set. You can reset a query's sort order back to `"search"` explicitly by doing:

    query = query.sort("search")

#### Priority Sort Order

Uses the priorities set in the spot back-end control panel to order the resultset.

    query = query.sort("priority")

#### Custom Sort Order

You can also specify a non-standard sort order; like sorting on a named option, or a metafield, if you've specified it in the Spot control panel.

    query = query.sort({"option":"Size"})

    query = query.sort({"product-metafield":{"namespace":"key"}})


### Variant-Level Querying

Variant-level functionality is a significant advantage that Spot has over other search engines on Shopify. From the get-go Spot was built to work
with Shopify's variant system, so you have a number of extra operators open to you that are specifically designed to make your life easier.

By default, resultsets are returned the same way Shopify normally returns things; as products. For example, if you ask for a "Red T-Shirt", and
there's a T-Shirt product, that has at least one variant that's red, we'll return the whole product, and all its variants. However, if you
use the allVariants property, like so, you'll receive a product cotaining ONLY the variants that match.

    query.option("Color", "Red").product_type("T-Shirt").allVariants(false).e().done(function(products) {
      console.log(products);
    });

If you want to narrow your search/query/filter to include ONLY products in which all variants match your query, you can do the following,
much as with and/or:

    spotAPI.s().all(query.option("Color", "Red").product_type("T-Shirt")).allVariants(false).e().done(function(products) {
      console.log(products);
    });

The above would only return you T-Shirts that have "Red" as the only color option.

In addition, Spot also lets you specify what's known as an "index-split", using the "split" property, like so.

    query.split("my-color-split").e().done(function(products) {
      console.log(products);
    });

By default, the split value is "none", which is always valid. Other splits must be pre-defined in the Spot back-end, but can do things like
break out the variants of each product by a named option, or a metafield. So, for example, if you set up a split on "Color" as an option, in
the back-end of Spot, and then activate it as above, what'll happen is your catalog will segment into one "product" per group of variants that have
a disctinct value of the particular option. For example, if you have a T-Shirt that is Red, Green, or Blue in Color, and has sizes from XS, S, M, L, XL,
for a total of 15 variants; specifying a color split will split this into 3 separate products, with 5 variants each.

You can also, simply split across all options, meaning that it'll split your catalog into one variant, one product; allowing you to see everything
at the variant-level.

## ResultSet Basics

### ResultSet Counting

To see the count included as part of the results, you can do:

    query = query.count(true)

By default, this will return the count how many products there are in a resultset. Normally, Spot will do an *approximate* count. This is significantly faster
that doing an actual count. For example, if your resultset is 10,000 products long; an approximate count allows us to really only grab the first hundred or so
products then extrapolate what an approximation of what the count is likely to be. This is usually pretty accurate, and very fast.

However, we recognize that under certain circumstances, it can be desirable to retrieve the *exact* amount of results. This takes longer, and necessitates more
computational power being devoted to your store (and a corresponding increase in cost), but it is possible, via the following call:

    query = query.count(true, "exact")

This will return the count as a second argument when you complete a query:

    query.e().done(function(products, count) {
      console.log(count);
    });


### ResultSet Faceting

#### Simple Faceting

This can be done via the .options query. In this, you can do something like:

    query = query.options(["product_type"])

Which will show you all the product types, as well as their counts. This information can be found in the third argument to "done", the full response body:

    query.e().done(function(products, count, resposne) {
      console.log(response);
    });

You can also facet on more than one facet at a time.

    query = query.options(["product_type", "vendor", {"option":"Size"}])

You can also be more specific, if you want only a subset of facets.

    query = query.options([{"product_type":["Dress", "Shoes"]}, "vendor", {"option":"Size"}])

#### Disjunctive Facet Counts

Now, when you get options back, usually you'll present these to the uesr as clickable facets. Frequently, you'll want to display counts next to the facets as well.
Normally, counts are simply the numbers of each facet in the resultset. However, there's usually an exception in the case of counts dispalyed for facets that are in the same category
as a facet that has been selected; that facet is usually stripped when computing the counts.

As an example, let's say you're faceting on Color and Product Type. For color we return the following result: `{"option":{"Color":{"Red":10,"Blue":"20","Green":15}}`.
If you're not clicking any of these facets, the counts are correct. Let's say the user then clicks on your "Red" facet. This of course reduces the resultset to the following: `{"option":{"Color":{"Red":10}}`.
This however, is not desirable. In traditional faceting schemes, the user is expecting the facet counts to be represented *as if there was no facet applied in the category in question*. The actual
result the user is expecting is still `{"option":{"Color":{"Red":10,"Blue":"20","Green":15}}`, despite having the facet selected.

This can be achieved by specifying the conditions that you apply are located under an `or` array, inside a `facets` array. For example, instead of the following to represent someone
clicking the "Red" facet:

    query.option("Color", "Red").options([{"option":"Color"}, "product_type"])

You want to do this:

    query.facets(spotAPI.s().option("Color", "Red"), spotAPI.s()).options([{"option":"Color"},"product_type"])

Likewise, if you wanted to use a product type facet, you'd do this:

    query.facets(spotAPI.s(), spotAPI.s().product_type("Jeans")).options([{"option":"Color"},"product_type"])

This is all handled for you if you use the SpotDOM SDK; but if you're writing your own DOM module, you'll need to wrap your options like that. Not only will this let spot know
that you explicitly want to have a particular query parameter be part of a facet, but it will also inform Spot's back-end analytics engine about your facet use.

#### Facet Grouping

In addition to faceting normally; you can also group facet values together, into a single facet. This is useful for stuff like
groups of product types, or colors. You can also do the same thing for groups of product prices.

##### Qualitative Groups

As an example for grouping together lists of items:

    query = query.options([
      {"product_type":[{"name":"Dresses","value":["Dress","Knit Dress","Jupe"]},{"name":"Coats","value":["Winter Jacket","Pea Coat"]}]}
      {"option":{"Color":[{"name":"Red","value":["Red","Magenta","Purple"]},{"name":"Blue","value":["Blue","Aqua","Cyan"]}]}}
    ])

##### Quantitative Groups

Here's an example for grouping things together based on an item's price.

    query = query.options([
      {"type":"price","name":"Price","values":[
        {"name":"Under 10$","value":"-10"},
        {"name":"10-20 $","value":"10-20"},
        {"name":"20-30 $","value":"20-30"},
        {"name":"30-40 $","value":"30-40"},
        {"name":"40-50 $","value":"40-50"},
        {"name":"50-60 $","value":"50-60"},
        {"name":"Over 60$","value":"60+"}
      ]}
    ])

### Multi-Target Search

A search or query can be targeted towards other things than `products`, like `pages`, `blogs`, `articles`, `collections` and `popularSearches`.

The targets for a search can be specified like so

    query = query.targets(["products", "collections", "pages", "popularSearches"]).e().done(function(products, count, results) { })

Whatever the first target is, is listed as the *primary target*. By default, this is set to products, and only products are searched. If you add more than
one target, they can be accessed in the options paramater of the done function, as shown above, under the variables `results.collections`, `results.pages`, etc..

## Examples

All the following examples below can be run in the Chrome debugger console, on any page in a Shopify store which includes this file, and instantiates
the SpotAPI. Try them out, to get a feel of exactly what happens!

Gets all products.

    spotAPI.s().e(function(products, count) {
      console.log(products);
    });

Gets all products in collection "my-collection-handle", over 10$ and under 20$, paginated by 50, on page 2, sorted by vendor.

    spotAPI.s().collection("my-collection-handle").price(">=", 10).price("<", 20).paginate(50).page(2).sort("price").e().done(function(products, count) {
      console.log(products);
    });

All products in collection "my-collections-handle" NOT over 10$ and under 20$, all with the vendor "My Vendor" that are available for purchase.

    spotAPI.s().collection("my-collection-handle").vendor("My Vendor").or(spotAPI.s().price("<", 10), spotAPI.s().price(">=", 20)).available(true).paginate(50).page(2).e(function(products, count) {
      console.log(products);
    });

Gets the first 50 results of a search page, for "Gold" performing a spelling correction if no results were found.,

    spotAPI.s().search("Gold").autoCorrect(true).paginate(50).e(function(products, count, options) {
      console.log(products);
    });

Uses JSONP to get the first 5 products in the store, sorted by the store owner's preference.

    spotAPI.s().jsonp(1).sort("priority").paginate(5).e(function(products) {
      console.log(products);
    });

Set a default for all queries to be 48 products, and only show things that have the tag "Showing", and always sort by bestselling.

    spotAPI.defaultQuery(spotAPI.s().paginate(48).tag("Showing").sort("bestselling"));
    spotAPI.s().page(4).e(function(products) {
      console.log(products);
    });

Filters based on options

    spotAPI.s().option("Color", "Red").paginate(48).e(function(products) {
      console.log(products);
    });

Searches through other targets than just products:

    spotAPI.s().targets(["products", "pages", "popularSearches"]).search("my search string").paginate(48).e(function(products, count, results) {
      console.log(products);
      console.log(results.pages);
      console.log(results.popularSearches);
    });


****************************************************************/


window.SpotAPI = window.SpotAPI || function(clusters, silenceErrors) {
  // ========== BEGIN INTERNALS =============
  // These are mostly internals, and can be ignored.
  var spotAPI = this;
  this._silenceErrors = silenceErrors || 0;
  this._defaultQuery = null;
  this._jsStyle = true; // set false to get Shopify backend object style
  this._fakeSessionStorage = {};
  this.clone = function(obj) {
    return new queryObject([], {}).merge(obj);
  };

  this.getSessionStorage = function(key) {
    var item = null;
    try {
      item = sessionStorage.getItem(key);
    } catch(error) {
      item = spotAPI._fakeSessionStorage[key];
    }
    if (!item)
      return null;
    return JSON.parse(item)['value'];
  };

  this.setSessionStorage = function(key, value) {
    try {
      window.sessionStorage.setItem(key, JSON.stringify({ value: value }));
    } catch(error) {
      spotAPI._fakeSessionStorage[key] = JSON.stringify({ value: value });
    }
    return this;
  };

  this.getStorage = function(key) {
    var item = null;
    try {
      item = localStorage.getItem(key)
      if (!item)
        item = sessionStorage.getItem(key);
    } catch(error) {
      item = spotAPI._fakeSessionStorage[key];
    }
    if (!item)
      return null;
    var json = JSON.parse(item);
    if (!json['expiry'] || json['expiry'] > new Date().getTime())
      return json['value'];
    return null;
  };
  this.setStorage = function(key, value, expiry) {
    var json = { "value": value };
    var storage = window.localStorage;
    if (expiry)
      json['expiry'] = (new Date().getTime())+(expiry*1000);
    else
      storage = window.sessionStorage;
    try {
      storage.setItem(key, JSON.stringify(json));
    } catch(error) {
      spotAPI._fakeSessionStorage[key] = JSON.stringify(json);
    }
  };

  this.baseURLs = [];
  this.successfulURL = null;
  this.baseHeaders = {};
  this._sessionId = this.getStorage("spotSessionId");


  // Customer identifier, can be cart token, or some other unique identifier. If no identifier is specified, the system will
  // attempt to use IP to disambiguage in a single session. This will allow for things like personalized recommendations, event tracking, and the like.
  // By default this should probably be the Shopify session ID. If you do not specify one, you will be assigned one via a response header.
  // Generally, PII should not be transmitted here for privacy reasons, so this should be kept to things like IDs, and other non-identifiable
  // information.
  this.sessionId = function(sessionId) {
    if (sessionId) {
      this._sessionId = sessionId;
      this.setStorage("spotSessionId", sessionId);
      return this;
    } else {
      return this._sessionId;
    }
  };

  // This can be set at the API level, but renders this stateful, technically.
  // Better to set this at the DOM level.
  this.clusters = function(clusters) {
    this.baseURLs = clusters.map(function(cluster) {
      if (!/^(\/|http)/.test(cluster))
        return "https://" + cluster;
      return cluster;
    });
    return this.baseURLs;
  };
  if (clusters)
    this.clusters(clusters);
  // This should generally be used in situations where query params need to be injected *outside* of the normal search parameters.
  // Things like tokens, sessions, etc..
  this.headers = function(defaults) {
    this.baseHeaders = defaults;
    return this;
  }

  this.encodeURIComponent = function(value) {
    return encodeURIComponent(value).replace(/[!'()*]/g, function(c) { return "%" + c.charCodeAt(0).toString(16).toUpperCase() });
  };


  this.encodeUrlVars = function(hash) {
    var query = "";
    var keys = Object.keys(hash).sort();
    for (var keyIdx = 0; keyIdx < keys.length; ++keyIdx) {
      var key = keys[keyIdx];
      if (typeof hash[key] == "object") {
        for (var i in hash[key]) {
          if (hash[key][i] != null)
            query += (query == "" ? "" : "&") + key + "=" + this.encodeURIComponent(hash[key][i]);
        }
      }
      else {
        if (hash[key] != null)
          query += (query == "" ? "" : "&") + key + "=" + this.encodeURIComponent(hash[key]);
      }
    }
    return query;
  };

  this.Deferred = function() {
    return new window.SpotAPI.DeferredObject();
  };

  // Boiler-plate.
  this.ajax = function(url, options, baseURLs, deferred, attempt) {
    var i;
    var spotAPI = this;
    if (!baseURLs) {
      baseURLs = this.baseURLs;
      // Go through, and insert the successful hostname at the front, if we succeeded.
      if (this.successfulURL) {
        for (i = 1; i < baseURLs.length; ++i) {
          if (baseURLs[i] == this.successfulURL) {
            var swap = baseURLs[0];
            baseURLs[0] = baseURLs[i];
            baseURLs[i] = swap;
            break;
          }
        }
      }
    }
    if (!baseURLs[0]) {
      deferred = deferred || this.Deferred();
      deferred.reject();
      return deferred;
    }
    if (!options)
      options = {};
    if (!options['dataType'])
      options['dataType'] = 'json';
    var method = options['type'] || "GET";
    if (options['data'] && options['contentType'] == 'json' && typeof(options.data) == "object" && options['type'] && options['type'] != "GET")
      options['data'] = JSON.stringify(options.data);
    if (!options['headers'])
      options['headers'] = {}
    Object.keys(this.baseHeaders).filter(function(e) { return !options['headers'][e]; }).forEach(function(e) { options['headers'][e] = spotAPI.baseHeaders[e]; });
    if (!/^(http)?s?:?\/?\//.test(url)) {
      options['url'] = baseURLs[0] + url;
      // We are careful with this. If contentType is anything other than text/plan, it'll trigger a pre-flight request. So always specify a content-type of text. The server will know what we mean.
      options['contentType'] = 'text/plain';
    } else {
      options['url'] = url;
    }

    if (!options['headers']['Content-Type'] && options['contentType'] && method != "GET") {
      if (options['contentType'] == "json") {
        options['headers']['Content-Type'] = "application/json; charset=UTF-8";
      } else {
        options['headers']['Content-Type'] = options['contentType'];
      }
    }

    if (!deferred)
      deferred = this.Deferred();

    var xhttp = new XMLHttpRequest();
    var internalDeferred = this.Deferred();
    xhttp.onreadystatechange = function() {
      if (this.readyState == 4) {
        if (this.status >= 200 && this.status <= 299) {
          var sessionId = xhttp.getResponseHeader("X-Session-Id");
          if (sessionId)
            spotAPI.sessionId(sessionId);
          if (options['dataType'] == "json") {
            var data = null;
            try {
              data = JSON.parse(this.responseText)
            }
            catch (e) {
              internalDeferred.reject(this, "" + e, e);
            }
            internalDeferred.resolve(data);
          }
          else
            internalDeferred.resolve(this.responseText);
        } else
          internalDeferred.reject(this, this.responseText, null);
      }
    };
    url = options['url'];
    if ((options['type'] || "GET") == "GET") {
      if (url.indexOf("?") == url.length - 1)
        url += this.encodeUrlVars(options['data']);
      else if (url.indexOf("?") != -1)
        url += "&" + this.encodeUrlVars(options['data']);
      else
        url += "?" + this.encodeUrlVars(options['data']);
    }
    // We keep this as a query variable because otherwise we'll trigger a CORS preflight request.
    if (this._sessionId)
      url += (url.indexOf("?") == -1 ? "?" : "&") + "sessionId=" + encodeURIComponent(this._sessionId);
    xhttp.open(method, url, options['async'] == null ? true : options['async']);
    Object.keys(options['headers']).forEach(function(i) {
      xhttp.setRequestHeader(i, options['headers'][i]);
    });
    internalDeferred.done(function() {
      spotAPI.successfulURL = baseURLs[0];
      deferred.resolve.apply(this, arguments);
    }).fail(function(xhr) {
      if (!xhr.status || (xhr.status >= 500 || xhr.status == 408)) {
        // Execute the query a second time, 200ms later, in case there's some
        // odd transient fault. This should be rare, but if it occurs, will
        // keep them using the system normally. It should also not trigger on
        // window unload in Safari; hopefully.
        if (!attempt || attempt == 1) {
          setTimeout(function() {
            spotAPI.ajax(url, options, baseURLs, deferred, 2);
          }, 200);
        } else {
          if (baseURLs.length > 1) {
            baseURLs.shift();
            spotAPI.ajax(url, options, baseURLs, deferred, 1);
          } else {
            deferred.reject.apply(this, arguments);
          }
        }
      } else {
        deferred.reject.apply(this, arguments);
        spotAPI.successfulURL = baseURLs[0];
      }
    });

    xhttp.send(options['data']);

    return deferred;
  };

  this.map = function(items, func) {
    return Array.prototype.slice.call(items).map(func);
  };
  this.flatMap = function(items, func) {
    return Array.prototype.slice.call(items).flatMap(func);
  };
  this.forEach = function(items, func) {
    return Array.prototype.slice.call(items).forEach(func);
  };
  this.grep = function(items, func) {
    return Array.prototype.slice.call(items).filter(func);
  };
  this.uniq = function(items) {
    return items.filter(function(i, idx) { return items.indexOf(i) == idx; });
  };
  // ============== END INTERNALS ==============


  // If false, will throw out errors as alerts. If true, will silently fail. Default is true.
  this.silenceErrors = function(silence) {
    this._silenceErrors = silence;
  };

  // This can be used to set the default query that comes out when you call .s() to start a query.
  this.defaultQuery = function(query) {
    if (arguments.length == 1)
      this._defaultQuery = this.clone(query);
    return this._defaultQuery;
  };


  // QUERY INTERFACE.
  function generateQualitativeQuery(field, operator, text, boost, range) {
    if (!operator)
      operator = "==";
    var hash = {};
    hash[field] = { };
    hash[field][operator] = text;
    if (boost) {
      hash[typeof(boost) == "object" ? "boost" : "weight"] = boost;
      if (range != null)
        hash['range'] = range;
    }
    return new queryObject(hash);
  }
  function generateQuantitativeQuery(field, operator, text, boost, range) {
    if (!operator)
      operator = "==";
    var hash = {};
    hash[field] = { };
    hash[field][operator] = text;
    if (boost) {
      hash[typeof(boost) == "object" ? "boost" : "weight"] = boost;
      if (range != null)
        hash['range'] = range;
    }
    return new queryObject(hash);
  }

  var queryObject = function(query, attributes) {
    var i;
    if (query && typeof(query) == "object" && !Array.isArray(query))
      query = [query];
    this.innerQuery = query || [];
    this.innerAttributes = attributes || {};
    this.innerQueryParameters = {};
    this.innerJSONP = 0;
    this.innerHostname = null;
    this.innerMethod = "GET";
    this.innerCache = null;
    this.innerSessionId = null;
    this.innerClusters = null;
    this.innerHeaders = null;

    this.clusters = function(clusters) {
      this.innerClusters = clusters.map(function(cluster) {
        if (!/^(\/|http)/.test(cluster))
          return "https://" + cluster;
        return cluster;
      });
      return this;
    };
    this.headers = function(headers) {
      this.innerHeaders = headers;
      return this;
    }


    // Used for analytics.
    this.category = function(category) {
      return this.merge(new queryObject(null, { category: category }));
    };

    // Internal.
    this.merge = function(otherQuery) {
      if (Array.isArray(otherQuery)) {
        this.innerQuery = this.innerQuery.concat(otherQuery);
        return this;
      }
      if (otherQuery.innerQuery)
        this.innerQuery = this.innerQuery.concat(otherQuery.innerQuery);
      for (var i in otherQuery.innerAttributes)
        this.innerAttributes[i] = otherQuery.innerAttributes[i];
      for (var i in otherQuery.innerQueryParameters)
        this.innerQueryParameters[i] = otherQuery.innerQueryParameters[i];
      this.innerJSONP = this.innerJSONP || otherQuery.innerJSONP;
      this.innerHostname = this.innerHostname || otherQuery.innerHostname;
      if (otherQuery.innerCache != null)
        this.innerCache = otherQuery.innerCache;
      return this;
    };
    this.query_param = function(key, value) {
      this.innerQueryParameters[key] = value;
      return this;
    };

    var quantitativeFields = ["id", "price", "grams", "compare_at_price", "inventory_quantity", "inventory_quantity_online", "priority", "variant_id"];
    var qualitativeFields = ["split_id", "handle", "title", "option1", "option2", "option3", "product_type", "vendor", "tag", "tags", "sku", "barcode", "related"];
    var booleanFields = ["available"];

    // Performs a free-form text search; triggers spellcheck if autoCorrect or spellCheck has been set to true.
    this.search = function(text) {
      return this.merge(new queryObject(null, { search: { query: text } }));
    };

    // Puts this query into "collection mode", changing the query slightly. Will make it so that it'll only return products in that collection.
    // If given no sort order, will default the collection's default sort order. Can also give the extra, special sort order, "manual" to use
    // the collection's manual sorting, if present.
    this.collection = function(collection_handle) {
      return this.merge(new queryObject(null, { collection: collection_handle }));
    };

    // Acts as a filter; doesn't do anything with sort orders; simply helps pare down the resultset to things in a particular collection.
    // Allows you to do cool things, like collection unions/intersections.
    this.in_collection = function(operator, handle, weight) {
      if (arguments.length == 1) {
        handle = operator;
        operator = "==";
      }
      return this.merge(generateQualitativeQuery("collection", operator, handle, weight));
    };
    // Acts as a filter; doesn't do anything with sort orders; simply helps pare down the resultset to things matching this particular search term.
    this.in_search = function(operator, handle, weight) {
      if (arguments.length == 1) {
        handle = operator;
        operator = "==";
      }
      return this.merge(generateQualitativeQuery("search", operator, handle, weight));
    };

    this.catalog = function(operator, handle, weight) {
      if (arguments.length == 1)
        return this.merge(new queryObject({ "catalog": operator }));
      return this.merge(generateQualitativeQuery("catalog", operator, handle, weight));
    };


    this.isEmptyQuery = function() {
      return this.innerQuery.length == 0;
    };

    this.hostname = function(hostname) {
      this.innerHostname = hostname;
      return this;
    }
    this.cache = function(cache) {
      this.innerCache = cache;
      return this;
    }
    this.omitExtraneousResults = function(value) {
      this.innerAttributes["omitExtraneousResults"] = value;
      return this;
    }
    this.popular = function(popular) {
      this.innerPopular = popular;
      return this;
    };
    this.boost = function(boost) {
      if (arguments.length == 0)
        return this.innerAttributes["boost"] || [];
      this.innerAttributes["boost"] = Array.isArray(boost) ? boost : boost.innerQuery;
      return this;
    };
    this.targets = function(targets) {
      this.innerAttributes["targets"] = targets;
      return this;
    };
    this.pins = function(pins) {
      this.innerAttributes["pins"] = pins;
      return this;
    };
    this.antipins = function(antipins) {
      this.innerAttributes["antipins"] = antipins;
      return this;
    };

    // operators are <, >, ==, !=, >=, <=, in, not_in. == is assumed by default.
    for (i = 0; i < quantitativeFields.length; ++i) {
      var closure = function(i) {
        return function(operator, operand, boost) {
          if (!spotAPI._silenceErrors && arguments.length < 1)
            alert("Requires an operator and operand, or just an operand if assuming ==.");
          if (arguments.length == 1) {
            operand = operator;
            operator = "==";
          }
          return this.merge(generateQuantitativeQuery(quantitativeFields[i], operator, operand, boost));
        };
      };
      this[quantitativeFields[i]] = closure(i);
    }
    // operators are  ==, !=, in, =~, ^, $, sub, in, not_in
    for (i = 0; i < qualitativeFields.length; ++i) {
      this[qualitativeFields[i]] = (function(i) {
        return function(operator, operand, boost) {
          if (arguments.length == 1) {
            operand = operator;
            operator = "==";
          }
          return this.merge(generateQualitativeQuery(qualitativeFields[i], operator, operand, boost));
        };
      })(i);
    }

    // operators are ==, !=
    for (i = 0; i < booleanFields.length; ++i) {
      this[booleanFields[i]] = (function(i) {
        return function(operator, bool, boost) {
          if (!spotAPI._silenceErrors && arguments.length < 1)
            alert("Requires an operator and operand, or just an operand if assuming ==.");
          if (arguments.length == 1) {
            bool = operator;
            operator = "==";
          }
          return this.merge(generateQualitativeQuery(booleanFields[i], operator, bool, boost));
        };
      })(i);
    }

    this.singularly_qualified_field = function(field_name, name, operator, operand, boost) {
      var hash = {};
      hash[name] = {};
      if (arguments.length == 3) {
        operand = operator;
        operator = "==";
      }
      if (!operator)
        operator = "==";
      hash[name][operator] = operand;
      var top_level = {}
      top_level[field_name] = hash;
      if (boost)
        top_level[typeof(boost) == "object" ? "boost" : "weight"] = boost;
      return this.merge(new queryObject(top_level));
    };

    this.option = function(name, operator, operand, boost) { return arguments.length == 2 ? this.singularly_qualified_field("option", name, operator) : this.singularly_qualified_field("option", name, operator, operand, boost); }
    this.product_custom_field = function(name, operator, operand, boost) { return arguments.length == 2 ? this.singularly_qualified_field("product-custom-field", name, operator) : this.singularly_qualified_field("product-custom-field", name, operator, operand, boost); }
    this["product-custom-field"] = this.product_custom_field;
    this.variant_custom_field = function(name, operator, operand, boost) { return arguments.length == 2 ? this.singularly_qualified_field("variant-custom-field", name, operator) : this.singularly_qualified_field("variant-custom-field", name, operator, operand, boost); }
    this["variant-custom-field"] = this.variant_custom_field;

    this.product_metafield = function(namespace, key, operator, operand, boost) {
      var hash = {};
      hash[namespace] = {};
      hash[namespace][key] = {};
      if (arguments.length == 3) {
        operand = operator;
        operator = "==";
      }
      if (!operator)
        operator = "==";
      hash[namespace][key][operator] = operand;
      var obj = {}
      obj["product-metafield"] = hash;
      if (boost)
        obj[typeof(boost) == "object" ? "boost" : "weight"] = boost;
      return this.merge(new queryObject(obj));
    };
    this['product-metafield'] = this.product_metafield;

    this.variant_metafield = function(namespace, key, operator, operand, boost) {
      var hash = {};
      hash[namespace] = {};
      hash[namespace][key] = {};
      if (arguments.length == 3) {
        operand = operator;
        operator = "==";
      }
      if (!operator)
        operator = "==";
      hash[namespace][key][operator] = operand;
      var obj = {};
      obj["variant-metafield"] = hash;
      if (boost)
        obj[typeof(boost) == "object" ? "boost" : "weight"] = boost;
      return this.merge(new queryObject(obj));
    };
    this['variant-metafield'] = this.variant_metafield;

    // This is used internally by the faceting system; in terms of filtering, this is a no-op.
    this.facets = function() {
      if (arguments.length >= 1 && !Array.isArray(arguments[0])) {
        if (arguments.length > 1)
          return this.merge(new queryObject({ "facets": spotAPI.map(arguments, function (e) { return e.innerQuery; }) }));
        else
          return this.merge(new queryObject({ "facets": arguments[0].innerQuery }));
      }
      else if (Array.isArray(arguments[0])) {
        return this.facets.apply(this, arguments[0]);
      }
      return this;
    };

    // This is used internally by the faceting system; in terms of filtering, this is a no-op.
    this.boundaries = function() {
      if (arguments.length >= 1 && !Array.isArray(arguments[0])) {
        if (arguments.length > 1)
          return this.merge(new queryObject({ "boundaries": spotAPI.map(arguments, function (e) { return e.innerQuery; }) }));
        else
          return this.merge(new queryObject({ "boundaries": arguments[0].innerQuery }));
      }
      else if (Array.isArray(arguments[0])) {
        return this.facets.apply(this, arguments[0]);
      }
      return this;
    };


    // Possibly some more optimizations can take place here.
    this.or = function() {
      if (arguments.length >= 1 && !Array.isArray(arguments[0])) {
        if (arguments.length > 1)
          return this.merge(new queryObject({ "or": spotAPI.map(arguments, function (e) { return e.innerQuery; }).flat() }));
        else
          return this.merge(new queryObject({ "or": arguments[0].innerQuery }));
      }
      else if (Array.isArray(arguments[0])) {
        return this.or.apply(this, arguments[0]);
      }
      else if (arguments.length == 0) { // Now that we're specifying facets as part of the query sometimes we need to pass in empty ors instead of ignoring empty facet groups
        return this.merge(new queryObject({ "or": spotAPI.map(arguments, function (e) { return e.innerQuery; }) }));
      }
      return this;
    };

    // Wraps an or around the specified arguments, but uses this query's' innerAttributes.
    this.setOr = function() {
      this.innerQuery = [{ "or": Array.from(arguments).map(function(a) { return { and: a.innerQuery } }) }];
      return this;
    };


    this.and = function() {
      if (arguments.length >= 1) {
        if (arguments.length > 1)
          return this.merge(new queryObject({ "and": spotAPI.map(arguments, function (e) { return e.innerQuery; }) }));
        else
          return this.merge(arguments[0]);
      }
      else if (Array.isArray(arguments[0]))
        return this.and.apply(this, arguments[0]);
      return this;
    };

    // Makes it so that this subquery must match EVERY variant instead of at >= 1 variant.
    this.all = function() {
      return this.merge(new queryObject({ "all": spotAPI.map(arguments, function (e) { return e.innerQuery; }) }));
    };

    // Valid fields for sorting are: "bestselling", "title", "price", "created", "priority".
    this.sort = function(field) {
      if (typeof(field) == "string") {
        var groups = /(\w+)-(ascending|descending)/.exec(field);
        if (groups && groups.length > 1) {
          if (groups[2] == "descending") {
            field = { desc: groups[1] };
          } else {
            field = { asc: groups[1] };
          }
        }
      }
      return this.merge(new queryObject(null, { sort: field }));
    };

    // Valid values here are "none", "auto", or an explicitly created split in the admin panel.
    // "none" will return an unsplit resultset; "auto" will return a split if it's appropriate based
    // on which facets have been selected, and an explicit split will always return that split.
    this.split = function(splitHandle) {
      return this.merge(new queryObject(null, { split: splitHandle }));
    };
    // Merges must be explicit. Can be passed either as a string, which is a simple merge handle,
    // or, as a hash that specifies both `handle` and `type`. `type` can be either `merge`,
    // which is the default, or `link`, which, instead of merging product results, will keep product
    // results separate, but provide additional information on each result in accordance with the merge.
    this.productMerge = function(merge) { // This is unfortunately titled, for legacy reasons.
      return this.merge(new queryObject(null, { merge: merge }));
    };

    // If set to true, will return the total count of objects in the resultset.
    // You can also set whether you want to get an exact count, or an approximate count.
    // Be warned, that setting an exact count can significantly slow down your search times,
    // as well as drive up your costs, as the server may have to do significantly more work
    // to determine the exact size of a resultset.
    this.count = function(field, behavior) {
      if (behavior)
        return this.merge(new queryObject(null, { count: field, countBehavior: behavior }));
      return this.merge(new queryObject(null, { count: field }));
    };

    // Sets how many results you'd like in your resulset. .rows and .limit are aliases of htis.
    this.paginate = function(pageAmount) {
      if (pageAmount == null)
        return this.innerAttributes['rows'];
      pageAmount = parseInt(pageAmount);
      if (pageAmount < 0 || pageAmount > 1000)
        alert("Must be a number between 0 and 1000");
      return this.merge(new queryObject(null, { rows: pageAmount }));
    };
    this.rows = this.paginate;
    this.limit = this.paginate;

    // Using this will skip over CORS headers for compatibility reasons; shouldn't really ever have to be used.
    this.jsonp = function(jsonp) {
      this.innerJSONP = jsonp;
      return this;
    }

    // Specifies which page you'd like; starts from 1; default 1.
    this.page = function(page) {
      return this.merge(new queryObject(null, { page: page }));
    };

    // Specifies which locale you'd like. If not specified, assumes the store's primary locale.
    this.locale = function(locale) {
      return this.merge(new queryObject(null, { locale: locale }));
    };

    // Specifies which currency you'd like. If not specified, assumes store default currency.
    // Note, this will *only* affect your input. (i.e. your desired price filters, and facets). It will
    // *not*, affect pricing. That should be converted after the fact to whatever is required.
    this.currency = function(currency) {
      return this.merge(new queryObject(null, { currency: currency }));
    };


    // Specifies the market this query is for. Can specify either a ISO country code, or the
    // handle of the market in question (the suffix, i.e. `intl` for international).
    this.market = function(market) {
      return this.merge(new queryObject(null, { market: market }));
    };

    // Specifies the companyId this query is for.
    this.companyLocationId = function(companyLocationId) {
      return this.merge(new queryObject(null, { companyLocationId: companyLocationId }));
    };

    // If set to true, when a .search is made, will check to see if any results are found on the input string; and if not, will report
    // a better match in the "spellCheck" attribute of the returned object, based on a heuristic.
    this.spellCheck = function(spellCheck) {
      return this.merge(new queryObject(null, { spellCheck: spellCheck }));
    };

    // If set to true, when a .search is made, will check to see if any results are found on the input string; and if not, will select
    // a better match, as well as report this info in the "spellCheck" attribute based on a heuristic, and search on that instead.
    this.autoCorrect = function(autoCorrect) {
      return this.merge(new queryObject(null, { autoCorrect: autoCorrect }));
    };

    // This is kept in for legacy reasons; doesn't do anything.
    this.index = function(index) {
      return this.merge(new queryObject(null, { index: index }));
    };

    // By default this is true; if set to false will make it so that ONLY variants match the query will be returned, rather than the entire variantset of any matching product.
    this.allVariants = function(allVariants) {
      return this.merge(new queryObject(null, { allVariants: allVariants }));
    };


    // Options can be passed like this ["product_type", "vendor", {"option":"Color"}, {"option":"Size"}, {"price": ["-10","10-20","20-40","40+"]}, {"tag": ["tag1", "tag2"]}, {"tag":{"^":"beginwtithis:"}}]
    // Likewise, you can also specify if you want the counts to ignore conditions on their counts.
    // I.E. If you have a product type condition, and are looking at product type, setting ignoreOptionConditions
    // to true will cause the system to ignore that condition while calculating the counts.
    // Price will go from 0-10, 10-20, 30-40, 40+
    // Disjunctive, by default is true, and refers to whether or not the system should recalculate the resultset per faceting group, if relevant (i.e. if you've selected "Red" as a color, it shouldn't affect the other color options.
    // Behaviour, by default is "approximate".
    this.facetOptions = function(options, disjunctive, behaviour) {
      var hash = { options: options };
      if (behaviour != null)
        hash['optionBehavior'] = behaviour;
      if (disjunctive != null)
        hash['optionDisjunctive'] = disjunctive;
      return this.merge(new queryObject(null, hash));
    };
    this.options = this.facetOptions;

    // Boundaries can be passed like this: [{"max":"price"},{"min":"price"}].
    this.boundaryOptions = function(boundaries) {
      return this.merge(new queryObject(null, { boundaries: boundaries }));
    };

    // Can restrict fields in the following manner. ["id", "handle"]
    // Useful for reducing the amount of time spent downloading the search results, which can be quite a while.
    // Processing time will be longer, though.
    this.fields = function(fieldListing) {
      return this.merge(new queryObject(null, { fields: fieldListing }));
    };

    // Chooses whether or not to make a GET or a POST call. Sites with larger queries may have to set this to true.
    // Eventually you'll be able to supply "auto" here, which will be the default, which will have the system choose based
    // on payload length which to use.
    this.post = function(post) {
      if (post)
        this.innerMethod = "POST";
      else
        this.innerMethod = "GET";
    };

    // Convenience function to request 0 rows, and only get options.
    this.optionsOnly = function(doneFunc) {
      return this.paginate(0).end(doneFunc);
    }
    // Returns collections with products.
    this.retrieveCollections = function(retrieveCollections) {
      return this.merge(new queryObject(null, { retrieveCollections: retrieveCollections }));
    };

    // When done constructing the query, this should be called to actually make the query; the result is contianed in the deferred.
    this.end = function(doneFunc) {
      var deferred = spotAPI.Deferred();
      if (doneFunc)
        deferred.done(doneFunc);

      if (this.innerPopular && this.innerAttributes.search)
        this.innerAttributes.search.popular_searches = this.innerPopular;
      var stringified = JSON.stringify({ query: this.innerQuery, attributes: this.innerAttributes });
      // If we're more than 8k, switch this to a POST so that the server doesn't spew out a "header too large" error. Also, Safari seems to have trouble with GETs, pushing them to POST.
      var method = this.innerMethod != "GET" || (stringified.length >= 3*1024) || spotAPI.isSafari() ? "POST" : "GET";
      var hash = method == "POST" ? stringified : {"q": stringified };

      var queryHash = {};
      if (this.innerHostname)
        queryHash['hostname'] = this.innerHostname;
      else if (window.Shopify)
        queryHash['hostname'] = window.Shopify.shop;
      if (this.innerCache)
        queryHash['cache'] = this.innerCache;
      if (this.innerQueryParameters) {
        var params = Object.keys(this.innerQueryParameters);
        for (var i = 0; i < params.length; ++i)
          queryHash[params[i]] = this.innerQueryParameters[params[i]];
      }

      var query = spotAPI.map(Object.keys(queryHash), function(e) { return e + "=" + encodeURIComponent(queryHash[e]) }).join("&");

      // Make this easier to flip over to POST.
      spotAPI.ajax((query || (!this.innerCache && this.innerCache != null) ? "?" + query : "") + (!this.innerCache && this.innerCache != null ? "cache=false" : ""), {
        type: method,
        dataType: (this.innerJSONP && method != "POST" ? "jsonp" : "json"),
        contentType: "application/json",
        headers: this.innerHeaders,
        data: hash
      }, this.innerClusters).done(function(results) {
        if (results['products'])
          deferred.resolve((spotAPI._jsStyle ? spotAPI.map(results.products, spotAPI.convertProduct ) : results.products), results.count, results);
        else if (results['collections'])
          deferred.resolve((spotAPI._jsStyle ? spotAPI.map(results.collections, spotAPI.convertCollection ) : results.collections), results.count, results);
        else if (results['blogs'])
          deferred.resolve(results.blogs, results.count, results);
        else if (results['articles'])
          deferred.resolve(results.articles, results.count, results);
        else if (results['pages'])
          deferred.resolve(results.pages, results.count, results);
      }).fail(function(xhr) {
        deferred.reject(xhr);
      });
      return deferred;
    };
    this.e = this.end;
  };

  this.start = function() {
    if (this._defaultQuery)
      return this.clone(this._defaultQuery);
    return new queryObject();
  };
  this.startEmpty = function() {
    return new queryObject();
  };
  this.s = function() {
    return this.start();
  };
  this.se = function() {
    return this.startEmpty();
  };

  this.spellCheck = function(phrase) {
    var deferred = spotAPI.Deferred();
    spotAPI.ajax("/spell-check", {
      type: "GET",
      dataType: "json",
      contentType: "json",
      data: { "phrase": phrase }
    }).done(function(result) {
      deferred.resolve(result.words, result.original, result.corrected);
    }).fail(function() {
      deferred.reject();
    });
  }

  this.getRelevantImages = function(product, variant) {
    if (product.original)
      return this.getRelevantImages(product.original, variant);
    var imageArray = (Array.isArray(product) ? product : [product]).flatMap(function(p) { return p.images });
    if (product.media && Array.isArray(product.media)) {
      var potentialArray = product.media.filter(function(e) { return e.media_type == "image" });
      if (potentialArray.length > 0)
        imageArray = potentialArray;
    }
    if (imageArray && imageArray.length > 0) {
      if (variant && variant.image_id) {
        var images = imageArray.filter(function(e) { return e.id == variant.image_id; });
        if (images.length > 0)
          return images;
      }
      var relevant = false;
      (Array.isArray(product) ? product : [product]).forEach(function(product) {
        for (var i = 0; i < product.variants.length; ++i) {
          if (product.variants[i].relevant === true)
            relevant = true;
        }
      });
      var extantImages = {};
      for (var i = 0; i < imageArray.length; ++i)
        extantImages[imageArray[i].id] = imageArray[i];
      var productMap = Object.fromEntries((Array.isArray(product) ? product : [product]).flatMap(function(p) { return p.images.map(function(i) { return [i.id, p] }) }));
      // if an image is associated to all variant_ids, and the product is not split, we treat it as if it was associated to none
      var isSplit = !Array.isArray(product) && product.split_id != product.id;
      var relevantImages = imageArray.filter(function(i) { return !i.variant_ids || i.variant_ids.length == 0 || (!isSplit && i.variant_ids.length >= productMap[i.id].variants.length) });
      var variantImages = [];
      // Bit of a short circuit, just in the case where nothing's werid, do normal shopify shit.
      if (!isSplit && !relevant && (Array.isArray(product) ? product[0] : product).image)
        return (!Array.isArray(product) ? [product] : product.filter(function(p) { return p.variants.length > 0; })).flatMap(function(i) { return i.images });
      (Array.isArray(product) ? product : [product]).forEach(function(product) {
        for (var i = 0; i < product.variants.length; ++i) {
          if (product.variants[i].image_id && (!relevant || product.variants[i].relevant) && extantImages[product.variants[i].image_id]) {
            var image = extantImages[product.variants[i].image_id];
            if (variantImages.filter(function(i) { return i.id == image.id }).length == 0) {
              if (imageArray[0] == image)
                variantImages.unshift(image);
              else
                variantImages.push(image);
            }
          }
        }
      });
      // filter out images associated to all variant ids, like above
      return variantImages.filter(function(image) { return isSplit || image.variant_ids.length < productMap[image.id].variants.length; }).concat(relevantImages);
    }
    return [];
  };

  
  this.getPreferredImage = function(product, variant, preferredImages) {
    if (Array.isArray(product)){
      var filteredProducts = product.filter(function(p) {
        if (preferredImages && p.images) {
          return p.images.filter(function(i) { return i.id == preferredImages[p.split_id]; }).length > 0;
        }
        if (variant) {
          var filteredVariants = p.variants.filter(function(v) { 
            return variant ? v.id == variant.id : (preferredImages[p.split_id] && v.image_id == preferredImages[p.split_id]);
          });
          return filteredVariants.length > 0;
        }
        return true;
      });
      if (filteredProducts.length == 0)
        return null;
      return this.getPreferredImage(filteredProducts[0], variant, preferredImages);
    }
    var relevantImages = this.getRelevantImages(product, variant);
    if (preferredImages && product.split_id && preferredImages[product.split_id]) {
      var image = relevantImages.filter(function(i) { return i.id == preferredImages[product.split_id] })[0];
      if (image)
        return image;
    }
    return null;
  };

  this.getFeaturedImage = function(product, variant, preferredImages) {
    if (product.original)
      return this.getFeaturedImage(product.original, variant ? product.original.variants.filter(function(v) { return v.id == variant.id })[0] : variant, preferredImages);
    return this.getPreferredImage(product, variant, preferredImages) || this.getRelevantImages(product, variant)[0];
  };

  this.convertCollection = function(collection) {
    return collection;
  };

  // Converts the incoming product to .js format from .json format.
  this.convertProduct = function(product) {
    if (typeof(product) == "object" && Array.isArray(product))
      return product.map(spotAPI.convertProduct);
    var feproduct = {}, o, i, v;
    feproduct.original = product;
    feproduct['id'] = product.id ? product.id : product.variant[0].product_id ;
    feproduct['title'] = product.title;
    feproduct['handle'] = product.handle;
    feproduct['description'] = product.body_html;
    feproduct['published_at'] = product.published_at;
    if (product.custom)
      feproduct['custom'] = product.custom;
    feproduct['created_at'] = product.created_at;
    feproduct['vendor'] = product.vendor;
    feproduct['type'] = product.product_type;
    feproduct['tags'] = product.tags ? product.tags.split(", ") : [];
    feproduct['media'] = product.media ? product.media : [];
    feproduct['images'] = product.images ? spotAPI.map(product.images, function(e) { return e.src }) : [];
    feproduct['featured_image'] = spotAPI.getFeaturedImage(product);
    if (feproduct['featured_image'])
      feproduct['featured_image'] = feproduct['featured_image'].src;
    feproduct['options'] = null;
    if (product.images.length > 0)
      feproduct['url'] = product.handle ? ("/products/" + product.handle + (feproduct['featured_image'] != product.images[0].src && product.variants.length > 0 ? "?variant=" + product.variants[0].id  : "")) : null;
    else
      feproduct['url'] = product.handle ? "/products/" + product.handle : null;
    feproduct['available'] = false;
    feproduct['price_min'] = null;
    feproduct['price_max'] = null;
    feproduct['compare_at_price_min'] = null;
    feproduct['compare_at_price_max'] = null;
    feproduct['metafields'] = {};
    if (product.metafields) {
      for (i = 0; i < product.metafields.length; ++i){
        var metafield = product.metafields[i];
        if (!feproduct['metafields'][metafield.namespace])
          feproduct['metafields'][metafield.namespace] = {}
        feproduct['metafields'][metafield.namespace][metafield.key] = metafield.value;
      }
    }

    if (product.options) {
      feproduct['options'] = [];
      for (o in product.options) {
        feproduct['options'].push({
          "name": product.options[o].name,
          "position": product.options[o].position,
          "values": product.options[o].values
        });
      }
    }
    if (product.collections)
      feproduct['collections'] = product.collections;

    var varimages = {};
    if (product.images) {
      for (i = 0; i < product.images.length; ++i)
        varimages[product.images[i].id] = product.images[i];
    }


    if (product.variants) {
      var variants = [];
      feproduct['variants'] = [];
      for (v in product.variants) {
        var convert_price = function(s) {
          if (s == null)
            return null;
          return Number(s * 100);
        };

        var myoptions = [];
        if (product.variants[v].option1) {
          myoptions.push(product.variants[v].option1);
        }
        if (product.variants[v].option2) {
          myoptions.push(product.variants[v].option2);
        }
        if (product.variants[v].option3) {
          myoptions.push(product.variants[v].option3);
        }

        var vprice = convert_price(product.variants[v].price);
        var vcompare_at = convert_price(product.variants[v].compare_at_price);

        var imageid = null;
        if (product.variants[v].image_id) {
          imageid = varimages[product.variants[v].image_id];
        }

        if (feproduct['price_min'] == null || feproduct['price_min'] > vprice) {
          feproduct['price_min'] = vprice;
        }

        if (feproduct['price_max']== null || feproduct['price_max']< vprice) {
          feproduct['price_max']= vprice;
        }
        if (vcompare_at != null && (feproduct['compare_at_price_min'] == null || feproduct['compare_at_price_min'] > vcompare_at)) {
          feproduct['compare_at_price_min'] = vcompare_at;
        }

        if (vcompare_at != null && (feproduct['compare_at_price_max'] == null || feproduct['compare_at_price_max'] < vcompare_at)) {
          feproduct['compare_at_price_max'] = vcompare_at;
        }
        if (product.variants[v].inventory_policy == "continue" || product.variants[v].inventory_quantity > 0 || product.variants[v].inventory_management == null) {
          feproduct['available'] = true;
        }

        var variant = {
          "id": product.variants[v].id,
          "title": product.variants[v].title,
          "option1": product.variants[v].option1,
          "option2": product.variants[v].option2,
          "option3": product.variants[v].option3,
          "sku": product.variants[v].sku,
          "requires_shipping": product.variants[v].requires_shipping,
          "taxable": product.variants[v].taxable,
          "featured_image": imageid,
          "available": true,
          "name": product.title + " - " + product.variants[v].title,
          "public_title": product.variants[v].title,
          "options": myoptions,
          "price": vprice,
          "weight": product.variants[v].weight,
          "compare_at_price": vcompare_at,
          "inventory_quantity": product.variants[v].inventory_quantity,
          "inventory_management": product.variants[v].inventory_management,
          "inventory_policy": product.variants[v].inventory_policy,
          "inventory_levels": product.variants[v].inventory_levels,
          "barcode": product.variants[v].barcode,
          "metafields": {}
        };

        if (product.variants[v].relevant)
          variant['relevant'] = product.variants[v].relevant;

        if (product.variants[v].metafields) {
          for (i = 0; i < product.variants[v].metafields.length; ++i){
            var metafield = product.variants[v].metafields[i];
            if (!variant['metafields'][metafield.namespace])
              variant['metafields'][metafield.namespace] = {}
            variant['metafields'][metafield.namespace][metafield.key] = metafield.value;
          }
        }
        variants.push(variant);
      } //endfor variants
      feproduct['variants'] = variants;
    }

    feproduct['compare_at_price_varies'] = true;
    if (feproduct['compare_at_price_max'] == feproduct['compare_at_price_min']) {
      feproduct['compare_at_price_varies'] = false;
    }

    feproduct['price_varies'] = true;
    if (feproduct['price_min'] == feproduct['price_max']) {
      feproduct['price_varies'] = false;
    }

    feproduct['price'] = feproduct['price_min'];
    feproduct['compare_at_price'] = feproduct['compare_at_price_min'];
    feproduct['split_id'] = product.split_id;
    if (product.relevancy != null)
      feproduct['relevancy'] = product.relevancy;

    return feproduct;
  };

  this.EventObject = function(type, arg1, arg2, arg3) {
    this.type = type;
    this.arg1 = arg1;
    this.arg2 = arg2;
    this.arg3 = arg3;

    this.name = function(){
      switch(this.type) {
        case 1:
          return "New Session";
        case 2:
          return "Logged In";
        case 3:
          return "Clicked Product";
        case 4:
          return "Added Product To Cart";
        case 5:
          return "Completed Order";
        default:
          return "unknown";
      }
    }
  };


  this._eventSink = null;

  this.eventSink = function(value) {
    if (arguments.length == 1) {
      this._eventSink = value;
      return this;
    }
    return this._eventSink;
  };

  this.sendEvents = function(events) {
    if (this.eventSink() == "spot") {
      // Don't send off search events (those are logged automatically).
      var hostname = window.Shopify ? "?hostname=" + encodeURIComponent(window.Shopify.shop) : "";
      return this.ajax("/events" + hostname, { type: "POST", dataType: "json", contentType: "json", data: { "events": this.map(this.grep(events, function(e) { return e.type; }), function(e) {
        var args = [e.arg1, e.arg2, e.arg3];
        if (typeof(e.arg1) == "object")
          args = Object.keys(e.arg1).sort().map(function(k) { return e.arg1[k] });
        return { type: e.type, arg0: args[0], arg1: args[1], arg2: args[2] };
      }) } }).fail(function(xhr){
        if (xhr.status == 400)
          spotAPI.eventSink(null);
      });
    } else if (this.eventSink() == "ga") {
      for (var i = 0; i < events.length; ++i) {
        var event = events[i];
        var params = event.arg1;
        if(params == null)
          params = {}
        if (typeof(window.gtag) != "undefined") {
          window.gtag('event', event.name(), params);
        } else if (typeof(window.ga) != "undefined") {
          var data = {
            'eventCategory': 'search',
            'eventAction': event.name(),
            'eventLabel': "" + Object.keys(params)[0] + ": " + Object.values(params)[0]
          }
          window.ga('send', 'event', data);
        }
      }
      return this.Deferred().resolve(null);
    } else {
      return this.Deferred().resolve(null);
    }
  };

  this.sendEvent = function(event) {
    this.sendEvents([event]);
  };

  // To be used in cases where we're navigating; i.e. a user clicks on a product. Send the actual event
  // on the next page, so we don't delay the user experience.
  this.deferredEvent = function(event) {
    var events = [];
    try {
      events = this.getStorage("spotDeferredEvents") || [];
    } catch (e) {
    }
    events.push(event)
    this.setStorage("spotDeferredEvents", events);
  };

  this.sendDeferredEvents = function() {
    var events = [];
    try {
      events = this.getStorage("spotDeferredEvents") || [];
    } catch (e) {
    }
    if (events.length > 0) {
      this.setStorage("spotDeferredEvents", []);
      return this.sendEvents(events.map(function(e) { return new spotAPI.EventObject(e.type, e.arg1, e.arg2, e.arg3); }));
    }
    var deferred = this.Deferred();
    deferred.resolve();
    return deferred;
  };


  this.getSizedImage = function(image, size) {
    if (!image)
      return null;
    if (typeof(image) == "object")
      image = image['src'];
    if (!/cdn\.shopify/.test(image))
      return image;
    if (/^\d+$/.test(size))
      size = size + "x" + size;
    return image.replace(/(?:_\d*x\d*)?(?=\.[^.]*(?:\?.*)?$)/, '_' + size);
  };

  this.getProductImage = function(product, variant, preferredImages) {
    return spotAPI.getFeaturedImage(product, variant, preferredImages);
  };

  this.formatMoney = function(money, format, currency) {
    if (currency) {
      money *= currency.rate;
      if (currency.rounding == "up")
        money = Math.ceil(money);
      if (currency.rounding == "down")
        money = Math.floor(money);
    }
    if (!format)
      format = "{{ amount }}";
    var dollars = "0", cents = "00";
    var result = format;
    if (money > 0) {
      money = "" + Math.round(money * 100);
      dollars = money.slice(0, -2);
      cents = money.slice(-2);
    }
    result = result.replace(/{{\s*amount_with_no_comma\s*}}/gi, dollars + "." + cents );
    result = result.replace(/{{\s*amount\s*}}/gi, dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ",") + "." + cents );
    result = result.replace(/{{\s*amount_no_decimals\s*}}/gi, dollars);
    result = result.replace(/{{\s*amount_with_comma_separator\s*}}/gi, dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ".") + "," + cents);
    result = result.replace(/{{\s*amount_no_decimals_with_comma_separator\s*}}/gi, dollars);
    result = result.replace(/{{\s*amount_with_apostrophe_separator\s*}}/gi, dollars.replace(/\B(?=(\d{3})+(?!\d))/g, "'") + "." + cents);
    return result;
  };

  this.getSizedImageDimensions = function(image, size) {
    if (!image)
      return null;
    var sizes = size.split("x");
    if (typeof(image) == "object" && sizes.length == 2 && image.width && image.height) {
      var max = Math.max(image.width, image.height);
      return [Math.round(sizes[0] * image.width / max), Math.round(sizes[1] * image.height / max)];
    }
    return null;
  };


  this.getProductTitle = function(product, relevantVariants) {
    if (Array.isArray(product)) {
      var titles = product.map(this.getProductTitle);
      var shortestLength = Math.min.apply(null, titles.map(function(e) { return e.length; }));
      var commonLength;
      for (commonLength = 1; commonLength < shortestLength; ++commonLength) {
        if (titles.filter(function(t) { return t.indexOf(titles[0].substr(0, commonLength)) == 0 }).length != titles.length) {
          commonLength = commonLength - 1;
          break;
        }
      }
      if (commonLength <= 2)
        return titles[0];
      return titles[0].substr(0, commonLength).replace(/[^\d\w]+$/, "");
    }
    if (typeof(relevantVariants) == 'object' && !Array.isArray(relevantVariants))
      relevantVariants = [relevantVariants];
    if (!relevantVariants && !product.variants)
      return product.title;
    var option1 = {};
    var option2 = {};
    var option3 = {};
    var i;
    if (relevantVariants) {
      for (i = 0; i < relevantVariants.length; ++i) {
        if (relevantVariants[i].option1 != null && relevantVariants[i].option1 != "")
          option1[relevantVariants[i].option1] = 1;
        if (relevantVariants[i].option2 != null && relevantVariants[i].option2 != "")
          option2[relevantVariants[i].option2] = 1;
        if (relevantVariants[i].option3 != null && relevantVariants[i].option3 != "")
          option3[relevantVariants[i].option3] = 1;
      }
    } else {
      var relevant = false;
      for (i = 0; i < product.variants.length; ++i) {
        if (product.variants[i].relevant)
          relevant = true;
      }
      for (i = 0; i < product.variants.length; ++i) {
        if (!relevant || product.variants[i].relevant) {
          if (product.variants[i].option1 != null && product.variants[i].option1 != "")
            option1[product.variants[i].option1] = 1;
          if (product.variants[i].option2 != null && product.variants[i].option2 != "")
            option2[product.variants[i].option2] = 1;
          if (product.variants[i].option3 != null && product.variants[i].option3 != "")
            option3[product.variants[i].option3] = 1;
        }
      }
    }
    var option1keys = Object.keys(option1);
    var option2keys = Object.keys(option2);
    var option3keys = Object.keys(option3);
    var extraElements = [];
    if (option1keys.length == 1 && option1keys[0] != "Default Title")
      extraElements.push(option1keys[0]);
    if (option2keys.length == 1)
      extraElements.push(option2keys[0]);
    if (option3keys.length == 1)
      extraElements.push(option3keys[0]);
    if (extraElements.length > 0)
      return product.title + " - " + extraElements.join(" / ");
    return product.title;
  };

  // Returns null if there is no particularly relevant list of variants.
  this.getRelevantVariants = function(product) {
    var products = Array.isArray(product) ? product : [product];
    var missingVariants = false;
    var relevant = products.filter(function(p) { return p.variants && p.variants.filter(function(v) { return v.relevant }).length > 0 }).length > 0;
    products.forEach(function(product) {
      if (product.variants) {
        var validOptions = [{}, {}, {}];
        if (product.options) {
          product.variants.filter(function(v) { return !relevant || v.relevant }).forEach(function(v) {
            validOptions[0][v.option1] = 1;
            if (product.options.length > 1)
              validOptions[1][v.option2] = 1;
            if (product.options.length > 2)
              validOptions[2][v.option3] = 1;
          });
          product.options.forEach(function(option, idx) {
            if (option.values) {
              option.values.filter(function(v) {
                if (!validOptions[idx][v])
                  missingVariants = true;
              });
            }
          });
        }
      }
    });
    if (!missingVariants)
      return null;
    return products.flatMap(function(p) { return p.variants.filter(function(v) { return !relevant || v.relevant }) });
  };
  this.getRelevantVariant = function(product) { return (this.getRelevantVariants(product) || [])[0]; }
  this.getProductPrice = function(product, variant) {
    if (Array.isArray(product))
      return Math.min.apply(this, product.map(function(p) { return spotAPI.getProductPrice(p); }));
    
    if (product.price) {
      if (variant)
        return variant.price / 100.0;
      return product.price / 100.0;
    }
    if (variant)
      return variant.price;
    return Math.min.apply(this, (this.getRelevantVariants(product) || product.variants).map(function(e) { return e.price / (product.original ? 100.0 : 1.0); }));
  };
  this.getProductCompareAtPrice = function(product, variant) {
    if (Array.isArray(product))
      return Math.min.apply(this, product.map(function(p) { return spotAPI.getProductCompareAtPrice(p); }));
    if (variant)
      return variant.compare_at_price;
    if (product.compare_at_price != null)
      return product.compare_at_price / 100.0;
    return Math.min.apply(this, (this.getRelevantVariants(product) || product.variants).map(function(e) { return e.compare_at_price / (product.original ? 100.0 : 1.0); }));
  };
  this.isSafari = function() {
    return /^((?!chrome|android).)*safari/i.test(window.navigator.userAgent);
  };
};

window.SpotAPI.DeferredObject = function() {
  // true is success, false is fail, null is pending.
  var deferred = this;
  this.state = null;
  this.payload = null;

  this._doneHandlers = [];
  this._failHandlers = [];
  this._alwaysHandlers = [];

  this.resolve = function() {
    deferred.payload = arguments;
    deferred.state = true;
    while (deferred._doneHandlers.length > 0) {
      var handler = deferred._doneHandlers.shift();
      handler.apply(deferred, deferred.payload);
    }
    return deferred;
  };
  this.reject = function() {
    deferred.payload = arguments;
    deferred.state = false;
    while (deferred._failHandlers.length > 0) {
      var handler = deferred._failHandlers.shift();
      handler.apply(deferred, deferred.payload);
    }
    return deferred;
  };
  this.done = function(handler) {
    if (deferred.state === true)
      handler.apply(deferred, deferred.payload);
    else
      deferred._doneHandlers.push(handler);
    return deferred;
  };
  this.fail = function(handler) {
    if (this.state === false)
      handler.apply(deferred, deferred.payload);
    else
      deferred._failHandlers.push(handler);
    return deferred;
  };
  this.always = function(handler) {
    if (this.state != null) {
      handler.apply(deferred, deferred.payload);
    } else {
      deferred._doneHandlers.push(handler);
      deferred._failHandlers.push(handler);
    }
    return deferred;
  };
  this.then = function(doneHandler, failHandler) {
    if (doneHandler)
      this.done(doneHandler);
    if (failHandler)
      this.fail(failHandler);
    return this;
  };
};

window.SpotAPI.DeferredObject.all = function() {
  var deferreds = new Array(arguments.length);
  for(var i = 0; i < deferreds.length; ++i) {
    deferreds[i] = arguments[i];
  }

  var allDeferred = new window.SpotAPI.DeferredObject();
  var results = [];
  var completedCount = 0;

  if (deferreds.length === 0) {
    allDeferred.resolve([]);
      return allDeferred;
  }
  deferreds.forEach(function(deferred, idx) {
      deferred.then(
          function(result) {
              results[idx] = result;
              completedCount++;
              if (completedCount === deferreds.length) {
                allDeferred.resolve(results);
              }
          },
          function(error) {
            allDeferred.reject(error); // Fail fast on first rejection
        }
      );
  });
  return allDeferred;
}

/***

SpotDOM Documentation
=====================

## Overview

### Who should use this class?

You should *not* use this class directly, most of the time; use `SpotDefault`. You should use this class only if you need to directly query or hook the internal state of Spot.

### What does this class do?

SpotDOM is a class that facilitates cleanly keeping *state* on a website using Spot. Whereas SpotAPI is a low-level querying interace that simply assists in constructing ajax requests,
SpotDOM helps assemble DOM elements, and maintain the state of what the user's looking at as they navigate through a product catalog. It intelligently keeps track of things like
page, active facets, current search, current sort order, stuff like that. It also provides easy ways to hook into it, and then render whatever DOM elements you think are appropriate.

### How do I use it? Give me a quickstart!

SpotDOM primarily uses the publisher-observer pattern to provide a layered architecutre. Ideally, most Spot-enhanced websites will have a 3-layered JS implementation. The first layer,
is the SpotAPI layer. This is the low level API. Above it, SpotDOM will sit, providing state, and helper functions. Above that, your own JS implementation will be sitting.
Primarily, what your implementation will do is register callback handlers with SpotDOM, which, depending on what's changing, will let you then render whatever HTML you'd like to render, or
affect the browser in whatever way you want. By default, SpotDOM does *not* affect the DOM, or the user's browser in *any* way; all this is done through callback handlers.

Essentially you'll want to instantiate this object like so:

    // Filters Integration
    spotAPI = spotAPI || new SpotAPI({{ shop.metafields.esafilters.clusters | json }});
    // Useful if multiple domains/domain switches occur.
    spotAPI.defaultQuery(spotAPI.s().hostname("{{ shop.permanent_domain }}"));
    window.spotDOM = window.spotDOM || new SpotDOM(spotAPI);

Below that, you can provide a number of options to the system; the easiest way to get started, is to ask for a basic facet pane, with the facets based on options that have been set up by the Spot back-end control panel.

    var options = {
      {% if template contains 'search' %}
        sections: {% if shop.metafields.esafilters.search-faceting %}{{ shop.metafields.esafilters.search-faceting | json }}{% else %}{% if shop.metafields.esafilters.general-faceting %}{{ shop.metafields.esafilters.general-faceting | json  }}{% else %}[]{% endif %}{% endif %}
      {% else %}
        sections: {% if collection and collection.metafields.esafilters.collections-faceting %}{{ collection.metafields.esafilters.collections-faceting | json  }}{% else %}{% if shop.metafields.esafilters.general-faceting %}{{ shop.metafields.esafilters.general-faceting | json  }}{% else %}[]{% endif %}{% endif %}
      {% endif %}
    };

    var sampleFacetPane = new window.spotDefault.FacetPane();
    for (var i = 0; i < sections.length; ++i) {
      if (sections[i].active)
        window.spotDOM.addFacet(new spotDOM.Facet(sections[i])).init();
    }
    document.body.appendChild(sampleFacetPane.element);

This will pop a facet pane onto your page, with generic HTML. You should inspect this function in this file if you really want to create your own facet pane with fancy functionality; this function doesn't do any magic, and only
uses the normal callback interface.

Alternatively, if you want to just test with how things COULD be, you can simply populates the facets with something static:

    options = {
      sections: [{"type":"tag","name":"Gender",values:[{"name":"Men","value":"Man"},{"name":"Women","value":"Woman"}]}, "Product Type", "Size",{"type":"vendor","name":"Vendor"},{"type":"price","name":"Price","values":[{"name":"Under 10$","value":"-10"},{"name":"10-20 $","value":"10-20"},{"name":"20-30 $","value":"20-30"},{"name":"30-40 $","value":"30-40"},{"name":"40-50 $","value":"40-50"},{"name":"50-60 $","value":"50-60"},{"name":"Over 60$","value":"60+"}]}]
    };

You also need to tell Spot how you'd want to render your products, so you'll want to register the 'products' callback handler. Here's a sample implementation.

    window.spotDOM.products(function(products) {
      $('.productgrid--items').empty();
      for (var i = 0; i < products.length; ++i) {
        var product = products[i];
        var productElement = $("<article class='productgrid--item imagestyle--natural productitem--emphasis' data-product-item tabindex='1'>\
        <div class='productitem' data-product-item-content>\
          <a class='productitem--image-link' href='" + product.url + "'>\
          <figure class='productitem--image'>\
          " + (product.featured_image ? "<img src='" + this.getSizedImage(product.featured_image, "275x275") + "'/>" : '<svg class="placeholder--image" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5"><path d="M324.5 212.7H203c-1.6 0-2.8 1.3-2.8 2.8V308c0 1.6 1.3 2.8 2.8 2.8h121.6c1.6 0 2.8-1.3 2.8-2.8v-92.5c0-1.6-1.3-2.8-2.9-2.8zm1.1 95.3c0 .6-.5 1.1-1.1 1.1H203c-.6 0-1.1-.5-1.1-1.1v-92.5c0-.6.5-1.1 1.1-1.1h121.6c.6 0 1.1.5 1.1 1.1V308z"></path><path d="M210.4 299.5H240v.1s.1 0 .2-.1h75.2v-76.2h-105v76.2zm1.8-7.2l20-20c1.6-1.6 3.8-2.5 6.1-2.5s4.5.9 6.1 2.5l1.5 1.5 16.8 16.8c-12.9 3.3-20.7 6.3-22.8 7.2h-27.7v-5.5zm101.5-10.1c-20.1 1.7-36.7 4.8-49.1 7.9l-16.9-16.9 26.3-26.3c1.6-1.6 3.8-2.5 6.1-2.5s4.5.9 6.1 2.5l27.5 27.5v7.8zm-68.9 15.5c9.7-3.5 33.9-10.9 68.9-13.8v13.8h-68.9zm68.9-72.7v46.8l-26.2-26.2c-1.9-1.9-4.5-3-7.3-3s-5.4 1.1-7.3 3l-26.3 26.3-.9-.9c-1.9-1.9-4.5-3-7.3-3s-5.4 1.1-7.3 3l-18.8 18.8V225h101.4z"></path><path d="M232.8 254c4.6 0 8.3-3.7 8.3-8.3s-3.7-8.3-8.3-8.3-8.3 3.7-8.3 8.3 3.7 8.3 8.3 8.3zm0-14.9c3.6 0 6.6 2.9 6.6 6.6s-2.9 6.6-6.6 6.6-6.6-2.9-6.6-6.6 3-6.6 6.6-6.6z"></path></svg>') + "\
          </figure>\
        </a>\
        <div class='productitem--info'>\
          <div class='productitem--price'>\
          <div class='price--compare-at visible' data-price-compare-at=''>\
          <span class='price--spacer'></span>\
          </div>\
          <div class='price--main' data-price=''>\
          <span class='money' data-currency-usd='" + this.formatMoney(product.price/100) + "' data-currency='USD'>" + this.formatMoney(product.price/100) + "</span>\
          </div>\
          </div>\
          <h2 class='productitem--title'>\
          <a href='" + product.url + "' tabindex='1'>\
          " + (options['split'] && options['split'] == "none" ? product.title : this.getProductTitle(product)) + "\
          </a>\
          </h2>\
          <div class='productitem--description'>\
          <p>" + product.description + "</p>\
          <a href='" + product.url + "' class='productitem--link'>\
          View full details\
          </a>\
          </div>\
        </div>\
        </div>\
        </article>");
        $('.productgrid--items').append(productElement);
      }
    })

This function is called whenever a query is made. As you can see from the sample above, an array of products is given, and then turned into HTML. Speaking of, if you're using paging as your pagination mechanism, you'll
probably want change the pagination listing whenever query count/page changes. For that, you simply hook whenver the count is refreshed, and redraw pagination.

    window.spotDOM.count(function(count) {
      var elements = this.pagedPaginationHelper({ count: count });
      $('.pagination--inner').empty();
      for (var i = 0; i < elements.length; ++i)
        $('.pagination--inner').append(elements[i]);
    })

You can also write out a "we're searching for X" if you're using spellcheck; this can be rendered like so: (TODO: this regex shouldn't be necessary).

    window.spotDOM.spellCheck(function(spellCheck){
      if (spellCheck.original.replace(/(^\s+|\s+$|[^A-Za-z0-9]+)/g, "").toLowerCase() != spellCheck.corrected.replace(/(^\s+|\s+$|[^A-Za-z0-9]+)/g, "").toLowerCase()) {
        $('#spelling-correction').show();
        $('#spelling-correction').html("<p class='corrected'>Showing results for <a href='/search?q=" + encodeURIComponent(spellCheck.corrected) + "'>" + $.map(spellCheck.words, function(e) { if (e.corrected != e.original) { return "<b>" + e.corrected + "</b>"; } return e.corrected; }).join(" ") + "</a>.</p>");
      } else {
        $('#spelling-correction').hide();
      }
    })

You'll also probably want to keep the query string up to date so that it can be interpreted by the system. If you want to do that, all you need to do is the following:

    window.spotDOM.query(function() {
      window.spotDOM.updateQueryString();
    });

That will update the query string for you. Most implementation of Spot will also generally want to make a new query every time any propertry of the search changes, like sort, page, etc... In those cases, all you need
to do is do something like the following:

    window.spotDOM.sort(function(sortOrder) {
      this.query();
    }).collection(function(collection) {
      this.query();
    }).search(function(search) {
      this.query();
    }).split(function(split) {
      this.query();
    }).page(function(page) {
      this.query();
    }).paginate(function(resultsPerPage) {
      this.query();
    });

This will make it so whenever any property changes, we automatically re-perform a query. The automatic facet panel does this automatically by default, whenever you click a facet. However, if you wanted to make an "apply" button
where things would only apply when you clicked that button; you'd simply remove these declarations, and in your facet pane, also remove any handler that calls .query(), and you'd suddenly have a facet panel/page that only queries
when *you* want it to.

There are probably also a few other things you want to do, like hook up a select dropdown that handles sort order (can easily be done by something like):

    var sortDropdown = $('#product_grid_sort');
    sortDropdown.empty();
    window.spotDOM.addSortOrder(function(sortOrder) {
      sortDropdown.append("<option id='" + sortOrder.getUniqueId() + "' value='" + sortOrder.getUniqueId() + "'>" + sortOrder.getName() + "</option>");
      if (sortDropdown.find("option").length > 0)
        sortDropdown.show();
    }).removeSortOrder(function(sortOrder) {
      $('#' + sortOrder.getUniqueId()).remove();
      if (sortDropdown.find("option").length == 0)
        sortDropdown.hide();
    });
    sortDropdown.unbind('change');
    sortDropdown.change(function() { window.spotDOM.sort($(this).val() == "best" ? "bestselling" : $(this).val()); });
    window.spotDOM.initDefaultSortOrders();
    window.spotDOM.sort(sortDropdown.val(), false);

You can see a more exhaustive list of individual methods below.

**/

window.SpotDOM = window.SpotDOM || function(spotAPI, options) {
  var spotDOM = this;
  options = options || {};
  this.spotAPI = spotAPI;
  if (!spotAPI)
    throw "Requires SpotAPI. Please pass a copy of the API to SpotDOM's constructor.";
  this.log = function(message) { if (console != undefined) console.log(message); };
  this.map = spotAPI.map;
  this.grep = spotAPI.grep;



  if (options['location']) {
    this.location = typeof(options.location) == 'object' ? options.location : new URL((typeof(options.location) == 'string' ? options.location : '/'), window.location);
    this.history = options.history;
  } else {
    this.location = window.location;
    this.history = options.history || window.history;
  }
  if (options['storage'] != null && !options['storage']) {
    this.setStorage = function() { };
    this.getStorage = function() { return null; };
  } else {
    this.getStorage = spotAPI.getStorage;
    this.setStorage = spotAPI.setStorage;
  }


  this.getUrlVars = function(href) {
    var vars = {}, hash;
    var href = href || this.location.href;
    href = href.replace(/#.*?$/, '');
    if (href.indexOf('?') == -1 || href.indexOf('?') == href.length-1)
      return {};
    var hashes = href.slice(this.location.href.indexOf('?') + 1).split('&');
    for(var i = 0; i < hashes.length; i++) {
      hash = hashes[i].split('=');
      if (vars[hash[0]] == null)
        vars[hash[0]] = decodeURIComponent(hash[1] != null ? hash[1].replace(/\+/g, ' ') : '');
      else {
        if(typeof vars[hash[0]] == "string")
          vars[hash[0]] = [vars[hash[0]]];
        vars[hash[0]].push(decodeURIComponent(hash[1] != null ? hash[1].replace(/\+/g, ' ') : ''));
      }

    }
    return vars;
  };

  this.encodeUrlVars = function(hash) { return "?" + spotAPI.encodeUrlVars(hash); }


  this.defaultDiacriticsRemovalMap = [
    {'base':'a', 'letters':'\u0061\u24D0\uFF41\u1E9A\u00E0\u00E1\u00E2\u1EA7\u1EA5\u1EAB\u1EA9\u00E3\u0101\u0103\u1EB1\u1EAF\u1EB5\u1EB3\u0227\u01E1\u00E4\u01DF\u1EA3\u00E5\u01FB\u01CE\u0201\u0203\u1EA1\u1EAD\u1EB7\u1E01\u0105\u2C65\u0250'},
    {'base':'aa','letters':'\uA733'},
    {'base':'ae','letters':'\u00E6\u01FD\u01E3'},
    {'base':'ao','letters':'\uA735'},
    {'base':'au','letters':'\uA737'},
    {'base':'av','letters':'\uA739\uA73B'},
    {'base':'ay','letters':'\uA73D'},
    {'base':'b', 'letters':'\u0062\u24D1\uFF42\u1E03\u1E05\u1E07\u0180\u0183\u0253'},
    {'base':'c', 'letters':'\u0063\u24D2\uFF43\u0107\u0109\u010B\u010D\u00E7\u1E09\u0188\u023C\uA73F\u2184'},
    {'base':'d', 'letters':'\u0064\u24D3\uFF44\u1E0B\u010F\u1E0D\u1E11\u1E13\u1E0F\u0111\u018C\u0256\u0257\uA77A'},
    {'base':'dz','letters':'\u01F3\u01C6'},
    {'base':'e', 'letters':'\u0065\u24D4\uFF45\u00E8\u00E9\u00EA\u1EC1\u1EBF\u1EC5\u1EC3\u1EBD\u0113\u1E15\u1E17\u0115\u0117\u00EB\u1EBB\u011B\u0205\u0207\u1EB9\u1EC7\u0229\u1E1D\u0119\u1E19\u1E1B\u0247\u025B\u01DD'},
    {'base':'f', 'letters':'\u0066\u24D5\uFF46\u1E1F\u0192\uA77C'},
    {'base':'g', 'letters':'\u0067\u24D6\uFF47\u01F5\u011D\u1E21\u011F\u0121\u01E7\u0123\u01E5\u0260\uA7A1\u1D79\uA77F'},
    {'base':'h', 'letters':'\u0068\u24D7\uFF48\u0125\u1E23\u1E27\u021F\u1E25\u1E29\u1E2B\u1E96\u0127\u2C68\u2C76\u0265'},
    {'base':'hv','letters':'\u0195'},
    {'base':'i', 'letters':'\u0069\u24D8\uFF49\u00EC\u00ED\u00EE\u0129\u012B\u012D\u00EF\u1E2F\u1EC9\u01D0\u0209\u020B\u1ECB\u012F\u1E2D\u0268\u0131'},
    {'base':'j', 'letters':'\u006A\u24D9\uFF4A\u0135\u01F0\u0249'},
    {'base':'k', 'letters':'\u006B\u24DA\uFF4B\u1E31\u01E9\u1E33\u0137\u1E35\u0199\u2C6A\uA741\uA743\uA745\uA7A3'},
    {'base':'l', 'letters':'\u006C\u24DB\uFF4C\u0140\u013A\u013E\u1E37\u1E39\u013C\u1E3D\u1E3B\u017F\u0142\u019A\u026B\u2C61\uA749\uA781\uA747'},
    {'base':'lj','letters':'\u01C9'},
    {'base':'m', 'letters':'\u006D\u24DC\uFF4D\u1E3F\u1E41\u1E43\u0271\u026F'},
    {'base':'n', 'letters':'\u006E\u24DD\uFF4E\u01F9\u0144\u00F1\u1E45\u0148\u1E47\u0146\u1E4B\u1E49\u019E\u0272\u0149\uA791\uA7A5'},
    {'base':'nj','letters':'\u01CC'},
    {'base':'o', 'letters':'\u006F\u24DE\uFF4F\u00F2\u00F3\u00F4\u1ED3\u1ED1\u1ED7\u1ED5\u00F5\u1E4D\u022D\u1E4F\u014D\u1E51\u1E53\u014F\u022F\u0231\u00F6\u022B\u1ECF\u0151\u01D2\u020D\u020F\u01A1\u1EDD\u1EDB\u1EE1\u1EDF\u1EE3\u1ECD\u1ED9\u01EB\u01ED\u00F8\u01FF\u0254\uA74B\uA74D\u0275'},
    {'base':'oi','letters':'\u01A3'},
    {'base':'ou','letters':'\u0223'},
    {'base':'oo','letters':'\uA74F'},
    {'base':'p','letters':'\u0070\u24DF\uFF50\u1E55\u1E57\u01A5\u1D7D\uA751\uA753\uA755'},
    {'base':'q','letters':'\u0071\u24E0\uFF51\u024B\uA757\uA759'},
    {'base':'r','letters':'\u0072\u24E1\uFF52\u0155\u1E59\u0159\u0211\u0213\u1E5B\u1E5D\u0157\u1E5F\u024D\u027D\uA75B\uA7A7\uA783'},
    {'base':'s','letters':'\u0073\u24E2\uFF53\u00DF\u015B\u1E65\u015D\u1E61\u0161\u1E67\u1E63\u1E69\u0219\u015F\u023F\uA7A9\uA785\u1E9B'},
    {'base':'t','letters':'\u0074\u24E3\uFF54\u1E6B\u1E97\u0165\u1E6D\u021B\u0163\u1E71\u1E6F\u0167\u01AD\u0288\u2C66\uA787'},
    {'base':'tz','letters':'\uA729'},
    {'base':'u','letters': '\u0075\u24E4\uFF55\u00F9\u00FA\u00FB\u0169\u1E79\u016B\u1E7B\u016D\u00FC\u01DC\u01D8\u01D6\u01DA\u1EE7\u016F\u0171\u01D4\u0215\u0217\u01B0\u1EEB\u1EE9\u1EEF\u1EED\u1EF1\u1EE5\u1E73\u0173\u1E77\u1E75\u0289'},
    {'base':'v','letters':'\u0076\u24E5\uFF56\u1E7D\u1E7F\u028B\uA75F\u028C'},
    {'base':'vy','letters':'\uA761'},
    {'base':'w','letters':'\u0077\u24E6\uFF57\u1E81\u1E83\u0175\u1E87\u1E85\u1E98\u1E89\u2C73'},
    {'base':'x','letters':'\u0078\u24E7\uFF58\u1E8B\u1E8D'},
    {'base':'y','letters':'\u0079\u24E8\uFF59\u1EF3\u00FD\u0177\u1EF9\u0233\u1E8F\u00FF\u1EF7\u1E99\u1EF5\u01B4\u024F\u1EFF'},
    {'base':'z','letters':'\u007A\u24E9\uFF5A\u017A\u1E91\u017C\u017E\u1E93\u1E95\u01B6\u0225\u0240\u2C6C\uA763'}
  ];
  this.diacriticsMap = {};
  for (var i = 0; i < this.defaultDiacriticsRemovalMap.length; i++) {
    var letters = this.defaultDiacriticsRemovalMap[i].letters;
    for (var j = 0; j < letters.length ; j++){
      this.diacriticsMap[letters[j]] = this.defaultDiacriticsRemovalMap[i].base;
    }
  }

  this.removeDiacritics = function(str) {
    return str.replace(/[^\u0020-\u007E]/g, function(a){
      return spotDOM.diacriticsMap[a] || a;
    });
  };

  this.handleize = function(handle) {
    if (handle != null)
      return this.removeDiacritics(handle.toLowerCase()).replace(/\s+/g, '-').replace(/[^a-z0-9_]+/g, '-').replace(/(^\-|\-$)/g, "");
    return null;
  };

  this.normalizeSearch = function(search) {
    return spotDOM.removeDiacritics(search.toLowerCase().replace(/^\s+|\s+$/, ""));
  };

  this.scalarField = function(object, field, defaultValue, options) {
    object['_' + field + "Listeners"] = [];
    object[field] = function(variable, callHandlers) {
      if (typeof(variable) == 'function') {
        object['_' + field + "Listeners"].push(variable);
        if (callHandlers)
          variable.call(object, object['_' + field]);
        return object;
      } else {
        if (arguments.length > 0) {
          if (arguments.length == 1)
            callHandlers = true;
          if (options && options['preset'])
            variable = options['preset'].call(object, variable);
          object['_' + field] = variable;
          if (callHandlers) {
            for (var i = 0; i < object['_' + field + "Listeners"].length; ++i)
              object['_' + field + "Listeners"][i].call(object, variable);
          }
          return object;
        }
        return object['_' + field];
      }
    };
    object['_' + field] = (options && options['preset']) ? options['preset'].call(object, defaultValue) : defaultValue;
  };

  this.arrayField = function(object, field, defaultValue, defaultPlural) {
    var plural = defaultPlural || field + "s";
    object['_' + plural] = defaultValue || [];
    var methods = [
      "add" + field.charAt(0).toUpperCase() + field.slice(1),
      "add" + plural.charAt(0).toUpperCase() + plural.slice(1),
      "remove" + field.charAt(0).toUpperCase() + field.slice(1),
      "remove" + plural.charAt(0).toUpperCase() + plural.slice(1)
    ];
    for (var idx = 0; idx < methods.length; ++idx) {
      var method = methods[idx];
      object['_' + method + "Listeners"] = [];
      object[method] = (function(method, idx) { return function(variable, callListeners) {
        var i, j, targetedMethod;
        if (typeof(variable) == 'function') {
          object['_' + method + "Listeners"].push(variable);
          if (callListeners) {
            if (idx == 0) {
              for (i = 0; i < object['_' + plural].length; ++i)
                variable.call(object, object['_' + plural][i]);
            } else
              variable.call(object, object['_' + plural]);
          }
          return object;
        } else {
          if (arguments.length == 0)
            throw "Requires an argument.";
          if (idx == 0 || idx == 1) {
            if (idx == 1)
              object['_' + plural] = object['_' + plural].concat(variable);
            else
              object['_' + plural].push(variable);
          } else {
            if (idx == 2)
              object['_' + plural] = object['_' + plural].filter(function(e) { return e != variable; });
            else {
              object['_' + plural] = object['_' + plural].filter(function(e) { return !variable.includes(e); });
            }
          }
          for (i = 0; i < object['_' + method + "Listeners"].length; ++i)
            object['_' + method + "Listeners"][i].call(object, variable);
          if (idx == 1 || idx == 3) {
            targetedMethod = methods[idx - 1];
            for (i = 0; i < object['_' + targetedMethod + "Listeners"].length; ++i) {
              for (j = 0; j < variable.length; ++j)
                object['_' + targetedMethod + "Listeners"][i].call(object, variable[j]);
            }
          } else {
            targetedMethod = methods[idx + 1];
            for (i = 0; i < object['_' + targetedMethod + "Listeners"].length; ++i)
              object['_' + targetedMethod + "Listeners"][i].call(object, [variable]);
          }
          return variable;
        }
      } })(method, idx);
    }
    object['_' + plural + "Listeners"] = [];
    object[plural] = function(variable, callListeners) {
      if (typeof(variable) == 'function') {
        object['_' + field + "sListeners"].push(variable);
        if (callListeners)
          variable.call(object, object['_' + plural]);
        return object;
      } else if (arguments.length > 0) {
        if (arguments.length == 1)
          callListeners = true;
        var oldArray = object['_' + plural];
        object['_' + plural] = variable;
        if (callListeners) {
          var oldMap = new Map();
          var newMap = new Map();
          for (var i = 0; i < oldArray.length; ++i)
            oldMap.set(oldArray[i], true);
          for (var i = 0; i < variable.length; ++i)
            newMap.set(variable[i], true);
          var removedElements = oldArray.filter(function(o) { return !newMap.has(o); });
          var addedElements = variable.filter(function(n) { return !oldMap.has(n); });

          for (var i = 0; i < object['_' + methods[2] + "Listeners"].length; ++i) {
            for (var j = 0; j < removedElements.length; ++j)
              object['_' + methods[2] + "Listeners"][i].call(object, removedElements[j]);
          }
          for (var i = 0; i < object['_' + methods[3] + "Listeners"].length; ++i)
            object['_' + methods[3] + "Listeners"][i].call(object, removedElements);

          for (var i = 0; i < object['_' + methods[0] + "Listeners"].length; ++i) {
            for (var j = 0; j < addedElements.length; ++j)
              object['_' + methods[0] + "Listeners"][i].call(object, addedElements[j]);
          }
          for (var i = 0; i < object['_' + methods[1] + "Listeners"].length; ++i)
            object['_' + methods[1] + "Listeners"][i].call(object, addedElements);

          for (var i = 0; i < object['_' + plural + "Listeners"].length; ++i)
            object['_' + plural + "Listeners"][i].call(object, variable);
        }
        return object;
      }
      return object['_' + plural];
    };
  };

  this.removeFieldListener = function(object, field, listener) {
    var fields = [
      '_' + field + 'Listeners',
      "add" + field.charAt(0).toUpperCase() + field.slice(1) + "Listeners",
      "add" + field.charAt(0).toUpperCase() + field.slice(1) + "sListeners",
      "remove" + field.charAt(0).toUpperCase() + field.slice(1) + "Listeners",
      "remove" + field.charAt(0).toUpperCase() + field.slice(1) + "sListeners"
    ];
    for (var i = 0; i < fields.length; ++i) {
      if (object[fields[i]])
        object[fields[i]] = object[fields[i]].filter(function(e) { return listener && e != listener; });
    }
  };

  this.listeners = function(object, method) {
    object['_' + method + "Listeners"] = [];
    object[method] = function(variable) {
      if (typeof(variable) == 'function') {
        object['_' + method + "Listeners"].push(variable);
        return object;
      } else {
        var results = [];
        for (var i = 0; i < object['_' + method + "Listeners"].length; ++i)
          results.push(object['_' + method + "Listeners"][i].apply(object, arguments));
        return results;
      }
    };
  };
  this.listener = function(object, method, defaultFunction) {
    object['_' + method + "Listener"] = defaultFunction;
    object[method] = function(variable) {
      if (typeof(variable) == 'function') {
        object['_' + method + "Listener"] = variable;
        return object;
      } else if (object['_' + method + "Listener"]) {
        return object['_' + method + "Listener"].apply(object, arguments);
      }
    };
  };


  this["_queryListeners"] = [];
  this["_queryWaiter"] = null;
  this["_queryRequests"] = 0;

  this.query = function(callback) {
    var deferred;
    if (callback && typeof(callback) == 'function') {
      this['_' + "queryListeners"].push(callback);
      return this;
    }

    // Set debug mode to true, if we make a query with the debugger open, and debugMode is not explicitly set to false.
    if (this.debugMode() == null && this.isDebuggerOpen())
      this.debugMode(true);

    ++this["_queryRequests"];
    if (this["_queryWaiter"])
      return this["_queryWaiter"];
    this["_queryWaiter"] = spotAPI.Deferred();

    this.beginQuery();
    var query = this.generateQuery();
    this.queryCount(this.queryCount() + 1);
    if (this.shopifyMode() == "shopify") {
      // Examine the inner attributes here, and try to finagle a URL that'll work.
      var search;
      var fieldParse, recursiveParse;
      var collection = query.innerAttributes['collection'];
      if (query.innerAttributes['search']) {
        if (typeof(query.innerAttributes['search']) == "object") {
          search = query.innerAttributes['search']["query"];
        } else {
          search = query.innerAttributes['search'];
        }
      }
      var tags = [];
      // var vendor = null;
      // var product_type = null;
      var page = parseInt(query.innerAttributes['page'] || 1);
      var sort = query.innerAttributes['sort'];

      var url;
      var vars = spotDOM.generateQueryVariables();
      if (!collection) {
        url = "/search/"

        fieldParse = function(type, field, value) {
          var actualValue;
          if (typeof(value) == "object") {
            var keys = Object.keys(value);
            if (keys.length > 0) {
              if (keys[0] != "==")
                return;
              actualValue = value[keys[0]];
            }
          } else {
            actualValue = value;
          }
          if (field == "product_type")
            return "product_type:\"" + actualValue + "\""
          if (field == "vendor")
            return "vendor:\"" + actualValue + "\""
          if (field == "tag" || field == "tags")
            return "tag:\"" + actualValue + "\""
          if (field == "sku")
            return "variants.sku:\"" + actualValue + "\""
          if (field == "barcode")
            return "variants.barcode:\"" + actualValue + "\""
        }

        recursiveParse = function(type, query) {
          var results = [];
          for (var i = 0; i < query.length; ++i) {
            if (typeof(query[i]) == "object") {
              var keys = Object.keys(query[i]);
              if (keys.length > 0) {
                if (keys[0] == "and" || keys[0] == "or")
                  results.push(spotDOM.grep(recursiveParse(keys[0], query[i][keys[0]]), function(e) { return e && e != ''; }).join(" " + keys[0].toUpperCase() + " "));
                else if (keys[0] == "facets")
                  results.push(spotDOM.grep(recursiveParse("and", query[i][keys[0]]), function(e) { return e && e != ''; }).join(" AND "));
                else if (keys[0] == "product_type" || keys[0] == "vendor" || keys[0] == "tag" || keys[0] == "tags" || keys[0] == "sku" || keys[0] == "barcode")
                  results.push(fieldParse(type, keys[0], query[i][keys[0]]));
              }
            }
          }
          return results;
        }
        var searchQuery = spotAPI.map(spotAPI.grep(recursiveParse("AND", query.innerQuery), function(e) { return e && e != ""; }), function(e) { return "(" + e + ")"; }).join(" AND ");
        // This is a double parentheses on purpose, to distinguish from legitimate search groupings.
        vars['q'] = (search ? search : "") + (searchQuery && searchQuery != "()" ? (search ? " AND (" : "(") + searchQuery + ")" : "");
      } else {
        url = "/collections/" + collection;


        fieldParse = function(field, value) {
          var actualValue;
          if (typeof(value) == "object") {
            var keys = Object.keys(value);
            if (keys.length > 0) {
              if (keys[0] != "==")
                return;
              actualValue = value[keys[0]];
            }
          } else {
            actualValue = value;
          }
          /* if (field == "product_type")
            product_type = actualValue;
          if (field == "vendor")
            vendor = actualValue; */
          if (field == "tag" || field == "tags")
            tags.push(actualValue);
          return actualValue;
        }

        recursiveParse = function(type, query) {
          for (var i = 0; i < query.length; ++i) {
            if (typeof(query[i]) == "object") {
              var keys = Object.keys(query[i]);
              if (keys.length > 0) {
                if (keys[0] == "and" || keys[0] == "or")
                  recursiveParse(keys[0], query[i][keys[0]]);
                else if (keys[0] == "facets")
                  recursiveParse("and", query[i][keys[0]]);
                else if (/*keys[0] == "product_type" || keys[0] == "vendor" || */keys[0] == "tag" || keys[0] == "tags") {
                  if (fieldParse(keys[0], query[i][keys[0]]))
                    break;
                }
              }
            }
          }
        }
        recursiveParse("AND", query.innerQuery);
        if (tags.length > 0)
          url += "/" + spotAPI.map(tags, function(e) { return encodeURI(spotDOM.handleize(e)) }).join("+");
        /* if (vendor)
          vars['vendor'] = vendor;
        if (product_type)
          vars['type'] = product_type;*/
      }

      if (!this.isUsingShopifyDefaultSort())
        vars['sort_by'] = this.sort().getShopifySortOrder();

      var queryString = spotAPI.encodeUrlVars(vars);
      if (queryString.length > 0)
        queryString = "?" + queryString;
      var target = this.location.origin + url + queryString;
      // We do this because Shopify improperly encodes parameters.
      if (decodeURIComponent(this.location.href) != decodeURIComponent(target)) {
        this.location.href = target;
        this.endQuery();
        return spotAPI.Deferred();
      } else {
        deferred = this["_queryWaiter"];
        this["_queryWaiter"] = null;
        this.endQuery();
        deferred.resolve();
        return deferred;
      }
    } else {
      deferred = spotAPI.Deferred();
      (spotDOM.shopifyMode() == "storefront" ? spotDOM.storefront().products({ 
        collection: query.innerAttributes.collection,
        search: query.innerAttributes.search != null && typeof(query.innerAttributes.search) == 'object' ? query.innerAttributes.search.query : query.innerAttributes.search,
        facets: spotDOM.facets(),
        paginate: spotDOM.paginate(),
        page: spotDOM.page(),
        sort: spotDOM.sort().getUniqueId(),
        locale: spotDOM.locale(),
        market: spotDOM.market()
      }) : query.e()).done(function(products, count, options) {
        if (count != null)
          spotDOM.count(count);
        if (options['options'])
          spotDOM.updateFacets(options['options'], options['boundaries']);
        spotDOM.updateStorefrontProducts(products).done(function(products) {
          spotDOM.products(products);
          if (options.spellCheck)
            spotDOM.spellCheck(options['spellCheck']);
          else
            spotDOM.spellCheck(null);
          if (options['warnings'] && options['warnings'].length > 0) {
            for (var i = 0; i < options['warnings'].length; ++i)
              console.log("Spot Warning: " + options['warnings'][i]);
            if (spotDOM.debugMode())
              deferred.reject({ status: 400, responseText: options['warnings'].join("\n") });
          }
          deferred.resolve(products, count, options);
        });
      }).fail(function(xhr) {
        deferred.reject(xhr);
      });

      deferred.fail(function() {
        spotDOM.fail.apply(spotDOM, arguments);
      });
    }

    for (i = 0; i < spotDOM["_queryListeners"].length; ++i) {
      var internal = i;
      deferred.done((function(internal) { return function() {
        spotDOM["_queryListeners"][internal].apply(spotDOM, arguments);
      } })(internal));
    }
    var queryWaiter = spotDOM["_queryWaiter"];
    deferred.done(function() {
      spotDOM.endQuery();
      var deferred = spotDOM["_queryWaiter"];
      var requests = spotDOM["_queryRequests"];
      spotDOM["_queryWaiter"] = null;
      spotDOM["_queryRequests"] = 0;
      if (requests == 1)
        deferred.resolve.apply(deferred, arguments);
      else
        spotDOM.query().done(function() { deferred.resolve.apply(deferred, arguments); }).fail(function() { deferred.reject.apply(deferred, arguments); });
    }).fail(function(xhr) {
      if (spotDOM.failBehavior() && (spotDOM.failBehavior() == "shopify" || spotDOM.failBehavior() == "storefront") && xhr.status != 400) {
        spotDOM.shopifyMode(spotDOM.failBehavior());
        spotDOM["_queryWaiter"] = null;
        spotDOM["_queryRequests"] = 0;
        spotDOM.query();
      } else {
        spotDOM["_queryWaiter"].reject();
      }
    });
    return queryWaiter;
  };
  this.isQuerying = function() { return this["_queryRequests"] > 0; }



  var vars = this.getUrlVars();



  /**

  ## Sub-Classes

  The SpotDOM class uses several sub-classes to keep track of everything; the three primary things that are complex enough to warrant classes are [Facets](#facets), [Facet Values](#facet-values), and [Sort Orders](#sort-orders).

  ### Facets

  Facets are objects that represent a particular category of values. As an example, a Facet would be "Color". A facet, when instantiated can take either a simple string, or an object describing what it represents. It can be in one of the following forms:

  #### new

      new Facet("product_type")

      new Facet("Color")

      new Facet({"option": "Color"})

      new Facet({"type": "price", "boundary": [null, null] })

      new Facet({"product-metafield": {"mynamespace":"mykey"}})

      new Facet({"type": "option", "option": "Color", "values": ["Red", "Blue"]})

      new Facet({"type": "option", "option": "Color", "values": [{"name": "Reddish", "value": "Red"},{"name": "Blueish", "value": "Blue"}]})

      new Facet({"type": "option", "option": "Color", "values": [{"name": "Reddish", "value": ["Red", "Magenta"]},{"name": "Blueish", "value": ["Blue", "Cyan"]}]})


  Once constructed, these are generally passed to `.addFacet` on the full DOM object, so that the system will keep track of which facets you'd like to use. After being added for the first time, `.init` should be called on the facet to initialize it. See the [SpotDefault](#spotdefault-documentation) for an example.

  There are two types of facets, option facets, and boundary facets.

  Option facets are lists of values that can retrieve a count of products. These are usually represented by checkboxes with labels for hte options, and a count next to them. These are the vast majority of facets.

  Boundary facets are a minimum and maximum value; and are usually represented by a range slider.

  **/
  this.Facet = function(spec) {
    var facet = this;
    this.spotDOM = spotDOM;

    var facetValueHash = {};
    // for option facets
    spotDOM.arrayField(this, 'facetValue');
    // for boundary facets
    spotDOM.scalarField(this, 'boundaryValues');
    spotDOM.scalarField(this, 'boundaryEdges');

    this.isValidLocale = function(locale) {
      return !locale || !spec.locales || spec.locales.length == 0 || spec.locales.indexOf(locale) != -1;
    };

    this.getBoundary = function() {
      return spec["boundary"];
    };

    this.getSortOrder = function() {
      return spec['sort'] || (spec['values'] ? "manual" : { asc: "name" });
    };

    this.getName = function(locale) {
      if (!locale)
        locale = this.spotDOM.locale();
      if (typeof(spec) == 'object') {
        if (spec['name']) {
          if (typeof(spec['name']) == 'object')
            return spec['name'][locale] || Object.values(spec['name'])[0];
          return spec['name'];
        }
        return Object.keys(spec)[0];
      }
      return spec;
    };

    this.getRelevantFacets = function(product) {
      return [];
    };

    this.smartTypeConversion = function() {
      var lower;
      if (typeof(spec) == 'object') {
        if (spec['type']) {
          if (spec['type'] == "option")
            return { "option": spec['option'] };
          else if (spec['type'] == "product-custom-field" || spec['type'] == "variant-custom-field") {
            var hash = {};
            hash[spec['type']] = spec['field'];
            return hash;
          } else if (spec['type'] == "product-metafield" || spec['type'] == "variant-metafield") {
            var hash = {};
            hash[spec['type']] = { }
            hash[spec['type']][spec['namespace']] = spec['key']
            return hash;
          }
          return spec['type'];
        }
        lower = this.getName().toLowerCase();
      } else
        lower = spec.toLowerCase();
      if (lower == 'type' || lower == 'product type' || lower == 'category')
        return "product_type";
      else if (lower == 'vendor' || lower == 'seller')
        return "vendor";
      else if (lower == 'price')
        return "price";
      else if (lower == 'compare_at_price')
        return "compare_at_price";
      else if (lower == 'grams')
        return "grams";
      else if (lower == 'inventory_quantity')
        return "inventory_quantity";
      else if (lower == 'tags')
        return "tags";
      else if (lower == 'option1' || lower == 'option2' || lower == 'option3' || lower == "collection")
        return lower;
      else {
        return {"option":spec};
      }
    };
    this.smartType = this.smartTypeConversion();
    if (typeof(this.smartType) == 'object')
      this.smartTypeName = this.smartType['name'] || this.smartType['option'] || this.smartType['key'] || this.smartType['variant-custom-field'] || this.smartType['product-custom-field'];
    else
      this.smartTypeName = this.smartType;



    this.isQuantitative = function() { return typeof(this.smartType) != "object" && (this.smartType == 'price' || this.smartType == 'compare_at_price' || this.smartType == 'grams' || this.smartType == 'inventory_quantity') };
    this.isMonetary = function() { return typeof(this.smartType) != "object" && (this.smartType == 'price' || this.smartType == 'compare_at_price'); };
    this.isQualitative = function() { return !this.isQuantitative() && !this.isBoolean(); }
    this.isBoolean = function() { return spec['level'] && spec['level'] == "bool"; }
    this.isOption = function() { return typeof(this.smartType) == "object" && this.smartType['option']; }
    this.isCustomField = function() { return typeof(this.smartType) == "object" && (this.smartType['product-custom-field'] || this.smartType['variant-custom-field']); }
    this.isMetafield = function() { return typeof(this.smartType) == "object" && (this.smartType['product-metafield'] || this.smartType['variant-metafield']); }
    this.isProductMetafield = function() { return typeof(this.smartType) == "object" && this.smartType['product-metafield']; }
    this.isVariantMetafield = function() { return typeof(this.smartType) == "object" && this.smartType['variant-metafield']; }
    this.isProductCustomField = function() { return typeof(this.smartType) == "object" && this.smartType['product-custom-field']; }
    this.isVariantCustomField = function() { return typeof(this.smartType) == "object" && this.smartType['variant-custom-field']; }
    this.getOption = function() { return typeof(this.smartType) == "object" && this.smartType['option']; }
    this.getNamespace = function() { return typeof(this.smartType) == "object" && Object.keys((this.smartType['product-metafield'] || this.smartType['variant-metafield']))[0]; }
    this.getKey = function() { return typeof(this.smartType) == "object" && Object.values((this.smartType['product-metafield'] || this.smartType['variant-metafield']))[0]; }
    this.getField = function() { return typeof(this.smartType) == "object" && (this.smartType['product-custom-field'] || this.smartType['variant-custom-field']); }
    // Becuase Shopify doesn't support ORing things, normally.
    this.singleSelection = function() { return spotDOM.shopifyMode() == "shopify" || spec['single']; };

    // Specifies the sorting algorithm for the inner facetValues.
    this.sortValuesFunction = null;
    if (this.getSortOrder() != "manual" && typeof(this.getSortOrder()) == 'object') {
      var sortDirection = Object.keys(this.getSortOrder())[0];
      var sortValue = Object.values(this.getSortOrder())[0];
      var compareFunction = (this.isQualitative() ? function(facetValueA, facetValueB) {
        if (sortDirection == "asc")
          return (facetValueA.getName() || "").localeCompare(facetValueB.getName() || "");
        return (facetValueB.getName() || "").localeCompare(facetValueA.getName() || "");
      } : function(facetValueA, facetValueB) {
        var halvesA = facetValueA.value.split("-");
        var halvesB = facetValueB.value.split("-");
        if (sortDirection == "asc")
          return parseFloat(halvesA[0] || 0) - parseFloat(halvesB[0] || 0);
        return parseFloat(halvesB[0] || 0) - parseFloat(halvesA[0] || 0);
      });
      this.sortValuesFunction = sortValue == "name" ? compareFunction : (function(facetValueA, facetValueB) {
        var difference = (facetValueA.count() || 0) - (facetValueB.count() || 0);
        if (difference == 0)
          difference = compareFunction(facetValueA, facetValueB)
        return sortDirection == "desc" ? -difference : difference;
      });
    }

    this.getOptionValue = function() {
      if (typeof(spec) == 'object') {
        if (spec['values']) {
          var values = spotDOM.map(spec['values'], function(e) {
            if (typeof(e) == "object") {
              if (e.name && facet.isQualitative())
                return { name: (typeof(e.name) == 'object' ? (e.name[spotDOM.locale()] || Object.values(e.name)[0]) : e.name), value: e.value };
              else if (facet.isBoolean())
                return { name: (typeof(e.name) == 'object' ? (e.name[spotDOM.locale()] || Object.values(e.name)[0]) : e.name), value: e.value == "true"};
              if (typeof(e.value) == "object" && e.value["*"])
                return {"value": {"*":e.value["*"]}};
              return e.value;
            }
            return e;
          });
          var hash = {};
          if (this.isOption()) {
            hash['option'] = { };
            hash.option[this.smartTypeName] = values;
            return hash;
          } else if (this.isMetafield()) {
            var field = this.isProductMetafield() ? 'product-metafield' : 'variant-metafield';
            hash[field] = { };
            hash[field][spec['namespace']] = { }
            hash[field][spec['namespace']][spec['key']] = { }
            hash[field][spec['namespace']][spec['key']] = values;
            return hash;
          } else if (this.isCustomField()) {
            var field = this.isProductCustomField() ? 'product-custom-field' : 'variant-custom-field';
            hash[field] = { };
            hash[field][this.smartTypeName] = values;
            return hash;
          }
          hash[this.smartTypeName] = values;
          return hash;
        }
      }
      return this.smartType;
    };

    this.getUniqueId = function(locale) {
      if (this.getName(locale))
        return "f_" + spotDOM.handleize(this.getName(locale));
      return "f_" + this.smartTypeName;
    };

    this.composeQuery = function(query) {
      if (this.getBoundary()) {
        var values = this.boundaryValues();
        if (values && (values[0] != null || values[1] != null)) {
          var internalQuery = spotDOM.spotAPI.se();
          // TODO: Genericize this.
          var func;
          if (typeof(this.smartType) == 'object') {
            if (this.smartType['product-custom-field']) {
              func = function(op, operand) { return internalQuery['product-custom-field'].call(internalQuery, facet.smartType['product-custom-field'], op, operand) };
            } else if (this.smartType['variant-custom-field']) {
              func = function(op, operand) { return internalQuery['variant-custom-field'].call(internalQuery, facet.smartType['variant-custom-field'], op, operand) };
            }
          } else {
            func = internalQuery[this.smartType];
          }
          if (func) {
            if (values[0] != null && values[1] != null) {
              internalQuery = func.call(internalQuery, "between", values);
            } else if (values[0] != null) {
              internalQuery = func.call(internalQuery, ">=", values[0]);
            } else if (values[1] != null) {
              internalQuery = func.call(internalQuery, "<=", values[1]);
            }
          }
          return internalQuery;
        }
        return query;
      }
      return query.or(spotDOM.map(this.facetValues(), function(e) { return e.composeQuery(spotDOM.spotAPI.se()); }).filter(function(e) { return !e.isEmptyQuery(); }));
    };

    this.isEnabled = function() {
      return (this.getBoundary() && this.boundaryValues() && this.boundaryValues().filter(function(value) { return value != null; }).length > 0) || this.facetValues().filter(function(e) { return e.enabled(); }).length > 0;
    };

    this.composeQueryString = function(vars) {
      if (this.getBoundary()) {
        var values = this.boundaryValues();
        var edges = this.boundaryEdges();
        if (values && edges) {
          if (values[0] != edges[0])
            vars[this.getUniqueId() + "_min"] = values[0];
          if (values[1] != edges[1])
            vars[this.getUniqueId() + "_max"] = values[1];
        }
      } else {
        var array = this.facetValues().filter(function(e) { return e.enabled(); });
        if (array.length > 0)
          vars[this.getUniqueId()] = spotDOM.map(array, function(e) { return e.getName(); });
      }
      return vars;
    };

    this.parseQueryString = function(vars) {
      if (this.getBoundary()) {
        var values = this.boundaryValues();
        var min = vars[this.getUniqueId() + "_min"];
        var max = vars[this.getUniqueId() + "_max"];
        if (min != null || max != null) {
          if (min != null)
            values[0] = min;
          if (max != null)
            values[1] = max;
          this.boundaryValues([values[0], values[1]]);
        }
      } else {
        var values = vars[this.getUniqueId()] || [];
        this.facetValues().forEach(function(e) {
          var enabled = values.indexOf(e.getName()) != -1;
          if (e.enabled() != enabled)
            e.enabled(enabled);
        });
      }
    }

    this.init = function(option) {
      var values = [];
      var valueEnabled = false;
      if (spec.values) {
        values = spotDOM.map(spec.values, function(e) {
          if (typeof(e) == "object")
            return { "value": e.value, name: e.name, swatch_id: e.swatch_id };
          return { "value": e, name: e };
        });
      } else if (spec.boundary && spec.boundary[0] != null && spec.boundary[1] != null) {
        var hash = [{ min: {} }, { max: {} }];
        hash[0].min[this.smartType] = parseFloat(spec.boundary[0]);
        hash[1].max[this.smartType] = parseFloat(spec.boundary[1]);
        values = hash;
      }

      var vars = option || spotDOM.getUrlVars();
      var facetsSelected = Object.keys(vars).filter(function(e) { return /^f_/.test(e); })
      if (this.isValidLocale(spotDOM.locale())) {
        if (facetsSelected.includes(facet.getUniqueId())) {
          valueEnabled = true;
          var selectedValues = vars[facet.getUniqueId()];
          if (typeof selectedValues == "string")
            selectedValues = [selectedValues];
          var unhandledValues = [];
          if (values.length > 0) {
            for (var j = 0; j < selectedValues.length; ++j) {
              var hasOne = false;
              for (var i = 0; i < values.length; ++i ){
                var name = typeof(values[i].name) == "object" ? values[i].name[spotDOM.locale()] : values[i].name;
                if (name == selectedValues[j]) {
                  values[i].enabled = true;
                  hasOne = true;
                }
              }
              if (!hasOne) {
                // If we're a wildcard value only, push the wildcard prefix onto the unhandledValue.
                if (values.length == 1 && values[0] && values[0].value["*"])
                  unhandledValues.push({ enabled: true, name: selectedValues[j], value: values[0].value["*"].replace(/\*$/, "") + selectedValues[j] });
                else
                  unhandledValues.push(selectedValues[j]);
              }
            }
          } else {
            unhandledValues = selectedValues;
          }
          values = spotDOM.map(unhandledValues, function(e) { return typeof(e) == 'object' ? e : { "value": e, enabled: true }; }).concat(values);
        } else if (facet.getBoundary()) {
          if (facetsSelected.includes(facet.getUniqueId() + "_min"))
            values.push({ lower_bound: vars[facet.getUniqueId() + "_min"] });
          if (facetsSelected.includes(facet.getUniqueId() + "_max"))
            values.push({ upper_bound: vars[facet.getUniqueId() + "_max"] });
        }
      }
      if (spotDOM.shopifyMode() == "shopify") {
        if (
          values.length > 0 && (
            ((spotDOM.isCollectionPage() || spotDOM.isSearchPage()) && (typeof(this.smartType) != "object" && (this.smartType == "tag" || this.smartType == "tags"))) ||
            ((spotDOM.isVendorCollectionPage() || spotDOM.isSearchPage()) && (typeof(this.smartType) != "object" && this.smartType == "vendor")) ||
            ((spotDOM.isProductTypeCollectionPage() || spotDOM.isSearchPage()) && (typeof(this.smartType) != "object" && this.smartType == "product_type"))
          )
        )
          this.update(values, true);
        else
          spotDOM.removeFacet(this);
      } else {
        this.update(values, true);
      }
      return valueEnabled;
    };

    this.update = function(option, init) {
      var values = [];
      var mergeValues = {};
      if (spec.values) {
        for (var i = 0; i < spec.values.length; ++i)
          mergeValues[typeof(spec.values[i].name) == "object" ? spec.values[i].name[spotDOM.locale() || "en"] : spec.values[i].name] = spec.values[i].value;
      }
      if (option && !Array.isArray(option)) {
        var actualType = this.smartTypeName;
        if (actualType == "tags" && option["tag"])
          actualType = "tag";
        var keys = option[actualType] ? Object.keys(option[actualType]) : [];
        var currentOption = option[actualType] ? option[actualType] : {}
        if (keys.length == 0 && option["option"]) {
          // To handle those cases where we're case insensitive.
          keys = option["option"][actualType] ? Object.keys(option["option"][actualType]) : (option["option"][actualType.toLowerCase()] ? Object.keys(option["option"][actualType.toLowerCase()]) : []);
          currentOption = option["option"][actualType] ? option["option"][actualType] : (option["option"][actualType.toLowerCase()] ? option["option"][actualType.toLowerCase()] : {})
        } else if (keys.length == 0 && (option["product-metafield"] || option["variant-metafield"])) {
          var key = option["product-metafield"] ? 'product-metafield' : 'variant-metafield';
          currentOption = Object.values(Object.values(option[key])[0])[0];
          keys = Object.keys(currentOption);
        } else if (keys.length == 0 && (option["product-custom-field"] || option["variant-custom-field"])) {
          var key = option["product-custom-field"] ? 'product-custom-field' : 'variant-custom-field';
          currentOption = Object.values(option[key])[0];
          keys = Object.keys(currentOption);
        }
        for (i = 0; i < keys.length; ++i)
          values.push({ "name": keys[i], "value": mergeValues[keys[i]] || keys[i], "count": currentOption[keys[i]] });
      } else if (option) {
        values = option;
      }

      if (this.getBoundary()) {
        var min, max, lower, upper;
        for (i = 0; i < values.length; ++i) {
          // TODO: Genericize this.
          if (values[i].min != null) {
            if (typeof(values[i].min) == 'object' && values[i].min['product-custom-field'] || values[i].min['variant-custom-field'] )
              min = Object.values(values[i].min)[0][this.smartTypeName];
            else
              min = values[i].min[this.smartTypeName];
          } else if (values[i].max != null) {
            if (typeof(values[i].max) == 'object' && values[i].max['product-custom-field'] || values[i].max['variant-custom-field'] )
              max = Object.values(values[i].max)[0][this.smartTypeName];
            else
              max = values[i].max[this.smartTypeName];
          } else if (values[i].lower_bound)
            lower = values[i].lower_bound;
          else if (values[i].upper_bound)
            upper = values[i].upper_bound;
        }
        if (min != null && max != null)
          this.boundaryEdges([min, max]);
        if (lower != null || upper != null)
          this.boundaryValues([lower, upper]);
      } else {
        var extantFacets = {};
        var newFacetValues = [];
        var wildcards = Object.values(facetValueHash).filter(function(e) { return e.isUnnamedWildcard(); });
        for (i = 0; i < values.length; ++i) {
          var value = values[i];
          var name = value["name"] != null && (typeof(value["name"]) == 'object' ? value["name"][spotDOM.locale() || "en"] : value["name"]);
          var wildcard = wildcards.filter(function(e) { 
            return name && name.toLowerCase().indexOf(e.value["*"].replace(/\*$/, "").toLowerCase()) == 0; 
          });
          if (wildcard.length > 0)
            value["name"] = name.slice(wildcard[0].value["*"].length - 1);
          var facetValue = new spotDOM.FacetValue(this, value);
          var id = facetValue.getUniqueId();
          if (!facetValueHash[id]) {
            newFacetValues.push(facetValue);
          } else {
            facetValue = facetValueHash[id];
          }
          extantFacets[id] = true;
          facetValue.update(value);
        }
        this.addFacetValues(newFacetValues);
        if (this.sortValuesFunction) {
          this.facetValues(this.facetValues().map(function(v) { return v; }).sort(this.sortValuesFunction));
        }
        Object.keys(facetValueHash).forEach(function(i) {
          if (!extantFacets[i])
            facetValueHash[i].count(0);
        });
      }
    };

    this.addFacetValue(function(facetValue) {
      var id = facetValue.getUniqueId();
      facetValueHash[id] = facetValue;
      return facetValue;
    });

    this.removeFacetValue(function(facetValue) {
      var id = facetValue.getUniqueId();
      delete facetValueHash[id];
    });

    this.getFacetValue = function(id) {
      return facetValueHash[id];
    };
  };


  /**

  ### FacetValues

  FacetValues are objects that represent an individual value that's part of a composing facet. An example here would be "Red", under the Facet "Color".

  FacetValues can be either "simple", or "composite". A simple FacetValue only comprises a single underlying value, whereas a composite FacetValue comprises more
  than one value, or a rule that describes a set of values. An example here would be "Red" simply referring to "Red" in the underlying catalog, vs. "Red" referring
  to a series of values like "Magenta", "Pink", "Red", etc.. in the underlying catalog.

  These are genereally not instantiated directly, but instead are instantiated as part of the values coming back from the filtering system. They do have a number of
  scalar variables, that, like the SpotDOM object as a whole, can be listened to by passing a function to them. Generally, these listeners are applied under an
  `addFacet` listener, which will trigger whenever a facet is added to the SpotDOM system. You can look at the [SpotDefault implementation](#spotdefault-documentation)
  for an example.

  **/
  this.FacetValue = function(facet, value) {
    this.facet = facet;
    this.name = value['name'];
    this.value = value['value'];
    this.swatch_id = value['swatch_id'];

    /**

    #### Variables

    ##### count

    This variable is changed when a filtering resultset comes back with a number that represents how many products lie under this facet. Generally used
    to update a faceting pane.

    **/
    spotDOM.scalarField(this, 'count', value['count']);
    /**

    ##### enabled

    Used to determine whether or not a particular facet value has been applied to the query. Generally used to check a checkbox, or provide some other
    indication to the user that the FacetValue is being used.

    **/
    spotDOM.scalarField(this, 'enabled', value['enabled'] != null ? value['enabled'] : false);
    /**


    #### Getters

    ##### getUniqueId

    Returns an ID that is unique to this facet. Usually is descriptive of the facet as well. Suitable for direct insertion into a query string, or
    for use as a DOM node id.

    **/


    this.getUniqueId = function(locale) {
      if (this.isUnnamedWildcard())
        return this.facet.getUniqueId(locale) + "_wildcard";
      if (this.facet.isQualitative() || this.facet.isBoolean())
        return this.facet.getUniqueId() + "_" + (typeof(this.getName(locale)) == "object" ? "wildcard" : spotDOM.handleize(this.getName(locale)));
      return this.facet.getUniqueId(locale) + "_" + spotDOM.handleize(this.value);
    };

    /**

    ##### getName

    Returns a nice-looking name for this facet, given the information you've supplied, for the specified, or default locale.

    **/
    this.getName = function(locale) {
      if (!locale)
        locale = this.facet.spotDOM.locale();
      if (this.name) {
        if (typeof(this.name) == 'object')
          return this.name[locale];
        return this.name;
      }
      if (this.facet.isQuantitative()) {
        if (this.value.charAt(0) == "-") {
          return "Under " + this.formatValue(this.value.slice(1));
        } else if (this.value.charAt(this.value.length-1) == "+") {
          return "Over " + this.formatValue(this.value.slice(0, this.value.length-1));
        } else {
          var groups = this.value.split(/\s*-\s*/);
          return this.formatValue(groups[0]) + " - " + this.formatValue(groups[1]);
        }
      }
      return typeof(this.value) != "object" ? this.value : null;
    };
    /**

    ##### getValue

    Returns the value of this facet; i.e. something that can be directly passed to Spot.

    **/
    this.getValue = function() { return this.value; };

    this.numericalList = null;
    this.getNumericalList = function() {
      if (this.numericalList)
        return this.numericalList;
      var names = [];
      for (var i in this.facet.values) {
        names.push(this.facet.values[i]);
      }
      names.sort(function(a,b) {
        var convertedA = parseInt(a.value);
        var convertedB = parseInt(b.value);
        if (isNaN(convertedA) && !isNaN(convertedB))
          return 1;
        if (!isNaN(convertedA) && isNaN(convertedB))
          return -1;
        if (isNaN(convertedA) && isNaN(convertedB))
          return 0;
        if (convertedA < convertedB)
          return -1;
        if (convertedA == convertedB)
          return 0;
        return 1;
      });
      this.numericalList = names;
      return names;
    };

    this.getNumericalIndex = function() {
      var names = this.getNumericalList();
      for (var i = 0; i < names.length && names[i] != this; ++i);
      return i;
    };

    this.formatValue = function(value) {
      if (value == null)
        return value;
      value = value.replace(/[^0-9.]+/g, '');
      if (this.facet.smartType.indexOf("price") != -1)
        return spotDOM.formatMoney(value);
      return value;
    };

    this.isUnnamedWildcard = function() {
      return typeof(this.value) == "object" && this.value["*"] && this.getName() == null;
    };

    this.enable = function() {
      this.enabled(true);
    };

    this.disable = function() {
      this.enabled(false);
    };

    this.toggle = function() {
      if (this.enabled())
        this.disable();
      else
        this.enable();
    };

    this.update = function(value) {
      if (this.count() != value.count)
        this.count(value.count);
      if (value.enabled != null && this.enabled() != value.enabled)
        this.enabled(value.enabled);
    };

    // Compose this into the query, if active.
    this.composeQuery = function(query, force) {
      var i, queries = [], value;
      if (this.enabled() || force) {
        if (this.facet.isOption()) {
          if (Array.isArray(this.value)) {
            for (i = 0; i < this.value.length; ++i) {
              if (typeof(this.value[i]) == "object") {
                Object.keys(this.value[i]).forEach(function(j) {
                  queries.push(spotDOM.spotAPI.se().option(this.facet.smartType['option'], j, this.value[i][j]));
                });
              } else {
                queries.push(spotDOM.spotAPI.se().option(this.facet.smartType['option'], this.value[i]));
              }
            }
            if (queries.length == 1)
              return query.merge(queries[0]);
            return query.or(queries);
          } else {
            if (typeof(this.value) == "object") {
              Object.keys(this.value).forEach(function(j) {
                query = query.option(this.facet.smartType['option'], j, this.value[j]);
              });
            } else {
              query = query.option(this.facet.smartType['option'], this.value);
            }
            return query;
          }
        } else if (this.facet.isMetafield()) {
          var target = this.facet.isProductMetafield() ? "product_metafield" : "variant_metafield";
          value = this.value;
          if (!Array.isArray(value))
            value = [value];
          for (i = 0; i < value.length; ++i) {
            if (typeof(value[i]) == "object") {
              Object.keys(value[i]).forEach(function(j) {
                queries.push(spotDOM.spotAPI.se()[target](this.facet.getNamespace(), this.facet.getKey(), value[i][j]));
              });
            } else {
              queries.push(spotDOM.spotAPI.se()[target](this.facet.getNamespace(), this.facet.getKey(), value[i]));
            }
          }
          if (queries.length == 1)
            return query.merge(queries[0]);
          return query.or(queries);
        } else if (this.facet.isCustomField()) {
          var target = this.facet.isProductCustomField() ? "product_custom_field" : "variant_custom_field";
          value = this.value;
          if (!Array.isArray(value))
            value = [value];
          for (i = 0; i < value.length; ++i) {
            if (typeof(value[i]) == "object") {
              Object.keys(value[i]).forEach(function(j) {
                queries.push(spotDOM.spotAPI.se()[target](this.facet.getField(), value[i][j]));
              });
            } else {
              queries.push(spotDOM.spotAPI.se()[target](this.facet.getField(), value[i]));
            }
          }
          if (queries.length == 1)
            return query.merge(queries[0]);
          return query.or(queries);
        } else if (this.facet.isQuantitative()) { // "0-99", "-1000-1000", "
          if (this.value.charAt(0) == "-")
            return query[this.facet.smartType]("<", this.value.slice(1));
          else if (this.value.charAt(this.value.length-1) == "+")
            return query[this.facet.smartType](">=", this.value.slice(0, this.value.length-1));
          else {
            var groups = this.value.split(/\s*-\s*/);
            return query[this.facet.smartType]("between", [groups[0], groups[1]]);
          }
        } else if (this.facet.isBoolean()) {
          if ((Array.isArray(this.value) ? this.value : [this.value]).filter(function(v) { return v == "true" || (typeof(v) == 'boolean' && v); }).length > 0)
            return query[this.facet.smartType]("==", true);
          return query[this.facet.smartType]("==", false);
        }

        value = this.value;
        if (!Array.isArray(value))
          value = [value];
        var apiField = this.facet.smartType;
        if (apiField == "collection")
          apiField = "in_collection";
        if (apiField == "search")
          apiField = "in_search";
        for (i = 0; i < value.length; ++i) {
          if (typeof(value[i]) == "object") {
            Object.keys(this.value[i]).forEach(function(j) {
              queries.push(spotDOM.spotAPI.se()[apiField], j, value[i][j]);
            });
          } else {
            queries.push(spotDOM.spotAPI.se()[apiField](value[i]));
          }
        }
        if (queries.length == 1)
          return query.merge(queries[0]);
        return query.or(queries);
      }
      return query;
    };
  };


  this.productExtraInfoSaved = spotAPI.getSessionStorage("spotProductExtraInfo") || {};
  this.productExtraInfoGrabbed = {};
  Object.keys(this.productExtraInfoSaved).forEach(function(id) {
    spotDOM.productExtraInfoGrabbed[id] = spotDOM.spotAPI.Deferred().resolve(spotDOM.productExtraInfoSaved[id]);
  });
  this.getExtraInfo = function(ids) {  
    if (!Array.isArray(ids))
      ids = [ids];

    var idsToGet = ids.filter(function(id) { return !spotDOM.productExtraInfoGrabbed[id] });
    if (idsToGet.length > 0) {
      idsToGet.forEach(function(id) {
        spotDOM.productExtraInfoGrabbed[id] = spotDOM.spotAPI.Deferred();
      });
      
      var query = spotDOM.spotAPI.s().locale(spotDOM.locale()).id("in", idsToGet);
      if (spotDOM.clusters())
        query = query.clusters(spotDOM.clusters());
      if (spotDOM.hostname())
        query = query.hostname(spotDOM.hostname());
      query.e().done(function(products) {
        products.forEach(function(product) {
          spotDOM.productExtraInfoGrabbed[product.id].resolve(product);
          spotDOM.productExtraInfoSaved[product.id] = product;
        });
        spotAPI.setSessionStorage("spotProductExtraInfo", spotDOM.productExtraInfoSaved);
      });
    }
    return window.SpotAPI.DeferredObject.all.apply(window.SpotAPI, ids.map(function(id) { return spotDOM.productExtraInfoGrabbed[id]; }));
  };


  function fieldMatches(fieldValue, fieldSpecifier) {
    return (Array.isArray(fieldValue) ? fieldValue : [fieldValue]).filter(function(fieldValue) {
      return fieldSpecifier.split(/\s*,\s*/).filter(function(spec) {
        if (spec.charAt(spec.length-1) == '*')
          return fieldValue.indexOf(spec.substr(0, spec.length-1)) == 0;
        return fieldValue == spec;
      }).length > 0;
    });
  }

  this.RetrieveFieldObject = function(field, object, parent) {
    while (field != null && object != null) {
      if (typeof(object) == "object" && !Array.isArray(object)) {
        var key = typeof(field) == "object" ? Object.keys(field)[0] : field;
        if (key == "option") {
          var optionName = typeof(field.option) == "object" ? Object.keys(field.option)[0] : field.option;
          if (!parent || (!parent.original || !parent.original.options) && !parent.options) {
            if (!object.variants)
              return null;
            return object.variants.flatMap(function(v) { return spotDOM.RetrieveFieldObject(field, v, object); });
          }
          if (typeof(optionName) == 'string')
            optionName = optionName.split(/\s*,\s*/);
          var optionIdx = (parent.original ? parent.original : parent).options.map(function(e, idx) {
            if (typeof(e) == "object")
              return optionName.indexOf(e.name) != -1 ? (idx+1) : null;
            return optionName.indexOf(e) != -1 ? (idx+1) : null;
          }).filter(function(e) { return e != null; })[0];
          if (!optionIdx)
            return null;
          return object["option" + optionIdx];
        } else if (key == "product-metafield") {
          var namespace = Object.keys(field[key])[0];
          var mkey = field[key][namespace];
          if (!object.metafields)
            return null;
          if (typeof(object.metafields) == "object" && !Array.isArray(object.metafields))
            return object.metafields[namespace] && object.metafields[namespace][mkey];
          return object.metafields.filter(function(e) { return e.namespace == namespace && e.key == mkey }).flatMap(function(e) { 
            if (e.type == "list.single_line_text_field")
              return JSON.parse(e.value);
            return e.value; 
          });
        } else if (key == "product-custom-field") {
          var fieldHandle = typeof(field['product-custom-field']) == "object" ? Object.keys(field['product-custom-field'])[0] : field['product-custom-field'];
          var fieldSpecifier = typeof(field['product-custom-field']) == "object" ? Object.values(field['product-custom-field'])[0] : null;
          if (!object || !object.custom || typeof(object.custom) != "object")
            return null;
          if (fieldSpecifier != null && fieldMatches(object.custom[fieldHandle], fieldSpecifier).length == 0)
            return null;
          return object.custom[fieldHandle];
        } else if (key == "variant-metafield") {
          var namespace = Object.keys(field[key])[0];
          var mkey = field[namespace][key];
          if (!object.metafields)
            return null;
          if (typeof(object.metafields) == "object" && !Array.isArray(object.metafields))
            return object.metafields[namespace] && object.metafields[namespace][key];
          return object.metafields.filter(function(e) { return e.namespace == namespace && e.key == mkey }).map(function(e) { 
            if (e.type == "list.single_line_text_field")
              return JSON.parse(e.value);
            return e.value; 
          });
        } else {
          if (typeof(field) != "object") {
            if (key == "product_type" || key == "type")
              return object["product_type"] || object["type"];
            return object[key];
          }

          var objectField = key == "tag" ? "tags" : key;
          object = objectField == "product_type" ? (object[objectField] || object["type"]) : object[objectField];
          field = field[key];
          if (key != "title") {
            if (typeof(field) != 'object')
              field = field.split(/\s*,\s*/);
            if (typeof(object) != 'object')
              object = object.split(/\s*,\s*/);
          }
        }
      } else {
        return fieldMatches(object, field);
      }
    }
    return null;
  }

  this.RetrieveValueCondition = function(product, condition) {
    var key = Object.keys(condition)[0];
    if (product.variants) {
      if (key == 'option') {
        var optionName = Object.keys(condition[key])[0];
        var optionIdx = product.options.filter(function(o) { return o.name == optionName; }).map(function(o) { return o.position; })[0];
        return [product.variants.map(function(v) { return v["option" + optionIdx]; }), condition[key][optionName]];
      }  else if (key == 'variant-custom-field') {
        var customHandle = Object.keys(condition.key)[0];
        return [product.variants.flatMap(function(v) { return v.custom && v.custom[customHandle] != null ? [v.custom[customHandle]] : []; }), condition[key][customHandle]];
      } else if (key == 'variant_id') {
        return [product.variants.map(function(v) { return v.id; }), condition[key]];
      } else if (key == 'variant-metafield') {
        var metafieldNamespace = Object.keys(condition.key)[0];
        var metafieldKey = Object.keys(condition[metafieldNamespace])[0];
        return [product.variants.flatMap(function(v) { return v.metafields ? v.metafields.filter(function(m) { return m.namespace == metafieldNamespace && m.key == metafieldKey }) : []; }), condition[key][optionName]];
      } else if (key == 'price' || key == 'compare_at_price' || key == 'price' || key == 'inventory_quantity' || key == 'inventory_quantity' || key == 'inventory_quantity_online' || key == 'option1' || key == 'option2' || key == 'option3') {
        return [product.variants.map(function(v) { return v[key]; }), condition[key]];
      } else if (key == 'available')
        return [product.variants.map(function(v) { return v.inventory_policy != 'deny' || (v.inventory_quantity_online != null ? v.inventory_quantity_online : v.inventory_quantity) > 0; }), condition[key]];
    }
    if (key == "product-custom-field") {
      var customField = Object.keys(condition[key])[0];
      return [product.custom != null ? product.custom[customField] : null, condition[key][customField]];
    } else if (key == "tag")
      return [product["tags"], condition[key]];
    return [product[key], condition[key]];
  }


  /**

  ### getBadges(product)

  Retrieves the badges associated with a particular product.

  [{"inventory_quantity":{">":0}}]

  **/
  this.evaluateCondition = function(product, condition) {
    if (condition.custom)
       return condition.custom(product);
    var values = spotDOM.RetrieveValueCondition(product, condition)
    var value = values[0];
    if (Array.isArray(value)) {
      if (value.length == 0)
        return false;
      if (condition.price || condition.compare_at_price)
        value = value.reduce(function(a,b) { return a < b ? a : b; });
      else if (condition.available != null)
        value = value.filter((v) => v).length > 0;
      else if (value.filter(function(v) { return isNaN(parseFloat(v)) }).length == 0)
        value = value.reduce(function(a,b) { return a+b; });
    }
    if (!Array.isArray(value))
      value = [value];
    return value.filter(function(value) {
      var remainder = values[1];
      var operator = typeof(remainder) == 'object' ? Object.keys(remainder)[0] : "==";
      var operand = typeof(remainder) == 'object' ? remainder[operator] : remainder;
      if (operator == "==") {
        if (Array.isArray(operand))
          return operand.indexOf(value) != -1;
        return value == operand;
      }
      if (operator == "!=") {
        if (Array.isArray(operand))
          return operand.indexOf(value) == -1;
        return value != operand;
      }
      if (operator == "<")
        return value < operand;
      if (operator == "<=")
        return value <= operand;
      if (operator == ">")
        return value > operand;
      if (operator == ">=")
        return value > operand;
      if (operator == "^")
        return value.indexOf(operand) == 0;
      if (operator == "$")
        return value.indexOf(operand) == (value.length - operand.length);
      if (operator == "=~") {
        if (value == null)
          return false;
        return new RegExp(operand).test(value);
      }
      if (operator == "!~") {
        if (value == null)
          return true;
        return !(new RegExp(operand).test(value));
      }
      if (operator == "contains") {
        if (value == null)
          return false;
        return value.indexOf(operand) != -1;
      }
      if (operator == "&&" || operator == "and")
        return operand.filter(function(o) { return spotDOM.evaluateCondition(product, o.field, o.operator, o.operand); }).length == operand.length;
      if (operator == "||" || operator == "or")
        return operand.filter(function(o) { return spotDOM.evaluateCondition(product, o.field, o.operator, o.operand); }).length > 0;
    }).length > 0;
  };

  this.Badge = function(badge, swatch) {
    this.conditions = badge.conditions;
    this.position = badge.position;
    this.name = badge.name;
    this.swatch = swatch;

    this.getName = function(locale) {
      return typeof(this.name) == 'object' ? this.name[locale || spotDOM.locale()] : this.name;
    };

    this.appliesTo = function(product) {
      return (Array.isArray(product) ? product : [product]).filter(function(product) {
        return badge.conditions.filter(function(condition) { return spotDOM.evaluateCondition(product, condition) }).length == badge.conditions.length;
      }).length > 0;
    };
  };

  this.getBadges = function(product) {
    return this.badges().filter(function(badge) { return badge.appliesTo(product); });
  };

  this.Banner = function(banner, swatches) {
    Object.assign(this, banner);
    this.media = banner.media.map(function(m) { return m.type == "swatch" ? Object.assign({}, m, swatches && swatches[m.swatch_id] || {}) : m; });
    this.getTitle = function(locale) {
      return this.title && (typeof(this.title.text) == 'object' ? this.title.text[locale || spotDOM.locale()] : this.title.text);
    };
    this.getSubtitle = function(locale) {
      return this.subtitle && (typeof(this.subtitle.text) == 'object' ? this.subtitle.text[locale || spotDOM.locale()] : this.subtitle.text);
    };
  };
  
  this.getBanners = function(position) {
    return this.banners().filter(function(banner) { return banner.position == position; });
  };

  this.BoostRule = function(boostRule) {
    this.rules = boostRule && boostRule.rules ? boostRule.rules : [];

    // Determining whether or not we require a product returned from Spot in order to actually
    // render this boost rule correctly.
    this.requiresAdditionalGet = function() {
      return this.rules.filter(function(r) {
        return r.source == "product" && (r.source_field['product-metafield'] || r.source_field['variant-metafield'] || r.source_field['product-custom-field'] || r.source_field['category'])
      }).length > 0;
    };

    this.getQuery = function(context, mandatory) {
      var boostQuery = spotDOM.spotAPI.se();
      function flattenField(field, should_split) {
        var list = [];
        var target = field;
        while (true) {
          if (typeof(target) != "object" || Array.isArray(target)) {
            if (should_split && !Array.isArray(target))
              target = target.split(/\s*,\s*/)
            list.push(target);
            break;
          }
          var key = Object.keys(target)[0];
          if (key == "collection")
            list.push("in_collection");
          else
            list.push(key);
          target = target[key];
        }
        return list;
      }


      // A rule looks like the following:
      // { "source_field": { "tag": "tag1, tag2" }, "target_field": { "tag": "tag3, tag4" } }
      // { "source_field": { "tag": "prefix*" } }
      // { "source_field": "product_type" }
      function splitValue(value) {
        return Array.isArray(value) ? value : value.split(/\s*,\s*/);
      }
      var boostRule = this;
      (context.products ? context.products : [context.product]).forEach(function(product, idx) {      
        boostRule.rules.filter(function(r) { return (mandatory && r.mandatory) || (!mandatory && !r.mandatory) }).forEach(function(rule, idx) {
          var source = rule.source;
          var operator = rule.mapping.indexOf("no") == 0 ? "!=" : "==";
          var isMapping = rule.mapping.indexOf("map") != -1;
          var field = typeof(rule.source_field) == "object" ? Object.keys(rule.source_field)[0] : rule.source_field;
          if (field == "collection") {
            operator = operator == "==" ? "in" : "not_in";
            if (source == "product") {
              if (context.collection && (!isMapping || context.collection.handle == rule.source_field.collection))
                boostQuery = boostQuery.in_collection(operator, isMapping ? rule.target_field.collection.split(/\s*,\s*/) : context.collection.handle.split(/\s*,\s*/), !mandatory ? rule.weight : null);
            } else if (source == "static") {
              boostQuery = boostQuery.in_collection(operator, rule.source_field.collection.split(/\s*,\s*/), !mandatory ? rule.weight : null);
            }
          } else if (source == "product" && product) {
            if (field == "manual-list" || field == "secondary-list") {
              var list = field == "manual-list" ? context.manual_recommendations : (context.secondary_lists && context.secondary_lists.filter(function(s) { return s.handle == rule.source_field[field] }).map(function(l) { return l.product_ids; })[0]);
              if (!list || list.length == 0) {
                boostQuery = boostQuery.id("in", []);
                return;
              }
              if (mandatory) {
                boostQuery = boostQuery.id("in", list);
                return;
              }
              var orQuery = spotAPI.se();
              list.forEach(function(id, idx) {
                orQuery = orQuery.id("==", id, rule.weight + (list.length - (idx + 1)) / 1000,  1.0);
              });
              boostQuery = boostQuery.or(orQuery);
            } else if (field == "related") {
              if (!product)
                return;
              if (mandatory)
                boostQuery = boostQuery.related("==", product.id);
              else
                boostQuery = boostQuery.related("==", product.id, rule.weight, 1.0);
            } else if (field == "tag" && !isMapping) {
              // Check for wildcards.
              splitValue(rule.source_field.tag).forEach(function(tag) {
                var target = new RegExp("^" + tag.replace(/\*/g, ".*").replace(/\|/g, "\\|") + "$");
                var tags = splitValue(product.tags);
                var matchedTags = tags.filter(function(e) { return target.test(e) }).slice(0, 19);
                if (matchedTags.length > 0) {
                  if (mandatory)
                    boostQuery = boostQuery.tag("in", matchedTags);
                  else
                    boostQuery = boostQuery.tag("in", matchedTags, rule.weight, 1.0);
                }
              });
            } else if (field == "title" && !isMapping) {
              var words = product.title.split(/\s+/).filter(function(t) { return t.length >= 2; });
              if (mandatory) {
                boostQuery = boostQuery.in_search(operator, words.join(" "));
              } else {
                words.forEach(function(word) {
                  boostQuery = boostQuery.in_search(operator, word, rule.weight / words.length);
                });
              }
            } else {
              var values = null;
              var isVariant = field == "variant-metafield" || field == "option1" || field == "option2" || field == "option3" || field == "option";
              if (isVariant) {
                var variantId = context.variantId;
                var variant = product.variants.filter(function(v) { return v.id == variantId })[0];
                if (variant && variantId) {
                  values = spotDOM.RetrieveFieldObject(rule.source_field, variant, product);
                } else {
                  values = {};
                  for (var i = 0; i < product.variants.length; ++i) {
                    var valid = spotDOM.RetrieveFieldObject(rule.source_field, product.variants[i], product);
                    if (valid)
                      values[valid] = 1;
                  }
                  values = Object.keys(values);
                  if (values.length == 0)
                    values = null;
                  else if (values.length == 1)
                    values = values[0];
                }
              } else
                values = spotDOM.RetrieveFieldObject(rule.source_field, product);
              if (values && (!Array.isArray(values) || values.length > 0)) {
                var argList = flattenField(rule.target_field || rule.source_field, rule.target_field && !rule.target_field.title);
                if (Array.isArray(argList[argList.length-1])) {
                  argList[argList.length-1] = argList[argList.length-1].flatMap(function(e) {
                    return (Array.isArray(values) ? values : [values]).map(function(v) { 
                      if (/\*$/.test(e)) {
                        if (rule.source_field && rule.source_field && rule.source_field.tag && /\*$/.test(rule.source_field.tag))
                          v = v.slice(rule.source_field.tag.length - 1);
                        return e.replace(/\*/, v);
                      } else
                        return e;
                    });
                  });
                }
                var key = argList.shift();
                if (rule.target_field) {
                  argList.splice(argList.length - 1, 0, Array.isArray(argList[argList.length-1]) ? (operator == "==" ? "in" : "not_in") : operator);
                  if (!mandatory) {
                    argList.push(rule.weight);
                    argList.push(1.0);
                  }
                } else {
                  argList.push(Array.isArray(values) ? (operator == "==" ? "in" : "not_in") : operator)
                  argList.push(values);
                  if (!mandatory) {
                    argList.push(rule.weight);
                    argList.push(1.0);
                  }
                }
                boostQuery = boostQuery[key].apply(boostQuery, argList);
              }
            }
          } else if (source == "static") {
            var argList = flattenField(rule.source_field, !rule.source_field.title);
            var key = argList.shift();
            if (Array.isArray(argList[argList.length-1]))
              operator = operator == "==" ? "in" : "not_in";
            argList.splice(argList.length - 1, 0, operator);
            if (field == "available") // TODO: Make this more general.
              argList.push(rule.mapping == "match" ? true : false)
            if (!mandatory)
              argList.push(rule.weight);


            if (field == "title" && !isMapping) {
              var words = rule.source_field.title.split(/\s+/).filter(function(t) { return t.length >= 2; });
              if (mandatory) {
                boostQuery = boostQuery.in_search(operator, words.join(" "));
              } else {
                words.forEach(function(word) {
                  boostQuery = boostQuery.in_search(operator, word, rule.weight / words.length);
                });
              }
            } else {
              boostQuery = boostQuery[key].apply(boostQuery, argList);
            }
          } else if (source == "session") {
            if (rule.source_field.url_param) {
              var param = Object.keys(rule.source_field.url_param)[0];
              if (context.params[param]) {
                var params = !Array.isArray(context.params[param]) ? [context.params[param]] : context.params[param];
                if (params.filter(function(e) { return e == rule.source_field.url_param[param] || rule.source_field.url_param[param] == "*" }).length > 0) {
                  var argList = flattenField(rule.target_field || rule.source_field);
                  var key = argList.shift();
                  argList.splice(argList.length - 1, 0, operator)
                  if (argList[argList.length-1] == "*")
                    argList[argList.length-1] = params[0];
                  if (!mandatory)
                    argList.push(rule.weight);
                  boostQuery = boostQuery[key].apply(boostQuery, argList);
                }
              }
            }
          } else if (source == "customer" || source == "session") {
            if (field == "tag" && context.customer) {
              if (isMapping) {
                var has = context.customer.tags.filter(function(e) { return e == rule.source_field[1] })[0];
                if (has) {
                  var argList = flattenField(rule.target_field || rule.source_field);
                  var key = argList.shift();
                  argList.splice(argList.length - 1, 0, operator)
                  if (!mandatory)
                    argList.push(rule.weight);
                  boostQuery = boostQuery[key].apply(boostQuery, argList);
                }
              } else {
                var target = new RegExp("^" + rule.source_field["tag"].replace(/\*/, ".*") + "$");
                context.customer.tags.filter(function(e) { return target.test(e) }).forEach(function(e) {
                  boostQuery = boostQuery.tag(operator, e, rule.weight);
                });
              }
            } else if (field == "recently-viewed") {
              if (!context.recently_viewed)
                return;
              context.recently_viewed.forEach(function(id, idx) {
                boostQuery = boostQuery.id("==", id, rule.weight + (context.recently_viewed.length - (idx + 1)) / 1000, 1.0);
              });
            } else if (field == "country" && context.customer.default_address && context.customer.default_address.country_code == rule.source_field[1]) {
              var argList = flattenField(rule.target_field);
              var key = argList.shift();
              argList.splice(argList.length - 1, 0, operator);
              if (!mandatory)
                argList.push(rule.weight);
              boostQuery = boostQuery[key].apply(boostQuery, argList);
            } else if (field == "url_parameter") {
              var vars = spotDOM.getUrlVars();

            }
          }
        });
      });
      return boostQuery;
    };
  };
  /**

  ### Personalization

  Personalizations are objects that represent a tied boost rule with a set options for how that boost rule should be applied and accumulated.

  #### Variables

  **/
  this.Personalization = function(options, boostRule) {
    var personalization = this;

    var id = options.id || "unknown";
    /**

    ##### duration

    An underlying list of boosts which are currently extant.

    **/
    spotDOM.scalarField(this, 'duration', options.duration);
    /**

    ##### appliedBoosts

    An underlying list of boosts which are currently extant.

    **/
    spotDOM.scalarField(this, 'appliedBoosts', []);
    /**

    ##### boostRule

    The boost rule that the personalization is based on.

    **/
    spotDOM.scalarField(this, 'boostRule', boostRule);
    /**

    ##### segmentation

    The way data is segmented for this personalization. Has three potential values.

    ###### "segmented"

    Data is kept completely independently from other personalization, and acts entirely independently from other personalizations.

    ###### "filtered"

    Data is kept together, but only affects the personalization if there's an applicable rule.

    ###### "unsegmented"

    Data is kept toegether, and affects the personalization in all cases.

    **/
    spotDOM.scalarField(this, 'segmentation', options.segmentation || "filtered");

    function deepCompareField(a, b) {
      if (typeof(a) != typeof(b))
        return false;
      if (typeof(a) == "object") {
        if ((Array.isArray(a) && !Array.isArray(b)) || (!Array.isArray(a) && Array.isArray(b)))
          return false;
        if (Array.isArray(a)) {
          if (a.length != b.length)
            return false;
          for (var i = 0; i < a.length; ++i) {
            if (!deepCompareField(a[i], b[i]))
              return false;
          }
        } else {
          var keysA = Object.keys(a).filter(function(e) { return e != "weight"; }),
            keysB = Object.keys(b).filter(function(e) { return e != "weight"; });
          if (keysA.length != keysB.length)
            return false;
          if (keysA[0] == keysB[0])
            return deepCompareField(a[keysA[0]], b[keysB[0]]);
        }
      }
      return a == b;
    }

    spotDOM.listener(this, 'apply', function(query) {
      return query.boost((query.innerAttributes["boost"] || []).concat(this.appliedBoosts()).filter(function(applied) {
        return true;
      }));
    });

    this.mergeAppliedBoost = function(boosts) {
      boosts.forEach(function(boost) {
        var applied = personalization.appliedBoosts().filter(function(applied) {
          return deepCompareField(applied, boost);
        });
        if (applied.length > 0)
          applied[0].weight += boost.weight;
        else if (personalization.appliedBoosts().length < 20 && boost.weight != 0)
          personalization.appliedBoosts().push(boost);
      });
      if (this.duration()) {
        var saved = spotDOM.getStorage("spotPersonalization") || {};
        saved[id] = this.appliedBoosts();
        spotDOM.setStorage("spotPersonalization", saved, this.duration() == "storage" ? new Date(new Date().getTime() + 86400 * 90 * 1000) : null);
      }
    };

    this.checkProductAgainstBoostRule = function(context) {
     	var boostQuery = this.boostRule().getQuery(context);
     	return boostQuery.innerQuery;
    };

    /**

    ##### enabledFacet

    Adds boosts whenever a facet is enabled.

    **/
    spotDOM.listener(this, 'enableFacet', function(facet) {

    });
    /**

    ##### viewedSearch

    Adds boosts whenever a search occurs.

    **/
    spotDOM.listener(this, 'viewSearch', function(products) {
      products.forEach(function(product) {
        var boosts = personalization.checkProductAgainstBoostRule({ product: product });
        if (boosts.length > 0) {
          boosts.forEach(function(e) {
            e.weight *= options.view_search_weight / products.length;
          });
        }
        personalization.mergeAppliedBoost(boosts);
      });
    });
    /**

    ##### viewedCollection

    Adds boosts whenever a list of products on a collection are viewed.

    **/
    spotDOM.listener(this, 'viewCollection', function(products) {
      products.forEach(function(product) {
        var boosts = personalization.checkProductAgainstBoostRule({ product: product });
        boosts.forEach(function(e) { e.weight *= options.view_collection_weight / products.length; });
        personalization.mergeAppliedBoost(boosts);
      });
    });
    /**

    ##### viewedProduct

    Adds boosts whenever a prodcut page is viewed.

    **/
    spotDOM.listener(this, 'viewProduct', function(product) {
      var boosts = personalization.checkProductAgainstBoostRule({ product: product });
      boosts.forEach(function(e) { e.weight *= options.view_product_weight; });
      personalization.mergeAppliedBoost(boosts);
    });
    /**

    ##### viewedVariant

    Adds boosts whenever a variant is selected, either when a product page is visited with a particular variant id, or when a variant is choen in the variant selector.

    **/
    spotDOM.listener(this, 'viewVariant', function(product, variant) {
      var boosts = personalization.checkProductAgainstBoostRule({ product: product, variant: variant});
      boosts.forEach(function(e) { e.weight *= options.view_variant_weight; });
      personalization.mergeAppliedBoost(boosts);
    });
    /**

    ##### addedProductToCart

    Adds boosts whenever a variant is added to cart, and also triggers checkout recommendaitons when applicable.

    **/
    spotDOM.listener(this, 'addedProductToCart', function(product, variant) {
      var boosts = personalization.checkProductAgainstBoostRule({ product: product, variant: variant});
      boosts.forEach(function(e) { e.weight *= options.add_product_to_cart_weight; });
      personalization.mergeAppliedBoost(boosts);
    });

    function isMatchingRule(boost, field) {
      var value = field && typeof(field) == 'object' ? Object.keys(field)[0] : field;
      var continuation = field && typeof(field) == 'object' ? Object.values(field)[0] : null;
      if (typeof(boost) != 'object' || boost[value] == null)
        return false;
      if (boost[value] != null && continuation == null)
        return true;
      return isMatchingRule(boost[value], continuation);
    }

    this.shouldApplyToPersonalization = function(boost) {
      if (this.segmentation() == "unsegmented")
        return true;
      return this.boostRule().rules.filter(function(rule) {
        return isMatchingRule(boost, rule.source_field);
      }).length > 0;
    };

    var savedWeights = spotDOM.getStorage("spotPersonalization");
    if (savedWeights) {
      if (this.segmentation() != "segmented") {
        var array = [];
        for (var i in savedWeights) {
          for (var j = 0; j < savedWeights[i].length; ++j) {
            if (this.shouldApplyToPersonalization(savedWeights[i][j]))
              array.push(savedWeights[i][j]);
          }
        }
        this.appliedBoosts(array);
      } else
        this.appliedBoosts(savedWeights[id] || []);
    }
  };


  /**

  ### Recommendation

  Recommendations are objects that represent an individual set of recommendations that are relevant to a particular product.

  #### Variables

  **/
  this.Recommendation = function(options, boostRule, sortOrder) {
    var recommendation = this;

    this.id = options.id;
    /**

    ##### name

    The name of the plain text name of the recommendation. Localized.

    **/
    spotDOM.scalarField(this, 'name', options.name);
    /**

    ##### split

    The split to use for this particular set of recommendations. If null, will use the current spotDOM's split.

    **/
    spotDOM.scalarField(this, 'split', options.split_id);
    /**

    ##### merge

    The split to use for this particular set of recommendations. If null, will use the current spotDOM's split.

    **/
    spotDOM.scalarField(this, 'merge', options.merge_id);
    /**

    ##### boostRule

    The boost rule that the recommendation is based on.

    **/
    spotDOM.scalarField(this, 'boostRule', boostRule);
    /**

    ##### sortOrder

    The sort order that the recommendation is based on.

    **/
    spotDOM.scalarField(this, 'sortOrder', sortOrder);
    /**

    ##### conditions

    The conditions that influence this recommendation.

    **/
    spotDOM.arrayField(this, 'condition', options.conditions || []);
    /**

    ##### queryTransform

    A list of query transforms to apply to the final query.

    **/
    spotDOM.arrayField(this, 'queryTransform', options.queryTransforms || []);
    /**

    ##### relevance

    Whether or not to show products that have nothing to do with the boost rule.
    Can have three values:

    * `any`: Show any product.
    * `and`: Show products that are relevant to all rules only.
    * `or`: Show products that are relevant to any rule in the boost.

    **/
    spotDOM.scalarField(this, 'relevance', options.relevance || "any");
    /**

    ##### mode

    The mode under which the recommendation will operate. Can be either `"spot"` or `"shopify"`. Will default to `"shopify"` if
    spotDOM.shopifyMode() is true.

    **/
    spotDOM.scalarField(this, 'mode', !boostRule ? "shopify" : "spot");
    /**

    ##### limit

    The amount of products to retrieve for this recommendation. Default 4.

    **/
    spotDOM.scalarField(this, 'limit', options.limit || 4);
    /**

    ##### context

    ###### product

    The product to use as a base for recommendations. Should be the product on the current page, if we're on the product page.
    Must be supplied. This function assumes that it is in Shopify front-end format, as that's the format that `{{ product | json }}` comes out in,
    which is generally where this comes from. Could also be retrieved via Spot, or Shopify's ajax API.

    ###### manual_recommendations

    A list of product ids to be used as a set of manual recommendations. Can be set up from the Spot control panel.

    ###### recently_viewed

    A list of products that have been recently viewed. Kept track of by SpotDefault, usually.

    ###### variantId

    The variant id to reference when looking at recommendations. By default, will pull this from the `variant` variable in the query string.
    Can be null.

    ###### collection

    The collection object that should be used in cnotext for recommendations. By default, will pull this from the address structure.

    ###### customer

    The customer object, to be used for personalized recommendations.

    **/
    spotDOM.scalarField(this, 'context');
    /**

    ##### products

    The array of products that have been recommended for this objecft.

    **/
    spotDOM.arrayField(this, 'product');

    /**

    #### Getters

    ##### getName

    Returns the localized name for this recommendation set.

    **/
    this.getName = function(locale) {
      return typeof(this.name()) == 'object' ? this.name()[locale || spotDOM.locale()] : this.name();
    };

    this.getQuery = function(context) {
      if (!context)
        context = this.context() || {};
      var query = spotDOM.spotAPI.s().rows(this.limit()).locale(spotDOM.locale()).allVariants(false).category("Recommendation");
      if (spotDOM.clusters())
        query = query.clusters(spotDOM.clusters());
      if (spotDOM.hostname())
        query = query.hostname(spotDOM.hostname());
      if (context.product || context.products)
         query = query.id("not_in", context.products ? context.products.map(function(p) { return p.id; }) : [context.product.id]);
      var sortOrder = this.sortOrder() ? (typeof(this.sortOrder()) == "object" ? this.sortOrder().getUniqueId() : this.sortOrder()) : "bestselling";
      if (this.boostRule()) {
        var boostQuery = this.boostRule().getQuery(context, false);
        if (this.relevance() == "and") {
          query = query.and(boostQuery);
        } else if (this.relevance() == "or") {
          query = query.or(boostQuery).boost(boostQuery);
        } else {
          query = query.boost(boostQuery);
        }
        query = query.and(this.boostRule().getQuery(context, true));
      }
      if (this.conditions().length > 0)
        query = query.merge(this.conditions());
      query = query.sort(sortOrder).split(this.split() || spotDOM.split()).productMerge(this.merge() || spotDOM.merge());
      this.queryTransforms().forEach(function(queryTransform) {
        queryTransform.forEach(function(t) {
          query = t.call(this, query, "recommendation");
        });
      });
      return query;
    };
    /**

    #### Functions

    ##### init

    Initializes the object, performing the query using the specified context, and then adds the products to the list.

    **/
    this.requiresAdditionalGet = function() {
      var products = this.context().products ? this.context().products : (this.context().product ? [this.context().product] : null);
      return this.boostRule() && products && products.filter(function(product) { return !product.metafields; }).length > 0 && this.boostRule().requiresAdditionalGet();
    };
    this.alreadyInited = false;
    this.init = function(context) {
      this.context(context);
      if (!this.alreadyInited) {
        this.alreadyInited = true;
        this.context(function(context) {
          this.query(context).done(function(products) {
            recommendation.removeProducts(recommendation.products());
            recommendation.addProducts(products);
          });
        }, true);
      }
      return this;
    };
    /**

    ##### query

    Returns a promise that resolves with the list of products that represent the recommendation.

    **/
    this.query = function(context) {
      var deferred = spotDOM.spotAPI.Deferred();
      if (recommendation.mode == "shopify" || spotDOM.shopifyMode()) {
        if (this.product()) {
          spotDOM.spotAPI.ajax('/recommendations/products.json', { data: { product_id: this.product().id, limit: this.limit() } }).done(function(data) {
            deferred.resolve(data.products);
          }).fail(function() {
            deferred.reject.apply(deferred, arguments);
          });
        }
      } else {
        // Check to see if we require us to grab product metafields with our boost rule.
        if (!context)
          context = this.context() || {};
        var internalDeferred = spotDOM.spotAPI.Deferred();
        var products = context.products ? context.products : (context.product ? [context.product] : null);
        if (this.requiresAdditionalGet()) {
          spotDOM.getExtraInfo(products.map(function(product) { return product.id;  })).done(function(products) {
            recommendation.context(Object.assign(Object.assign({}, recommendation.context()), { products: products }), false);
            internalDeferred.resolve();
          }).fail(function() { internalDeferred.resolve(); });
        } else
          internalDeferred.resolve();
        internalDeferred.done(function() {
          recommendation.getQuery().e().done(function(products) {
            deferred.resolve(products);
          }).fail(function() {
            deferred.reject.apply(deferred, arguments);
          });
        });
      }
      return deferred;
    };
  };

  /**

  ### SortOrders

  Sort orders are objects which represent a potential sort order on the Shopify store. There are number of default ones. Generally, this is populated from the Spot control panel, but can also be manually populated.


  #### new

  Takes in the field string, the direction, as well as an optional name. Also takes in JSON format as passed in from the Spot-controlled metafields.

      new spotDOM.SortOrder(field)

      new spotDOM.SortOrder("created-desc")

      new spotDOM.SortOrder("created-descending")

      new spotDOM.SortOrder(field, direction)

      new spotDOM.SortOrder("created", "desc")

      new spotDOM.SortOrder(field, direction, name)

      new spotDOM.SortOrder("created", "desc", "My Created Order")

      new spotDOM.SortOrder({"option":"Size"}, "desc", "My Sizes")

      new spotDOM.SortOrder({"product-metafield":{"mynamespace":"mykey"}}, "asc", "My Metafield Ordering")

      new spotDOM.SortOrder(json)

      new spotDOM.SortOrder({"name":"Best Match","property":"search","direction":"desc","type":"special","active":true})

      new spotDOM.SortOrder({"name":"My Metafield Ordering","property":"product-metafield","direction":"desc","type":"numeric","active":true,"namespace":"mynamespace","key":"mykey"})

  **/
  this.SortOrder = function(field, direction, name, hidden) {
    this.spotDOM = spotDOM;
    this.simple = false;
    this.hidden = hidden;


    this.isShopifySortOrder = function() {
      if (typeof(this.field) == "object")
        return false;
      return this.field == "search" || this.field == "manual" || this.field == "created" || this.field == "title" || this.field == "price" || this.field == "best-selling" || this.field == "sales";
    };

    if (typeof(field) == "object" && (field['label'] || field['name'])) {
      this.name = field['label'] || field['name'];
      this.active = field['active'] != null ? field['active'] : undefined;
      if (field.property == "option") {
        this.field = {"option": field.option};
      } else if (field.property == "product-custom-field" || field.property == "variant-custom-field") {
        this.field = { };
        this.field[field.property] = field.field;
      } else if (field.property == "product-metafield" || field.property == "variant-metafield") {
        this.field = { };
        this.field[field.property] = { }
        this.field[field.property][field.namespace] = field.key;
      } else {
        this.field = field.property;
      }
      this.direction = field.direction;
    } else {
      if (!direction && typeof(field) == "string") {
        var groups = /^(.*?)-(asc|desc)(ending)?$/.exec(field);
        if (groups) {
          field = groups[1];
          direction = groups[2];
        }
      }

      this.field = field;
      this.direction = direction;
      this.name = name;
      if (!this.isShopifySortOrder())
        this.simple = true;
    }
    if (this.field == "manual")
      this.direction = "asc";
    else if (this.field == "best-selling" || this.field == "search")
      this.direction = "desc";
    if (!this.direction)
      this.direction = "asc";

    /**

    #### Getters


    ##### getName

    Returns a nice looking name that represents this sort order. If `name` was specified on construction, uses that. Takes in a locale; otherwises, uses the default one.

    **/

    this.getName = function(locale) {
      if (!locale)
        locale = this.spotDOM.locale();
      if (this.name) {
        if (typeof(this.name) == 'object')
          return this.name[locale];
        return this.name;
      }
      if (this.field == "created" && this.direction == "asc")
        return "Oldest to Newest";
      if (this.field == "created" && this.direction == "desc")
        return "Newest to Oldest";
      if (this.field == "title" && this.direction == "asc")
        return "A-Z";
      if (this.field == "title" && this.direction == "desc")
        return "Z-A";
      if (this.field == "title" && this.direction == "desc")
        return "Z-A";
      if (this.field == "price" && this.direction == "asc")
        return "Price: Low to High";
      if (this.field == "price" && this.direction == "desc")
        return "Price: High to Low";
      if (this.field == "best-selling" && this.direction == "desc")
        return "Best Selling";
      if (this.field == "manual" && this.direction == "asc")
        return "Featured";
      if (this.field == "search" && this.direction == "desc")
        return "Best Match";

      var internalField = this.field;
      if (typeof(this.field) == "object")
        internalField = this.field['option'] || (this.field['product-metafield'] ? Object.values(this.field['product-metafield'])[0] : null) || (this.field['variant-metafield'] ? Object.values(this.field['variant-metafield'])[0] : null);
      return (internalField.charAt(0).toUpperCase() + internalField.slice(1) + " " + (this.direction == "asc" ? "Ascending" : "Descending")).replace(/_/g, " ");
    };

    /**

    ##### getUniqueId

    Returns an ID that is unique to this sort order. Usually is descriptive of the facet as well. Suitable for direct insertion into a query string, or
    for use as a DOM node id. Should ideally match Shopify's *front-end* sort order values identically, in most cases. (Back-end sort orders are called
    completely different things for some reason.)

    **/
    this.getUniqueId = function(locale) {
      if (typeof(this.field) == "object")
        return spotDOM.handleize(this.getName(locale));
      if ((this.field == "manual" && this.direction == "asc") || (this.field == "search" && this.direction == "desc"))
        return this.field;
      if (this.simple)
        return this.field + (this.direction == "desc" ? "-descending" : "");
      if (this.field == "best-selling" || (this.field == "sales" && this.direction == "desc"))
        return "best-selling";
      return this.field + "-" + this.direction + "ending";
    };

    /**

    ##### getShopifySortOrder

    Returns what the Shopify sort order value would be for this sort option; used when `shopifyMode` is active.

    **/
    this.getShopifySortOrder = function() {
      return this.getUniqueId();
    };

    /**

    ##### isShopifySortOrder

    Returns whether or not this sort order would be applicable in Shopify, or whether this is a custom sort order.

    **/
    /**

    ##### isRelevant

    Returns true if this sort order is relevant to the current situation (i.e. will ignore things like "best match" if you're not searching).

    **/
    this.isRelevant = function() {
      if (this.field == "search")
        return spotDOM.search() || spotDOM.isSearchPage();
      if (this.field == "manual")
        return spotDOM.collection() || spotDOM.isCollectionPage();
      if (spotDOM.shopifyMode() == "storefront") {
        if (spotDOM.isSearchPage())
          return this.field == "price" || this.field == "search";
        return this.isShopifySortOrder();
      } else if (spotDOM.shopifyMode() == "shopify") {
        return this.isShopifySortOrder();
      }
      return true;
    };

    /**

    ##### isVisible

    Returns true if this sort order is both relevant and listed as visible.

    **/
    this.isVisible = function() {
      if (this.active !== undefined && this.active === false)
        return false;
      return this.isRelevant();
    };
  };


  /**

  ## Events

  SpotDOM will fire off certain events which should be handled by your implementation. A detailed list is below. A callback handler can be supplied by simply passing a single function as the argument
  to the listed event.

  ### products

  The **most important** event. A listener which receives the list of products when a query complete. This should be generally used to populate your various DOM elements.

  For example, to simply list all prodcuts returned by a query, you can write the following.

    spotDOM.products(function(products) { console.log(products); });

  **/
  this.scalarField(this, 'products');
  /**

  ### spellCheck

  A listener which receives the result of a spellingCheck, if one was performed. It is called with an object representing the results of the check, which includes the original string, and what
  it was corrected to.

  **/
  this.scalarField(this, 'spellCheck');
  /**

  ### beginQuery

  Called when a query in requested, before any work is done. Can be used to alter the state of the page before things are processed into the query.

  ### query

  Called immediately after results are returned, contains all products, and resultset information. If you need to do any manual processing of the return data, to populate some information about the resultset,
  such as to update facets, or pagination, it's usually done in here.

  **/
  this.listeners(this, 'beginQuery');
  /**

  ### endQuery

  Called when a query has been completed after all work is done, and all handlers are called.

  **/
  this.listeners(this, 'endQuery');
  /**
  ## Variables

  Various variables exist on the SpotDOM object. All variables can be hooked with a listener, which runs whenever the variable is set, in the exact same manner as events listed above.
  This should be your main way of interacting with the SpotDOM object to customize its behaviour.

  ### Array Variables

  Array variables have two functions; .addVariableName and .removeVariableName. Both of these can be hooked, by passing a function to either.

  #### facet

  Contains a list of all facets to be used by the system. The full list can be retrieved with `.facets()`, and individual facets can be added/removed with
  `.addFacet(facet)` or `.removeFacet(facet)`.

  **/
  this.arrayField(this, 'facet');
  /**

  #### condition

  Contans a list of all conditions to be applied to all queries generated from the system. Individual conditiosn can be added/removed with
  `.addCondition` or `.removeCondition`.

  **/
  this.arrayField(this, 'condition');
  /**

  #### recommendations

  Contains a list of all recommendations to be made by the system. The full list can be retrieved with `.recommendations()`, and individual facets can be added/removed with
  `.addRecommendation(recommendations)` or `.removeRecommendation(recommendation)`.

  **/
  this.arrayField(this, 'recommendation');
  /**

  #### checkoutRecommendation

  The recommendation to use at checkout, if applicable.

  **/  
  this.scalarField(this, 'checkoutRecommendation');
  /**

  #### cart

  The current cart.

  **/
  this.scalarField(this, 'cart');
  /**

  #### redirects

  Contains a list of all redirects to be made by the system. The full list can be retrieved with `.redirects()`, and individual redirects can be added/removed with
  `.addRedirect(redirect)` or `.removeRedirect(redirect)`.

  Redirects will be acted upon when a user decides to search, by either hitting enter, or hitting "Show Results".

  A sample redirect looks like: `{"locale":"en","term":"Shoes","type":"exact"}`

  **/
  this.arrayField(this, 'redirect');
  /**

  #### sortOrder

  Contains a list of all possible sort orders for this view. The full list can be retrieved with `.sortOrders()`, and individual orders can be added/removed
  with `.addSortOrder(sortOrder)` or `.removeSortOrder(sortOrder)`.

  **/
  this.arrayField(this, 'sortOrder');
  // If our current sort order is transient, swap it for the one we've just entered, if their IDs are identical.
  this.addSortOrder(function(sortOrder) {
    if (this.sort() && sortOrder.getUniqueId() == this.sort().getUniqueId())
      this.sort(sortOrder, false);
  });
  // This function does magic to retrieve a sort order from amongst the listed array.
  // Takes all sort of stuff. unique ids, original sort order declrations, field descriptors.
  this.getSortOrder = function(magic) {
    return spotDOM.sortOrders().filter(function(e, idx) {
      if (magic == null)
        return null;
      if (magic.getUniqueId)
        return magic.getUniqueId() == e.getUniqueId();
      if (magic.label)
        return magic.label == e.getName();
      if (typeof(magic) == "number" || (typeof(magic) == 'string' && !isNaN(parseFloat(magic)))) {
        if (e.id != null)
          return magic == e.id;
        return magic == idx;
      }
      if (typeof(magic) == "string")
        return magic == e.getName() || magic == e.getUniqueId();
      return false;
    })[0];
  };
  /**

  ### Scalar Variables

  Scalar variables can be hooked by simply calling the name of the variable, and passing a function, like so:

      spotDOM.collection(function(collection) { console.log("The collection I set is: " + collection); })

  #### accessToken

  A storefront access token. If supplied, will automatically query Shopify's storefront API, and update most variables
  on products with information that is currently the most up-to-date. Introduces an additional delay in requests, but will
  ensure that most properties are 100% up to date.

  **/
  this.scalarField(this, 'hostname', options.hostname || (window.Shopify && window.Shopify.shop));
  this.scalarField(this, 'headers', options.headers);
  this.scalarField(this, 'clusters', options.clusters);
  this.scalarField(this, 'accessToken', options.settings && options.settings.access_token);
  this.scalarField(this, 'storefrontVersion', options.settings && options.settings.storefront_version);
  this.scalarField(this, 'storefrontOnlyNonPrimary', true);
  this.scalarField(this, 'storefrontEnabled', false);
  this.scalarField(this, 'storefrontType', 'deferred');
  this.scalarField(this, 'domain', this.location.hostname || this.hostname());
  this.storefront = function() {
    return this.domain() && this.accessToken() && this.storefrontVersion() && new window.SpotStorefront(this.domain(), this.accessToken(), this.storefrontVersion());
  };
  this.updateStorefrontProducts = function(products, token) {
    if (!token && !this.accessToken() || products.length == 0 || !this.storefrontEnabled() || (this.storefrontOnlyNonPrimary() && spotDOM.isPrimaryLocale() && spotDOM.isPrimaryMarket()))
      return spotAPI.Deferred().resolve(products);
    var deferred = spotAPI.Deferred();
    this.storefront().query(
      (spotDOM.market() ? 'query @inContext(country: ' + spotDOM.market() + ', language: ' + spotDOM.locale().toUpperCase() + ') ' : '') + 
      (!spotDOM.market() ? 'query @inContext(language: ' + spotDOM.locale().toUpperCase() + ') ' : '') +
      '{ ' +
        products.map(function(product) {
          return 'p' + product.id + ' : product(id: "gid://shopify/Product/' + product.id + '") { id title handle tags vendor productType descriptionHtml variants(first: 100) { nodes { id price { amount } compareAtPrice { amount } sku selectedOptions { value } } } }';
        }).join("\n") +
      '}'
    ).done(function(response) {
      var storefrontProducts = {};
      var storefrontVariants = {};
      Object.values(response.data).forEach(function(product) {
        product.variants.nodes.forEach(function(variant) {
          var id = variant.id.replace(/\D/g, "");
          storefrontVariants[id] = {
            price: variant.price.amount,
            compare_at_price: variant.compareAtPrice && variant.compareAtPrice.amount,
            sku: variant.sku,
            option1: variant.selectedOptions[0].value,
            option2: (variant.selectedOptions.length > 1 ? variant.selectedOptions[1].value : null),
            option3: (variant.selectedOptions.length > 2 ? variant.selectedOptions[2].value : null)
          };
        });
        var id = product.id.replace(/\D/g, "");
        storefrontProducts[id] = {
          title: product.title,
          handle: product.handle,
          product_type: product.productType,
          tags: product.tags.join(", "),
          vendor: product.vendor,
          body_html: product.descriptionHtml
        };
      });
      products = products.map(function(product) {
        (product.original ? product.original.variants : product.variants).forEach(function(variant) {
          if (storefrontVariants[variant.id])
            Object.assign(variant, storefrontVariants[variant.id]);
        });
        if (storefrontProducts[product.id])
          Object.assign(product.original ? product.original : product, storefrontProducts[product.id]);
        if (product.original)
          product = Object.assign({}, product, spotAPI.convertProduct(product.original));
        
        return product;
      });
      if (spotDOM.storefrontType() == 'deferred')
        deferred.resolve(products);
      else
        document.dispatchEvent(new CustomEvent('SpotRefresh', { detail: { products: products } }));
    });
    if (this.storefrontType() == 'progressive')
      deferred.resolve(products);
    return deferred;
  };
  /**

  #### collection

  Denotes the collection to search in. By default, this is taken from the address bar path. If not present, no collection is used.
  **/
  this.getCollectionPage = function() {
    var groups = /\/collections\/([^#\/?]+)/.exec(this.location.pathname);
    return groups && groups.length > 1 && groups[1] != "vendors" && groups[1] != "types" ? groups[1] : null;
  };
  this.scalarField(this, 'collection', this.getCollectionPage());
  /**

  #### queryCount

  The amount of queries this instance has issued.

  **/
  this.scalarField(this, 'queryCount', 0);
  /**

  #### sort

  Denotes the sort order. By default, this is taken from the query paramters. Possible values include all normal Shopify values, for the sorting dropdown, as well as "search", "featured", and any custom sort orders you may have set up in Spot.

  Can also take in a `SortOrder` object from the `sortOrders` array. If the `sort` is set to be a string, it will automaticalliy wrap the incoming string in a `SortOrder` object before setting it. This is known as a transient sort order. This sort order
  is not automatically passed to listeners, and once unset, will be removed from the list.

  **/
  this.scalarField(this, 'defaultSort');
  this.scalarField(this, 'sort', vars['sort_by'], {
    "preset": function(value) {
      if (typeof(value) == "string") {
        var orders = spotAPI.grep(this.sortOrders(), function(e) { return e.getUniqueId() == value });
        if (orders.length > 0)
          return orders[0];
        return new this.SortOrder(value);
      } else if (value != null) {
        return value;
      } else {
        return this.sort() || this.defaultSort();
      }
    }
  });
  this.isUsingDefaultSort = function() {
    return this.sort() == this.defaultSort() ||
        (!this.defaultSort() && this.isCollectionPage() && this.sort().field == "manual" && this.sort().direction == "asc") ||
        (!this.defaultSort() && this.isSearchPage() && this.sort().field == "search" && this.sort().direction == "desc");
  };
  this.isUsingShopifyDefaultSort = function() {
    return this.isUsingDefaultSort() && (
      !this.sort() ||
      (this.isCollectionPage() && this.sort().field == "manual" && this.sort().direction == "asc") ||
      (this.isSearchPage() && this.sort().field == "search" && this.sort().direction == "desc"));
  };
  /**

  #### search

  The free-form text search used currently. Taken from the "q" query parameter, by default. If not present, performs no textual search.

  **/
  this.scalarField(this, 'search', vars['q'] && vars['q'].replace(/( AND )?\(\(.*/g, "") ? vars['q'].replace(/( AND )?\(\(.*/g, "") : null);
  /**

  #### searchBoost

  A list of boosts to apply on various search terms. This is referred to as "search pins" in the admin control panel.

  **/
  this.arrayField(this, 'searchBoost');
  /**
  #### autoCorrect

  Determines whether queries should be made with autoCorrect. On by default.

  **/
  this.scalarField(this, 'autoCorrect', true);
  /**

  #### pins

  The pins used for this query. Pins are set up in the Spot control panel. By default, there are no pins.
  This is an array of product ids, that will force the products, in the order specified, to always be
  at the top of the returned query. Search pins may or may not appear in this array, even if set up in the
  control panel.

  **/
  this.scalarField(this, 'pins', vars['pins'] || []);
  /**

  #### antipins

  The antipins used for this query. Antipins are set up in the Spot control panel. By default, there are no antipins.
  This is an array of product ids, that will force the products, in the order specified, to always be
  at the bottom of the returned query. Search antipins may or may not appear in this array, even if set up in the
  control panel.

  **/
  this.scalarField(this, 'antipins', vars['antipins'] || []);
  /**

  #### boosts

  The boosts used for this query. Boosts are set up in the Spot control panel by boost rules.
  By default, there are no boosts. They can however be applied conditionally on certain resultsets,
  and in certain contexts.

  This is a query derived from boost rules that boost certain products and results over others.

  **/
  this.arrayField(this, 'boost', vars['boosts'] || []);
  /**

  #### personalization

  The personalization governing this query.

  **/
  this.scalarField(this, 'personalization');
  /**

  #### badges

  The badges used for this particular state.

  **/
  this.arrayField(this, 'badge', []);
  /**

  #### banners

  The banners used for this particular state.

  **/
  this.arrayField(this, 'banner', []);
  /**

  #### split

  The split used for this query. Splits are set up in the Spot control panel. By default, the split is "auto".

  **/
  this.scalarField(this, 'split', vars['split'] || 'auto');
  /**

  #### merge

  The merge used for this query. Merges are set up in the Spot control panel. By default, the merge is "none".

  **/
  this.scalarField(this, 'merge', vars['merge']);
  /**

  #### page

  The page used. By default, this is 1, unless specified in the query string to be different.

  **/
  this.scalarField(this, 'page', parseInt(vars['page']) || 1);
  /**

  #### locale

  The locale used. By default, this is `Shopify.locale`. If no locales are set up on your store, this will be ignored by Spot. Shouldn't be modified after init, generally.
  This is becuase unless the rest of the site is also JS-based, huge amounts of text is also not going to be translated.

  **/
  this.scalarField(this, 'locale', options.locale || (window.Shopify ? window.Shopify.locale : null));
  /**

  #### currency

  The currency used. By default, this is taken from `Shopify.currency`. Takes a hash in the form of `{ code: "3LC", "rate": 1.0000 }`. Will be replaced by what the server
  says is correct.

  **/
  this.scalarField(this, 'currency', options.currency || (window.Shopify && window.Shopify.currency ? { code: window.Shopify.currency.active, rate: window.Shopify.currency.rate } : null));
  /**

  #### market

  The market used. By default, this is taken from `Shopify.country`. Takes a the form of a string, such as "US", or "CA".

  **/
  this.scalarField(this, 'market', options.market || (window.Shopify && window.Shopify.country ? window.Shopify.country : null));
  /**

  #### catalog

  The catalog id(s) to use.

  **/
  this.scalarField(this, 'catalog', options.catalog);
  /**

  ## companyId

  The id associated with this customer's logged in account. Used to access specific catalogs.
  If present, takes priority over market.

  **/
  this.scalarField(this, 'companyId', options.companyId);
  /**

  #### fields

  The fields required of the returned products. By specifying an array here, you can reduce the amount of bandwidth required to retrieve a page of products.
  By default, this is empty, and the query will return the entire product data.

  **/
  this.scalarField(this, 'fields');
  /**

  #### count

  The amount of products in the last resultset queried, as returned by a query.

  **/
  this.scalarField(this, 'count');
  /**

  #### countBehavior

  The type of count that is performed. See the [SpotAPI counting section](#resultset-counting) for details. By default, this is "approximate".

  **/
  this.scalarField(this, 'countBehavior', options.countBehavior || 'approximate');
  /**

  #### optionBehavior

  The type of count that is performed for facets. The four supported values are `none`, `exists`, `approximate` and `exact`. By default, this is `approximate`. The meanings of this are below, in decreasing order of speed.

  * `none`: Will not pass options to Spot, but will instead simply keep track of facets without counting. Fastest.
  * `exists`: Will return a 1 or 0, depending on whether or not the facet has any values in the resultset. Pretty Fast.
  * `approximate`: What this means varies depending on the situation, but generally it means that if you have a decent amount of facets and/or products returned, the counts displayed next to each facet won't be exact. Semi-Fast.
  * `exact`: Will return the exact amount of products on a particular facet. Slowest.

  **/
  this.scalarField(this, 'optionBehavior', options.optionBehavior || 'approximate');
  /**

  #### maxFacetValuesPerFacet

  The maximum amount of values returned for facets in this query. For performance reasons this number is clamped to 1000, maximum.

  **/
  this.scalarField(this, 'maxFacetValuesPerFacet', 100);

  /**

  #### paginate

  The amount of products to be returned in a given page of products. By default, this is taken from the query string, in the `rows`, or `paginate` parameters.
  If neither of these are specified, it's taken from whatever spot's default query is, and if that's not specified, the default is 12.

  **/
  this.scalarField(this, 'paginate', options.paginate || parseInt(vars['rows'] || vars['paginate'] || spotAPI.s().innerAttributes.rows || 12));
  /**

  #### allVariants

  By default, false. Determines whether or not we should return ALL the variants present for a particular product that gets a hit from the query. This property can have
  three values.

  * `false`: Retrieve only variants which match your criteria.
  * `"flag"`: Retrieve all variants for your product resultset, but add a true boolean of "relevant" in the product JSON to denote which variants are relevant for your query.
  * `true`: Retrieve all variants for your product resultset.

  As an example, if this is false, when you query for every "Green" variant that's in stock, you'll get only products who have a green variant that's in stock, and a Red in stock variant on the same product will be omitted.
  If true, you'd get that, Red in stock variant as well. If `"flag"`, you'd get the Red variant, but it wouldn't have `"relevant":true`, whereas the Green variants would.

  **NB**: When using `"flag"`, absence of the key `relevant` is considered `false`.


  **/
  this.scalarField(this, 'allVariants', false);
  /**

  #### omitExtraneousResults

  By default, false. Determines whether or not we should omit extraneous results from search that may
  or may not be relevant. If set to true, or a specific number, it will omit results that have a normalized relevancy score below
  the specified threshold (or `0.8` if `true`). Normalized scores are computed via the following formula:

      score = (ln(relevancy) - ln(lowest_relevancy_in_set)) / (ln(highest_relveancy_in_set) - ln(lowest_relevancy_in_set))

  A bit of a sledgehammer, and will not work in all situations, but if a store uses very broad terms that have highly relevant results
  and little relevant results together, can be used to work through the situation in a transparent manner.

  **/
  this.scalarField(this, 'omitExtraneousResults', options['omitExtraneousResults'] != null ? options['omitExtraneousResults'] : false);
  /**

  #### debugMode

  A boolean flag. Has three different modes, true, false, and null. By default, the value is the value of the "debugMode" key in session storage.

  If true, the following happens:

  * Warnings are promoted to errors, and throw exceptions.
  * `failBehaviour` is set to `null`.

  If false, the following happens:

  * Warnings are logged to a console, if present, but otherwise ignored.

  If null, the following happens:

  * Upon a request being made, if the debugger is open in at least Chrome, or if Firebug is being used, debugMode is set to true.
  * Warnings are logged to a console, if present, but otherwise ignored.

  **/
  this.scalarField(this, 'debugMode', this.getStorage("spotDebugMode"));
  if (this.debugMode())
    this.log("This instance of Spot has booted in 'Debug Mode'. Warnings are promoted to errors, and failBehaviour has been forced to null.");

  // This is somewhat ill-supported. Consider removing this.
  this.debuggerOpened = false;
  this.isDebuggerOpen = function() {
    /* if (window.console) {
      if (window.console.firebug)
        this.debuggerOpened = true;
      try {
        var devtools = function(){};
        devtools.toString = function() {
          this.debuggerOpened = true;
        };
        console.log('%c', devtools);
      } catch (e) {

      }
    }*/
    return this.debuggerOpened;
  };
  /**
  #### shopifyMode

  If this is set to "shopify", or "storefront", either because of a Spot outage, or other reason, Spot will do its best to serve queries using Shopfy's native system, although it will not be possible to offer
  the full Spot featureset. Should be hooked to enable/disable Spot specific features over and above faceting (if you have any). Related to `failBehavior` below.

  By default, if this is set, its value is stored in sessionStorage. This value will persist until the user quits or restarts their browser,
  or until 10 minutes have passed, whichever is quicker. Whenever a page loads in shopifyMode, a console log will be emitted informing the user of this.

  When set to true, the implementation will remove all facets that cannot be served up by Shopify's native system. This means the following:

  * Anything that is not a tag-based facet will be removed.
  * If the user is on the search page, in addition to tag-based facets, it will also preserve product_type facets, and vendor facets, if the `values` for these are presented to the system as part of its initialization (see [facets](#facets) below).

  **/
  this.scalarField(this, 'shopifyMode', this.getStorage("spotShopifyMode") || false);
  if (this.shopifyMode())
    this.log("This instance of Spot has booted in 'Shopify Mode', due to an error encountered previously. To disable this functionality, make sure you call spotDOM.failBehavior(null) immediately after instantiating. To disable it transiently, call spotDOM.shopifyMode(false) in your console.");
  /**

  #### failBehavior

  By default this is set to `null`. Other valid values are `"shopify"`. When set to `"shopify"`, when a Spot query fails, for whatever reason, Spot will set `shopifyMode`
  to true, and call all listeners. This allows you to gracefully degrade into a shopify-powered version of search and faceting. While nowhere near as powerful,
  or sporting many features, this should enable the site to still serve products and searches, even if most of the powerful functionality is lost while the outage
  occurs. When this is set to `null`, and `shopifyMode()` is true, it will automatically set `shopifyMode(false)`.

  **/
  this.scalarField(this, 'failBehavior', null);
  this.failBehavior(function(behavior) {
    if (behavior == null && this.shopifyMode())
      this.shopifyMode(false);
  });
  this.debugMode(function(debug) {
    this.setStorage("spotDebugMode", debug);
    if (debug != null) {
      if (debug) {
        this.failBehaviour(null);
      }
    }
  }, true);
  this.listeners(this, 'fail');

  /**

  #### moneyFormat

  The format you want to render prices in, by default. By default this is '${{amount}}'.

  **/
  this.scalarField(this, 'moneyFormat', options.moneyFormat || '${{amount}}');

  /**

  #### queryTransform

  An array that transform the primary query, just before it's sent off. Can be used to add additional conditions
  to every query, simply by doing:

    window.spotDOM.addQueryTransform([function(query) {
      return query.available(true);
    }]);

  Takes an array of functions to run.

  **/
  this.arrayField(this, 'queryTransform', options.queryTransforms || []);
  /**

  ### searchRuleHandle

  The handle associated with the search rule; used to generate URLs from searches
  to product pages.

  **/
  this.scalarField(this, 'searchRuleHandle');
  /**

  ## Functions

  A number of utility functions which make managing dom elements easier.

  ### getProductTitle

  Takes in a product, and returns an appropriate description, based on returned variants, if `allVariants` is false, and splits. Generally should be preferred over simply using `product.title`.
  Basically, loop through all three options on a product. If all variants share the same value for this option, add the option to our returned string.

  As an example, if the result of a product query returns a product called "T-Shirt" has "Size" and "Color" as options, if our returned variants are ["Red", "Small"], ["Red", "Large"],
  ["Green", "Small], ["Green", "Large"] the title would be output as "T-Shirt".

  If the same query has the result of ["Red", "Small"], ["Red", "Large"], the returned title would be "T-Shirt - Red".

  **/
  this.getProductTitle = spotAPI.getProductTitle;
  this.getRelevantVariant = spotAPI.getRelevantVariant;
  this.getRelevantVariants = spotAPI.getRelevantVariants;
  this.getProductPrice = spotAPI.getProductPrice;
  this.getProductCompareAtPrice = spotAPI.getProductCompareAtPrice;
  /**


  ### getProductPrice

  Takes in a product, and returns an appropriate price, based on returned variants, if `allVariants` is false, and splits.

  **/
  /**


  ### getProductCompareAtPrice

  Takes in a product, and returns an appropriate compreAtPrice, based on returned variants, if `allVariants` is false, and splits.

  **/

  /**

  ### getProductURL

  Takes in a product, and returns an appropriate URL. Will either return a direct link to the product, or one nestled in a collection, or with a variant if applicable.

  **/
  this.getProductURL = function(product, variant) {
    if (Array.isArray(product))
      return this.getProductURL(product[0], variant);
    var prefix = "";
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root && window.Shopify.routes.root != "/")
      prefix = window.Shopify.routes.root.replace(/\/$/, "");
    else if (!this.isPrimaryLocale())
      prefix = "/" + this.locale();
    variant = variant || this.getRelevantVariant(product);
    var suffix = "";
    if (variant && variant.id)
      suffix = "?variant=" + encodeURIComponent(variant.id);
    var override = this.getOverrideRuleHandle();
    if (override)
      suffix = suffix + (suffix == "" ? "?" : "&") + "rule=" + encodeURIComponent(override);
    else if (this.isSearchPage() && this.searchRuleHandle())
      suffix = suffix + (suffix == "" ? "?" : "&") + "rule=" + encodeURIComponent(this.searchRuleHandle());
    if (this.collection() && this.collection() != "all")
      return prefix + '/collections/' + this.collection() + '/products/' + product.handle + suffix;
    return prefix + '/products/' + product.handle + suffix;
  };
  this.getCollectionURL = function(collection) {
    var prefix = "";
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root && window.Shopify.routes.root != "/")
      prefix = window.Shopify.routes.root;
    else if (!this.isPrimaryLocale())
      prefix = "/" + this.locale();
    return prefix = "/collections/" + collection.handle;
  };
  /**


  ### getProductImage

  Takes in a product, and returns an appropriate description, based on returned variants, if `allVariants` is false, and splits. Generally should be preferred over simply using `product.image`.

  Allows for the system to remove irrelevant images from results that only return some variants, or results that are using a split.

  **/
  this.getProductImage = spotAPI.getProductImage.bind(spotAPI);
  this.getPreferredImage = spotAPI.getPreferredImage.bind(spotAPI);

  /**

  ### formatMoney

  Takes in the value from Spot, converts it to the local currency be used, as well as an optional formatting string, and returns a nicely formatted string. For example, passing "1000", could return "$1,000.00", depending
  on the money format.

  **/
  this.formatMoney = function(money, format, currency) {
    return spotAPI.formatMoney(money, format || spotDOM.moneyFormat(), currency || (!this.market() && this.currency()));
  };
  /**

  ### getSizedImage

  Takes in an image url/object, and a size, and then returns the relevant URL for that image, sized at a particular size, using Shopify's image CDN.

  **/
  this.getSizedImage = spotAPI.getSizedImage;
  /**

  ### getSizedImageDimensions

  Takes in an image url/object, and a size, and then returns the relevant dimensions for that image, at that size.

  **/
  this.getSizedImageDimensions = spotAPI.getSizedImageDimensions;
  /**

  ### isSearchPage

  Tells you whether or not you're on Shopify's search page.

  **/
  this.isSearchPage = function() {
    return /\/search\b/.test(this.location.pathname);
  };
  /**

  ### isCollectionPage

  Tells you whether or not you're on a collection page.

  **/
  this.isCollectionPage = function() {
    return /\/collections\b/.test(this.location.pathname) && !this.isProductPage();
  };
  /**

  ### isThankYouPage

  Tells you whether or not you're on the order completed "Thank you" page.

  **/
  this.isThankYouPage = function() {
    return /\/orders\b/.test(this.location.pathname);
  };
  /**

  ### getOverrideRuleHandle

  Retrieves the ruleset handle that should be overriding for this page. Comes
  from either the ?rule= query parameter, or, if on a collection page, the
  bit beyond the collection handle, or, if the user entered their session with
  an `override=` parameter, persists that in their session, and uses that.

  **/
  var vars = this.getUrlVars();
  this.scalarField(this, 'override', options.override || this.getStorage("override"));
  this.override(function(override) { this.setStorage("override", override); })
  if (vars['override'])
    this.override(vars['override']);
  this.getOverrideRuleHandle = function() {
    var vars = this.getUrlVars();
    if (vars['rule'])
      return vars['rule'];
    if (this.isCollectionPage()) {
      var rule = /\/collections\/[^\/]+\/([\-\w]+)/.exec(this.location.pathname);
      if (rule && rule[1])
        return rule[1];
    }
    if (this.override())
      return this.override();
    return null;
  };
  /**

  ### isPrimaryLocale

  Tells you whether or not the currently loaded locale is the primary locale.

  **/
  this.isPrimaryLocale = function() {
    return !window.Shopify || this.location.pathname.indexOf(window.Shopify.locale) != 1;
  };
  /**

  ### isPrimaryMarket

  Tells you whether or not the currently loaded market is the primary market.

  **/
  this.isPrimaryMarket = function() {
    return !window.Shopify || !window.Shopify.currency || window.Shopify.currency.rate == 1.0;
  };

  /**

  ### getCollectionPage

  Gets the handle of the collection page you're on from the URL.

  **/

  /**

  ### hasVariantID

  Checks whether or not the page has explicitly selected a variant id in the query string.

  **/
  this.hasVariantID = function() {
    if (this.isProductPage())
      return this.getUrlVars()['variant'] != null;
    return false;
  };

  /**

  ### getVariantID

  Computes the selected variant ID from the product PDP.

  **/
  this.getVariantID = function() {
    if (this.isProductPage()) {
      var id = this.getUrlVars()['variant'];
      if (id)
        return id;
      id = document.querySelector("form[action='/cart/add'] input[name='id']")
      return id && id.value;
    }
  };
  /**

  ### isVendorCollectionPage

  Tells you whether or not you're on Shopify's vendor filtration collection.

  **/
  this.isVendorCollectionPage = function() {
    return /\/collections\/vendors\b/.test(this.location.href) && !this.isProductPage();
  };
  /**

  ### getVendorCollectionPage

  Returns the vendor queried on the vendor collection page.

  **/
  this.getVendorCollectionPage = function() {
    return this.isVendorCollectionPage() && this.getUrlVars()['q'];
  };
  /**

  ### isProductTypeCollectionPage

  Tells you whether or not you're on Shopify's product type filtration collection.

  **/
  this.isProductTypeCollectionPage = function() {
    return /\/collections\/types\b/.test(this.location.href) && !this.isProductPage();
  };
  /**

  ### getProductTypeCollectionPage

  Returns the product type quereid on the product type collection page.

  **/
  this.getProductTypeCollectionPage = function() {
    return this.isProductTypeCollectionPage() && this.getUrlVars()['q'];
  };


  /**

  ### getCollectionPageTags

  Returns a list of tags that the system is currently filtering by, Shopify style. (present at the end of the path on a collection, separated by '+')

  **/
  this.getCollectionPageTags = function() {
    var groups = /\/collections\/[^/]+\/(.*)/.exec(this.location.pathname);
    if (groups && groups[1])
      return spotAPI.map(groups[1].split("+"), function(e) { return decodeURI(e); });
    return [];
  };
  /**

  ### isProductPage

  Tells you whether or not you're on a product page.

  **/
  this.isProductPage = function() {
    return /\/products/.test(this.location.href);
  };

  /**

  ### getProductPage

  Gets the handle of the product who's page you're on.

  **/
  this.getProductPage = function() {
    var groups = /\/products\/([^#\/?]+)/.exec(this.location.pathname);
    return groups && groups.length > 1 ? groups[1] : null;
  };

  /**

  ### generateQueryVariables

  Given the existing facets, sort order, page and search, generates a hash that represents the variables that should be placed into the query string.

  **/
  this.generateQueryVariables = function() {
    var vars = this.getUrlVars();
    for (var i in vars) {
      if (/^f_/.test(i) || i == "page" || i == "q" || i == "sort_by")
        delete vars[i];
    }
    this.composeFacetQueryString(vars);
    if (this.page() > 1)
      vars['page'] = this.page();
    if (this.sort() && !this.isUsingDefaultSort())
      vars['sort_by'] = this.sort().getUniqueId();
    if (this.search())
      vars['q'] = this.search();
    return vars;
  };
  /**

  ### generateQueryString

  Optionally takes in a hash. Calls `generateQueryVariables`, and merges it with the provided hash, then URL-encodes everything to produce the query string that should be placed
  into the browser's query string.

  **/
  this.generateQueryString = function(mergeHash) {
    var vars = this.generateQueryVariables();
    if (mergeHash) {
      for (var i in mergeHash)
        vars[i] = mergeHash[i];
    }
    return this.encodeUrlVars(vars);
  };
  /**

  ### updateQueryString

  Optionally takes in a hash. Calls `generateQueryString`, and places it into the browser's address bar, `using window.history.pushState`, if applicable.

  **/
  this.updateQueryString = function(mergeHash, isReplace) {
    var url = this.location.protocol + "//" + this.location.host + this.location.pathname;
    var str = this.generateQueryString(mergeHash);
    if (str != "?")
      url += this.generateQueryString(mergeHash);
    if (this.history && url != this.location.href) {
      if (isReplace)
        this.history.replaceState({}, document.title, url);
      else
        this.history.pushState({}, document.title, url);
    }
  };
  /**

  ### parseQueryString(string)

  Takes in a query string (uses window.location.href if not specified), and sets the appropriate variables.

  **/
  this.parseQueryString = function(string) {
    var vars = this.getUrlVars(string);
    spotDOM.queryGuard(function() {
      spotDOM.parseFacetQueryString(vars);
      if (vars['page'] != null && !isNaN(parseInt(vars['page']))) {
        if (parseInt(vars['page']) != spotDOM.page())
          spotDOM.page(parseInt(vars['page']));
      } else if (spotDOM.page() != 1)
        spotDOM.page(1);
      if (vars['sort_by'] != null && (!spotDOM.sort() || vars['sort_by'] != spotDOM.sort().getUniqueId()))
        spotDOM.sort(vars['sort_by']);
      else if (vars['sort_by'] == null && !spotDOM.isUsingDefaultSort())
        spotDOM.sort(spotDOM.defaultSort());
      if (vars['q'] != null && vars['q'] != spotDOM.search())
        spotDOM.search(vars['q']);
    });
  };

  /**

  ### generateQuery

  Generates a query object from spotAPI that takes into account all the selected facets, various behaviour modifiers, sorting, search, spell check,
  split, and everything like that, and returns the actual spotAPI object that does the work, and can be `.e()`'d to retrieve the desired resultset.

  **/
  this.generateQuery = function() {
    var query = this.spotAPI.s().count(this.countBehavior() != "none", this.countBehavior()).allVariants(this.allVariants()).paginate(this.paginate()).page(this.page());
    if (this.hostname())
      query = query.hostname(this.hostname());
    if (this.clusters()) 
      query = query.clusters(this.clusters());
    if (this.headers()) 
      query = query.headers(this.headers());
    if (this.sort())
      query = query.sort(this.sort().direction == "asc" ? {"asc": this.sort().field } : { "desc": this.sort().field });
    if (this.collection())
      query = query.collection(this.collection());
    if (this.getVendorCollectionPage())
      query = query.vendor(this.getVendorCollectionPage());
    else if (this.getProductTypeCollectionPage())
      query = query.product_type(this.getProductTypeCollectionPage());
    if (this.conditions() && this.conditions().length > 0)
      query = query.merge(this.conditions());
    query = this.composeFacetQuery(query, this.optionBehavior());
    if (this.search() != null)
      query = query.search(this.search()).autoCorrect(this.autoCorrect()).omitExtraneousResults(this.omitExtraneousResults());
    if (this.fields())
      query = query.fields(this.fields());
    if (this.split())
      query = query.split(this.split());
    if (this.merge())
      query = query.productMerge(this.merge());
    if (spotDOM.market()) {
      query = query.market(spotDOM.market());
      query = query.catalog({ "market": spotDOM.market() });
    }
    if (spotDOM.catalog())
      query = query.catalog(spotDOM.catalog());
    if (this.currency())
      query = query.currency(this.currency());
    if (this.locale())
      query = query.locale(this.locale());
    if (this.isUsingDefaultSort()) {
      var pins = [];
      if (this.pins())
        pins = this.pins();
      if (this.antipins())
        query = query.antipins(this.antipins());
      if (this.search() && this.searchBoosts().length > 0) {
        var search = this.normalizeSearch(this.search());
        var searchPins = {};
        this.searchBoosts().filter(function(e) {
          var term = spotDOM.normalizeSearch(e.term);
          var idx = search.indexOf(term);
          return idx != -1 && (
            (e.type == "substring") ||
            (e.type == "exact" && idx == 0 && search.length == term.length) ||
            (e.type == "word" && (idx == 0 || /\w/.test(search.charAt(idx-1))) && (idx == search.length || /\w/.test(search.charAt(idx+1)))));
        }).forEach(function(e) { e.products.forEach(function(p) { searchPins[p[0]] = (searchPins[p[0]] || 0) + p[1]; }); });
        pins = pins.concat(Object.keys(searchPins).sort(function(a,b) { return searchPins[b] - searchPins[a] }));
      }
      if (pins.length > 0)
        query = query.pins(pins);
      var boostQuery = this.spotAPI.se();
      this.boosts().forEach(function(b) { return boostQuery.merge(b); });
      query = query.boost(boostQuery);
    }
    
    if (this.personalization())
      query = this.personalization().apply(query);
    this.queryTransforms().forEach(function(queryTransform) {
      queryTransform.forEach(function(t) {
        query = t(query, "main");
      });
    });
    return query;
  };
  /**

  ### initDefaultSortOrders

  Initializes the system with the default set of Shopify sort options.

  * Featured (if on collections page)
  * Best Match (if on search page)
  * Price: Low to High
  * Price: High to Low
  * A-Z
  * Z-A
  * Oldest to Newest
  * Newest to Oldest
  * Best Selling

  **/
  this.initDefaultSortOrders = function() {
    if (this.isCollectionPage())
      this.addSortOrder(new this.SortOrder("manual", "asc"));
    else if (this.isSearchPage())
      this.addSortOrder(new this.SortOrder("search", "desc"));
    this.addSortOrder(new this.SortOrder("price", "asc"));
    this.addSortOrder(new this.SortOrder("price", "desc"));
    this.addSortOrder(new this.SortOrder("title", "asc"));
    this.addSortOrder(new this.SortOrder("title", "desc"));
    this.addSortOrder(new this.SortOrder("created", "asc"));
    this.addSortOrder(new this.SortOrder("created", "desc"));
    this.addSortOrder(new this.SortOrder("best-selling", "desc"));
  };
  /**

  ### queryGuard

  Ensures that only one query is run within this block, at the end, if .query() was ever called. If it's never called, then no query is run. Useful for things like initailizations where one might
  have queries triggered in multiple places on changing things. Returns a deferred that contains the query payload if one occurred, or one that resolves with an empty argument list if no query was
  run.

  As an example:

      spotDOM.queryGuard(function() {
        spotDOM.query();
        spotDOM.query();
        spotDOM.query();
      });

  In the above example, only one query would be run.

      spotDOM.queryGuard(function() {
      });

  In this example, no queries are run.

  These can also be nested, but only the query will only ever be run on the outer-most block.

      spotDOM.queryGuard(function() {
        spotDOM.query();
        spotDOM.queryGuard(function() {
          spotDOM.query();
        });
        spotDOM.query();
        spotDOM.query();
      });
      // Query will run here.

  In the above example, a query will only run when the comment is reached.

  **/
  this.queryGuard = function(block) {
    if (this["_queryWaiter"]) {
      block();
      return this["_queryWaiter"];
    }
    this["_queryWaiter"] = spotAPI.Deferred();
    block();
    var deferred = this["_queryWaiter"];
    this["_queryWaiter"] = null;
    if (this["_queryRequests"]) {
      this["_queryRequests"] = 0;
      this.query().done(function() {
        deferred.resolve.apply(deferred, arguments);
      }).fail(function() {
        deferred.reject.apply(deferred, arguments);
      });
    } else {
      deferred.resolve();
    }
    return deferred;
  };


  /**

  ## Tying it Together

  A great way to get an idea of how all this works together is to look at SpotDefault's sample implementation of a panel. It gives a concise overall view
  of how to build a faceting panel using the above techniques. You can see it [here](#spotdefault-documentation-components-facetpane).

  **/

  this.updateFacets = function(options, boundaries) {
    var facets = this.facets();
    var optionIdx = 0;
    var boundaryIdx = 0;
    for (var i = 0; i < facets.length; ++i) {
      if (facets[i].isValidLocale(spotDOM.locale())) {
        var boundary = facets[i].getBoundary();
        if (boundary) {
          if (boundaries && boundaries[boundaryIdx]) {
            var boundaryArray = [];
            boundaryArray[0] = (boundary[0] == null ? boundaries[boundaryIdx++] : boundary[0]);
            boundaryArray[1] = (boundary[1] == null ? boundaries[boundaryIdx++] : boundary[1]);
            facets[i].update(boundaryArray);
          }
        } else {
          facets[i].update(options[optionIdx++]);
        }
      }
    }
  };


  this.composeFacetQuery = function(query, behavior) {
    if (!behavior)
      behavior = this.optionBehavior();
    var facetArray = [];
    var boundaryArray = [];
    var facetingQuery = spotDOM.spotAPI.se();
    var boundaryQuery = spotDOM.spotAPI.se();
    var facets = this.facets();
    for (var i = 0; i < facets.length; ++i) {
      if (facets[i].isValidLocale(spotDOM.locale())) {
        var boundary = facets[i].getBoundary();
        if (boundary) {
          if (boundary[0] == null)
            boundaryArray.push({ "min": facets[i].getOptionValue() });
          if (boundary[1] == null)
            boundaryArray.push({ "max": facets[i].getOptionValue() });
          boundaryQuery = facets[i].composeQuery(boundaryQuery);
        } else {
          facetArray.push(facets[i].getOptionValue());
          facetingQuery = facets[i].composeQuery(facetingQuery);
        }
      }
    }
    query = query.facets(facetingQuery);
    if (behavior != "none")
      query = query.facetOptions(facetArray, true, behavior);
    if (boundaryArray.length > 0) {
      query = query.boundaries(boundaryQuery)
      query = query.boundaryOptions(boundaryArray);
    }
    return query;
  };
  this.composeFacetQueryString = function(vars) {
    var facets = this.facets();
    for (var i in facets) {
      if (facets[i].isValidLocale(spotDOM.locale()))
        facets[i].composeQueryString(vars);
    }
    return vars;
  };
  this.parseFacetQueryString = function(vars) {
    var facets = this.facets();
    for (var i in facets) {
      if (facets[i].isValidLocale(spotDOM.locale()))
        facets[i].parseQueryString(vars);
    }
  };

  // Persist this across sessions.
  this.shopifyMode(function(mode) {
    this.setStorage("spotShopifyMode", mode, 1*60);
  });
};

window.SpotStorefront = function(domain, storefrontAccessToken, storefrontVersion) {
  var storefront = this;
  this.domain = domain;
  this.storefrontAccessToken = storefrontAccessToken;
  this.storefrontVersion = storefrontVersion;
  this.query = function(query, variables) {
    var deferred = new window.SpotAPI.DeferredObject();
    fetch("https://" + this.domain + "/api/" + this.storefrontVersion + "/graphql.json", {
      method: "POST",
      headers: {
        'X-Shopify-Storefront-Access-Token': this.storefrontAccessToken,
        'Accept': "application/json",
        'Content-Type': "application/json"
      },
      body: JSON.stringify({
        query: query,
        variables: variables
      })
    }).then(function(r) { return r.json() }).then(function(r) {
      deferred.resolve(r);
    });
    return deferred;
  };
  this.settings = function(metafields) {
    metafields = metafields.concat([{ namespace: "esafilters", key: "settings" }, { namespace: "esafilters", key: "clusters" }]);
    var shopMetafields = metafields.filter(function(m){ return !m.collection });
    var collection = metafields.filter(function(m){ return m.collection }).map(function(m) { return m.collection; })[0];
    var collectionMetafields = metafields.filter(function(m){ return m.collection }).map(function(m) { return { namespace: m.namespace, key: m.key }; });
    return this.query('query($shopMetafields: [HasMetafieldsIdentifier!]!' + (collection ? ', $collectionMetafields: [HasMetafieldsIdentifier!]!' : '') + ') { ' +
        'shop { metafields(identifiers: $shopMetafields) { key value type } primaryDomain { host } }' +
        (collection ? ("\n" + 'collection(handle: "' + collection + '") {  id handle image { height width altText url } title metafields(identifiers: $collectionMetafields) { key value type } }') : '') +
    '}', { shopMetafields: shopMetafields, collectionMetafields: collectionMetafields });
  };

  this.variantNodeQuery = "\
			nodes {\
				id\
				barcode\
				quantityAvailable\
				compareAtPrice { amount }\
				price { amount }\
				sku\
				image { id }\
				taxable\
				selectedOptions { name value }\
			}\
			pageInfo { endCursor hasNextPage }\
	";
	this.imageNodeQuery = "\
		nodes {\
			id\
			... on MediaImage {\
				id\
				image {\
					id\
					url\
					altText\
					height\
					width\
				}\
			}\
		}\
		pageInfo {\
			hasNextPage\
			endCursor\
		}\
	";
	this.productNodeQuery = "id\
    handle\
    vendor\
    productType\
    descriptionHtml\
    createdAt\
    updatedAt\
    publishedAt\
    tags\
    title\
    media(first: 100) { " + this.imageNodeQuery + " }\
    variants(first: 100) { " + this.variantNodeQuery + "}\
    options{\
      id\
      name\
      values\
    }";

  this.translateQueryFilters = function(facets) {
    if (!facets)
      return [];
    var activeFacetValues = facets.flatMap(function(f) { return f.facetValues(); }).filter(function(f) { return f.enabled() });
    var translatedFacets = [];
    activeFacetValues.forEach(function(fv) {
      var f = fv.facet.targetFacet || fv.facet;
      if (
        f.smartType == 'product_type' ||
        f.smartType == 'available' ||
        f.smartType == 'vendor') {
        translatedFacets.push({ [f.smartType]: fv.getValue() });
      } else if (f.isOption()) {
        translatedFacets.push({ variantOption: { name: f.getOption(), value: fv.getValue() } });
      } else if (f.isProductMetafield()) {
        translatedFacets.push({ productMetafield: { namespace: f.getNamespace(), key: f.getKey(), value: fv.getValue() } });
      } else if (f.isVariantMetafield()) {
        translatedFacets.push({ variantMetafield: { namespace: f.getNamespace(), key: f.getKey(), value: fv.getValue() } });
      }
    });
    return translatedFacets;
  };

  this.convertGQLId = function(gqlid) {
    if (!gqlid)
      return null;
    return parseInt(gqlid.replace(/^.*\//, ""));
  };

  this.translateProduct = function(product) {
    return {
      id: this.convertGQLId(product.id),
      handle: product.handle,
      title: product.title,
      vendor: product.vendor,
      product_type: product.productType,
      body_html: product.descriptionHtml,
      created_at: product.createdAt,
      updated_at: product.updatedAt,
      published_at: product.publishedAt,
      tags: product.tags.join(", "),
      options: product.options.map(function(o, idx) { return  { name: o.name, position: idx+1, id: storefront.convertGQLId(o.id), values: o.values } }),
      images: product.media.nodes.filter(function(i) { return i.image; }).map(function(i) {
        return {
          id: storefront.convertGQLId(i.image.id),
          alt: i.image.altText,
          height: i.image.height,
          width: i.image.width,
          src: i.image.url,
          variant_ids: product.variants.nodes.filter(function(v) { return v.image && v.image.id == i.image.id; })
        };
      }),
      variants: product.variants.nodes.map(function(v) {
        return {
          id: storefront.convertGQLId(v.id),
          compare_at_price: v.compareAtPrice ? v.compareAtPrice.amount : null,
          price: v.price ? v.price.amount : null,
          inventory_quantity: v.quantityAvailable,
          inventory_quantity_online: v.quantityAvailable,
          sku: v.sku,
          image_id: v.image ? storefront.convertGQLId(v.image.id) : null,
          taxable: v.taxable,
          title: v.selectedOptions.map(function(o) { return o.value }).join(" / "),
          option1: v.selectedOptions[0].value,
          option2: v.selectedOptions.length > 1 ? v.selectedOptions[1].value : null,
          option3: v.selectedOptions.length > 2 ? v.selectedOptions[2].value : null
        };
      })
    };
  };

  this.translateSortKey = function(sort) {
    switch (sort.replace(/\-(ascending|descending)$/, "")) {
      case "search": return "RELEVANCE";
      case "price": return "PRICE";
      case "manual": return "MANUAL";
      case "title": return "TITLE";
      case "created": return "CREATED";
    }
    return null;
  };
  
  this.products = function(options) {
    var query;
    var variables = {};
    var defaultQueryContents = "edges {\
      node {\
        ... on Product {\
          " + this.productNodeQuery + "\
        }\
      }\
    }\
    pageInfo { \
      endCursor\
      hasNextPage\
    }";
    if (options.search) {
      query = "query searchProducts($search: String!, $first: Int, $productFilters: [ProductFilter!], $sortKey: SearchSortKeys, $reverse: Boolean) @inContext(language: " + (options.locale || "en").toUpperCase() + ", country: " + (options.market || "us").toUpperCase() + ") {\
        search(query: $search, first: $first, types: PRODUCT, productFilters: $productFilters, sortKey: $sortKey, reverse: $reverse) {\
          " + defaultQueryContents + "\
          productFilters {\
            id\
            label\
            type\
            values { count id label }\
          }\
          totalCount\
        }\
      }";
      variables['search'] = options.search;
      variables['sortKey'] = options.sort && options.sort == "price" ? this.translateSortKey(options.sort) : "RELEVANCE";
      variables['reverse'] = !!/descending/.test(options.sort);
    } else if (options.collection) {
      query = "query collectionProducts($collection: String!, $first: Int, $productFilters: [ProductFilter!], $sortKey: ProductCollectionSortKeys, $reverse: Boolean) @inContext(language: " + (options.locale || "en").toUpperCase() + ", country: " + (options.market || "us").toUpperCase() + ") {\
        collectionByHandle(handle: $collection) {\
          products(first: $first, filters: $productFilters, sortKey: $sortKey, reverse: $reverse) {\
            " + defaultQueryContents + "\
            filters {\
              id\
              label\
              type\
              values { count id label }\
            }\
          }\
        }\
      }";
      variables['collection'] = options.collection;
      variables['sortKey'] = options.sort && options.sort != "collection" ? this.translateSortKey(options.sort) : "COLLECTION_DEFAULT";
      variables['reverse'] = !!/descending/.test(options.sort);
    } else {
      query = "query products($first: Int, $productFilters: [ProductFilter!]) @inContext(language: " + (options.locale || "en").toUpperCase() + ", country: " + (options.market || "us").toUpperCase() + ") {\
        products(first: $first, filters: $productFilters) {\
          " + defaultQueryContents + "\
          filters {\
            id\
            label\
            type\
            values { count id label }\
          }\
        }\
      }";
    }
    variables['first'] = options.paginate; 
    variables['productFilters'] = this.translateQueryFilters(options.facets);
    var deferred = new window.SpotAPI.DeferredObject()
    // TODO: Refactor this to be in spotDOM or something.
    this.query(query, variables).done(function(r) {
      var results = (r['data']['collectionByHandle'] && r['data']['collectionByHandle']['products']) || r['data']['search'];
      if (results) {
        var products = results['edges'].map(function(v){ return storefront.translateProduct(v['node']) });
        var count = results['totalCount'];
        var filters = results['filters'] || results['productFilters'];
        var facets = options.facets.filter(function(f) { return f.isValidLocale(options.locale) }).map(function(f) { 
          var targetId = null;
          if (f.getBoundary())
            return {};
          if (f.smartType == 'available') {
            targetId = "filter.v.availability";
          } else if (f.smartType == 'price') {
            targetId = "filter.v.price";
          } else if (f.smartType == 'product_type') {
            targetId = "filter.v.productType";
          } else if (f.smartType == 'vendor') {
            targetId = "filter.v.vendor";
          } else if (f.isOption()) {
            targetId = "filter.v.option." + window.spotDOM.handleize(f.getOption()); 
          } else if (f.isProductMetafield()) {
            targetId = "filter.p.m." + window.spotDOM.handleize(f.getNamespace()) + "." + window.spotDOM.handleize(f.getKey()); 
          } else if (f.isVariantMetafield()) {
            targetId = "filter.v.m." + window.spotDOM.handleize(f.getNamespace()) + "." + window.spotDOM.handleize(f.getKey()); 
          }
          var targetFacet = filters.filter(function(value) { return value.id == targetId || value.label == f.getName() })[0];
          if (targetFacet) {
            var groups;
            groups = /option\.(\w+)$/.exec(targetFacet.id);
            if (groups) 
              f.targetFacet = new window.spotDOM.Facet({ type: "option", option: groups[1] });
            groups = /p\.m\.(\w+)\.(\w+)$/.exec(targetFacet.id);
            if (groups) 
              f.targetFacet = new window.spotDOM.Facet({ type: "product-metafield", namespace: groups[1], key: groups[2] });
            groups = /v\.m\.(\w+)\.(\w+)$/.exec(targetFacet.id);
            if (groups) 
              f.targetFacet = new window.spotDOM.Facet({ type: "variant-metafield", namespace: groups[1], key: groups[2] });
          }
          var values = targetFacet ? Object.fromEntries(targetFacet.values.map(function(v) { return [v.label, v.count]; })) : {};
          if (f.isOption()) {
            values = { option: { [f.getOption()]: values } };
          } else if (f.isProductMetafield()) {
            values = { "product-metafield": { [f.getNamespace()]: { [f.getKey()]: values } } };
          } else if (f.isVariantMetafield()) {
            values = { "variant-metafield": { [f.getNamespace()]: { [f.getKey()]: values } } };
          } else if (f.isProductCustomField()) {
            values = { "product-custom-field": { [f.getField()]: values } };
          } else if (f.isVariantCustomField()) {
            values = { "variant-custom-field": { [f.getField()]: values } };
          }
          return values;
        });
        deferred.resolve(products, count, { products: products, count: count, options: facets }); 
      } else {
        deferred.reject();
      }
    }).fail(function(xhr) { deferred.reject(xhr); });
    return deferred;
  };
};

/***

SpotEvent Documentation
=======================

## Overview

`SpotEvent` is a class that provides an easy way to send common events via the API to Spot, or alternatively, to your google analytics account. It is used by `SpotDefault`, the default implementation.

In order to be configured, as part of the `SpotDefault.init`, you can specify the default sink as either `"ga"`, to funnel events to Google Analytics, or `"spot`" to use Spot's event API.

As an alternative, if you want to change the way events are fed into Google Analytics, or Spot, you can specify your own `SpotEvent` class, and pass it in via the [`event` parameter to `SpotDefault.init`](#)

**/

window.SpotEvent = window.SpotEvent || function(spotAPI, target) {
  this.target = target;
  this.spotAPI = spotAPI;

  /**

  ### beginSession

  Allows for tracking a user the second they get on the page, rather than when they engage with the search system.

  **/
  this.beginSession = function() {
    return this.spotAPI.sendEvent(new this.spotAPI.EventObject(1));
  };

  /**

  ### loggedIn(customerId)

  If you want to identify a session to a particular customer, you can use this to tie a custoemr ID to a session.

  **/
  this.loggedIn = function(customerId) {
    return this.spotAPI.sendEvent(new this.spotAPI.EventObject(2, parseInt(customerId)));
  };
  /**

  ### clickedProduct(productId, position)

  Logs when a product is clicked on, and what position it was in, in a particular collection. Useful for personalizing the user's results, and figuring out the user's likes.

  **/
  this.clickedProduct = function(productId, variantId, position) {
    return this.spotAPI.deferredEvent(new this.spotAPI.EventObject(3, parseInt(productId), variantId ? parseInt(variantId) : 0, position));
  };


  /**

  ### addedProductToCart(productId, variantId, quantity)

  Logs when a proudct is added to cart, and how much of it. By default, called on page load when we determine the difference between
  this cart and the last loaded cart.

  **/
  this.addedProductToCart = function(productId, variantId, quantity) {
    return this.spotAPI.sendEvent(new this.spotAPI.EventObject(4, parseInt(productId), variantId ? parseInt(variantId) : 0, quantity));
  };
  /**

  ### addedProductsToCart(items)

  Logs when a list of products are added to cart, and how much of it. By default, called on page load when we determine the difference between
  this cart and the last loaded cart.

  **/
  this.addedProductsToCart = function(products) {
    return this.spotAPI.sendEvents(products.map(function(product) { return new this.spotAPI.EventObject(4, parseInt(product.productId), product.variantId ? parseInt(product.variantId) : 0, product.quantity || 1) }));
  };
  /**

  ### deferredAddedProductToCart(productId, variantId, quantity)

  Logs when a proudct is added to cart, and how much of it. Sends as a deferred event; should only be used in circumstances when
  a page reload is imminent. Not used by default.

  **/
  this.deferredAddedProductToCart = function(productId, variantId, quantity) {
    return this.spotAPI.deferredEvent(new this.spotAPI.EventObject(4, parseInt(productId), variantId ? parseInt(variantId) : 0, quantity));
  };

  /**

  ### completedOrder(orderId)

  Logs when a user completes an order, allowing conversion to be tracked alongside search engagement.

  **/
  this.completedOrder = function(orderId) {
    return this.spotAPI.sendEvent(new this.spotAPI.EventObject(5, parseInt(orderId)));
  };

}

/***

SpotDefault Documentation
=========================

## Overview

`SpotDefault` is a class that provides a sample implementation of spot, using `SpotDOM`, and `SpotAPI`. Allows you to use out of the box pagination, and pane construction. The individual components are detailed below.

**If you are going to change SpotDefault's code**, copy the entire object to your own snippet, rename it (to `MyShopSpot`, or something similar), and make the changes there.

### Who should use this class?

`SpotDefault` *should* be the way you use Spot. It's generally pretty configurable, and almost any stylistic elements can be overidden or changed with CSS, or a different initialization call. This should be preferred way
to change look and feel, or affect functionality.

### Sample Usage

The minimal way to initialize the `SpotDefault` class in your Shopify store is the following liquid snippet.

```html
  {{ "spot.js" | asset_url | script_tag }}
  <script type='text/javascript'>
    SpotDefault.init({
      clusters: {{ shop.metafields.esafilters.clusters | default: "null" }},
      hostname: {{ shop.permanent_domain | json }},
      settings: {% assign settings_theme_id = "settings-" | append: theme.id %}{% assign settings = shop.metafields.esafilters[settings_theme_id] | default: shop.metafields.esafilters.settings %}{% if settings %}{{ settings | json }}{% else %}{}{% endif %},
      rule: {% assign rule = collection.metafields.esafilters[settings_theme_id] | default: collection.metafields.esafilters.settings %}{{ rule.rule | json }},
      moneyFormat: "{{ shop.money_format }}",
      locale: {% if localization %}"{{ locallization.language.iso_code }}"{% else %}null{% endif %},
      currency: {% if localization %}"{{ locallization.language.iso_code }}"{% else %}null{% endif %},
      market: {% if localization %}"{{ locallization.market.handle }}"{% else %}null{% endif %}
      // By default, you can also pass in containers that will be automatically set up with relevant bits.
      productContainer: ".main-collection-grid products, .main-search-grid products",
      paginationConatiners: [".pagination"],
      facetPaneContainer: ".sidebar",
      searchBars: ["input[type='q']"],
      resultsStatusContainer: ".results-status",
      productRenderer: function(product) {
        return this.createElement(`<div>${this.spotDOM.getProductTitle(product)}</div>`);
      }
    });
  </script>
```

This will initialize all the normal components of spot automatically. If you want to use each component individually, read on. For information about all the parameters you can pass to the `init` function, please see the [`init` function](#spotdefault-documentation-functions-init)

## Components

**/
window.SpotDefault = window.SpotDefault || function(spotDOM, spotEvent, language) {
  if (!spotDOM)
    throw "Requires SpotDOM. Please pass a copy of the API to SpotDefault's constructor.";

  var spotDefault = this;
  this.spotDOM = spotDOM;
  this.spotEvent = spotEvent;
  this.options = {};
  spotDOM.arrayField(this, 'recentSearch', spotDOM.getStorage("spotRecentSearches") || [], "recentSearches");
  this.addRecentSearch(function(search) {
    spotDOM.setStorage("spotRecentSearches", this.recentSearches());
  });
  spotDOM.arrayField(this, 'recentProduct', spotDOM.getStorage("spotRecentProducts") || []);
  this.addRecentProduct(function(product) {
    spotDOM.setStorage("spotRecentProducts", this.recentProducts());
  });
  spotDOM.arrayField(this, 'activeRule', []);

  this.language = Object.assign({
    "filter": {
      "en": "Filter",
      "fr": "Filtrer"
    },
    "load-more": {
      "en": "Load More Products",
      "fr": "Télécharger plus de produits",
    },
    "load-previous": {
      "en": "Load Previous Products",
      "fr": "Télécharger produits précédents",
    },
    "show-all": {
      "en": "Show all Results",
      "fr": "Afficher tous les résultats"
    },
    "out-of-stock": {
      "en": "Out of Stock",
      "fr": "Epuisée"
    },
    "show-count": {
      "en": "Show {{ count }} Results",
      "fr": "Afficher {{ count }} résultats"
    },
    "view-details": {
      "en": "View full details",
      "fr": "Voir détails complète"
    },
    "showing-results-for": {
      "en": "Showing results for",
      "fr": "Affichage des résultats pour"
    },
    "popular-searches": {
      "en": "Popular Searches",
      "fr": "Recherches populaires"
    },
    "show-more": {
      "en": "Show More",
      "fr": "Voir plus"
    },
    "show-less": {
      "en": "Show Less",
      "fr": "Voir moins"
    },
    "related-products": {
      "en": "Related Products",
      "fr": "Produits connexes"
    },
    "clear-all": {
      "en": "Clear All",
      "fr": "Tout effacer"
    },
    "sort": {
      "en": "Sort",
      "fr": "Trier"
    },
    "search": {
      "en": "Search",
      "fr": "Recherche"
    },
    "search-title": {
      "en": "Search: {{ count }} results found for \"{{ search }}\"",
      "fr": "Recherche: {{ count }} résultats trouvés pour « {{ search }} »"
    },
    "search-empty-title": {
      "en": "Search: No results found for \"{{ search }}\"",
      "fr": "Recherche: Aucun résultat trouvé pour « {{ search }} »"
    },
    "related-title": {
      "en": "Related products for {{ product }}",
      "fr": "Produits connexes for {{ product }}"
    },
    "results-status": {
      "en": "{{ start }} - {{ end }} of {{ count }} products",
      "fr": "{{ start }} - {{ end }} de {{ count }} produits"
    },
    "results-status-search": {
      "en": "{{ start }} - {{ end }} of {{ count }} products for \"{{ search }}\"",
      "fr": "{{ start }} - {{ end }} de {{ count }} produits pour « {{ search }} »"
    },
    "price-filter-parsers": {
      "en": {
        "under": [/under\s+([\d\.,]+)\W?/i],
        "over": [/over\s+([\d\.,]+)\W?/i]
      },
      "fr": {
        "under": [/sous\s+([\d\.,]+)\W?/i],
        "over": [/plus\s+de\s+([\d\.,]+)\W?/i]
      }
    }
  }, language || {});

  this.template = function(template, values) {
    return template.replace(/{{\s*([^}]+?)\s*}}/g, function(m, v) {
      var parts = v.split(/\./);
      var current = values;
      for (var i = 0; i < parts.length; ++i)
        current = current[parts[i]];
      return current != null ? current + "" : "";
    });
  };

  this.templateExplicitLocale = function(key, locale, values) {
    if (!this.language[key])
      throw "Can't find template " + key;
    return this.template(this.language[key][locale || spotDOM.locale()] || this.language[key]["en"], values || {});
  };
  this.templateLocale = function(key, values) {
    return this.templateExplicitLocale(key, null, values);
  };

  /**

  ### PagedPaginationHelper

  This is a small helper function that returns a set of elements based on your inputs that represents a pagination control for `SpotDOM`.

  The pagination helper can be used like so:

      var paginationHelper = window.spotDefault.pagedPaginationHelper({ count: totalProductCount });
      document.querySelector('body').appendChild(paginationHelper.element);

  Below is the implementation of the helper:

  %{PagedPaginationHelper}

  **/

  /** %+{PagedPaginationHelper} **/
  this.pagedPaginationHelper = function(options) {
    var count = options['count'];
    var page = options['page'] || spotDOM.page();
    var pageInterval = options['interval'] || 2;
    var containerElement = options['containerElement'] || this.createElement("<ul class='pagination-container'></ul>");
    var prevElement = options['prevElement'] || this.createElement("<li class='prev'>&larr;</li>");
    var nextElement = options['nextElement'] || this.createElement("<li class='next'>&rarr;</li>");
    var pageElement = options['pageElement'] || this.createElement("<li></li>");
    var maxElement = options['maxElement'] ||  options['pageElement']  || this.createElement("<li class='max'></li>")
    var minElement = options['minElement'] ||  options['pageElement']  || this.createElement("<li class='min'></li>")
    var activePageElement = options['activePageElement'] || options['pageElement'] || this.createElement("<li class='active'></li>");
    var elipsisElement = options['elipsisElement'] || this.createElement("<li class='disabled'>...</li>");

    var resultsPerPage = options['paginate'] || spotDOM.paginate();
    var maxPages = Math.ceil(count / resultsPerPage);
    var min = Math.max(1, page - pageInterval);
    var max = Math.min(page + pageInterval, maxPages);

    var elements = [];
    if (min != 1 || max != 1) {
      if (page > 1) {
        elements.push(prevElement.cloneNode(true));
        elements[elements.length-1].addEventListener("click", function(e) { spotDOM.page(spotDOM.page()-1); e.preventDefault(); });
      }
      if (min > 1) {
        elements.push(minElement.cloneNode(true));
        elements[elements.length-1].innerText = 1;
        elements[elements.length-1].addEventListener("click", function(e) { spotDOM.page(1); e.preventDefault(); });
        if (min > 2 && elipsisElement)
          elements.push(elipsisElement.cloneNode(true));
      }

      for (var i = min; i <= max; ++i) {
        elements.push((i == page ? activePageElement : pageElement).cloneNode(true));
        elements[elements.length-1].innerText = i;
        elements[elements.length-1].addEventListener("click", function(e) { spotDOM.page(parseInt(this.innerText)); e.preventDefault(); });
      }

      if (max < maxPages) {
        if (max < maxPages - 1)
          elements.push(elipsisElement.cloneNode(true));

        elements.push(maxElement.cloneNode(true));
        elements[elements.length-1].innerText = maxPages;
        elements[elements.length-1].addEventListener("click", function(e) { spotDOM.page(maxPages); e.preventDefault(); });
      }

      if (page < maxPages) {
        elements.push(nextElement.cloneNode(true));
        elements[elements.length-1].addEventListener("click", function(e) { spotDOM.page(spotDOM.page()+1);  e.preventDefault(); });
      }
    }
    spotDOM.spotAPI.forEach(elements, function(e) { containerElement.appendChild(e); });
    return containerElement;
  };
  /** %-{PagedPaginationHelper} **/


  this.getDocHeight = function() {
    var D = document;
    return Math.max(D.body.scrollHeight, D.documentElement.scrollHeight, D.body.offsetHeight, D.documentElement.offsetHeight, D.body.clientHeight, D.documentElement.clientHeight);
  };

  this.getWindowOffset = function() {
    return window.pageYOffset || window.scrollY;
  };

  this.getWindowHeight = function() {
    return window.innerHeight;
  };

  this.defaultImageSize = 275;
  this.getNoImageHTML = function(width, height) {
    var actualWidth = width !== undefined ? width : this.defaultImageSize;
    var actualHeight = height !== undefined ? height : this.defaultImageSize;
    return '<svg class="placeholder--image" ' + (actualWidth ? ('width="' + actualWidth + '"') : "") + ' ' + (actualHeight ? ('height="' + actualHeight + '"') : "")  + ' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5"><path d="M324.5 212.7H203c-1.6 0-2.8 1.3-2.8 2.8V308c0 1.6 1.3 2.8 2.8 2.8h121.6c1.6 0 2.8-1.3 2.8-2.8v-92.5c0-1.6-1.3-2.8-2.9-2.8zm1.1 95.3c0 .6-.5 1.1-1.1 1.1H203c-.6 0-1.1-.5-1.1-1.1v-92.5c0-.6.5-1.1 1.1-1.1h121.6c.6 0 1.1.5 1.1 1.1V308z"></path><path d="M210.4 299.5H240v.1s.1 0 .2-.1h75.2v-76.2h-105v76.2zm1.8-7.2l20-20c1.6-1.6 3.8-2.5 6.1-2.5s4.5.9 6.1 2.5l1.5 1.5 16.8 16.8c-12.9 3.3-20.7 6.3-22.8 7.2h-27.7v-5.5zm101.5-10.1c-20.1 1.7-36.7 4.8-49.1 7.9l-16.9-16.9 26.3-26.3c1.6-1.6 3.8-2.5 6.1-2.5s4.5.9 6.1 2.5l27.5 27.5v7.8zm-68.9 15.5c9.7-3.5 33.9-10.9 68.9-13.8v13.8h-68.9zm68.9-72.7v46.8l-26.2-26.2c-1.9-1.9-4.5-3-7.3-3s-5.4 1.1-7.3 3l-26.3 26.3-.9-.9c-1.9-1.9-4.5-3-7.3-3s-5.4 1.1-7.3 3l-18.8 18.8V225h101.4z"></path><path d="M232.8 254c4.6 0 8.3-3.7 8.3-8.3s-3.7-8.3-8.3-8.3-8.3 3.7-8.3 8.3 3.7 8.3 8.3 8.3zm0-14.9c3.6 0 6.6 2.9 6.6 6.6s-2.9 6.6-6.6 6.6-6.6-2.9-6.6-6.6 3-6.6 6.6-6.6z"></path></svg>';
  };

  this.generateImage = function(image, options) {
    if (!options)
      options = {};
    var startDimensions = (options.startResolution || this.defaultImageSize) + "x" + (options.startResolution || this.defaultImageSize);
    var endDimensions = (options.endResolution || options.startResolution || this.defaultImageSize) + "x" + (options.endResolution || options.startResolution || this.defaultImageSize);
    var dimensions = image && spotDOM.getSizedImageDimensions(image, endDimensions);
    var imageURL = image && this.spotDOM.getSizedImage(image, startDimensions);
    var element = this.createElement(image ? "<img width='" + (dimensions ? dimensions[0] : "") + "' height='" + (dimensions ? dimensions[1] : "") + "' src='" + imageURL + "'/>" : this.getNoImageHTML((dimensions ? dimensions[0] : ""), (dimensions ? dimensions[1] : "")));
    if (options.alt)
      element.setAttribute('alt', options.alt);
    if (options.klass && typeof(options.klass) == 'string')
      options.klass = options.klass.split(/\s+/);
    if (options.klass && Array.isArray(options.klass))
      options.klass.forEach(function(k) { element.classList.add(k); });
    if (options.styles) {
      Object.keys(options.styles).forEach(function(key) {
        element.style[key] = options.styles[key];
      });
    }
    if (options.fetchpriority)
       element.fetchpriority = options.fetchpriority;
    if (typeof(image) == 'object' && options.endResolution) {
      var loader = function() {
        element.setAttribute('src', spotDefault.spotDOM.getSizedImage(image, options.endResolution + "x" + options.endResolution));
        element.removeEventListener('load', loader);
      };
      element.addEventListener('load', loader);
    }
    element.addEventListener('error', function() {
      if (element.parentNode)
        element.parentNode.replaceChild(spotDefault.createElement(spotDefault.getNoImageHTML(options.endResolution || options.startResolution, options.endResolution || options.startResolution)), element);
    });
    return element;
  }


  this.getProductImage = function(product, variant) {
    return spotDOM.getProductImage(product, variant, this.preferredImages);
  };
  this.getPreferredImage = function(product, variant) {
    return spotDOM.getPreferredImage(product, variant, this.preferredImages);
  };

  /** %+{DefaultProductRenderer} **/
  this.defaultProductRenderer = function(product, position) {
    var image = spotDefault.getProductImage(product);
    var possibleToHaveSplit = spotDOM.facets().flatMap(function(e) { return e.facetValues(); }).filter(function(e) { return e.enabled(); }).length > 0;
    var title = !Array.isArray(product) && (((!spotDOM.split() || spotDOM.split() == "auto") && !possibleToHaveSplit) || spotDOM.split() == "none") ? product.title : spotDOM.getProductTitle(product);
    var price = spotDefault.spotDOM.getProductPrice(product);
    var element = spotDefault.createElement("<div class='product " + (spotDOM.pins().indexOf(product.id) != -1 ? "pin" : "") + "'>\
      <a class='product--image' href='" + spotDOM.getProductURL(product) + "'></a>\
      <div class='product--info'>\
        <h4 class='product--info--title'>\
          <a href='" + spotDOM.getProductURL(product) + "' tabindex='0'>\
            " + title + "\
          </a>\
        </h4>\
        <div class='product--info--price'>\
          <span class='money' data-currency-usd='" + spotDOM.formatMoney(price) + "' data-currency='USD'>" + spotDOM.formatMoney(price) + "</span>\
        </div>\
      </div>\
    </div>");
    element.querySelector('.product--image').appendChild(spotDefault.generateImage(image, { alt: title, startResolution: 100, endResolution: 750, fetchpriority: (position != null && position < 8 ? "high" : null) }));
    spotDefault.getBadges(product).forEach(function(badge) { element.appendChild(spotDefault.renderBadge(badge)); });
    return element;
  };
  /** %-{DefaultProductRenderer} **/


  /**

  ### ScrollPaginationHelper

  This is a small helper class that sets up a listener to auto-retrieve a new set of products when the bottom
  of the page is reached. Used for "infinite scrolling" implementations.

  The pagination helper can be used like so:

      new window.spotDefault.ScrollPaginationHelper().attach();

  Below is the implementation of the helper:

  %{ScrollPaginationHelper}

  **/

  /** %+{ScrollPaginationHelper} **/
  this.ScrollPaginationHelper = function(options) {
    var scroll = this;
    if (!options)
      options = {}
    this.reachedProductEnd = false;
    this.triggerHeight = typeof(options['triggerHeight']) == 'function' ? options['triggerHeight'] : function() {
      return spotDefault.getDocHeight() - (options['triggerHeight'] || 300);
    };
    this.topOfPage = options['topOfPage'] || 0;
    this.preservePosition = options['preservePosition'] || false;
    this.shouldTrigger = true;
    this.hadOneLoad = false;
    this.loadingDownFrom = false;

    this.intersectedMap = new Map();
    this.updatePage = function() {
      if (!this.loadingDownFrom) {
        var products = Array.from(this.intersectedMap.values().filter(function(v) { return v.visibility })).sort(function(a,b) { return b.position - a.position; });
        var lastProduct = products[0];
        if (lastProduct)
          spotDefault.viewedPage(Math.floor(lastProduct.position / spotDOM.paginate()) + 1);
      }
    };
    this.intersectionObserver = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        scroll.intersectedMap.get(entry.target).visibility = entry.isIntersecting == true;
      });
      scroll.updatePage();
    });
    this.getViewedPageOffset = function(product) {
      if (!product)
        return 0;
      // Get the earliest product in the page, get its offset from the top of its container, and return it.
      var targetPosition = (spotDefault.viewedPage() - 1) * spotDOM.paginate();
      var firstProductOnPage = Array.from(this.intersectedMap.values().filter(function(v) { return v.position >= targetPosition })).sort(function(a,b) { return a.position - b.position; })[0];
      if (firstProductOnPage && firstProductOnPage.element) {
        var pageRect = firstProductOnPage.element.getBoundingClientRect();
        var productRect = product.element.getBoundingClientRect();
        return productRect.top - pageRect.top;
      }
      return null;
    };

    this.productListener = function(products) {
      scroll.hadOneLoad = true;
      scroll.reachedProductEnd = products.length < spotDOM.paginate();
      products.forEach(function(product) { 
        if (product.element) {
          scroll.intersectedMap.set(product.element, product); 
          scroll.intersectionObserver.observe(product.element); 
          product.element.addEventListener('click', function(e) {
            spotDefault.setBackfill(product);
          });
        }
      });
      setTimeout(function() {
        scroll.shouldTrigger = true;
        scroll.scrollListener();
      }, 500);
    };

    this.scrollListener = function() {
      if (scroll.shouldTrigger && !scroll.reachedProductEnd) {
        var scrollHeight = typeof(scroll.triggerHeight) == 'function' ? scroll.triggerHeight() : scroll.triggerHeight;
        if (spotDefault.getWindowOffset() + spotDefault.getWindowHeight() >= scrollHeight) {
          scroll.shouldTrigger = false;
          spotDOM.page(spotDOM.page()+1);
        }
      }
    };


    this.attach = function(target) {
      if (!target)
        target = window;
      this.attached = target;
      spotDOM.products(this.productListener);
      spotDOM.page(function(page) {
        var scrollTop = typeof(scroll.topOfPage) == 'function' ? scroll.topOfPage() : scroll.topOfPage;
        if (!scroll.preservePosition && page == 1 && scroll.hadOneLoad && document.documentElement.scrollTop > scrollTop) {
          window.scrollTo({ left: 0, top: scrollTop, behavior: options.scrollBehavior || "smooth" });
        }
      });
      target.addEventListener("scroll", this.scrollListener);
      return this;
    };
    this.detach = function() {
      spotDOM.removeFieldListener(spotDOM, 'products', this.productListener);
      if (this.attached) {
        this.attached.removeEventListener("scroll", this.scrollListener);
        this.attached = null;
      }
      return this;
    };

    this.loadDownFrom = function(loadDownFromPage, onPageGrabbed) {
      var deferred = spotDOM.spotAPI.Deferred();
      var talliedProducts = [];
      this.loadingDownFrom = true;
      var countDown = function(page) {
        if (page > 0) {
          spotDOM.generateQuery().page(page).options([]).e().done(function(products) {
            if (onPageGrabbed)
              onPageGrabbed(products, page);
            talliedProducts = products.concat(talliedProducts);
            countDown(page-1);
          });
        } else 
          deferred.resolve(talliedProducts);
      };
      if (loadDownFromPage > 1)
        countDown(loadDownFromPage - 1);
      else
        deferred.resolve();
      deferred.done(function() {
        scroll.loadingDownFrom = false;
        scroll.updatePage();
      });
      return deferred;
    }
  };
  /** %-{ScrollPaginationHelper} **/

  /**

  ### FacetPane

  The Spot default pane is a great example of how to use the SpotDOM system. This single subclass provides a fully featured pane, built off the SpotDOM module. It can be dropped into most themes, and work
  with a minimal amount of extra styling.

  The pane can be instantiated by simply calling new:

      var facetPane = new window.spotDefault.FacetPane();
      document.body.appendChild(facetPane.paneElement);

  Below is the implementation of the pane:

  %{Pane}

  #### DoubleRangeSlider

  Spot also provides a double-ended range slider, that is used for certain facets (like price), in order to set the minimum and maximum prices.

  Its implementation is as follows:

  %{DoubleRangeSlider}

  The double range slider can be easily styled with CSS, or, alternatively, you can provide an override by simply assigning `DoubleRangeSlider` in `SpotDefault`, after
  initialization, before Pane creation.

  **/

  /** %+{DoubleRangeSlider} **/
  this.DoubleRangeSlider = function(options) {
    if (!options)
      options = {};
    var slider = this;
    spotDOM.listener(this, 'change');
    spotDOM.listener(this, 'drag');
    var css = "\
      bar { border-radius: 4px;  background-color: #ddd; border-top: 1px solid #aaa; border-bottom: 1px solid #aaa; display: inline-block; padding: 3px 0; width: 33%; position: relative; vertical-align: top; top: 7px; }\
      drange bar.fill { border-left: 0; border-right: 0; border-radius: 0; background: #07f; }\
      drange { display: inline-block; position: relative; width: 100px; height: 20px; margin: 5px 12px 5px 0; }\
      drange handle { position: absolute; padding: 7px; background: #fff; top: 2.5px; border: 1px solid #ccc; border-radius: 10px; cursor: pointer; z-index: 10; margin-left: -7px; }\
      drange handle:hover, drange handle.active { background: #ccc; }\
      drange handle:hover, drange handle.active, drange handle.last-active { z-index: 100; }\
      drange:hover bar, drange.active bar { background-color: #ccc; }\
      drange:hover bar.fill, drange.active bar.fill { background-color: #05d; }\
    ";

    this.element = spotDefault.createElement('<drange><style type="text/css">' + css + '</style><input type="hidden" name="min"/><input type="hidden" name="max"/><handle></handle><bar></bar><bar class="fill"></bar><bar></bar><handle></handle></drange>');
    var inputs = this.element.querySelectorAll('input')
    this.values = function(values) {
      inputs[0].value = values[0];
      inputs[1].value = values[1];
    };
    spotDOM.scalarField(this, 'step', options.step || 0.01);
    var handles = this.element.querySelectorAll('handle');
    this.updateBar = function() {
      var bars = this.element.querySelectorAll('bar');
      inputs.forEach(function(b, i) {
        handles[i].style.left = b.value*100 + "%";
      });
      bars[0].style.width = inputs[0].value*100 + "%";
      bars[1].style.width = (inputs[1].value - inputs[0].value)*100 + "%";
      bars[2].style.width = ((1.0 - inputs[1].value)*100) + "%";
    };
    var drag = function(x, i) {
      var p = (x - slider.element.getBoundingClientRect().x) / slider.element.offsetWidth;
      if (i == 0 && p < 0)
        inputs[i].value = 0;
      else if (i == 1 && p > 1)
        inputs[i].value = 1;
      else if (i == 0 && p > inputs[1].value)
        inputs[i].value = inputs[1].value;
      else if (i == 1 && p < inputs[0].value)
        inputs[i].value = inputs[0].value;
      else
        inputs[i].value = Math.round(p / slider.step()) * slider.step();
      slider.updateBar();
      slider.drag(Math.max(inputs[0].value, 0.0), Math.min(inputs[1].value, 1.0));
    };
    handles.forEach(function(h, i) {
      var endEvent = null;
      var dragEvent = function(e) { drag((e.changedTouches ? e.changedTouches[0].clientX : e.clientX), i); e.preventDefault(); };
      var startEvent = function(e) {
        e.preventDefault();
        handles.forEach(function(e) { e.classList.remove('last-active'); });
        [slider.element, h].forEach(function (e) { e.classList.add('active', 'last-active') });
        ['mousemove', 'touchmove'].forEach(function(e) { document.addEventListener(e, dragEvent); });
        endEvent = function(e) {
          [slider.element, h].forEach(function(e) { e.classList.remove('active') });
          ['mousemove', 'touchmove'].forEach(function(e) { document.removeEventListener(e, dragEvent); });
          ['mouseup', 'touchend'].forEach(function(e) { document.removeEventListener(e, endEvent); });
          slider.change(Math.max(inputs[0].value, 0.0), Math.min(inputs[1].value, 1.0));
        };
        ['mouseup', 'touchend'].forEach(function(e) { document.addEventListener(e, endEvent); });
      };
      h.addEventListener('touchstart', startEvent);
      h.addEventListener('mousedown', function(e) { if (e.which == 1) startEvent(e); });
    });
    this.values(options.value ? options.value : [0.0, 1.0]);
    this.updateBar();
  };
  /** %-{DoubleRangeSlider} **/

  this.BreadcrumbPane = function(options) {
    var pane = this;
    var paneId = options['id'] || "spot-default-pane";
    
    spotDOM.arrayField(this, 'facet');
    spotDOM.arrayField(this, 'breadcrumb');
    
    this.defaultBreadcrumbRenderer = function(facetValue) {
      var element;
      if (facetValue instanceof spotDOM.Facet) {
        element = spotDefault.createElement("<button class='breadcrumb btn button breadcrumb-" + facetValue.getUniqueId() + " breadcrumb-" + facetValue.getUniqueId() + "' role='button' id='" + pane.paneId + "-breadcrumb-" + facetValue.getUniqueId() + "'><span class='breadcrumb-facet-name'>" + pane.getFacetName(facetValue) + ": </span><span class='name'></span> <span class='remove'>&times;</span></div>");
        facetValue.panes[paneId].breadcrumbRefreshFunction = function() {
          if (element) {
            var boundaryValues = facetValue.boundaryValues();
            var valueString = "";
            if (boundaryValues[0] != null && boundaryValues[1] != null)
              valueString = pane.formatMoney(boundaryValues[0]) + ' - ' + pane.formatMoney(boundaryValues[1]);
            else if (boundaryValues[0] != null)
              valueString = pane.formatMoney(boundaryValues[0]) + '+';
            else if (boundaryValues[1] != null)
              valueString = '-' + pane.formatMoney(boundaryValues[1]);
            element.querySelector('.name').innerText = valueString;
          }
        };
        facetValue.panes[paneId].breadcrumbRefreshFunction();
        facetValue.boundaryValues(function() { facetValue.panes[paneId].breadcrumbRefreshFunction(); });
        facetValue.boundaryEdges(function() { facetValue.panes[paneId].breadcrumbRefreshFunction(); });
        element.addEventListener('click', function() { facetValue.boundaryValues([null, null]); });
      } else if (facetValue instanceof spotDOM.FacetValue) {
        element = spotDefault.createElement("<button class='breadcrumb btn button breadcrumb-" + facetValue.facet.getUniqueId() + " breadcrumb-" + facetValue.getUniqueId() + "' role='button' id='" + pane.paneId + "-breadcrumb-" + facetValue.getUniqueId() + "'><span class='breadcrumb-facet-name'>" + pane.getFacetName(facetValue.facet) + ": </span><span class='name'>" + pane.getFacetValueName(facetValue) + "</span> <span class='remove'>&times;</span></div>");
        element.addEventListener('click', function() { facetValue.enabled(false); });
      } else if (facetValue.product && facetValue.recommendation) { // Is the relatedProduct object.
        element = spotDefault.createElement("<button class='breadcrumb btn button breadcrumb-related-product' role='button' id='" + pane.paneId + "-breadcrumb-related-product'><span class='breadcrumb-facet-name'>" + facetValue.recommendation.getName() + ": </span><span class='name'>" + spotDOM.getProductTitle(facetValue.product) + "</span> <span class='remove'>&times;</span></div>");
        element.addEventListener('click', function() { spotDefault.relatedProduct(null); });
      } else if (facetValue.type && facetValue.type == "search") {
        element = spotDefault.createElement("<button class='breadcrumb btn button breadcrumb-related-product' role='button' id='" + pane.paneId + "-breadcrumb-search'><span class='breadcrumb-facet-name'>" + spotDefault.templateLocale('search') + ": </span><span class='name'>" + spotDOM.search() + "</span> <span class='remove'>&times;</span></div>");
        element.addEventListener('click', function() { spotDOM.search(null); });
      }
      return element;
    };
    this.formatMoney = options['formatMoney'] || function(price, format) { return spotDefault.spotDOM.formatMoney(price, format); }
    this.getFacetName = options['getFacetName'] || function(facet) {  return facet.getName(); };
    this.getFacetValueName = options['getFacetValueName'] || function(facetValue) { return facetValue.getName() };
    this.breadcrumbRenderer = options['breadcrumbRenderer'] || this.defaultBreadcrumbRenderer;

    this.breadcrumbsElement = spotDefault.createElement("<div class='breadcrumbs'></div>");
    // Every time a breadcrumb is added, add breadcrumbs to the breadcrumb element.
    // Also add in a "Clear All" button; this is more sensible as a default, because it's easily hidden with CSS, if unwanted,
    // rather than forcing the integrator to write one on their own.
    // Ideally most of this should be controllable with CSS, rather than JS, for ease of debugging.
    this.clearBreadcrumbsElement = spotDefault.createElement("<button class='clear-breadcrumbs btn button'><span class='name'>" + spotDefault.templateLocale("clear-all") + "</span> <span class='remove'>&times;</span></div>");
    this.clearBreadcrumbsElement.addEventListener("click", function(e) {
      spotDOM.queryGuard(function() { spotDOM.spotAPI.forEach(pane.breadcrumbs(), function(facetValue) { 
        if (facetValue instanceof spotDOM.Facet)
          facetValue.boundaryValues([null, null]); 
        else
          facetValue.enabled(false); 
      }); });
      e.preventDefault();
    });
    this.addBreadcrumb(function(facetValue) {
      // facet values for non-boundaries, the facet itself for a boundary
      facetValue.panes[paneId].breadcrumbElement = facetValue.panes[paneId].breadcrumbElement || this.breadcrumbRenderer(facetValue);
      pane.breadcrumbsElement.appendChild(facetValue.panes[paneId].breadcrumbElement);
      pane.breadcrumbsElement.appendChild(this.clearBreadcrumbsElement);
    }).removeBreadcrumb(function(facetValue) {
      if (facetValue instanceof spotDOM.Facet) {
        spotDOM.removeFieldListener(facetValue, 'boundaryValues', facetValue.panes[paneId].breadcrumbRefreshFunction);
        spotDOM.removeFieldListener(facetValue, 'boundaryEdges', facetValue.panes[paneId].breadcrumbRefreshFunction);
      }
      if (facetValue.panes[paneId].breadcrumbElement)
        facetValue.panes[paneId].breadcrumbElement.remove();
      facetValue.panes[paneId].breadcrumbElement = null;
      if (pane.breadcrumbs().length == 0)
        this.clearBreadcrumbsElement.remove();
    });
    if (!spotDOM.isSearchPage()) {
      var searchBreadcrumb = null;
      spotDOM.search(function(search) {
        if (searchBreadcrumb) {
          pane.removeBreadcrumb(searchBreadcrumb);
          searchBreadcrumb = null;
        }
        if (search) {
          searchBreadcrumb = { type: "search", panes: {}, facet: { getUniqueId: function() { return "search-" + spotDOM.handleize(search); }, getName: function() { return "search"; } }, enabled: function(enabled) { if (!enabled) { spotDOM.search(null); } }, getName: function() { return "search: \"" + search + "\""; }, getUniqueId: function() { return "search"; } };
          searchBreadcrumb.panes[paneId] = {};
          pane.addBreadcrumb(searchBreadcrumb);
        }
      }, true);
    }
    
    this.addFacet(function(facet) {
      if (!facet.omitBreadcrumbs) {
        if (!facet.panes)
          facet.panes = {};
        if (!facet.panes[paneId])
          facet.panes[paneId] = {};
        if (facet.getBoundary()) {
          facet.boundaryValues(function() {
            if (facet.isEnabled())
              pane.addBreadcrumb(facet);
            else
              pane.removeBreadcrumb(facet);
          });
        } else {
          facet.addFacetValues(function(facetValues) {
            facetValues.forEach(function(facetValue) {
              if (!facetValue.panes)
                facetValue.panes = {};
              if (!facetValue.panes[paneId])
                facetValue.panes[paneId] = {};
              if (facetValue.isUnnamedWildcard() || (pane.getFacetValueName(facetValue) == "" && pane.hideEmptyValues))
                return;
              facetValue.enabled(function(enabled) {
                // If we're in single selection mode (becuase we're in ShopifyMode, or if this is a sorting facet), DISABLE all other facets in this category.
                if (enabled) {
                  pane.addBreadcrumb(facetValue);
                } else {
                  if (facetValue.panes[paneId].breadcrumbElement)
                    pane.removeBreadcrumb(facetValue);
                }
              }, facetValue.enabled());
            });
          }, true).removeFacetValue(function(facetValues) {
            facetValues.forEach(function(facetValue) {
              if (facetValue.panes[paneId].breadcrumbElement) {
                facetValue.panes[paneId].breadcrumbElement.remove();
                facetValue.panes[paneId].breadcrumbElement = null;
              }
            });
          });
        }
      }
    });
  };
  /** %+{Pane} **/
  this.FacetPane = function(options) {
    if (!options)
      options = {};
    // Basic default integration of the Spot pane; this can be easily replaced with one's own implementation.
    // Assumes that you're eventually going to call `spotDefault.setupQueryOnChange`; this is purely a graphical
    // interface to the SpotOM model.
    var pane = this;
    var paneId = options['id'] || "spot-default-pane";
    this.paneId = paneId;
    this.paneElement = spotDefault.createElement("<div class='pane facet-pane'></div>");
    this.closePane = spotDefault.createElement("<div class='facet-pane-close'></div>");
    if (options['classes'])
      spotDOM.spotAPI.forEach(options['classes'], function(e) { pane.paneElement.classList.add(e); });
    var breadcrumbsEnabled = options['breadcrumbs'] === undefined || options['breadcrumbs'];
    this.paneElement.appendChild(this.closePane);
    this.facetsElement = spotDefault.createElement("<div class='facets'></div>");
    this.paneElement.appendChild(this.facetsElement);

    spotDOM.beginQuery(function() { pane.paneElement.classList.add(spotDefault.options.loadingClass || "loading"); }).endQuery(function() { pane.paneElement.classList.remove(spotDefault.options.loadingClass || "loading"); });

    spotDOM.arrayField(this, 'facet');

    this.getFacetName = options['getFacetName'] || function(facet) {  return facet.getName(); };
    this.getFacetValueName = options['getFacetValueName'] || function(facetValue) { return facetValue.getName() };
    this.formatMoney = options['formatMoney'] || function(price, format) { return spotDefault.spotDOM.formatMoney(price, format); }
    this.showMoreLimit = options['showMoreLimit'];
    this.inactiveLimit = options['inactiveLimit'] != null ? options['inactiveLimit'] : 1;
    this.inactiveSubsequentLimit = options['inactiveSubsequentLimit'] != null ? options['inactiveSubsequentLimit'] : 0;
    this.hideEmptyValues = options['hideEmptyValues'] != null ? options['hideEmptyValues'] : true;
    this.facetClasses = options['facetClasses'] || [];
    this.facetValueClasses = options['facetValueClasses'] || [];
    this.facetFilter = options['facetFilter'] || function(facet) { return true; };
    this.omitInactiveValues = options['omitInactiveValues'] || false;
    this.omitHiddenValues = options['omitHiddenValues'] || false;

    this.closePane.addEventListener('click', function(e) { pane.paneElement.classList.remove('expanded'); });

    this.isFacetValueActive = options['isFacetValueActive'] || function(facetValue, count) {
      count = count !== undefined ? count : facetValue.count();
      return facetValue.sortOrder || !pane.hideEmptyValues || (count != null && count > 0) || (facetValue.isUnnamedWildcard() && pane.getFacetValueName(facetValue) != "" && pane.getFacetValueName(facetValue) != null);
    };
    this.isFacetValueVisible = options['isFacetValueVisible'] || function(facetValue) {
      return facetValue.facet.active;
    };

    // This is *only* called when needed; if omitHiddenFacetValues is true, we literally just don't create or add any of the relevant elements.
    // This is *significantly* faster for very large amounts of facet values (500+).
    this.defaultCountRenderer = function(count) {
      return count != null && spotDOM.optionBehavior() != "exists" ? "(" + count + ")" : '';
    };


    this.defaultFacetValueRenderer = function(facet, facetValue) {
      if (facet.getBoundary()) {
        var increment = 1.0;
        var precision = 0.01;
        facet.panes[paneId].slider = new spotDefault.DoubleRangeSlider();
        facet.panes[paneId].textElement = spotDefault.createElement("<span class='facet-range-text'><span class='facet-range-name'></span><span class='facet-range-min'></span><span class='facet-range-divider'></span><span class='facet-range-max'></span></span>");
        facet.panes[paneId].inputElement = spotDefault.createElement("<div class='facet-range'><span class='facet-range-input'><input type='number'/></span><span> - </span><span  class='facet-range-input'><input type='number'/></span></div>");
        var inputs = facet.panes[paneId].inputElement.querySelectorAll('input');
        facet.panes[paneId].updateFacetElements = function(inputValues) {
          var originalValues = inputValues || facet.boundaryValues() || [null, null];
          facet.panes[paneId].element.classList.remove('inactive');
          var edges = facet.boundaryEdges();
          if (edges) {
            var values = [
              Math.round(Math.max(originalValues[0] != null ? originalValues[0] : edges[0], edges[0]) / precision) * precision,
              Math.round(Math.min(originalValues[1] != null ? originalValues[1] : edges[1], edges[1]) / precision) * precision
            ];
            var width = (edges[1] - edges[0]);
            facet.panes[paneId].slider.step(increment / width);
            facet.panes[paneId].slider.values([
              (values[0] - edges[0]) / width,
              (values[1] - edges[0]) / width
            ]);
            facet.panes[paneId].slider.updateBar();
            if (facet.isMonetary()) {
              inputs[0].placeholder = pane.formatMoney(edges[0], "{{ amount_with_no_comma }}");
              inputs[1].placeholder = pane.formatMoney(edges[1], "{{ amount_with_no_comma }}");
              inputs[0].value = originalValues[0] != null ? pane.formatMoney(values[0], "{{ amount_with_no_comma }}") : '';
              inputs[1].value = originalValues[1] != null ? pane.formatMoney(values[1], "{{ amount_with_no_comma }}") : '';
              values = values.map(function(e) { return pane.formatMoney(e); });
            } else {
              inputs[0].placeholder = edges[0];
              inputs[1].placeholder = edges[1];
              inputs[0].value = originalValues[0] != null ? values[0] : '';
              inputs[1].value = originalValues[1] != null ? values[1] : '';
            }
            facet.panes[paneId].textElement.querySelector('.facet-range-name').innerText = pane.getFacetName(facet) + ": ";
            facet.panes[paneId].textElement.querySelector('.facet-range-min').innerText = values[0];
            facet.panes[paneId].textElement.querySelector('.facet-range-divider').innerText = " - ";
            facet.panes[paneId].textElement.querySelector('.facet-range-max').innerText = values[1];
            facet.panes[paneId].element.classList.toggle('inactive', Math.abs(edges[0] - edges[1]) < 0.01);
          }
        };
        inputs.forEach(function(input) {
          input.addEventListener('blur', function(e) {
            facet.boundaryValues([
              inputs[0].value != '' ? inputs[0].value : null,
              inputs[1].value != '' ? inputs[1].value : null,
            ]);
          });
          input.addEventListener('keypress', function(e) {
            if (e.key == "Enter") {
              facet.boundaryValues([
                inputs[0].value != '' ? inputs[0].value : null,
                inputs[1].value != '' ? inputs[1].value : null,
              ]);
            }
          });
        });
        facet.boundaryEdges(function(values) { facet.panes[paneId].updateFacetElements(); });
        facet.boundaryValues(function(values) { facet.panes[paneId].updateFacetElements(); });
        facet.panes[paneId].slider.drag(function(val1, val2) {
          var edges = facet.boundaryEdges();
          facet.panes[paneId].updateFacetElements([
            val1 != 0.0 ? val1 * (edges[1] - edges[0]) + edges[0] : null,
            val2 != 1.0 ? val2 * (edges[1] - edges[0]) + edges[0] : null
          ]);
        });
        facet.panes[paneId].slider.change(function(val1, val2) {
          var edges = facet.boundaryEdges();
          facet.boundaryValues([
            val1 != 0.0 ? val1 * (edges[1] - edges[0]) + edges[0] : null,
            val2 != 1.0 ? val2 * (edges[1] - edges[0]) + edges[0] : null
          ]);
        });
        (facet.panes[paneId].element.querySelector('.facet-values .facet-organizer') || facet.panes[paneId].element.querySelector('.facet-values')).appendChild(facet.panes[paneId].slider.element);
        (facet.panes[paneId].element.querySelector('.facet-values .facet-organizer') || facet.panes[paneId].element.querySelector('.facet-values')).appendChild(facet.panes[paneId].textElement);
        if (options['rangeInputs'])
          (facet.panes[paneId].element.querySelector('.facet-values .facet-organizer') || facet.panes[paneId].element.querySelector('.facet-values')).appendChild(facet.panes[paneId].inputElement);
        return facet.panes[paneId].element;
      } else {
        if (facetValue.panes[paneId].element)
          return facetValue.panes[paneId].element;
        var facet = facetValue.facet;
        // Create a facet value, with a checkbox to indicate whether or not it's active.
        // "name" is present so that the back button functions properly.
        var swatches = spotDefault.options && spotDefault.options.settings && spotDefault.options.settings.swatches;
        var swatch = swatches && facetValue.swatch_id && swatches[facetValue.swatch_id];
        facetValue.panes[paneId].element = spotDefault.createElement("<div class='facet-value facet-value-" + facetValue.getUniqueId() + (swatch ? " facet-value-swatched" : "") + "' role='option' id='" + pane.paneId + "-facet-value-" + facetValue.getUniqueId() + "' data-value-id='" + facetValue.getUniqueId() + "'>" + (swatch ? "<div class='facet-value-swatch' style='" + (swatch.color ? "background-color: #" + swatch.color + ";" : "") + (swatch.image ? "background-image: url" + swatch.image + ");" : "") + "'></div>" : "") + "<input name='" + facetValue.getUniqueId() + "' type='" + (facet.singleSelection() ? "radio" : "checkbox") + "'/> <span class='name'>" +
          pane.getFacetValueName(facetValue) + "</span> <span class='count'></span></div>");
        pane.facetValueClasses.forEach(function(cl) { facetValue.panes[paneId].element.classList.add(cl); })
        facetValue.panes[paneId].element.setAttribute("title", pane.getFacetValueName(facetValue));
        facetValue.panes[paneId].addedElement = false;
        if (facetValue.panes[paneId].enableable == null)
          facetValue.panes[paneId].enableable = true;

        var activeFacetValues = facet.facetValues().filter(function(facetValue) { return pane.isFacetValueActive(facetValue); });
        if (!facet.panes[paneId].showingAll && activeFacetValues.indexOf(facetValue) > pane.showMoreLimit)
          spotDefault.hideElementQuick(facetValue.panes[paneId].element);

        // Any time a facetValue's count changes, update what it reads.
        facetValue.count(function(count) {
          var isActive = pane.isFacetValueActive(facetValue, count);
          facetValue.panes[paneId].element.querySelector(".count").innerText = pane.countRenderer(count);
          // When we remove a facetValue, make sure to remove the breadcrumb and hide the dom element (this way the order of the elements doesn't jump around arbitrarily).
          // spotDefault.toggleElementQuick(facetValue.panes[paneId].element, isActive);
          facetValue.panes[paneId].element.classList.toggle("inactive", !isActive);
          if (isActive) {
            facetValue.panes[paneId].wasActive = true;
            facetValue.panes[paneId].element.classList.add('was-active');
          }
        }, true).enabled(function(enabled) {
          // If a facet gets enabled, make sure that its checkbox is checked.
          facetValue.panes[paneId].element.querySelector("input").checked = enabled;
          facetValue.panes[paneId].element.setAttribute("aria-selected", enabled);
          // Add a breadcrumb if enabled, remove its breadcrumb if not, if this facet produces breadcrumbs. This setting is only relevant for panes.
          facetValue.panes[paneId].element.classList.toggle("active", enabled);
        }, facetValue.enabled());
        // If the checkbox is clicked, update the facetValue's enability. Add in a query guard for potential sort facets.
        facetValue.panes[paneId].element.addEventListener("click", function(e) {
          if (facetValue.panes[paneId].enableable)
            spotDOM.queryGuard(function() { facetValue.enabled(!facetValue.enabled()); })
        });
        return facetValue.panes[paneId].element;
      }
    };

    
    this.facetValueRenderer = options['facetValueRenderer'] || this.defaultFacetValueRenderer;
    this.countRenderer = options['countRenderer'] || this.defaultCountRenderer;

    if (breadcrumbsEnabled)
      this.breadcrumbPane = new spotDefault.BreadcrumbPane(options);

    // Every time a facet is added, hook up all connectors and insert into the DOM.
    this.addFacet(function(facet) {
      if (!facet.panes)
        facet.panes = {};
      if (!facet.panes[paneId])
        facet.panes[paneId] = {};
      var helpText = facet.getBoundary() ? "Slider for " + pane.getFacetName(facet) + ". Drag the slider to adjust the range of values shown." : "List of " + pane.getFacetName(facet) + " options. Click an option to filter the the products shown.";
      facet.panes[paneId].element = spotDefault.createElement("<div id='" + pane.paneId + "-facet-" + facet.getUniqueId() + "' class='facet inactive facet-" + facet.getUniqueId() + "' role='listbox'><div class='facet-title'>" + pane.getFacetName(facet) + "</div><div class='facet-values' title='" + helpText + "'><div class='facet-organizer'></div></div></div>");
      pane.facetClasses.forEach(function(cl) { facet.panes[paneId].element.classList.add(cl); })
      facet.panes[paneId].element.setAttribute('title', facet.getName());
      facet.panes[paneId].showMoreElement = pane.showMoreLimit != null ? spotDefault.createElement("<div class='facet-value-show-more'></div>") : null;
      facet.panes[paneId].showingAll = pane.showMoreLimit == null;


      facet.panes[paneId].updateFacetElements = function() {
        var newFacetValues = facet.facetValues().filter(function(fv) { return fv.panes[paneId].element && !fv.panes[paneId].addedElement; });
        var facetValuesElement = (facet.panes[paneId].element.querySelector(".facet-values .facet-organizer") || facet.panes[paneId].element.querySelector(".facet-values"));
        if (newFacetValues.length > 0) {
          var facetValueFragment = document.createDocumentFragment();
          // For many hundreds of facet values that may or may not be shown, this can be slow.
          // Consider instead having a function that only adds elements as needed.
          newFacetValues.forEach(function(facetValue) {
            facetValue.panes[paneId].addedElement = true;
            facetValueFragment.appendChild(facetValue.panes[paneId].element);
          });
          facetValuesElement.appendChild(facetValueFragment);
          if (facet.panes[paneId].showMoreElement)
            facetValuesElement.appendChild(facet.panes[paneId].showMoreElement);
        }
        // Check if in order.
        var childArray = Array.from(facetValuesElement.children);
        if (facet.facetValues().filter(function(fv) { return fv.panes[paneId].element && fv.panes[paneId].addedElement; }).filter(function(fv, currentIdx) {
          return childArray.indexOf(fv.panes[paneId].element) != currentIdx;
        }).length > 0) {
          facet.facetValues().filter(function(fv) { return fv.panes[paneId].element && fv.panes[paneId].addedElement; }).forEach(function(fv) {
            facetValuesElement.appendChild(fv.panes[paneId].element);
          });
          if (facet.panes[paneId].showMoreElement)
            facetValuesElement.appendChild(facet.panes[paneId].showMoreElement);
        }
        
        var activeCount = facet.facetValues().filter(function(e) { return pane.isFacetValueActive(e); }).length;
        var isActiveFacet;
        if (!facet.panes[paneId].wasActive)
          isActiveFacet = activeCount > (typeof(pane.inactiveLimit) == 'function' ? pane.inactiveLimit(facet) : pane.inactiveLimit);
        else
          isActiveFacet = activeCount > (typeof(pane.inactiveSubsequentLimit) == 'function' ? pane.inactiveSubsequentLimit(facet) : pane.inactiveSubsequentLimit);
        if (isActiveFacet) {
          facet.panes[paneId].wasActive = true;
          facet.panes[paneId].element.classList.add('was-active');
        }
        facet.panes[paneId].element.classList.toggle("inactive", !isActiveFacet);
      };
      // The show more element button; not required, but can make the list smaller if a threshold is specified.
      if (facet.panes[paneId].showMoreElement) {
        facet.panes[paneId].updateShowMoreElements = function() {
          facet.panes[paneId].showMoreElement.innerText = facet.panes[paneId].showingAll ? ("- " + spotDefault.templateLocale("show-less")) : ("+ " + spotDefault.templateLocale("show-more"));
          var activeFacetValues = facet.facetValues().filter(function(e) { return pane.isFacetValueActive(e); });
          if (!facet.panes[paneId].showingAll) {
            facet.facetValues().forEach(function(e) { spotDefault.hideElementQuick(e.panes[paneId].element); });
            activeFacetValues.filter(function(e,idx) { return idx < pane.showMoreLimit; }).forEach(function(e) { spotDefault.showElement(e.panes[paneId].element); });
          } else {
            activeFacetValues.forEach(function(e) { spotDefault.showElement(e.panes[paneId].element); });
          }
          spotDefault.toggleElementQuick(facet.panes[paneId].showMoreElement, activeFacetValues.length > pane.showMoreLimit);
        };
        facet.panes[paneId].updateShowMoreElements();
        facet.panes[paneId].showMoreElement.addEventListener('click', function() {
          facet.panes[paneId].showingAll = !facet.panes[paneId].showingAll;
          facet.panes[paneId].updateShowMoreElements();
        });
      }
      spotDOM.endQuery(function() {
        facet.panes[paneId].updateFacetElements();
        if (facet.panes[paneId].updateShowMoreElements)
          facet.panes[paneId].updateShowMoreElements();
      });

      // Allow for us to register a click on a facet element; allowing for us to easily "open/close" the facet via CSS if we want to.
      facet.panes[paneId].element.querySelector(".facet-title").addEventListener("click", function() {
        if (!facet.panes[paneId].element.classList.contains("active")) {
          if (options['singleActive'])
            spotDOM.spotAPI.forEach(spotDOM.facets(), function(f) { f.panes[paneId].element.classList.remove("active"); });
          facet.panes[paneId].element.classList.add("active");
          facet.panes[paneId].active = true;
          if (pane.omitHiddenValues) {
            facet.facetValues().filter(function(facetValue) { return !pane.omitInactiveValues || pane.isFacetValueActive(facetValue); }).forEach(function(facetValue) {
              pane.facetValueRenderer(facet, facetValue);
            });
          }
          facet.panes[paneId].updateFacetElements();
        } else {
          facet.panes[paneId].element.classList.remove("active");
          facet.panes[paneId].active = false;
        }
      });
      if (options.collapsible == "visible") {
        facet.panes[paneId].element.classList.add("active");
        facet.panes[paneId].active = false;
      }


      // Two different types of facets. Boundary facets, which are represented normally by sliders,
      // and option facets, which are represented by a list of options with a checkbox.
      if (facet.getBoundary()) {
        pane.facetValueRenderer(facet);
      } else {
        // Every time we get a value for a facet, add it to the list; don't necessarily create it's elements though, unless it's visible.
        facet.addFacetValues(function(facetValues) {
          facetValues.forEach(function(facetValue) {
            if (!facetValue.panes)
              facetValue.panes = {};
            if (!facetValue.panes[paneId])
              facetValue.panes[paneId] = {};
            if (facetValue.isUnnamedWildcard() || (pane.getFacetValueName(facetValue) == "" && pane.hideEmptyValues))
              return;
            facetValue.count(function(count) {
              var isActive = pane.isFacetValueActive(facetValue, count);
              var isVisible = pane.isFacetValueVisible(facetValue, count);
              if (isActive) {
                if (facet.panes[paneId].element) {
                  if (facet.isValidLocale(spotDOM.locale()))
                    spotDefault.showElementQuick(facet.panes[paneId].element);
                }
              }
              if ((isActive || !pane.omitInactiveValues) && (isVisible || !pane.omitHiddenValues))
                pane.facetValueRenderer(facet, facetValue);
            }, true).enabled(function(enabled) {
              // If we're in single selection mode (becuase we're in ShopifyMode, or if this is a sorting facet), DISABLE all other facets in this category.
              if (facet.singleSelection() && enabled)
                facet.facetValues().forEach(function(e) { if (e != facetValue) { e.enabled(false); } });
              if (enabled) {
                facet.panes[paneId].element.classList.add("active");
                facet.panes[paneId].active = true;
                if (pane.omitHiddenValues)
                  pane.facetValueRenderer(facet, facetValue);
              }
            }, facetValue.enabled());
          });
          facet.panes[paneId].updateFacetElements();
        }, true).removeFacetValue(function(facetValues) {
          facetValues.forEach(function(facetValue) {
            if (facetValue.panes[paneId].element)
              facetValue.panes[paneId].element.remove();
          });
        });
      }
      // If we have a sorting facet, ensure that we insert before it, rather than after it.
      if (pane.sortingFacet && pane.sortingFacet != facet)
        pane.sortingFacet.panes[paneId].element.insertAdjacentElement("beforebegin", facet.panes[paneId].element);
      else
        pane.facetsElement.appendChild(facet.panes[paneId].element);
      spotDefault.showElement(pane.paneElement);

      spotDOM.locale(function(locale) {
        if (facet.panes[paneId].element && !facet.isValidLocale(spotDOM.locale()))
          spotDefault.hideElementQuick(facet.panes[paneId].element);
      }, true);
    }, true).removeFacet(function(facet) {
      // When we remove a facet, make sure to remove its dom elements. This shouldn't really ever occur,
      // but if you want to remove things programatically, this allows support for it.
      if (facet.panes[paneId] && facet.panes[paneId].element)
        facet.panes[paneId].element.remove();
      if (this.facets().length == 0)
        spotDefault.hideElement(pane.paneElement);
    });

    spotDOM.addFacet(function(facet) {
      if (!pane.facetFilter(facet))
        return;
      pane.addFacet(facet);
      if (pane.breadcrumbPane)
        pane.breadcrumbPane.addFacet(facet);
    }, true).removeFacet(function(facet) {
      pane.removeFacet(facet);
      if (pane.breadcrumbPane)
        pane.breadcrumbPane.removeFacet(facet);
    });
    var oldRelatedProduct = null;
    spotDefault.relatedProduct(function(relatedProduct) {
      if (pane.breadcrumbPane) {
        if (relatedProduct) {
          if (!relatedProduct.panes)
            relatedProduct.panes = {};
          if (!relatedProduct.panes[paneId])
            relatedProduct.panes[paneId] = {};
          pane.breadcrumbPane.addBreadcrumb(relatedProduct);
        } else if (oldRelatedProduct)
          pane.breadcrumbPane.removeBreadcrumb(oldRelatedProduct);
        oldRelatedProduct = relatedProduct;
      }
    }, true);

    if (options['includeSorting']) {
      // If we are including sorting, wrap sort orders in facet objects, so we use the entirely same stuff.
      // This is NOT added into the general DOM object, only the pane.
      var SortingFacet = function() {
        this.omitBreadcrumbs = true;
        this.sort = true;
        this.getUniqueId = function(locale) { return 'sort_by'; };
        this.singleSelection = function() { return true; }
        this.getName = function(locale) { return spotDefault.templateExplicitLocale('sort', locale); };
        this.getBoundary = function() { return null; }
        this.isValidLocale = function() { return true; };
        spotDOM.arrayField(this, 'facetValue');
        this.addFacetValue(function(facetValue) {
          facetValue.facet = this;
        });
      };
      var SortingFacetValue = function(sortOrder) {
        var facetValue = this;
        this.sortOrder = sortOrder;
        this.sortOrder.facetFacade = this;
        this.count = function(func) { if (func == null) return null; return this; };
        this.getUniqueId = function(locale) { return this.sortOrder.getUniqueId(); };
        this.getName = function() { return this.sortOrder.getName(); };
        this.isUnnamedWildcard = function() { return false; }
        spotDOM.scalarField(this, 'enabled');
        spotDOM.sort(function(sort) {
          var isOurSortOrder = sort == facetValue.sortOrder || (options['disallowNullSort'] && facetValue.getUniqueId() == 'manual' && spotDOM.isCollectionPage() && spotDOM.isUsingDefaultSort());
          if (options['disallowNullSort'])
              facetValue.enableable = !isOurSortOrder;
          if (isOurSortOrder && !facetValue.enabled())
            facetValue.enabled(true);
          else if (!isOurSortOrder && facetValue.enabled())
            facetValue.enabled(false);
        }, true);
        this.enabled(function(enabled) {
          if (enabled)
            spotDOM.sort(facetValue.sortOrder);
        });
      };
      this.sortingFacet = new SortingFacet();
      this.sortingFacet.panes = {};
      this.sortingFacet.panes[paneId] = {};
      spotDOM.addSortOrder(function(sortOrder) {
        if (sortOrder.isRelevant()) {
          var facetValue = new SortingFacetValue(sortOrder);
          facetValue.panes = {};
          facetValue.panes[paneId] = {};
          pane.sortingFacet.addFacetValue(facetValue);
          pane.facetValueRenderer(pane.sortingFacet, facetValue);
        }
      }, true).removeSortOrder(function(sortOrder) {
        pane.sortingFacet.removeFacetValue(sortOrder.facetFacade);
      });
      pane.addFacet(this.sortingFacet);
    }
  };
  this.Pane = this.FacetPane;
  /** %-{Pane} **/

  /**

  **To make a custom pane**, you can simply copy the above code from `spot.js`, and paste it into your theme, renaming the pane's class to something appropriate. From there, you can pass a copy instantiated with
  new (e.g. `new CustomPane(paneOptions)`) in as the `pane` option to `SpotDefault.init`. Or, you can simply attach the pane and hook it up manually, if you're so inclined (in that case, see the SpotDefault
  section for details).

  **/


  /**

  ### RecommendationsPane

  The recommendations pane object provided with SpotDefault is an easy-to-use drop in system for providing recommendations when on a product display page, or potentially as part of a product modal. By default
  the pane will display *all* recommendations sets together that have been set up in the spot admin panel, with a simple header denoting the name of the recommendation.

  **/
  this.RecommendationsPane = function(options, productRenderer) {
    var pane = this;

    if (!options)
      options = {};

    this.recommendationsId = options['id'] || "spot-default-recommendations";
    this.paneElement = spotDefault.createElement("<div class='recommendations'></div>");
    if (options['classes'])
      spotDOM.spotAPI.forEach(options['classes'], function(e) { pane.paneElement.classList.add(e); });
    spotDOM.listener(this, 'productRenderer', productRenderer || spotDefault.defaultProductRenderer);
    spotDOM.arrayField(this, 'recommendation');

    // Allows for linking into full page recommendations.
    this.getRecommendationLink = function(recommendation, context) {
      return context && context.product && (window.Shopify ? window.Shopify.routes.root : '/') + 'search?' + spotDOM.spotAPI.encodeUrlVars({ recommendation_id: recommendation.id, product_id: context.product.id });
    };

    this.addRecommendation(function(recommendation) {
      recommendation.init(spotDefault.context());
      recommendation.element = spotDefault.createElement("<div class='recommendation'><a class='name'></a><div class='products'></div></div>");
      recommendation.heading = recommendation.element.querySelector(".name");
      recommendation.productContainer = recommendation.element.querySelector(".products");
      recommendation.contextListener = function(context) {
        recommendation.context(context);
      };
      recommendation.context(function(context) {        
        if (pane.getRecommendationLink(recommendation, context)) {
          recommendation.heading.href = pane.getRecommendationLink(recommendation, context);
        } else {
          recommendation.heading.href = '';
        }
      }, true);
      spotDefault.context(recommendation.contextListener);
      recommendation.name(function(name) {
        recommendation.heading.innerText = recommendation.getName();
      }, true).addProduct(function(product) {
        product.element = pane.productRenderer(product);
        recommendation.productContainer.appendChild(product.element);
        spotDefault.showElement(recommendation.element);
      }, true).removeProduct(function(product) {
        product.element.remove();
        product.element = null;
        if (recommendation.products().length == 0)
          spotDefault.hideElement(recommendation.element);
      });
      spotDefault.hideElementQuick(recommendation.element);
      this.paneElement.appendChild(recommendation.element);
    }, true).removeRecommendation(function(recommendation) {
      spotDOM.removeFieldListener(recommendation, 'context', recommendation.contextListener);
      recommendation.element.remove();
      recommendation.element = null;
    });
  };

  this.totalCollectionsToGet = [];
  this.getCollectionPromise = spotDOM.spotAPI.Deferred();
  this.getCollection = function(collection) {
    var storefront = spotDOM.storefront();
    var deferred = spotDOM.spotAPI.Deferred();
    if (storefront) {
      storefront.settings([{ collection: collection, namespace: "esafilters", key: "settings" }]).done(function(r) {
        if (r.data && r.data.collection) {
          deferred.resolve({
            id: r.data.collection.id.replace(/.*\//, ""),
            handle: r.data.collection.handle,
            metafields: r.data.collection.metafields.filter(function(m) { return m; }).map(function(m) { return { key: "settings", namespace: "esafilters", value: JSON.parse(m.value) } })
          });
        }
      });
      return deferred;
    }
    if (spotDefault.isReady()) {
      spotDOM.spotAPI.s().targets(["collections"]).id(collection.id).e().done(function(collections) {
        deferred.resolve(collections[0]);
      });
      return deferred;
    }
    if (this.totalCollectionsToGet.length == 0) {
      spotDefault.ready(function() {
        spotDOM.spotAPI.s().targets(["collections"]).or([
           spotDOM.spotAPI.se().id("in", spotDefault.totalCollectionsToGet.filter(function(c) { return typeof(c) == 'number' })),
           spotDOM.spotAPI.se().handle("in", spotDefault.totalCollectionsToGet.filter(function(c) { return typeof(c) == 'string' }))
        ]).e().done(function(collections) {
          spotDefault.getCollectionPromise.resolve(collections);
        });
      });
    }
    this.getCollectionPromise.done(function(collections) {
      if (typeof(collection) == 'string')
        deferred.resolve(collections.filter(function(c) { return c.handle == collection })[0]);
      else
        deferred.resolve(collections.filter(function(c) { return c.id == collection })[0]);
    });
    this.totalCollectionsToGet.push(collection);
    return deferred;
  };

  this.CollectionPane = function(options) {
    var pane = this;
    if (!options)
      options = {};
    if (!options.collection)
      throw "Requires a collection.";
    options = Object.assign({ }, spotDefault.options, options);

    this.element = options.element || spotDefault.createElement("<div class='spot-collection " + (options.className || "") + "'></div>");

    var deferred = spotDOM.spotAPI.Deferred();
    if (typeof(options.collection) == 'object' && options.collection.metafields) {
      deferred.resolve(options.collection);
    } else {
      spotDefault.getCollection(typeof(options.collection) == 'object' ? options.collection.id : options.collection).done(function(collection) {
        deferred.resolve(collection);
      });
    }
    deferred.done(function(collection) {
      pane.spotDefault = options.spotDefault || new window.SpotDefault(new window.SpotDOM(spotDOM.spotAPI, Object.assign({ }, options, {
        storage: false,
        location: '/collections/' + collection.handle + (options.adhoc ? '/' + options.adhoc : ''),
        optionBehavior: "none",
        countBehavior: "none",
      })));
      pane.spotDefault.setupContext({ collection: collection }).setupObjects(options).setupProductRender(pane.element, options.productRenderer || spotDefault.productRenderer, true).setupActiveRules(collection.metafields && collection.metafields.filter(function(m) { return m.key == "settings" && m.namespace == "esafilters" }).map(function(m) { return m.value && m.value.rule })[0]);
      pane.spotDefault.spotDOM.query();
    });
    return this;
  };
  /**

  ### KeyupListener & SearchBar

  This provides an easy way for you to listen when someone is typing into a text box, and feed those search request into Spot, either for a quick-search, or for automatic page refreshing
  on a collections page. KeyupListener provides the bulk of functionality, SearchBar provides an easy way to create a customized search dropdown.

  #### KeyupListener(element)

  A sample way to use this is to hook an input bar into setting the search for SpotDOM, as so:

      new window.spotDefault.KeyupListener(document.querySelector('.productgrid--search-form-field')).doneTyping(function(search) {
        window.spotDOM.search(search);
      });

  Has the following configurable attributes:

  * `element`: The input element we're listening on.
  * `minCharacters`: The minimum amount of characters that must be present before engaging. Default: 1.
  * `interval`: The minimal interval we wait before running `doneTyping` normally. Default: 200 miliseconds.
  * `doneTyping`: The primary function; takes in the resulting string. Is called periodically as a user is typing.
  * `submit`: The listener for when the element receives an "enter".
  * `focus`: The listener that fires when the element receives focus.
  * `unfocus`: The listener that fires when the element loses focus.

  The implementation of this is below:

  %{KeyupListener}

  **/

  /** %+{KeyupListener} **/
  this.KeyupListener = function(element) {
    var keyupListener = this;

    spotDOM.scalarField(this, 'element');
    spotDOM.scalarField(this, 'minCharacters', 1);
    spotDOM.scalarField(this, 'interval', 120);
    spotDOM.listener(this, 'doneTyping');
    spotDOM.listener(this, 'submit');
    spotDOM.listener(this, 'focus', function() {
      this.doneTyping(this.element().value);
    });
    spotDOM.listener(this, 'unfocus', function() { });

    if (!element)
      throw "Requires an element to attach to.";

    this.timeout = null;
    this.lastSearch = element.value;
    this.oldElement = null;

    this.handler = function(e) {
      var length = element.value.replace(/\s*/g, "").length;
      if (length > keyupListener.minCharacters()) {
        if (keyupListener.timeout) {
          clearTimeout(keyupListener.timeout);
          keyupListener.timeout = null;
        }
        var timeoutFunction = function() {
          keyupListener.lastSearch = element.value;
          keyupListener.doneTyping(element.value);
        };
        if (keyupListener.lastSearch != element.value) {
          if (keyupListener.interval() > 0)
            keyupListener.timeout = setTimeout(timeoutFunction, keyupListener.interval());
          else
            timeoutFunction();
        }
      } else if (length == keyupListener.minCharacters()) {
        if (keyupListener.lastSearch != element.value) {
          keyupListener.lastSearch = element.value;
          keyupListener.doneTyping(element.value);
        }
      } else if (keyupListener.lastSearch != '') {
        keyupListener.lastSearch = '';
        keyupListener.doneTyping('');
      }
      if (e.keyCode === 13)
        keyupListener.submit(element.value);
    };

    this.submitHandler = function(e) {
      e.preventDefault();
      keyupListener.submit(element.value);
    };

    this.element(function(element) {
      if (this.oldElement) {
        this.oldElement.removeEventListener('keyup', this.handler);
        this.oldElement.removeEventListener('focus', this.focus);
        this.oldElement.removeEventListener('blur', this.unfocus);
        if (this.oldElement.closest('form'))
          this.oldElement.closest('form').removeEventListener('submit', this.submitHandler);
      }
      element.autocomplete = 'off';
      element.addEventListener('keyup', this.handler);
      element.addEventListener('focus', this.focus);
      element.addEventListener('blur', this.unfocus);
      if (element.closest('form'))
        element.closest('form').addEventListener('submit', this.submitHandler);
      this.oldElement = element;
    });
    this.element(element);
  };
  /** %-{KeyupListener} **/
  /**

  #### SearchBar(inputElement, maxProducts, showAll, options)

  `SearchBar` uses the `KeyListener` class to hook into an input element and display an "instant-search" display below it.

  This class is fairly configurable.

  * `maxProducts` refers to the amount of products that are to be retrieved in a dropdown. Default 5.
  * `showAll` determines whether or not a show all button will be rendered that brings you to the search page.

  `options` can take the following options. You should specify almost none of these.

  * `dropdownContainer` specifies the container in which to put the search results.
  * `popularSearchContainer` specifies the container in which to dump rendered popular search results.
  * `resultsContainer` specifies the container in which to dump renderered results.
  * `imageSize` specifies the size of the image returned. Default: `"64x64"`.
  * `resultRenderer` specifies the function that renders results from either Spot or Shopify, depending.
  * `getQuery` specifies the fucntion that generates the query that returns results from Spot.
  * `results` specifies a custom result rendering function entirely.
  * `doneTyping` specifies a custom function to pass to the internal keyupListener.
  * `resize` specifies the resize listener.
  * `rule` specifies the ruleset to apply boost rules, conditions, splits and merges from.
  * `targets` specifies what we should be searching. By default, this is "prodcuts", "collections", and "popularSearches".
  * `redirectCollection` specifies whether or not to redirect to a collection if we have an exact title match. default to `true`.
  * `redirectPage` specifies whether or not to redirect to a page if we have an exact title match. default to `true`.
  * `redirectProduct` specifies whether or not to redirect to a product if we have an exact title match. default to `false`.

  The code for this class is below:

  **/
  /** %+{SearchBar} **/
  this.SearchBar = function(inputElement, maxProducts, showAll, options) {
    if (showAll === undefined)
      showAll = true;
    var searchBar = this;
    this.showAll = showAll;
    this.spotDefault = spotDefault;
    this.keyupListener = new spotDefault.KeyupListener(inputElement);
    this.inputElement = inputElement;
    spotDOM.scalarField(this, 'maxProducts', maxProducts || 5);
    spotDOM.scalarField(this, 'redirectCollection', options["redirectCollection"] != null ? options["redirectCollection"] : true);
    spotDOM.scalarField(this, 'redirectPage', options["redirectPage"] != null ? options["redirectPage"] : true);
    spotDOM.scalarField(this, 'redirectProduct', options["redirectProduct"] != null ? options["redirectProduct"] : false);
    spotDOM.scalarField(this, 'redirectRelevantIfNoProducts', options["redirectRelevantIfNoProducts"] != null ? options["redirectRelevantIfNoProducts"] : true);
    spotDOM.scalarField(this, 'defaultResults', options['defaultResults']);
    spotDOM.arrayField(this, 'target', options["targets"] || ["products", "collections", "popularSearches"]);
    spotDOM.scalarField(this, 'imageSize', options['imageSize'] || '64');
    spotDOM.arrayField(this, 'queryTransform', options.queryTransforms || []);
    spotDOM.arrayField(this, 'resultsTransform', options.resultsTransforms || []);
    spotDOM.listeners(this, 'fail');
    spotDOM.listener(this, 'resultRenderer', options['resultRenderer'] || function(result, source) {
      if (source != 'shopify') {
        if (result.type == 'popular_search') {
          var searchElement = spotDefault.createElement("<a href='/search?q=" + encodeURIComponent(result.term) + "'></a>");
          searchElement.innerText = result.term;
          searchElement.addEventListener('click', function(e) {
            e.preventDefault();
            spotDefault.redirectToSearchPage(result.term);
          });
          return searchElement;
        }

        var isProduct = Array.isArray(result) || result.variants;
        var url = !isProduct ? result.url || ("/" + result.type + "s/" + result.handle)  : spotDOM.getProductURL(result);
        var image = isProduct ? spotDOM.getProductImage(result) : result.image;
        var element = spotDefault.createElement("<a href='" + url + "' class='" + result.type + " item'>\
          <div class='image'></div>\
          <div class='info'>\
            <h2 class='title'>"  + (isProduct ? spotDOM.getProductTitle(result) : result.title) + "</h2>" +
            (isProduct ? "<div class='price'>\
              <span class='money' data-currency-usd='" + spotDOM.formatMoney(spotDOM.getProductPrice(result)) + "' data-currency='USD'>" + spotDOM.formatMoney(spotDOM.getProductPrice(result)) + "</span>\
            </div>" : "") +
          "</div>\
        </div>");
        element.querySelector('.image').appendChild(spotDefault.generateImage(image, { alt: result.title, startResolution: this.imageSize(), endResolution: this.imageSize() }));
        return element;
      }
      var url = result.url;
      return spotDefault.createElement("<a href='" + url + "' class='product item'>\
        <div class='image'>\
        " + (result.image ? "<img src='" + spotDOM.getSizedImage(result.image, searchBar.imageSize()) + "'/>" : '<svg class="placeholder--image" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5"><path d="M324.5 212.7H203c-1.6 0-2.8 1.3-2.8 2.8V308c0 1.6 1.3 2.8 2.8 2.8h121.6c1.6 0 2.8-1.3 2.8-2.8v-92.5c0-1.6-1.3-2.8-2.9-2.8zm1.1 95.3c0 .6-.5 1.1-1.1 1.1H203c-.6 0-1.1-.5-1.1-1.1v-92.5c0-.6.5-1.1 1.1-1.1h121.6c.6 0 1.1.5 1.1 1.1V308z"></path><path d="M210.4 299.5H240v.1s.1 0 .2-.1h75.2v-76.2h-105v76.2zm1.8-7.2l20-20c1.6-1.6 3.8-2.5 6.1-2.5s4.5.9 6.1 2.5l1.5 1.5 16.8 16.8c-12.9 3.3-20.7 6.3-22.8 7.2h-27.7v-5.5zm101.5-10.1c-20.1 1.7-36.7 4.8-49.1 7.9l-16.9-16.9 26.3-26.3c1.6-1.6 3.8-2.5 6.1-2.5s4.5.9 6.1 2.5l27.5 27.5v7.8zm-68.9 15.5c9.7-3.5 33.9-10.9 68.9-13.8v13.8h-68.9zm68.9-72.7v46.8l-26.2-26.2c-1.9-1.9-4.5-3-7.3-3s-5.4 1.1-7.3 3l-26.3 26.3-.9-.9c-1.9-1.9-4.5-3-7.3-3s-5.4 1.1-7.3 3l-18.8 18.8V225h101.4z"></path><path d="M232.8 254c4.6 0 8.3-3.7 8.3-8.3s-3.7-8.3-8.3-8.3-8.3 3.7-8.3 8.3 3.7 8.3 8.3 8.3zm0-14.9c3.6 0 6.6 2.9 6.6 6.6s-2.9 6.6-6.6 6.6-6.6-2.9-6.6-6.6 3-6.6 6.6-6.6z"></path></svg>') +
        "</div>\
        <div class='info'>\
          <h2 class='title'>"  + result.title + "</h2>\
          <div class='price'>\
            " + result.type.charAt(0).toUpperCase() + result.type.slice(1) + "\
          </div>\
        </div>\
      </div>");
    });

    if (options.dropdownContainer)
      spotDefault.resolveQuerySelector(options.dropdownContainer, function(dropdownContainer) { searchBar.dropdownContainer = dropdownContainer; })
    if (options.resultsContainer)
      spotDefault.resolveQuerySelector(options.resultsContainer, function(resultsContainer) { searchBar.resultsContainer = resultsContainer; })
    if (options.popularSearchContainer)
      spotDefault.resolveQuerySelector(options.popularSearchContainer, function(popularSearchContainer) { searchBar.popularSearchContainer = popularSearchContainer; })

    this.searchNonSpot = false;

    spotDOM.listener(this, 'getQuery', options['getQuery'] || function(search) {
      var query = spotDOM.spotAPI.s().locale(spotDOM.locale()).paginate(this.maxProducts()).targets(this.targets()).search(search).allVariants(spotDOM.allVariants()).autoCorrect(spotDOM.autoCorrect()).category("QuickSearch");
      if (spotDOM.hostname())
        query = query.hostname(spotDOM.hostname());
      if (spotDOM.currency())
        query = query.currency(spotDOM.currency());
      if (spotDOM.market())
        query = query.market(spotDOM.market());
      if (spotDOM.catalog())
        query = query.catalog(spotDOM.catalog());
      if (showAll && showAll == "count")
        query = query.count(true);
      var options = spotDefault.options;
      var rules = spotDefault.getRules({ search: search, override: spotDOM.getOverrideRuleHandle() });

      if (rules && rules.length > 0) {
        var rule = rules[0];
        var defaultConditions = options.conditions || rule.conditions || [];
        var pins = options.pins || rule.pins || [];
        var antipins = options.antipins || rule.antipins || [];
        var boosts = options.boosts || rule.boosts || [];

        var conditionList = rules.map(function(r) { return [JSON.parse(JSON.stringify(r.conditions || [])), r.disjunctive ? "or" : "and"] }).filter(function(r) { return r[0]; });
        for (var i = conditionList.length - 2; i >= 0; --i)
          spotDefault.substituteDefaultConditions(conditionList[i][0], conditionList[i+1]);  
        var defaultConditions = conditionList.length > 0 ? conditionList[0][0] : [];
        if (defaultConditions.length > 0)
          query = query.merge(defaultConditions);

        var mergeId = rules.reduce(function(value, rule) { return value != null ? value : rule.merge_id; }, null);
        if (mergeId && mergeId != "none") {
          query.split("none");
          query.productMerge(mergeId);
        } else {
          var splitId = rules.reduce(function(value, rule) { return value != null ? value : rule.split_id; }, null);
          query.split(splitId != null ? splitId : "auto");
        }

        var pins = [];
        if (rule.pins)
          pins = rule.pins;
        if (rule.antipins)
          query = query.pins(rule.antipins);
        
        if (spotDOM.searchBoosts().length > 0) {
          var search = spotDOM.normalizeSearch(search);
          var searchPins = {};
          spotDOM.searchBoosts().filter(function(e) {
            var term = spotDOM.normalizeSearch(e.term);
            var idx = search.indexOf(term);
            return idx != -1 && (
              (e.type == "substring") ||
              (e.type == "exact" && idx == 0 && search.length == term.length) ||
              (e.type == "word" && (idx == 0 || /\w/.test(search.charAt(idx-1))) && (idx == search.length || /\w/.test(search.charAt(idx+1)))));
          }).forEach(function(e) { return e.products.forEach(function(p) { searchPins[p[0]] = (searchPins[p[0]] || 0) + p[1]; }); });
          pins = pins.concat(Object.keys(searchPins).sort(function(a,b) { return searchPins[b] - searchPins[a] }));
        }
        if (pins.length > 0)
          query = query.pins(pins);
        
        if (rule.boosts && spotDefault.options.settings.boost_rules) {
          var boostQuery = spotDOM.spotAPI.se();
          var andQuery = spotDOM.spotAPI.se();
          rule.boosts.filter(function(id) { return spotDefault.options.settings.boost_rules[id]; }).forEach(function(id) {
            boostQuery = boostQuery.merge(new spotDOM.BoostRule(spotDefault.options.settings.boost_rules[id]).getQuery({ }, false));
            andQuery = andQuery.merge(new spotDOM.BoostRule(spotDefault.options.settings.boost_rules[id]).getQuery({ }, true));
          });
          query = query.boost(boostQuery).and(andQuery);
        }
      }
      if (spotDOM.personalization())
        query = spotDOM.personalization().apply(query);

      spotDOM.queryTransforms().forEach(function(queryTransform) {
        queryTransform.forEach(function(t) {
          query = t.call(this, query, "search-bar");
        });
      });
      this.queryTransforms().forEach(function(queryTransform) {
        queryTransform.forEach(function(t) {
          query = t.call(this, query, "recommendation");
        });
      });
      return query;
    });

    this.updateMatchElement = function(inputElement, dropdownContainer) {
      if (dropdownContainer) {
        var width = Math.max(inputElement.offsetWidth, 400);
        var absolutePosition = inputElement.getBoundingClientRect();
        var onLeft = absolutePosition.x <= document.body.clientWidth / 2;
        dropdownContainer.style.width = width + "px";
        dropdownContainer.style.left = onLeft ? inputElement.offsetLeft + "px" : null;
        dropdownContainer.style.right = onLeft ? null : inputElement.offsetLeft + "px";
        dropdownContainer.style.top = (inputElement.offsetTop + inputElement.offsetHeight) + "px";
        if (window.outerWidth < 400)
          dropdownContainer.style.height = 'calc(100% - ' + dropdownContainer.style.top + ')'
      }
    };

    this.exactTitleMatchURL = null;
    this.checkTitleMatches = function(search, results) {
      var url = null;
      var normalizedSearch = spotDOM.normalizeSearch(search);
      if (this.redirectCollection() && results.collections) {
        var targets = results.collections.filter(function(collection) { return spotDOM.normalizeSearch(collection.handle) == normalizedSearch || spotDOM.normalizeSearch(collection.title) == normalizedSearch });
        if (targets.length > 0)
          url = "/collections/" + targets[0].handle;
      }
      if (this.redirectPage() && results.pages && !url) {
        var targets = results.pages.filter(function(page) { return spotDOM.normalizeSearch(page.handle) == normalizedSearch || spotDOM.normalizeSearch(page.title) == normalizedSearch });
        if (targets.length > 0)
          url = "/pages/" + targets[0].handle;
      }
      if (this.redirectProduct() && results.products && !url) {
        var normalized = spotDOM.normalizeSearch(search);
        var targets = results.products.filter(function(product) { return spotDOM.normalizeSearch(product.handle) == normalizedSearch || spotDOM.normalizeSearch(product.title) == normalizedSearch });
        if (targets.length > 0)
          url = "/products/" + targets[0].handle;
      }
      if (this.redirectRelevantIfNoProducts() && results.products && results.products.length == 0 && ((results.collections && results.collections[0]) || (results.pages && results.pages[0]))) {
        if (results.collections[0])
          url = "/collections/" + results.collections[0].handle;
        else if (results.pages[0])
          url = "/pages/" + results.pages[0].handle;
      }
      return url;
    };
    this.convertResults = function(results) {
      if (results.products)
        results.products = results.products.map(function(p) { var np = spotDOM.spotAPI.convertProduct(p); np.type = "product"; return np;  });
      if (results.collections)
        results.collections = results.collections.map(function(c) { var nc = spotDOM.spotAPI.convertCollection(c); nc.type = "collection"; return nc;  });
      if (results.pages)
        results.pages = results.pages.map(function(page) { page.type = "page"; page.url = "/pages/" + page.handle; return page; });
      if (results.blogs)
        results.blogs = results.blogs.map(function(blog) { blog.type = "blog"; blog.url = "/blogs/" + blog.handle; return blog; });
      if (results.articles)
        results.articles = results.articles.map(function(article) { article.type = "article"; article.url = "/blogs/" + spotDefault.options.blogs[article.blog_id] + "/" + article.handle; return article; });
      if (results.popularSearches)
        results.popularSearches = results.popularSearches.map(function(s) { s.type = "popular_search"; return s; });
      return results;
    }
    
    spotDOM.listener(this, 'results', options['results'] || function(search, results, count, nonSpotResults) {
      if (!nonSpotResults)
        nonSpotResults = [];
      if (!this.dropdownContainer) {
        this.dropdownContainer = spotDefault.createElement('<div style="display:none;" class="search-dropdown"><div class="search-dropdown-contents"></div></div>');
        this.inputElement.insertAdjacentElement('afterend', this.dropdownContainer);
      }
      this.updateMatchElement(this.inputElement, this.dropdownContainer);
      if (!this.resultsContainer) {
        this.resultsContainer = spotDefault.createElement('<div class="results"></div>');
        this.dropdownContainer.querySelector('.search-dropdown-contents').appendChild(this.resultsContainer);
      }
      if (!this.popularSearchContainer && this.targets().indexOf("popularSearches") != -1) {
        this.popularSearchContainer = spotDefault.createElement('<div class="popularSearches"></div>');
        this.dropdownContainer.querySelector('.search-dropdown-contents').appendChild(this.popularSearchContainer);
      }
      this.resultsContainer.innerHTML = '';
      if (this.popularSearchContainer)
        this.popularSearchContainer.innerHTML = '';

      this.exactTitleMatchURL = this.checkTitleMatches(search, results);

      this.convertResults(results);

      var fullResults = (results.products || []).concat(results.collections || []).concat(results.pages || []).concat(results.blogs || []).concat(results.articles || []).sort(function(a,b) { return b.relevancy - a.relevancy; });
      this.resultsTransforms().flatMap(function(rt) { return rt; }).forEach(function(rt) { fullResults = rt(fullResults) });
      fullResults = fullResults.slice(0, this.maxProducts());
      if (fullResults.length > 0 || nonSpotResults.length > 0 || (results.popularSearches && results.popularSearches.length > 0)) {
        nonSpotResults.forEach(function(result) { searchBar.resultsContainer.appendChild(searchBar.resultRenderer(result, 'shopify')); });
        fullResults.forEach(function(result, i) {
          var element = searchBar.resultRenderer(result, 'spot');
          searchBar.resultsContainer.appendChild(element);
          if (result.type == "product" && element.querySelector("a")) {
            element.querySelector("a").addEventListener('click', function() {
              var relevantVariant = spotDOM.getRelevantVariant(result);
              if (result.id && spotEvent)
                spotEvent.clickedProduct(result.id, relevantVariant ? relevantVariant.id : null, i);
            });
          }
        });
        if (results.popularSearches) {
          var element = spotDefault.createElement("<h2 class='heading'></h2>");
          element.innerText = spotDefault.templateLocale("popular-searches");
          this.popularSearchContainer.appendChild(element);
          // Slight hack, for now.
          var alreadyExtant = {};
          results.popularSearches.forEach(function(popularSearch) {
            if (!alreadyExtant[popularSearch.term]) {
              searchBar.popularSearchContainer.appendChild(searchBar.resultRenderer(popularSearch, 'spot'));
              alreadyExtant[popularSearch.term] = true;
            }
          });
        }
        if (showAll === undefined || showAll) {
          if (!this.showAllButton) {
            this.showAllButton = spotDefault.createElement("<a class='show-all-results'></a>");
            this.showAllButton.addEventListener('click', function(e) {
              e.preventDefault();
              spotDefault.redirectToSearchPage(searchBar.inputElement.value);
            });
          }
          this.showAllButton.textContent = spotDefault.templateLocale(count != null ? 'show-count' : 'show-all', { count: count });
          this.showAllButton.setAttribute('href', '/search?q=' + encodeURIComponent(search));
          searchBar.dropdownContainer.querySelector('.search-dropdown-contents').appendChild(this.showAllButton);
        }
        searchBar.toggleDropdown(searchBar.defaultResults() || !/^\s*$/.test(search));
      } else {
        searchBar.hideDropdown();
      }
    });

    this.isShown = false;
    this.clickHandler = options['clickHandler'] || function(e) {
      if (!searchBar.dropdownContainer.contains(e.target) && !searchBar.inputElement.contains(e.target) && e.target != searchBar.inputElement && e.target != searchBar.dropdownContainer)
        searchBar.toggleDropdown(false);
    };
    this.toggleDropdown = function(show) {
      if (show != this.isShown) {
        spotDefault.toggleElement(this.dropdownContainer, show);
        this.isShown = show;
        if (show)
          document.addEventListener('click', this.clickHandler);
        else
          document.removeEventListener('click', this.clickHandler);
      }
    };
    this.showDropdown = function() { this.toggleDropdown(true); };
    this.hideDropdown = function() { this.toggleDropdown(false); };
    this.inputElement.addEventListener('keydown', function(e) {
      if (e.keyCode == 27)
        searchBar.toggleDropdown(false);
    });

    this.doneTyping = options['doneTyping'] || function(search) {
      if (!spotDOM.shopifyMode() && search !== searchBar.lastResult) {
        var nonSpotResults = null;
        var spotResults = null;
        if (search != '' || !searchBar.defaultResults()) {
          searchBar.getQuery(search).e().done(function(products, count, options) {
            spotResults = [products,count,options];
            if (!searchBar.searchNonSpot || nonSpotResults)
              searchBar.results(search, options, count, nonSpotResults);
          }).fail(function() { searchBar.fail.apply(searchBar, arguments); });
          if (searchBar.searchNonSpot) {
            var path = '/search';
            if (!spotDOM.isPrimaryLocale())
              path = '/' + spotDOM.locale() + '/search';
            spotDOM.spotAPI.ajax({ url: path, dataType: 'html', data: { q: search, type: "article,page", view: "json" }}).done(function(data) {
              nonSpotResults = JSON.parse(data)['results'];
              if (spotResults)
                searchBar.results(search, spotResults[0], spotResults[1], spotResults[2], nonSpotResults);
            });
          }
        } else {
          if (typeof(searchBar.defaultResults()) == 'string') {
            searchBar.getQuery(searchBar.defaultResults()).e().done(function(products, count, options) {
              searchBar.results(search, options, count);
            }).fail(function() { searchBar.fail.apply(searchBar, arguments); });
          } else {
            searchBar.defaultResults().e().done(function(products, count, options) {
              searchBar.results(search, options, count);
            }).fail(function() { searchBar.fail.apply(searchBar, arguments); });;
          }
        }
        searchBar.lastResult = search;
      }
    };
    this.keyupListener.doneTyping(this.doneTyping);
    this.submit = options['submit'] || function(search) {
      if (spotDefault.getSearchPageRedirect(spotDefault.spotDOM.locale(), search))
        spotDefault.redirectToSearchPage(search);
      else if (searchBar.exactTitleMatchCollectionURL)
        spotDOM.location.href = spotDefault.redirectToCollectionPage(searchBar.exactTitleMatchCollectionURL, search);
      else if (searchBar.exactTitleMatchURL)
        spotDOM.location.href = searchBar.exactTitleMatchURL;
      else
        spotDefault.redirectToSearchPage(search);
    };
    this.keyupListener.submit(this.submit);

    this.highlightWords = function(suppliedWords, targetWords) {
      if (!Array.isArray(suppliedWords))
        suppliedWords = suppliedWords.replace(/(^\s+|\s+$)/g, "").split(/\s+/);
      if (!Array.isArray(targetWords))
        targetWords = targetWords.replace(/(^\s+|\s+$)/g, "").split(/\s+/);
      var highlightedSearch = '';
      var highlightedWords = [];
      var i;
      for (i = 0; i < targetWords.length; ++i) {
        highlightedWords[i] = 0;
        for (var j = 0; j < suppliedWords.length; ++j) {
          if (targetWords[i].toLowerCase().indexOf(suppliedWords[j].toLowerCase()) == 0)
            highlightedWords[i] = suppliedWords[j].length;
          if (suppliedWords[j].toLowerCase().indexOf(targetWords[i]) == 0)
            highlightedWords[i] = targetWords[i].length;
        }
      }

      for (i = 0; i < targetWords.length; ++i) {
        if (highlightedWords[i]) {
          highlightedSearch += (i != 0 ? " " : "") + "<span class='text-highlight'>" + targetWords[i].slice(0, highlightedWords[i]) + "</span>" + targetWords[i].slice(highlightedWords[i]);
        } else {
          highlightedSearch += (i != 0 ? " " : "") + targetWords[i];
        }
      }
      return highlightedSearch;
    };

    
    var possibleSentenceStructures = { "en": [
      ["adjective", "noun"],
      ["adjective", "noun"],
      ["adjective", "noun"],
      ["adjective", "noun"],
      ["adjective", "noun"],
      ["adjective", "noun"],
      ["adjective", "noun"],
      ["adjective", "noun"],
      ["adjective", "noun"],
      ["adjective", "noun"],
      ["adjective", "noun"],
      ["adjective", "noun"],
      ["adjective", "noun"],
      ["adjective", "adjective", "noun"],
      ["adjective", "adjective", "noun"],
      ["adjective", "adjective", "noun"],
      ["adjective", "adjective", "adjective", "noun"],
      ["noun"],
      ["adjective", "noun", "suffix"],          
      ["noun", "suffix"],
      ["adjective", "compound"],
      ["compound"],
      ["compound", "suffix"]
    ], "fr": [
      ["noun", "adjective"],
      ["noun", "adjective"],
      ["noun", "adjective"],
      ["noun", "adjective"],
      ["noun", "adjective"],
      ["noun", "adjective"],
      ["noun", "adjective"],
      ["noun", "adjective"],
      ["noun", "adjective"],
      ["noun", "adjective"],
      ["noun", "adjective", "adjective"],
      ["noun", "adjective", "adjective", "adjective"],
      ["noun"],
      ["noun", "adjective", "suffix"],          
      ["noun", "suffix"],
      ["compound"],
      ["compound", "adjective"],
      ["compound", "suffix"],
    ] };
    this.randomCounter = 0;
    
    // pseudo-random so that we can get some consistency in search results if nothing's changing.
    this.getRandomNumber = function() {
      var pseudorandomNumbers = [635736524201,258190580035,874367428283,318958029706,607912645105,253493399810,812422749490,383026956854,704987501251,734200777544,580246906743,679737990712,550053370913,810681006021,168582973143,859413112601,247623393283,250888266250,410115044860,84054517167,173938558504,143294279278,864393559087,871056056563,566611920876,128121412183,905210365868,54040091481,361616903602,111535069445,448727739680,923939875779,858275031442,177933089812,268832783468,892351562596,58664571459,694526417818,112961901741,704883197452,478513194129,1640090893,291589512710,192894720321,992037699909,258803139828,205396244909,296442096563,811359902549,893591904956,486644833722,18824298423,48192732326,115820085056,359442110208,623923026976,825712332589,318132123390,214423171172,692185861583,544293183994,105080857750,697154348001,180923012375,426591915085,661707281580,229535264853,851480093772,723954257897,190466343565,426132406940,932353395853,134046395620,978659516039,392267519290,676594068134,813859538455,89568950712,160509138067,177692509485,455812766194,747090976006,252794932748,954958140953,888716804217,769315036230,465691620706,72251687714,559118266071,230289491443,838348318540,968451174788,609402813027,235884550582,451713629286,450528110869,492504461519,899556561901,251886844023,17490858346,622779725677,422913496272,721504672730,573257027662,260317226752,515880293196,625397225175,766909953164,41548646569,139191358696,618075850565,428564967968,510513260818,890747471356,561761227134,497633359709,958638572391,975542229923,508568082371,257664619736,460691587693,50490525787,446203433604,778834957929,393011852331,790304908612,793303579735,46821106352,957766299368,513916696116,911651560509,37167239879,412400075848,893803821764,434426742330,829858463277,243454614178,659568279658,301911901788,992820814461,433367116499,334429176522,61415972270,779264175297,80762135140,706814398970,192382807720,956584926885,860228872858,752421312711,129693213386,228988698207,385905005575,664076291427,900099408676,537744197912,291742060026,112180789325,183753334544,968325951261,405616471765,707839685298,443223109680,399560850466,498107217762,249902300669,7680788672,373799829212,781543174930,847709389254,594659693674,317255753683,250713913738,580557080348,333616291614,204770845272,551444307270,405612784771,515310183878,942815676995,873188932941,496725013827,822681577469,178516078826,251164579355,791360473378,949528928136,361618209297,74691056732,961504242730,233065599540,761824664215,801859758611,292123893208,118683680635,737779860810,225028847590,548702606624,46420858545,968690119430,793711351190,109991281798,258282768894,88296927684,736060668040,715741830456,336413852230,337469876035,110374811969,679614924594,209954746404,500462761950,624968974788,408181928363,332218953014,675921434151,577663479953,377064191118,601636243820,912709151873,652383741589,189499857056,954940332823,608813628836,465478248845,91350423246,927644044090,915933580301,651962676839,965733014479,586391866735,440350556669,223750795059,771691302545,274877719412,900538191085,797918714434,31824741950,467360259596,352434773895,791175997919,986433886141,532219480409,273820356140,593118068894,604985550475,416421952233,499835439931,179766999337,733657063043,667268200417,403018798237,302754958272,230110354459,10579676409,134616142579,808774162707,228825745402,57864890272,401233287095,407241910865,443268558498,975429840163,494716203769,238267003314,161470671644,925437211487,866599988853,402837384242,861510405464,295267237675,825368327162,535685974986,960719260932,633010928209,209518611505,939665237608,430709556586,472478890293,567074511430,497928810288,938604520916,982806510856,258391551373,854163411004,907888436727,440774137868,440151215562,347043924006,790430573215,676659416810,421249308190,126631977392,780449440562,868243412029,883393825002,319361202181,823074923588,787072886064,798800498882,174433546096,731961657864,973933435554,63199529969,775898311484,454959166260,427532402216,293858862622,177531808655,452629338588,525571904461,459526789759,76138476168,985521374327,807781281697,942763712230,325436830577,62294880978,386270021964,856358619392,477783482381,367374990719,513077166623,406324349507,973258505027,664805688374,834476565949,416875997400,751843482098,730940596673,156019509166,800983024570,699452396949,584800802947,894152484087,407336756104,35432981448,709188497143,515358447829,825305081775,168975018650,651449787044,81080323239,48694378202,683900210683,189231625137,898265788519,442568997298,522948390360,488402885366,296418112300,921280185996,529424885572,977859829404,740869115809,217143075966,739566551771,145188063659,80052630102,325411472080,4977182479,9708172444,396420372279,770499637396,40859503092,562517082149,74002302533,9577182837,50538561889,149603428276,651290556322,722131584038,956062866614,302439324394,340065285513,717891828545,582742887647,344734531940,756052501316,911300306189,94724372321,694008664785,126103602954,79797356444,579604101684,867644295162,662234011544,657457941565,31311083541,46105220373,781799521360,467531785578,508754526484,657641265154,714893942750,310351069499,957964091172,861280395248,787415655198,580672464749,910625680069];
      var pseudorandomNumber = pseudorandomNumbers[(this.randomCounter++) % pseudorandomNumbers.length];
      return pseudorandomNumber;
    }
    this.getRandomElement = function(elements) {
      return elements[this.getRandomNumber() % elements.length];
    }
    // biases elements earlier in the array.
    this.getRandomBiasedElement = function(elements) {
      var randomNumber = this.getRandomNumber() / 1000000000000;
      var target;
      // Pick the first 10%.
      if (randomNumber < 0.3)
        target = this.getRandomNumber() % Math.ceil(elements.length / 10)
      // Pick the next 10%.
      else if (randomNumber < 0.6)
        target = this.getRandomNumber() % (Math.ceil(elements.length / 10) + Math.ceil(elements.length / 10))
      // Pick the next 20%.
      else if (randomNumber < 0.8)
        target = this.getRandomNumber() % (Math.ceil(elements.length / 5) + Math.ceil(elements.length / 10) * 2)
      // Pick the next 10%.
      else if (randomNumber < 0.9)
        target = this.getRandomNumber() % (Math.ceil(elements.length / 10) + Math.ceil(elements.length / 10) * 4)
      // Pick the remainder
      else
        target = this.getRandomNumber() % (Math.ceil(elements.length / 2) + Math.ceil(elements.length / 10) * 5);
      return elements[target % elements.length];
    }
    
    function handleizeWithSpaces(handle) {
      if (handle != null)
        return spotDOM.removeDiacritics(handle.toLowerCase()).replace(/\s+/g, ' ').replace(/(and|et|&)/gi, 'and').replace(/[^a-z0-9_ ]+/g, ' ').replace(/(^\s+|\s+$)/g, "").replace(/e?s?\b/g, "");
      return null;
    };
    // This function returns auto-suggestions based on key terms in your product data, as determined
    // by the search algorithm. This may be moved to the back-end at some point, but is actually quite
    // impressive in its own right, when fed good data.
    this.suggestSearches = function(search, results, highImpactFields, options) {
      if (!options)
        options = {};
      this.randomCounter = 0;
      var normalizedSearch = handleizeWithSpaces(results?.spellCheck?.corrected ?? search);
      var possibleStructures = options.possibleStructures || possibleSentenceStructures[spotDOM.locale()];
      var isEmpty = results.products && results.products.length == 0;
      if (!possibleStructures || normalizedSearch.length <= 2 || isEmpty)
        return [];
      var constructedPhrases = [];
      // Determine a series of important words form search results, and a pre-specified list of fields.
      var importantWords = results.products.flatMap(function(p) { return p; }).flatMap(function(p) { return highImpactFields.flatMap(function(highImpactField, inverse_importance) { 
        var value = spotDOM.RetrieveFieldObject(highImpactField.field, p);
        if (Array.isArray(value))
          value = spotDOM.spotAPI.uniq(value);
        if (value == null)
          return [];
        if (!Array.isArray(value))
          value = [value];
        value = value.flatMap(function(v) { return highImpactField.transform ? highImpactField.transform(v) : v; });
        var importance = (highImpactFields.length - inverse_importance);
        return value.filter(function(v) { return v != null; }).map(function(v) { return { field: highImpactField, importance: importance*importance, term: v, normalizedTerm: handleizeWithSpaces(v) }; });
      }) }).toSorted(function(a,b) { return a.importance - b.importance }).filter(function(v) { return v.normalizedTerm != "" });
      var importantHash = Object.fromEntries(importantWords.map(function(v) { return [v.normalizedTerm, v]; }));
      importantWords.filter(function(v) { return importantHash[v.normalizedTerm] != v; }).forEach(function(v) {
        importantHash[v.normalizedTerm].importance += v.importance;
      });
      importantWords = importantWords.filter(function(v) { return importantHash[v.normalizedTerm] == v }).toSorted(function(a,b) { return b.importance - a.importance });

      var termSort = function(a,b) {
        if (a.importance == b.importance)
          return a.normalizedTerm.length - b.normalizedTerm.length;
        return b.importance - a.importance;
      };
      
      var searchedImportantWords = importantWords.filter(function(iw) { return iw.normalizedTerm.indexOf(normalizedSearch) != -1 }).toSorted(termSort);
      if (searchedImportantWords.filter(function(si) { return si.field.exact; }).length > 0)
        return searchedImportantWords.filter(function(si) { return si.field.exact; }).map(function(si) { return si.term; });

      searchedImportantWords = searchedImportantWords[0];
      if (!searchedImportantWords || searchedImportantWords.field.type == "compound") {
        var normalizedWords = normalizedSearch.split(/\s+/);
        var potentialWords = normalizedWords.flatMap(function(nw) {
          var exactMatch = importantWords.filter(function(v) { return v.normalizedTerm == nw && v.field.type != "compound"; }).toSorted(termSort)[0];
          if (exactMatch)
            return exactMatch;
          exactMatch = importantWords.filter(function(v) { return v.normalizedTerm.indexOf(nw) == 0  && v.field.type != "compound"; }).toSorted(termSort)[0];
          if (exactMatch)
            return exactMatch;
          exactMatch = importantWords.filter(function(v) { return v.normalizedTerm == nw && v.field.type == "compound"; }).toSorted(termSort)[0];
          if (exactMatch)
            return exactMatch;
          exactMatch = importantWords.filter(function(v) { return v.normalizedTerm.indexOf(nw) == 0  && v.field.type == "compound" }).toSorted(termSort)[0];
          if (exactMatch)
            return exactMatch;
          return null;
        }).filter(function(si) { return si });
        if (potentialWords.length == normalizedWords.length)
          searchedImportantWords = potentialWords;
        else
          searchedImportantWords = [searchedImportantWords];
      } else {
        searchedImportantWords = [searchedImportantWords];
      }
      // remove redundant words
      searchedImportantWords = searchedImportantWords.filter(function(si, idx) {
        return si && (idx == 0 || searchedImportantWords[idx - 1].normalizedTerm.indexOf(si.normalizedTerm.split(/\s+/)[0]) == -1)
      });
      constructedPhrases = spotDOM.spotAPI.uniq(importantWords.filter(function(w) { return w.normalizedTerm.indexOf(normalizedSearch) == 0 && !w.field.no_suggest; }).map(function(w) { return w.term; }).concat(searchedImportantWords.map(function(w) { return w.term; }).join(" ")));
      constructedPhrases = constructedPhrases.filter(function(cp1, idx1) {
        var normalized = handleizeWithSpaces(cp1);
        return constructedPhrases.filter(function(cp2, idx2) { return idx1 != idx2 && handleizeWithSpaces(cp2).indexOf(normalized) == 0 && cp1.split(/\s+/).length == cp2.split(/\s+/).length; }).length == 0;
      });
      // Remove any duplicate phrases.
      if (searchedImportantWords.length > 0) {
        var nonSearchedImporantWords = importantWords.filter(function(w) { return searchedImportantWords.indexOf(w) == -1 && !w.field.no_suggest; });
        // Once we have which terms were important that we seearched, used `possibleStructures` and build a framework around 
        var possiblePossibleStructures = possibleStructures.filter(function(possibleStructure) {
          var remainingTerms = possibleStructure;
          var matchedAllTerms = true;
          searchedImportantWords.forEach(function(si) {
            var relevantIndex = remainingTerms.indexOf(si.field.type);
            if (relevantIndex != -1) {
              remainingTerms = remainingTerms.filter(function(v, idx) { return idx != relevantIndex; });
            } else {
              matchedAllTerms = false; 
            }
            
          });
          return matchedAllTerms && possibleStructure.length > searchedImportantWords.length;
        });
        while (constructedPhrases.length < 10 && possiblePossibleStructures.length > 0) {
          var remainingWords = searchedImportantWords.map(function(v) { return v; });
          var possibleStructure = this.getRandomElement(possiblePossibleStructures);
          var constructedPhrase = [];
          var alreadyUsedFields = remainingWords.map(function(f) { return f.field; });
          possibleStructure.forEach(function(type, i) {
            var validWord = remainingWords.filter(function(v) { return v.field.type == type; })[0];
            if (validWord) {
              constructedPhrase.push(validWord.term);
              remainingWords = remainingWords.filter(function(v) { return v != validWord; });
            } else {
              var potentials = nonSearchedImporantWords.filter(function(v) { 
                return v.field.type == type && 
                alreadyUsedFields.indexOf(v.field) == -1 &&
                constructedPhrase.indexOf(v.term) == -1 &&
                searchedImportantWords.filter(function(si) { return si.term.indexOf(v.term) != -1 }).length == 0 &&
                constructedPhrase.filter(function(cp) {
                  var lastTerm = v.normalizedTerm.split(/\s+/)[0];
                  var lastTermCP = spotDOM.normalizeSearch(cp).split(/\s+/).reverse()[0];
                  return lastTerm.indexOf(lastTermCP) != -1 || lastTermCP.indexOf(lastTerm) != -1;
                }).length == 0;
              });
              if (potentials.length > 0) {
                var potential = this.getRandomBiasedElement(potentials);
                alreadyUsedFields.push(potential.field);
                constructedPhrase.push(potential.term);
              } else {
                constructedPhrase.push(null);
                possiblePossibleStructures = possiblePossibleStructures.filter(function(ps) { return ps != possibleStructure; });
              }
            }
          });
          if (constructedPhrase.filter(function(v) { return v != null; }).length == constructedPhrase.length)
            constructedPhrases.push(constructedPhrase.join(" "));
        }
        var scores = {};
        constructedPhrases.forEach(function(phrase) {
          var total = 0;
          if (searchedImportantWords.length > 0 && handleizeWithSpaces(phrase).indexOf(searchedImportantWords[0].normalizedTerm) == 0)
            total += 1000;
          if (searchedImportantWords.length > 0 && handleizeWithSpaces(phrase) == normalizedSearch)
            total += 10000;
          total -= phrase.length;
          scores[phrase] = total;
        });
        // move any phrase that has the search in it to the top
        constructedPhrases = spotDOM.spotAPI.uniq(constructedPhrases).toSorted(function(a,b) { return scores[b] - scores[a]; }).slice(0, 5).map(function(v) { return v.toLowerCase(); }).sort(function(a,b) { return a.length - b.length; });
      }
      return constructedPhrases;
    };

    window.addEventListener("resize", options['resize'] || function() {
      searchBar.updateMatchElement(searchBar.inputElement, searchBar.dropdownContainer);
    });
    if (options.init)
      options.init.call(this);
  };
  /** %-{SearchBar} **/
  /**

  ## Variables

  ### paginationType

  The type of pagination to use. Values are as follows:

  * `null`: Uses no pagination out of the box.
  * `"load"`: Adds a button into paginationConatiners (or the productContainer, if no pagination containers are specified), that loads more products on click.
  * `"infinite"`: Upon reaching the bottom of the page, more products are loaded.
  * `"paged"`: Fills the paginationContainers with a set of page numbers, and a next and prev button.

  Should generally not be called directly, but should be initialized as part of a call to `SpotDefault.init`, or, if calling individual methods directly, `setupPagination`.

  **/
  spotDOM.scalarField(this, 'paginationType');
  /**

  ### paginationOffset

  The intial page that a user had loaded.

  Used mainly in situation where someone is directly linked to a page, or if they hit the back button.
  In certain configurations, this will cause this to be set; it basically means "we're displaying the resultset starting at this page".
  
  Generally coupled with a "Load Previous" button in "infinite" scroll mode.
  **/
  spotDOM.scalarField(this, 'paginationOffset');
  /**

  ### viewedPage

  The currently viewed page.
  
  Keeps track of where the user is on the page, and what bit of the page they're viewing to accurately track back clicks, and the like.
  
  **/
  spotDOM.scalarField(this, 'viewedPage');
  /**

  ## Functions

  ### getMatchingSearchRule(locale, list, search)

  Gets the redirect rule that matches this particular search query, given a list.

  **/
  this.getMatchingSearchRule = function(locale, list, search) {
    var normalizedSearch = this.spotDOM.normalizeSearch(search);
    var candidate = list.filter(function(e) {
      if (e.locale && locale && e.locale != locale)
        return false;
      return spotDefault.spotDOM.removeDiacritics(e.term.toLowerCase()).split(/\s*,\s*/).filter(function(term) {
        var i = normalizedSearch.indexOf(term);
        return (
            i != -1 && (
            e.type == "substring" ||
            e.type == "exact" && (i == 0 && normalizedSearch.length == term.length) ||
            e.type == "word" && (i + e.term.length == normalizedSearch.length || /\W/.test(normalizedSearch.charAt(i+e.term.length)))
          ));
      }).length > 0;
    })[0];
    if (candidate)
      return candidate;
    return null;
  };
  /**

  ### getSearchPageRedirect(locale, search)

  Gets the URL that should be redirected to, given a particular search.  Will check to see if there's any terms in the redirect hierarchy that should redirect somewhere other than the search page. Will remove the relevant terms from the query,
  and silently pass through the filter to the collections page as the `context` url parameter that will either apply a facet, or perform a free-form search on the collection.

  **/
  this.getSearchPageRedirect = function(locale, search) {
    var candidate = this.getMatchingSearchRule(locale, this.spotDOM.redirects(), search);
    if (candidate) {
      if (candidate.url.indexOf("//") != -1)
        return candidate.url;
      // Determine which part of the candidate existed in the search query, and redirect people.
      candidate.term.split(/\s*,\s*/).forEach(function(term) {
        var index = spotDefault.spotDOM.removeDiacritics(search.toLowerCase()).indexOf(spotDefault.spotDOM.removeDiacritics(term.toLowerCase()));
        if (index != -1)
          search = (search.substr(0, index).replace(/\S+$/, "") + search.substr(index + term.length).replace(/^\S+/, "")).replace(/\s\s/, " ").replace(/(^\s*|\s*$)/, "");
      });
      if (!search)
        return candidate.url;
      return candidate.url + (candidate.url.indexOf("?") == -1 ? "?" : "&") + "context=" + encodeURIComponent(search);
    }
    return null;
  }
  this.getSearchPageURL = function(search) {
    return this.getSearchPageRedirect(this.spotDOM.locale(), search) || ((window.Shopify ? window.Shopify.routes.root : '/') + "search?q=" + encodeURIComponent(search));
  };
  /**

  ### redirectToSearchPage(search)

  A function that redirects you to wherever `getSearchPageRedirect` tells you to redirect, given the specified search, in the current locale.

  **/
  this.redirectToSearchPage = function(search) {
    spotDOM.location.href = this.getSearchPageURL(search);
  }
  /**

  ### redirectToCollectionPage(url, search)

  **/
  this.redirectToCollectionPage = function(url, search) {
    spotDOM.location.href = url;
  };
  /**

  ### canUseInitialProducts

  Checks to see whether we can use the initial set of products for this particular collection, given the URL.

  **/
  this.canUseInitialProducts = function() {
    return !(spotDOM.sort() || spotDOM.collection() != spotDOM.getCollectionPage() || spotDOM.isSearchPage() || spotDOM.search() || spotDOM.spotAPI.grep(spotDOM.spotAPI.flatMap(spotDOM.facets(), function(f) { return f.facetValues(); }), function(fv) { return fv.enabled(); }).length > 0);
  };
  /**

  ### needsInitialQuery

  Checks to see whether we're searching anything, have any sort of facets require getting any facets, or do anything
  that needs to be done before showing products to the user. If false, then Spot does not need to be queried to correctly
  render the list of products for this particular collection.

  %{needsInitialQuery}


  **/
  this.needsInitialQuery = function() {
    /** %+{needsInitialQuery} **/
    return this.canUseInitialProducts() || (spotDOM.facets().length > 0 && spotDOM.optionBehavior() != "none");
    /** %-{needsInitialQuery} **/
  };  
  /**

  ##### addedProductToCart

  Calls the appropriate personzalition, event, and checkout recommendaiton methods.

  **/
  spotDOM.listener(this, 'addedProductToCart', function(product, variant, quantity) {
    if (spotDOM.personalization())
      spotDOM.personalization().addedProductToCart(product, variant, quantity);
    spotEvent.addedProductToCart(product ? product.id : (variant ? variant.product_id : null), variant ? variant.id : null, quantity);
    if (spotDOM.checkoutRecommendation()) {
      spotDOM.getExtraInfo((spotDOM.cart() ? spotDOM.cart().items.map(function(i) { return i.product_id }) : []).concat(product.id)).done(function(products) {
        spotDOM.checkoutRecommendation().getQuery({ products: products }).productMerge("none").e().done(function(products) {
          spotDOM.spotAPI.ajax("https://" + window.location.hostname +  "/cart/update.js", { type: "POST", contentType: "json", data: { attributes: {
            "_recommendationId": spotDOM.checkoutRecommendation().id,
            "_recommendation": JSON.stringify({ 
              id: spotDOM.checkoutRecommendation().id,
              name: spotDOM.checkoutRecommendation().getName(),
              products: products.map(function(p) { return { 
                id: p.id,
                title: spotDOM.getProductTitle(p),
                image: spotDOM.getProductImage(p),
                variants: (spotDOM.getRelevantVariants(p) || p.variants).map(function(v) { return { id: v.id } })
              } }) 
            })
          } } });
        });
      });
    }
  });
  /**

  ### ready(done)

  Fires a function when DOMContentLoaded fires, or has already been fired.

  **/
  this.ready = window.SpotDefault.ready;
  this.isReady = window.SpotDefault.isReady;
  // Loaded is specifically, after the first query has returned.
  spotDOM.scalarField(this, 'loaded', false);
  // Products rendered is specifically after the first set of products have been rendered.
  spotDOM.scalarField(this, 'productsRendered', false);
  /**

  ### resolveQuerySelector(querySelector, done, timeout, interval)

  Calls the specified function when the element specified is loaded onto the page.

  This method is fairly important. By default, all `resolveQuerySelector` calls
  will be called on `DOMContentLoaded`. However, you can force a check by dispatching
  `document.dispatchEvent("SpotRender")`, or by using `spotDefault.checkRenders()`. 

  Standard practice with a default Spot installation is to dispatch the event immediately
  after the container for your products loads, so that products can be loaded onto the page
  as quickly as possible.
  **/
  this.checkRenders = function() { document.dispatchEvent(new CustomEvent('SpotRender')); };
  this.resolveQuerySelector = function(querySelector, done, timeout, interval) {
    if (typeof(querySelector) == 'string') {
      var result = document.querySelector(querySelector);
      if (result)
        done(result, false);
      else if (!this.isReady()) {
        var rendered = false;
        document.addEventListener('SpotRender', function(e) {
          if (!rendered) {
            result = document.querySelector(querySelector);
            if (result) {
              done(result, true);
              rendered = true;
            }
          }
        });
        this.ready(function() {
          if (!timeout) {
            if (!rendered) {
              done(document.querySelector(querySelector), true);
              rendered = true;
            }
          } else {
            if (!rendered) {
              var element = document.querySelector(querySelector);
              if (!element) {
                var recurring;
                var iterations = 0;
                var iterationTarget = Math.ceil(timeout / interval);
                recurring = setInterval(function() {
                  var element = document.querySelector(querySelector);
                  if (element || iterations++ > iterationTarget) {
                    clearInterval(recurring);
                    done(element, true);
                    rendered = true;
                  }
                }, interval || 100);
              } else {
                done(element, true);
                rendered = true;
              }
            }
          }
        });
      }
    } else if (querySelector && querySelector.done)
      querySelector.done(function(e) { done(e, true); })
    else
      done(querySelector, false);
    return this;
  };
  this.resolveCondition = function(condition, done, timeout) {
    var result = condition();
    if (result)
      done(result);
    else {
      if (!this.isReady()) {
        var rendered = false;
        document.addEventListener('SpotRender', (e) => {
          if (!rendered) {
            result = condition();
            if (result) {
              done(result, true);
              rendered = true;
            }
          }
        });
        this.ready(function() {
          if (!rendered)
            done(condition());
        });
      } else 
        done(false);
    }
    return this;
  }
  /**

  ### createElement(html)

  A helper simple function that takes in a string, and returns an HTML element.

  **/
  this.createElement = function(text) {
    return document.createRange().createContextualFragment(text).children[0];
  };
  /**

  ### popularElement(stuff)

  A helper simple function that takes in a string, array, or node, and sticks it in a node.

  **/
  this.populateElement = function(element, stuff, options) {
    if (!options)
      options = {};
    this.resolveQuerySelector(element, function(e) { 
      if (Array.isArray(stuff)) {
        stuff.forEach(function(s) { spotDefault.populateElement(element, s, options); options.append = true; });
      } else if (typeof(stuff) == 'string') {
        spotDefault.populateElement(element, spotDefault.createElement(stuff), options);
      } else {
        if (!options.append)
          element.innerHTML = '';
        element.appendChild(stuff);
      }
    });
  };
  /**

  ### toggleElement(element, show)

  A helper simple function that takes in a dom element, and either hides or shows it.

  **/
  this.toggleElement = function(element, show) {
    if (element) {
      if (show) {
        var currentDisplay = element.ownerDocument && element.ownerDocument.defaultView.getComputedStyle(element)['display'];
        if (currentDisplay == "none" || element.style.display == "none") {
          if (!element.dataset.initial_display && element.style.display == "none") {
            element.style.display = null;
            currentDisplay = element.ownerDocument && element.ownerDocument.defaultView.getComputedStyle(element)['display'];
            if (!currentDisplay || currentDisplay == "none") {
              element.dataset.initial_display = "block";
              element.style.display = "block";
            } else {
              element.dataset.initial_display = currentDisplay;
            }
          } else {
            element.style.display = element.dataset.initial_display || 'block';
          }
        }
      } else {
        var currentDisplay = element.ownerDocument && element.ownerDocument.defaultView.getComputedStyle(element)['display'];
        if (currentDisplay != "none") {
          element.dataset.initial_display = element.dataset.initial_display || currentDisplay || 'block';
          element.style.display = 'none';
        }
      }
      return element;
    }
  };
  this.toggleElementQuick = function(element, show) {
    if (element)
      element.style.display = show ? 'block' : 'none';
  };

  /**

  ### showElement(element)

  A helper simple function that takes in a dom element, and shows it.

  **/
  this.showElement = function(element) { return this.toggleElement(element, true); };
  this.showElementQuick = function(element) { return this.toggleElementQuick(element, true); };
  /**

  ### hideElement(element)

  A helper simple function that takes in a dom element, and hides it.

  **/
  this.hideElement = function(element) { return this.toggleElement(element, false); };
  this.hideElementQuick = function(element) { return this.toggleElementQuick(element, false); };
  /**

  ### setupQueryOnChange

  Sets up listeners equivalent to the following, so that new queries are issued when significant attributes change. Normally called by `SpotDefault.init`.

  %{setupQueryOnChange}

  This is useful in most setups, though may be omitted if you want to have an 'apply' button that triggers queries. Can be used by simply typing:

      window.spotDefault.setupQueryOnChange();

  **/
  this.setupQueryOnChange = function() {
    /** %+{setupQueryOnChange} **/
    this.spotDOM.sort(function() {
      this.page(1);
    }).collection(function() {
      this.page(1);
    }).search(function() {
      this.page(1);
    }).split(function() {
      this.page(1);
    }).merge(function() {
      this.page(1);
    }).paginate(function() {
      this.page(1);
    }).currency(function() {
      this.page(1);
    }).locale(function() {
      this.page(1);
    }).addFacet(function(facet) {
      facet.boundaryValues(function() {
        spotDOM.page(1);
      });
      facet.addFacetValue(function(facetValue) {
        spotDOM.queryGuard(function() {
          facetValue.enabled(function() {
            spotDOM.page(1);
          });
        });
      }, true);
    }, true).page(function() {
      this.query();
    }).addCondition(function() {
      this.query();
    }).removeCondition(function() {
      this.query();
    });
    this.relatedProduct(function() {
      spotDOM.query();
    });
    /** %-{setupQueryOnChange} **/
  };
  /**

  ### renderProductElement(product, offset)

  A helper function that uses the product product renderer to render a product's tile, and attaches all appropraite listeners, and sets all cache variables.

  * `product` is the product to render
  * `offset` is the offset from 0 of this product in the resultset.

  **/
  this.renderProductElement = function(product, offset) {
    var child = spotDefault.productRenderer(product, offset);
    if (typeof(child) == 'string')
      child = spotDefault.createElement(child);
    product.position = offset;
    product.element = child;
    child.addEventListener('click', function() {
      var relevantVariant = spotDOM.getRelevantVariant(product);
      if (product.id && spotEvent)
        spotEvent.clickedProduct(product.id, relevantVariant ? relevantVariant.id : null, offset);
    });
    return product.element;
  };
  /**

  ### renderProductsFragment(products, offset)

  A helper function that renders a list of products, and their corresponding banner tiles as a fragment.

  * `products` is an array of products.
  * `offset` is the offset from 0 of this set of product in the resultset.

  **/
  this.renderProductsFragment = function(products, offset) {
    var productFragment = document.createDocumentFragment();
    for (var i = 0; i < products.length; ++i) {
      if (products[i].split_id && spotDefault.displayedProducts[products[i].split_id])
        continue;
      spotDefault.displayedProducts[products[i].split_id] = products[i];
      spotDOM.getBanners(offset + i, products[i]).forEach(function(banner) {
        productFragment.appendChild(spotDefault.bannerRenderer(banner));
      });
      productFragment.appendChild(products[i].element || spotDefault.renderProductElement(products[i], offset + i));
    }
    return productFragment;
  };
  /**

  ### setupProductRender(productContainer, productRenderer, shouldImmediatelyRenderProducts)

  A helper function that renders products as specified by the productRenderer. Normally called by `SpotDefault.init`.

  * `productContainer` is a querySelector for a div that will be cleared, and contain the received products.
  * `productRenderer` is a function that takes in a product, and returns an element that
  * `shouldImmediatelyRenderProducts` determines whether or not we should render the products immediately onto the page, or wait one query. This allows for grabbing of facets, without affecting the actual loaded products.

  **/
  
  this.setupProductRender = function(productContainer, productRenderer, shouldImmediatelyRenderProducts) {
    if (shouldImmediatelyRenderProducts === undefined)
      shouldImmediatelyRenderProducts = false;
    var renderer = productRenderer || this.defaultProductRenderer;
    if (typeof(renderer) == 'string') {
      var rendererString = renderer;
      renderer = function(product) {
        product.url = spotDOM.getProductURL(product);
        product.image = spotDOM.getProductImage(product, null, spotDefault.preferredImages);
        product.title = spotDOM.getProductTitle(product);
        product.price = product.price / 100.0;
        return spotDefault.createElement(spotDefault.template(rendererString, { product: product }));
      };
    }
    this.productRenderer = renderer;
    // Always render products, regardless of where we are on the page.
    spotDefault.spotDOM.products(function(products) {
      for (var i = 0; i < (products || []).length; ++i)
        spotDefault.renderProductElement(products[i], i + (spotDOM.page() - 1)*spotDOM.paginate());
    }, true);
    document.addEventListener('SpotRefresh', function(e) {
      // If we ever need to refresh tiles, go thorugh, call the product renderer again, and replace the element.
      for (var i = 0; i < e.detail.products.length; ++i) {
        var child = spotDefault.productRenderer(e.detail.products[i], e.detail.products[i].position);
        if (e.detail.products[i].element)
          e.detail.products[i].replaceWith(child);
        e.detail.products[i].element = child;
        child.addEventListener('click', (function(i) { return function() {
          var relevantVariant = spotDOM.getRelevantVariant(e.detail.products[i]);
          if (e.detail.products[i].id && spotEvent)
            spotEvent.clickedProduct(e.detail.products[i].id, relevantVariant ? relevantVariant.id : null, i + spotDOM.paginate()*(spotDOM.page()-1));
        } })(e.detail.products[i].position));
      }
    });
    spotDefault.resolveQuerySelector(productContainer, function(productContainer, delayed) {
      spotDefault.productContainer = productContainer;
      spotDefault.spotDOM.beginQuery(function() { productContainer.classList.add(spotDefault.options.loadingClass || "loading"); }).endQuery(function() { productContainer.classList.remove(spotDefault.options.loadingClass || "loading"); });
      spotDefault.spotDOM.products(function(products) {
        productContainer.classList.remove(spotDefault.options.loadingClass || "loading");
        if (products && shouldImmediatelyRenderProducts) {
          if (!spotDefault.displayedProducts)
            spotDefault.displayedProducts = {};
          if (spotDefault.paginationType() == "paged" || spotDefault.spotDOM.page() == 1) {
            // To guard against showing duplicate products if a collection updates mid-browse.
            spotDefault.displayedProducts = {};
            productContainer.innerHTML = '';
          }
          productContainer.appendChild(spotDefault.renderProductsFragment(products, (spotDOM.page() - 1)*spotDOM.paginate()));
          if (!spotDefault.productsRendered())
            spotDefault.productsRendered(true);
        }
      }, delayed || shouldImmediatelyRenderProducts);
    });
    this.spotDOM.query(function() {
      shouldImmediatelyRenderProducts = true;
    });
    return this;
  };
  /**

  ### setupFacetGroups(facets, facetGroups)

  A helper function that sets up facets in a default manner. Normally called by `SpotDefault.init`.

  **/
  this.setupFacetGroups = function(facets, facetGroups) {
     this.facets = Object.fromEntries(Object.keys(facets).map(function(fid) { return [fid, new spotDOM.Facet(facets[fid])] }));
     this.facetGroups = Object.fromEntries(Object.keys(facetGroups).filter(function(fgid) { return facetGroups[fgid]; }).map(function(fgid) {
      if (Array.isArray(facetGroups[fgid]))
        return [fgid, facetGroups[fgid].map(function(fid) { return spotDefault.facets[fid]; }) ]
      return [fgid, facetGroups[fgid].facets ? facetGroups[fgid].facets.map(function(fid) { return spotDefault.facets[fid]; }) : []];
    }));
  };
  this.getFacetGroup = function(fgid) { return fgid && this.facetGroups ? this.facetGroups[fgid] : (fgid == 0 ? [] : null); }
  /**

  ### setupRecommendations(recommendations, boostRules, sortOrders, context)

  A helper function that sets up recommendations in a default manner with the specified context. Normally called by `SpotDefault.init`.
  Currently, context is only used to fill `relatedProduct`.

  **/


  // If present, will affect the ruleset by skewing it towards a recommendation
  // takes the form of { recommendation: recommendation, product: product }
  spotDOM.scalarField(this, 'relatedProduct');
  
  this.recommendations = {};
  this.setupRecommendations = function(recommendations, boostRules, checkout) {
    this.recommendations = Object.fromEntries(Object.keys(recommendations).map(function(rid) {
      var recommendation = Object.assign({ id: rid }, recommendations[rid]);
      return [rid, new spotDOM.Recommendation(recommendation, recommendation.boost_rule_id ? new spotDOM.BoostRule(boostRules[recommendation.boost_rule_id]) : null, spotDOM.getSortOrder(recommendation.sort_order_id))];
    }));
    if (checkout && checkout.recommendation_id && this.recommendations[checkout.recommendation_id])
      spotDOM.checkoutRecommendation(this.recommendations[checkout.recommendation_id]);
    spotDefault.context(function(context) {
      if (context && context.params.recommendation_id && context.params.product_id) {
        var recommendation = spotDefault.getRecommendation(parseInt(context.params.recommendation_id));
        if (recommendation) {
          spotDOM.getExtraInfo([context.params.product_id]).done(function(products) {
            if (products.length > 0)
              spotDefault.relatedProduct({ recommendation: recommendation, product: products[0] });
          });
        }
      }
    }, true);
    spotDOM.addQueryTransform([function(q) {
      if (spotDefault.relatedProduct()) {
        var rows = q.paginate();
        return q.merge(spotDefault.relatedProduct().recommendation.getQuery({ product: spotDefault.relatedProduct().product })).paginate(rows);
      }
      return q;
    }]);
    return this;
  };
  /**

  ### setupPersonalizations(personalization, boostRules)

  A helper function that sets up personalization in a default manner with the specified context. Normally called by `SpotDefault.init`.

  **/
  this.setupPersonalizations = function(personalizations, boostRules) {
    this.personalizations = Object.fromEntries(personalizations.map(function(personalization) { return [personalization.id, new spotDOM.Personalization(personalization, new spotDOM.BoostRule(boostRules[personalization.boost_rule_id]))] }));
    spotDOM.query(function(products, count, options) {
      if (spotDOM.personalization()) {
        if (spotDOM.search()) {
          spotDOM.personalization().viewSearch(products);
        } else if (spotDOM.collection()) {
          spotDOM.personalization().viewCollection(products);
        }
      }
    });
    spotDefault.ready(function() {
      if (spotDOM.personalization()) {
        if (spotDefault.context() && spotDefault.context().product && spotDOM.isProductPage()) {
          if (spotDOM.hasVariantID()) {
            var variantId = spotDefault.context().context.variantId || spotDOM.getVariantID();
            spotDOM.personalization().viewVariant(spotDefault.context().product, spotDefault.context().product.variants.filter(function(v) { return v.id == variantId })[0]);
          } else {
            spotDOM.personalization().viewProduct(spotDefault.context().product);
          }
        }
      }
    });
    return this;
  };
  /**

  ### setupBoosts(boostRules, searchBoosts)

  A helper function that sets up boosts in a default manner with the specified context. Normally called by `SpotDefault.init`.

  **/
  this.boostRules = {};
  this.setupBoosts = function(boostRules, searchBoosts) {
    var boostQuery = spotDOM.spotAPI.se();
    this.boostRules = Object.fromEntries(Object.keys(boostRules).map(function(id) { return [id, new spotDOM.BoostRule(boostRules[id])] }));
    if (searchBoosts)
      spotDOM.addSearchBoosts(searchBoosts);
    return this;
  };
  /**

  ### setupBadges(badges, badgeGroups)

  A helper function that sets up badges in a default manner with the specified context. Normally called by `SpotDefault.init`.

  **/
  this.setupBadgeGroups = function(badges, badgeGroups, swatches) {
     this.badges = Object.fromEntries(Object.keys(badges || {}).map(function(bid) { return [bid, new spotDOM.Badge(badges[bid], badges[bid].swatch_id ? swatches[badges[bid].swatch_id] : null)] }));
     this.badgeGroups = Object.fromEntries(Object.keys(badgeGroups || {}).map(function(bgid) {
      if (Array.isArray(badgeGroups[bgid]))
        return [bgid, { display: "first", badges: badgeGroups[bgid].map(function(bid) { return spotDefault.badges[bid]; }) }]
      return [bgid, { display: (badgeGroups[bgid] && badgeGroups[bgid].display) || "first", badges: ((badgeGroups[bgid] && badgeGroups[bgid].badge_ids) || []).map(function(bid) { return spotDefault.badges[bid]; }) }]
    }));
  };
  this.getBadgeGroup = function(bgid) { return bgid ? this.badgeGroups[bgid] : (bgid == 0 ? { display: "first", badges: [] } : null); }
  this.getBadges = function(product) {
    var badges = spotDOM.getBadges(product);
    if (this.badgeGroup) {
      if (this.badgeGroup && this.badgeGroup.display == "first")
        badges = badges.length > 0 ? [badges[0]] : [];
    }
    return badges;
  };

  this.setupBanners = function(banners, swatches) {
    this.banners = Object.fromEntries(banners.map(function(banner) { return [banner.id, new spotDOM.Banner(banner, swatches)] }));
  }

  /**

  ### setupSortOrderDropdown(sortDropdown, preserveExisting)

  A helper function that sets up sort orders in the specified `<select>` element. Normally called by `setupSortOrders`.

  **/

  this.setupSortOrderDropdown = function(sortDropdown, preserveExisting) {
    this.resolveQuerySelector(sortDropdown, function(sortDropdown) {
      if (!sortDropdown)
        return;
      if (!preserveExisting)
        sortDropdown.innerHTML = '';
      spotDOM.addSortOrder(function(sortOrder) {
        if (sortOrder.isVisible()) {
          sortOrder.element = sortDropdown.querySelector('option[value="' + sortOrder.getUniqueId() + '"]');
          if (sortOrder && !sortOrder.element) {
            sortOrder.element = spotDefault.createElement("<option value='" + sortOrder.getUniqueId() + "'>" + sortOrder.getName() + "</option>");
            sortDropdown.appendChild(sortOrder.element);
          }
        }
      }, true).removeSortOrder(function(sortOrder) {
        if (sortOrder.element)
          sortOrder.element.remove();
      });
      spotDOM.sort(function(sortOrder) {
        sortDropdown.querySelectorAll('option').forEach(function(e) { e.removeAttribute('selected'); });
        if (sortOrder && sortOrder.element)
          sortOrder.element.selected = true;
      }, spotDOM.sort() != null);
      sortDropdown.addEventListener("change", function(e) {
        spotDOM.sort(this.value);
        e.preventDefault();
      });
    });
  };

  /**

  ### setupSortOrders(sortDropdown, sortOrders)

  A helper function that sets up sort orders in a default manner. Normally called by `SpotDefault.init`.

  * `sortDropdown` is the `<select>` DOM element that should house the sort orders.
  * `sortOrders` is a list of sort orders relevant for this particular page.

  **/
  this.setupSortOrders = function(sortOrders) {
    for (var i = 0; i < sortOrders.length; ++i)
      sortOrders[i].id = i;
    var selectedObjectSortOrder = null;
    var defaultObjectSortOrder = null;
    if (sortOrders)
      sortOrders.map(function(e) { return new spotDOM.SortOrder(e); }).forEach(function(order) { spotDOM.addSortOrder(order) });
    else
      spotDOM.initDefaultSortOrders();
  };
  /**

  ### setupPagination(paginationType, paginationContainers, currentCount, options)

  A helper function that sets up pagination in a default manner. Normally called by `SpotDefault.init`.

  * `paginationType` can be either 'infinite', 'paged', 'load', or null.
  * `paginationContainers` is valid for `paged`; will fill the specified containers with pagination elements.
  * `productContainer` is the product container we'll be using; this is relevant for placing "Load Previous" elements, and infinite scrolling.
  * `currentCount` takes in current amount of products on the page; only valid for `load`, or for Shopify Mode. Optional.
  * `options` can contain the following options:
      * Any instantiation options for `ScrollPaginationHelper`.
      * `noBackfillHeight` which will prevent Spot from keeping track of the page height, and backfilling the result container height if it knows what it should be based on previous queries.
      * `noScrollToTop`

  In order to assure that a user always returns to the same height upon product loading, the minHeight of the productContainer should
  be set in CSS to be high enough so that it encompasses the largest resultset. Spot will then pare this down as it has more information.

  **/
  this.setupPagination = function(paginationType, paginationContainers, productContainer, currentCount, options) {
    if (!options)
      options = {};
    this.countListener = null;
    this.productListener = null;
    if (!paginationContainers)
      paginationContainers = [];
    this.scrollPaginationHelper = new spotDefault.ScrollPaginationHelper(options);
    
    this.setBackfill = function(product) {
      var scrollHeight = window.scrollY;
      if (options["loadPrevious"] && (this.paginationType() == "infinite" || this.paginationType() == "load-more"))
        scrollHeight = this.scrollPaginationHelper.getViewedPageOffset(product);
      spotDOM.setStorage("spotBackfillHeight", { page: spotDefault.viewedPage(), url: spotDOM.location.href, height: spotDefault.productContainer.offsetHeight, windowWidth: window.offsetWidth, scrollY: scrollHeight });
    };
    this.getBackfill = function() {
      var heightInformation = spotDOM.getStorage("spotBackfillHeight");
      if (heightInformation && spotDOM.location.href == heightInformation.url && spotDOM.page() == heightInformation.page && heightInformation.windowWidth == window.offsetWidth)
        return heightInformation;
      return null;
    };
    

    this.paginationType(function(paginationType) {
      if (paginationType != 'infinite')
        spotDefault.scrollPaginationHelper.detach();
      if (paginationType == 'paged') {
        var child = spotDefault.createElement("<div></div>");
        spotDefault.countListener = function(count) {
          // If we're using paged pagination, render pagination.
          paginationContainers.forEach(function(paginationContainer) {
            spotDefault.resolveQuerySelector(paginationContainer, function(container) {
              if (container) {
                container.innerHTML = '';
                container.appendChild(spotDefault.pagedPaginationHelper(Object.assign({ count: count }, options)))
              }
            });
          });
          var scrollTop = (typeof(options.topOfPage) == 'function' ? options.topOfPage() : options.topOfPage) || 0;
          if (spotDefault.scrollHadOneLoad && document.documentElement.scrollTop > scrollTop) {
            // For Safari and Firefox, if the product container changes size (say becuase you're at the end of the collection)
            // this scrollTo is ignored. So in those cases, do an instant scroll.
            var behavior = spotDOM.page() * spotDOM.paginate() > count ? "instant" : (options.scrollBehavior || "smooth");
            if (behavior == "smooth" && spotDOM.spotAPI.isSafari()) {
              // Also, Safari will randomly ignore the scroll order even if the height isn't changing.
              setTimeout(function() {
                window.scrollTo({ left: 0, top: scrollTop, behavior: behavior });
              }, 100);
            } else {
              window.scrollTo({ left: 0, top: scrollTop, behavior: behavior });
            }
          }
          spotDefault.scrollHadOneLoad = true;
        };
      } else if (paginationType == 'load') {
        var createButton = function() {
          var button = spotDefault.createElement("<button class='spot-load-more button btn'></button>");
          button.textContent = spotDefault.templateLocale('load-more');
          button.addEventListener("click", function(e) { spotDOM.page(spotDOM.page()+1); e.preventDefault(); });
          return button;
        };
        var appendButton = function() {
          paginationContainers.forEach(function(paginationContainer) {
            spotDefault.resolveQuerySelector(paginationContainer, function(container) {
              container.innerHTML = '';
              container.appendChild(createButton());
            });
          });
        };
        spotDefault.productListener = function(products) {
          document.querySelectorAll('.spot-load-more').forEach(function(e) { e.remove(); });
          if (products && products.length == spotDOM.paginate())
            appendButton();
          if (spotDOM.page() == 1) {
            var scrollTop = (typeof(options.topOfPage) == 'function' ? options.topOfPage() : options.topOfPage) || 0;
            if (spotDefault.scrollHadOneLoad && document.documentElement.scrollTop > scrollTop) {
              window.scrollTo({ left: 0, top: scrollTop, behavior: options.scrollBehavior || "smooth" });
            }
            spotDefault.scrollHadOneLoad = true;
          }
        };
        if (currentCount && currentCount > spotDOM.paginate())
          appendButton();
      }
    });
    this.paginationType(paginationType);
    this.paginationType(function() {
      if (!spotDOM.shopifyMode())
        spotDOM.page(1);
    });
    spotDOM.page(function(page) {
      spotDefault.viewedPage(page);
    }, true);
    spotDefault.viewedPage(function() { spotDefault.updateQueryString(true); });
    spotDOM.count(function(count) {
      if (count != null && spotDefault.countListener)
        spotDefault.countListener(count);
    }, true);
    spotDOM.products(function(products) {
      if (spotDefault.productListener)
        spotDefault.productListener(products);
    }, true);

    spotDOM.shopifyMode(function(shopifyMode) {
      var paginationType = spotDefault.paginationType();
      if (shopifyMode == 'shopify' && (paginationType == 'load' || paginationType == 'infinite'))
        spotDefault.paginationType('paged');
      if (shopifyMode == 'storefront' && (paginationType == 'paged'))
        spotDefault.paginationType('infinite');
    }, true);
    // In the case where we load things up; we're required to progress through the various pages in order to
    // load our position correctly, previously.
    window.addEventListener('pagehide', function(event) {
      // spotDefault.setBackfill();
    });
    // We issue a query in the case where we reload the page, just to make sure everything's correct.
    window.addEventListener('pageshow', function(event) {
      var heightInformation = spotDefault.getBackfill();
      if (event.persisted && window.spotDefault.paginationType() == "paged") { 
          window.spotDOM.query();
      } else if (!event.persisted) {
        if (!window.spotAPI.isSafari()) {
          if (productContainer) {
            if (window.scrollY == 0 && heightInformation) {
              window.spotDefault.resolveQuerySelector(productContainer, function() {
                if (history.scrollRestoration)
                  history.scrollRestoration = "manual";
                window.scrollTo(0, heightInformation.scrollY);
              });
            }
          }
        } else {
          if (productContainer && heightInformation) {
            window.spotDefault.resolveQuerySelector(productContainer, function() {
              // Ugh. Safari. This is a bad hack. Find some way to remove this. Dear god, find some way to remove this.
              // For background; even aftrer a document ready, if you try to window.scrollTo, it immediately scrolls back to the top.
              // No JS is `window.scrollTop`, the browser's just doing it.
              window.scrollTo(0, heightInformation.scrollY);
              /*setTimeout(function() {
                window.scrollTo(0, heightInformation.scrollY);
              }, 500);*/
            });
          }
        }
      }
    });
    if (!spotDOM.shopifyMode() && (paginationType == 'load' || paginationType == 'infinite')) {
      if (options['loadPrevious']) {
        spotDefault.paginationOffset(spotDOM.page());
        if (spotDOM.page() > 1) {
          var button = spotDefault.createElement("<button class='spot-load-previous button btn'></button>");
          button.textContent = spotDefault.templateLocale('load-previous');
          
          spotDefault.productsRendered(function(rendered) {
            if (rendered) {
              spotDefault.resolveQuerySelector(productContainer, function(productContainer) {
                productContainer.insertAdjacentElement("beforeBegin", button);
                button.addEventListener("click", function(e) { 
                  button.disabled = true;
                  button.classList.add(spotDefault.options.loadingClass || "spot-loading");
                  spotDefault.scrollPaginationHelper.loadDownFrom(spotDefault.paginationOffset(), function(products, page) {
                    var originalRect = productContainer.getBoundingClientRect();
                    Array.from(spotDefault.renderProductsFragment(products, (page - 1) * spotDOM.paginate()).children).reverse().forEach((p) => productContainer.insertAdjacentElement("afterBegin", p));
                    var newRect = productContainer.getBoundingClientRect();
                    spotDefault.scrollPaginationHelper.productListener(products);
                    if (spotDefault.paginationOffset() - 1 == page)
                      window.scrollTo({ left: 0, top: (newRect.height - originalRect.height) + window.scrollY, behavior: "instant" });
                  }).done(function() {
                    button.remove();
                    spotDefault.paginationOffset(1);
                  }); 
                  e.preventDefault(); 
                });
              });
            }
          });
        }
        if (paginationType == 'infinite')
            spotDefault.scrollPaginationHelper.attach();
      } else {
        // Fix the instance where when we hit the back button, make sure that we've saved the productContainer height, and
        // back-fill a min-height into the container. This allows for much less jumping, and will also have the browser
        // stay at the same place on the page.
        var currentPage = spotDOM.page();
        if (currentPage > 1) {
          if (productContainer && !options['noBackfillHeight']) {
            spotDefault.resolveQuerySelector(productContainer, function(productContainer) {
              if (productContainer) {
                spotDOM.endQuery(function() {
                  if (currentPage == spotDOM.page())
                    productContainer.style.removeProperty('min-height');
                });
                var heightInformation = spotDefault.getBackfill();
                if (heightInformation && heightInformation.height)
                  productContainer.style.minHeight = heightInformation.height + "px";
              }
            });
          }
        }
        this.scrollPaginationHelper.loadDownFrom(currentPage).done(function() {
          if (paginationType == 'infinite')
            spotDefault.scrollPaginationHelper.attach();
        });
      }
    } else if (paginationType == 'paged') {
      var heightInformation = spotDefault.getBackfill();
      if (heightInformation && heightInformation.height) {
        spotDefault.resolveQuerySelector(productContainer, function(productContainer) {
          if (productContainer) {
            spotDOM.beginQuery(function() {
              productContainer.style.minHeight = heightInformation.height + "px";
            });
            spotDOM.endQuery(function() {
              productContainer.style.minHeight = '';
            });
          }
        });
      }
    }
    if (spotDOM.shopifyMode() && currentCount != null)
      spotDOM.count(currentCount);
  };

  /**

  ### setupSearchBars(searchBars, localSearch, options, defer)

  A helper function that sets up search bars in a default manner. Normally called by `SpotDefault.init`.

  * `searchBars` is a node list representing the set of search bars you want to apply the behaviour to. Should be input boxes.
  * `localSearch`  determines whether or not the search bar should apply to the current view, or as a dropdown. Defaults to false.
  * `backfillSearchBars` determines whether or not we should backfill all search bars as searches are made anywhere on the page (or from the address bar)
  * `options` will be passed direclty to the `options` argument for the `SearchBar` class.
  * `defer` will only apply the search bar after the page has been fully loaded.

  **/
  this.setupSearchBars = function(searchBars, localSearch, backfillSearchBars, options, defer) {
    if (!options)
      options = {};
    var internalSetup = function() {
      spotDefault.spotSearchBars = [];
      spotDOM.spotAPI.forEach(searchBars, function(searchBar) {
        spotDefault.resolveQuerySelector(searchBar, function(searchBar) {
          if (searchBar) {
            if (backfillSearchBars) {
              this.spotDOM.search(function() {
                // Update the search bars if we make a search query, and do it immediately.
                searchBar.value = spotDefault.spotDOM.search();
              }, true);
            }
            if (!localSearch) {
              spotDefault.spotSearchBars.push(new spotDefault.SearchBar(searchBar, options.maxProducts, options.showAll, options));
            } else {
              spotDefault.spotSearchBars.push(new spotDefault.KeyupListener(searchBar).doneTyping(function(search) {
                spotDefault.spotDOM.search(search);
              }));
            }
          }
        });
      });
    };
    if (defer)
      window.addEventListener('load', internalSetup);
    else
      internalSetup();
  };

  /**

  ### setupEvents(eventSink, customerId, orderId, cart)

  A helper function that sets up basic events. Normally called by `SpotDefault.init`.

  * eventSink is the string that represents where you want events to flow to. See [eventSink](#eventSink) under SpotAPI.
  * customerId is the id of the current customer. This will tag this session as belonging to this particular customer.
  * orderId is the id of the current order, if this is a thank you page. Tags the order as belonging to this particular session.
  * cart is the object representing the current cart, passed in from `{{ cart | json }}`. Will keep track of the cart, and inform the events API
    a client adds something to the cart for analytics purposes.

  **/
  this.setupEvents = function(eventSink, customerId, orderId, cart) {
    if (eventSink && eventSink != "none") {
      this.spotDOM.spotAPI.eventSink(eventSink);
      this.spotDOM.spotAPI.sendDeferredEvents();
      if (this.spotEvent) {
        if (this.spotDOM.isThankYouPage()) {
          if (this.spotDOM.spotAPI.sessionId() && orderId)
            this.spotEvent.completedOrder(orderId);
        } else if (!this.spotDOM.spotAPI.sessionId() && !this.spotDOM.getStorage("spotBegunSession")) {
          this.spotEvent.beginSession();
          this.spotDOM.setStorage("spotBegunSession", true);
        }
        if (!this.spotDOM.getStorage("spotLoggedIn") && customerId) {
          this.spotEvent.loggedIn(customerId);
          this.spotDOM.setStorage("spotLoggedIn", true);
        }
        if (cart) {
          var oldCart = this.spotDOM.getStorage("spotCart");
          var addedItems = [];
          if (oldCart) {
            // Determine the differences between this cart, and the previous cart.
            var oldItems = Object.fromEntries(oldCart.items.map(function(i) { return [i.key, i]; }));
            addedItems = cart.items.filter(function(i) { return oldItems[i.key] && oldItems[i.key].quantity < i.quantity; });
          }
          if (addedItems.length > 0)
            this.spotEvent.addedProductsToCart(addedItems.map(function(i) { return { variantId: i.variant_id, productId: i.product_id, quantity: i.quantity }; }));
          this.spotDOM.spotAPI.setStorage("spotCart", { items: cart.items.map(function(i) { return { key: i.key, variant_id: i.variant_id, product_id: i.product_id, quantity: i.quantity }; }) });
        }
      }
    }
  };

  
  spotDOM.scalarField(this, 'context');
  this.setupContext = function(options) {
    this.context({ product: options.product, variantId: options.variantId || spotDOM.getVariantID(), customer: options.customer, collection: options.collection || (spotDOM.getCollectionPage() && { handle: spotDOM.getCollectionPage() }), params: options.params, manual_recommendations: options.productSettings && options.productSettings.recommended_ids, secondary_lists: options.productSettings && options.productSettings.secondary_lists, freeform: options.context || (options.params && options.params['context']), recently_viewed: this.recentProducts().filter(function(e) { return e.product; }).map(function(e) { return e.product.id; }).reverse() });
    return this;
  };

  /**

  ### setupFreeformContext(context)

  A helper function that parses the freeform context (if present), and then does appropriate things with it. Amongst other things, it does:

  * Run algorithm for both context, and search if present.
  * Parses the context for price filters manually, and applies them (i.e. "under 50"), if they don't match a facet, removing it from the string.
  * Parses the freeform context, and matches it to existing filters, and applies them if appropriate, removing them from the string.

  If on a collections page, in addition:
  * Match context to sort orders ('bestselling skirts'), and select the appropriate sort order.
  * Append the context to the search string.

  **/
  this.setupFreeformContext = function(options, context) {
    if (!options)
      options = {};
    for (var stringId = 0; stringId < 2; ++stringId) {
      var str = stringId == 0 ? context.freeform : spotDOM.search();
      if (!str)
        continue;
      if ((options['search'] === false || options['search'] == null) && stringId == 1 && spotDOM.isSearchPage())
        continue;
      if (options['collection'] === false && stringId == 1 && spotDOM.isCollectionPage())
        continue;
      var contextWords;
      var contextWords = str.split(/\s+/);
      var getPossibleWords;
      getPossibleWords = function(i, contextWords) {
        var handleized = spotDOM.handleize(contextWords[i]);
        var results = [];
        (/s$/i.test(handleized) ? [handleized.replace(/s$/i, ""), handleized] : [handleized]).forEach(function(handleized) {
          if (i < contextWords.length - 1) {
            var words = getPossibleWords(i+1, contextWords);
            var newWords = words.map(function(w) { return { idx: i, length: w.length + 1, handle: handleized + "-" + w.handle }; })
            results = results.concat(words.concat(newWords).concat([{ idx: i, handle: handleized, length: 1 }]));
          } else
            results = results.concat([{ idx: i, handle: handleized, length: 1 }]);
        });
        return results;
      };
      var possibleValues = getPossibleWords(0, contextWords);
      var nameHash = Object.fromEntries(possibleValues.map(function(pv) { return [pv.handle, pv]; }));
      var removedIndices = [];

      var validFacets = null;
      if (stringId == 1 && spotDOM.isSearchPage() && (typeof(options['search']) == 'object' && Array.isArray(options['search']))) {
        validFacets = Object.fromEntries(options['search'].map(function(s) { return [s, true]; }));
      }
        
      var enabledFacets = [];

      spotDOM.facets().filter(function(f) { return f.isValidLocale(spotDOM.locale()) && (!validFacets || validFacets[f.getUniqueId()]); }).forEach(function(facet) {
        facet.facetValues().forEach(function(facetValue) {
          var handle = spotDOM.handleize(facetValue.getName());
          if (!facetValue.enabled() && nameHash[handle]) {
            facetValue.enabled(true);
            enabledFacets.push(facetValue);
            removedIndices.push(nameHash[handle]);
          }
        });
      });
      if (spotDOM.isCollectionPage()) {
        spotDOM.sortOrders().forEach(function(sortOrder) {
          var handle = spotDOM.handleize(sortOrder.getName());
          if (nameHash[handle]) {
            removedIndices.push(nameHash[handle]);
            spotDOM.sort(sortOrder);
          }
        });
      }
      removedIndices.sort(function (a,b) { return b.idx - a.idx; }).forEach(function(ri) {
        contextWords.splice(ri.idx, ri.length);
      });
      str = contextWords.join(" ");
      var priceLanguage = this.language["price-filter-parsers"][spotDOM.locale()] || this.language["price-filter-parsers"]["en"];
      var overPriceFilter = priceLanguage.over.filter(function(r) { return r.test(str); })[0];
      var underPriceFilter = priceLanguage.under.filter(function(r) { return r.test(str); })[0];
      if (overPriceFilter) {
        var groups = overPriceFilter.exec(str);
        if (groups) {
          var number = parseFloat(groups[1]);
          if (number) {
            spotDOM.addQueryTransform([function(query) {
              return query.price(">=", number);
            }]);
          }
          str = str.replace(overPriceFilter, "");
        }
      }
      else if (underPriceFilter) {
        var groups = underPriceFilter.exec(str);
        if (groups) {
          var number = parseFloat(groups[1]);
          if (number) {
            spotDOM.addQueryTransform([function(query) {
              return query.price("<", number);
            }]);
          }
          str = str.replace(underPriceFilter, "");
        }
      }
      str = str.replace(/\s+/, " ").replace(/(^\s+|\s+$)/, "");

      if (stringId == 0)
        context.freeform = str;
      else if (stringId == 1) {
        var originalString = spotDOM.search();
        if (originalString != str && options.updateSearch) {
          spotDOM.search(str);
          spotDOM.endQuery(function() {
            if (!spotDefault.loaded() && spotDOM.count() == 0) {
              spotDOM.queryGuard(function() {
                spotDOM.search(originalString);
                enabledFacets.forEach(function(fv) { fv.enabled(false); });
              });
            }
          });
        }
      }
    }
    if (spotDOM.isCollectionPage() && context.freeform) {
      spotDOM.search(spotDOM.search() ? (spotDOM.search() + " " + context.freeform) : context.freeform)
      context.freeform = null;
    } else if (context.freeform == "")
      context.freeform = null;
  };

  this.setupShopifyModeFallback = function(products, all_tags) {
    // Manually feed in the prodcut listing if we're in Shopify mode and we have a listing
    if (products) {
      var runOnce = false;
      spotDOM.endQuery(function() {
        if (spotDOM.shopifyMode()) {
          if (!runOnce) {
            spotDOM.products(products);
            runOnce = true;
          }
        }
      });
    }
    // If we have a list of tags, remove irrelevant tags from the page.
    if (all_tags && all_tags.length < 1000) {
      var hash = {};
      for (var i = 0; i < all_tags.length; ++i)
        hash[all_tags[i]] = true;
      spotDOM.addFacet(function(facet) {
        if (facet.smartType == "tags" || facet.smartType == "tag") {
          facet.addFacetValue(function(facetValue) {
            var values = facetValue.getValue();
            if ((typeof(values) == 'object' && Array.isArray(values) ? values : [values]).filter(function(e) { return hash[e]; }).length == 0)
              facetValue.count(0);
          }, true);
        }
      }, true);
    }
  };

  this.searchOverrides = null;
  // Gets the matching rule given a search, or given no search.
  this.getRules = function(context) {
    var rules = [];

    (Array.isArray(context.rule) ? context.rule : [context.rule]).forEach(function(rule) {
      if (rule && typeof(rule) == "object") {
        if (context.override && rule.views && rule.views[context.override])
          rules.push(rule.views[context.override]);
        else if (context.override && spotDefault.rulesets && spotDefault.rulesets[context.override])
          rules.push(spotDefault.rulesets[context.override]);
        rules.push(rule);
      } else if (rule && spotDefault.rulesets && spotDefault.rulesets[rule]) {
        if (context.override && spotDefault.rulesets[context.override])
          rules.push(spotDefault.rulesets[context.override]);
        rules.push(spotDefault.rulesets[rule]);
      }
    });
    if (!context.rule && context.override && this.rulesets[context.override])
      rules.push(this.rulesets[context.override]);
    if (context.search != null && this.searchOverrides) {
      var rule = this.getMatchingSearchRule(spotDOM.locale(), this.searchOverrides, context.search);
      if (rule && this.rulesets && this.rulesets[rule.rule]) {
        rules.push(this.rulesets[rule.rule]);
      }
    }
    if ((context.search != null || spotDOM.isSearchPage()) && this.searchRule)
      rules.push(this.searchRule);
    if (this.generalRule)
      rules.push(this.generalRule);
    return rules;
  };
  /**

  ### getCollectionPageTagsThatAreNotRulesOrFacets

  **/
  this.getCollectionPageTagsThatAreNotRulesOrFacets = function(options) {
    var facetHash = Object.fromEntries(spotDOM.facets().filter(function(f) { return ["tags", "tag"].indexOf(f.smartType) != -1; }).flatMap(function(f) { return f.facetValues(); }).map(function(fv) {
      var value = fv.getValue();
      if (typeof(value) == "string")
        return [spotDOM.handleize(value), true];
      else if ((Array.isArray(value)) && value.length == 1)
        return [spotDOM.handleize(value[0]), true];
      return null;
    }).filter(function(v) { return v != null; }));
    var possibleRulesets = Object.assign({}, spotDefault.rulesets, Object.fromEntries(spotDefault.activeRules().flatMap(function(v) { return Object.keys(v.views || {}) }).map(function(v) { return [v, true] })));
    return spotDOM.getCollectionPageTags().filter(function(tag) { return !possibleRulesets[tag] && !facetHash[spotDOM.handleize(tag)] });
  };

  /**

  ### getRecommendation(id, options)

  Pulls out a recommendation rule by id, and initializes it if necessary.

    spotDefault.getRecommendation("My Recommendation").init(context).addProducts((products) => {
      products.forEach((p) => {
        document.querySelector('#myRecommendationPane').appendChild(spotDefault.productRenderer(p));
      });
    });

  **/
  this.cachedRecommendations = {};
  this.getRecommendation = function(id_or_name, options) {
    var recommendation = Object.values(spotDefault.recommendations || {}).filter(function(recommendation) {
      if (typeof(id_or_name) == "number")
        return recommendation.id == id_or_name;
      return Object.values(typeof(recommendation.name) != "object" ? { "en": recommendation.name } : recommendation.name).filter(function(e) { return e == id_or_name }).length > 0;
    })[0] || this.cachedRecommendations[id_or_name];
    if (recommendation)
      return recommendation;
    options = options || this.options;
    if (!options || !options.settings || !options.settings.recommendations)
      return null;
    var recommendation = null;
    if (typeof(id_or_name) == "string") {
      recommendation = Object.values(options.settings.recommendations).filter(function(r) {
        return Object.values(typeof(r.name) == "string" ? { en: r.name } : r.name).filter(function(n) { return n == id_or_name }).length > 0;
      })[0];
    } else {
      recommendation = options.settings && options.settings.recommendations[id_or_name];
    }
    if (!recommendation)
      return recommendation;
    this.cachedRecommendations[id_or_name] = new spotDOM.Recommendation(recommendation, recommendation.boost_rule_id ? new spotDOM.BoostRule(options.settings.boost_rules[recommendation.boost_rule_id]) : null, new spotDOM.SortOrder(options.settings.sort_orders[recommendation.sort_order_id]));
    return this.cachedRecommendations[id_or_name];
  };


  this.renderBadge = function(badge) {
    var element = this.createElement('<div class="spot-badge"></div>');
    element.classList.add("spot-badge-" + spotDOM.handleize(badge.getName("en")));
    if (badge.position)
      element.classList.add("spot-badge-position" + badge.position);
    if (badge.swatch && badge.swatch.name && badge.swatch.name[0])
      element.classList.add("swatch-" + spotDOM.handleize(badge.swatch.name[0]));
    if (badge["class"])
      badge["class"].split(/\s+/).forEach(function(c) { element.classList.add(c); });
    element.innerText = badge.getName();
    return element;
  };

  this.defaultBannerRenderer = function(banner, position, locale) {
    var element = spotDefault.createElement("<a class='banner banner-size-" + (banner.size || "normal") + "'><div class='banner-title'></div><div class='banner-subtitle'></div></a>");
    element.href = banner.link || '#';
    var title = element.querySelector('.banner-title');
    var subtitle = element.querySelector('.banner-subtitle');
    title.innerText = banner.getTitle(locale) || '';
    title.style.color = banner.title.color;
    subtitle.innerText = banner.getSubtitle(locale) || '';
    subtitle.style.color = banner.subtitle.color;
    (banner.media || []).forEach(function(media) {
      if (media.type == "swatch") {
        if (media.image) {
          var image = spotDefault.createElement('<img class="banner-image-' + media["handle"] + '"/>');
          image.src = media.image;
          element.appendChild(image);
        } else {
          element.style.backgroundColor = "#" + media.color;
        }
      }
    });
    if (banner["class"])
      banner["class"].split(/\s+/).forEach(function(c) { element.classList.add(c); });
    return element;
  };
  this.bannerRenderer = this.defaultBannerRenderer;

  this.setupRulesets = function(rulesets, generalRuleId, searchRuleId) {
    if (rulesets)
      Object.keys(rulesets).forEach(function(handle) { rulesets[handle].handle = handle; });
    this.rulesets = rulesets;
    this.spotDOM.searchRuleHandle(searchRuleId);
    this.generalRule = rulesets[generalRuleId];
    this.searchRule = rulesets[searchRuleId];
  };

  // Parses, and sets up all objects from a simple hash.
  this.setupObjects = function(settings) {
    if (settings.redirects)
      spotDOM.addRedirects(settings.redirects);
    if (settings.cart)
      spotDOM.cart(settings.cart);
    if (settings.sort_orders)
      spotDefault.setupSortOrders(settings.sort_orders);
    if (settings.facets && settings.facet_groups)
      spotDefault.setupFacetGroups(settings.facets, settings.facet_groups);
    if (settings.boost_rules && settings.search_boosts)
      spotDefault.setupBoosts(settings.boost_rules, settings.search_boosts);
    if (settings.badges && settings.badge_groups)
      spotDefault.setupBadgeGroups(settings.badges, settings.badge_groups, settings.swatches);
    if (settings.banners && settings.banners)
      spotDefault.setupBanners(settings.banners, settings.swatches);
    if (settings.recommendations && settings.boost_rules)
      spotDefault.setupRecommendations(settings.recommendations, settings.boost_rules, settings.checkout);
    if (settings.personalizations && settings.boost_rules)
      spotDefault.setupPersonalizations(settings.personalizations, settings.boost_rules);
    if (settings.rules)
      spotDefault.setupRulesets(settings.rules, settings.general_rule, settings.search_rule);
    return this;
  };

  spotDOM.scalarField(this, 'rule');
  spotDOM.arrayField(this, 'boost');
  
  this.applyAdHocPins = function(context, rulePins) {
    if (context.params.pin) {
      var pinType = context.params.pin_type || "sku";
      rulePins = (Array.isArray(context.params.pin) ? context.params.pin : [context.params.pin]).map(function(pin) {
        var hash = {};
        hash[pinType] = pin;
        return hash;
      }).concat(rulePins);
    }
    return rulePins;
  };

  this.substituteDefaultConditions = function(conditions, parent) {
    for (var i = 0 ; i < conditions.length; ++i) {
      if (conditions[i] == "default") {
        if (!parent) {
          conditions[i] = {"and":[]};
          continue;
        }
        var hash = { };
        hash[parent[1]] = parent[0];
        conditions[i] = hash;
      }
      else if (typeof(conditions[i]) == "object") {
        var values = Object.values(conditions[i]);
        if (Array.isArray(values[0]))
          this.substituteDefaultConditions(values[0], parent);
      }
    }
    return conditions;
  };

  
  this.applyRules = function(rules) {
    var context = {};
    context.params = spotDOM.getUrlVars();
    var defaultSortOrder = spotDOM.getSortOrder(rules.reduce(function(value, rule) { return value != null ? value : (rule.sort_option_id || rule.sort_order_id); }, null));
    var selectedSortOrder = spotDOM.getSortOrder((spotDOM.isSearchPage() || spotDOM.isCollectionPage()) && (spotDOM.sort() || defaultSortOrder));

    if (!selectedSortOrder && !spotDOM.sort() && spotDOM.isSearchPage())
      selectedSortOrder = spotDOM.sortOrders().filter(function(so) { return so.getUniqueId() == "search" })[0];

    var rule = rules[0] || {};
    // TOOD: Fix me, conditions here need to be copied, in the case where they have situation defualt subbed in. JSON.parse/stringify is not the way to do this.
    var conditionList = rules.map(function(r) { return [JSON.parse(JSON.stringify(r.conditions || [])), r.disjunctive ? "or" : "and"] }).filter(function(r) { return r[0]; });
    for (var i = conditionList.length - 2; i >= 0; --i)
      this.substituteDefaultConditions(conditionList[i][0], conditionList[i+1]);

    var defaultConditions = conditionList.length > 0 ? conditionList[0][0] : [];
    var pins = rules.reduce(function(value, rule) { return value != null ? value : rule.pins; }, null) || [];
    var antipins = rules.reduce(function(value, rule) { return value != null ? value : rule.antipins; }, null) || [];
    var boostRuleIds = rules.reduce(function(value, rule) { return value != null ? value : (rule.boost_rule_ids || rule.boosts); }, null) || [];

    if (spotDOM.isCollectionPage())
      defaultConditions = defaultConditions.concat(spotDefault.getCollectionPageTagsThatAreNotRulesOrFacets().map(function(tag) { return { "tag": { "==":  tag } } }));

    spotDOM.queryGuard(function() {
      if (selectedSortOrder)
        spotDOM.sort(selectedSortOrder);
      if (defaultSortOrder)
        spotDOM.defaultSort(defaultSortOrder);
      spotDOM.conditions(defaultConditions);

      if (!spotDOM.search()) {
        var sortOrderId = rules.reduce(function(value, rule) { return value != null ? value : (rule.sort_option_id || rule.sort_order_id); }, null);
        var sortOrder = spotDOM.getSortOrder(sortOrderId);
        if (!spotDOM.sort() || spotDefault.loaded())
          spotDOM.sort(sortOrder);
      }

      var mergeId = rules.reduce(function(value, rule) { return value != null ? value : rule.merge_id; }, null);
      if (mergeId && mergeId != "none") {
        spotDOM.split("none");
        spotDOM.merge(mergeId);
      } else {
        var splitId = rules.reduce(function(value, rule) { return value != null ? value : rule.split_id; }, null);
        spotDOM.split(splitId != null ? splitId : "auto");
        spotDOM.merge("none");
      }

      var facetGroupId = rules.reduce(function(value, rule) { return value != null ? value : rule.facet_group_id }, null);
      var badgeGroupId = rules.reduce(function(value, rule) { return value != null ? value : rule.badge_group_id }, null);
      var banners = rules.reduce(function(value, rule) { return value != null ? value : rule.banners }, null);
      var recommendationIds = rules.reduce(function(value, rule) { return value != null ? value : rule.recommendation_ids; }, null);
      var facets = spotDefault.getFacetGroup(facetGroupId);
      var appliedFacet;
      if (facets) {
        spotDOM.facets(facets);
        for (var i = 0; i < facets.length; ++i)
          appliedFacet = facets[i].init() || appliedFacet;
      }

      pins = spotDefault.applyAdHocPins(context, pins);

      spotDOM.pins(pins);
      spotDOM.antipins(antipins);
      spotDOM.recommendations((recommendationIds || []).map(function(id) {  
        return spotDefault.getRecommendation(id);
      }).filter(function(recommendation) { return recommendation != null }));

      var boostQuery = spotDOM.spotAPI.se();
      boostRuleIds.forEach(function(boostRuleId) {
        if (spotDefault.boostRules && spotDefault.boostRules[boostRuleId]) {
          boostQuery = boostQuery.merge(spotDefault.boostRules[boostRuleId].getQuery(context, false));
          var condition = spotDefault.boostRules[boostRuleId].getQuery(context, true);
          if (condition && condition.innerQuery)
            condition.innerQuery.forEach(function(condition) { spotDOM.addCondition(condition) });
        }
      });
      spotDefault.boosts().forEach(function(boost) {
        boostQuery = boostQuery.merge(boost);
      });
      spotDOM.boosts([boostQuery]);
      spotDefault.badgeGroup = spotDefault.getBadgeGroup(badgeGroupId);
      spotDOM.banners((banners || []).map(function(b) { return new spotDOM.Banner(Object.assign(b, spotDefault.banners[b.banner_id]), spotDefault.swatches); }));
      spotDefault.preferredImages = rule.preferred_images;
      spotDOM.badges(spotDefault.badgeGroup ? spotDefault.badgeGroup.badges : []);

      var personalizationId = rules.reduce(function(value, rule) { return value != null ? value : rule.personalization_id; }, null);
      var personalization = spotDefault.personalizations && personalizationId && spotDefault.personalizations[personalizationId];
      if (personalization)
        spotDOM.personalization(personalization);
    });
  };

  this.updateActiveRules = function(merge) {
    var rules = spotDefault.getRules(Object.assign({ search: !spotDOM.isSearchPage() ? null : (spotDOM.search() != null ? spotDOM.search() : ''), override: spotDOM.getOverrideRuleHandle(), rule: spotDefault.rule() }, merge || {}));
    if (spotDefault.activeRules().length != rules.length || spotDefault.activeRules().filter(function(r, idx) { return rules[idx] != r; }).length > 0) {
      spotDefault.activeRules(rules);
    }
  };

  this.setupActiveRules = function(rule) {
    // Every time the rules change (be it through a search override, or some other mechanism), re-run pins, boosts, recommendations.
    // Normaly this should happen exactly once, on page load, but can change throughout under certain circumstances.
    this.activeRules(this.applyRules);
    this.spotDOM.search(function(search) { spotDefault.updateActiveRules({ search: !spotDOM.isSearchPage() && !search ? null : search }); });
    if (rule)
      this.rule(rule);
    this.rule(function(rule) {  spotDefault.updateActiveRules({ rule: rule }); }, true);
  };

  this.updateQueryString = function(isReplace) {
    spotDOM.updateQueryString(Object.assign(
      { context: spotDefault.context().freeform, page: spotDefault.viewedPage() && spotDefault.viewedPage() > 1 ? spotDefault.viewedPage() : null }, 
      spotDefault.relatedProduct() ? { recommendation_id: spotDefault.relatedProduct().recommendation.id, product_id: spotDefault.relatedProduct().product.id } : { recommendation_id: null, product_id: null }
    ), isReplace); 
  };
};


/**

### init(options)

The default initailization method. Pass in a bunch of functions, get a functional version of Spot on your website in seconds.

Returns a promise that will be resolved upon full initialization (i.e. Spot is fully ready to accept any additional calls). Most
custom integrations with the website should be done in this promise.

Hooks up the actual state of Spot to your physical web page.

Takes the following options:

#### clusters (string[])

Required. The list of clusters that Spot is present on. Retrieved from `{{ shop.metafields.esafilters.clusters | json }}`

#### event (SpotEvent?)

A SpotEvent object to use in place of the normal one. Otherwise, will use the default.

#### init (function(spotAPI, spotDOM, spotDefault))

A function that is called at the very beginning of initialiation, immediately after `SpotAPI`, `SpotDOM`, and `SpotDefault` are initialized.

#### sortOrders (SortOrder[])

The list of sort orders that Spot is supposed to used.

Overrides the list specified by the currently active display rule. Should not generally be used; mostly deprecated.

#### facets (Facet[])

The list of facets that Spot is supposed to use in this context.

Overrides the list specified by the currently active display rule. Should not generally be used; mostly deprecated.

#### split (string)

The product split to use.

Overrides the split specified by the currentlay active display rule.

#### settings (object)

The full list of all facet groups, sort orders, splits, and rulesets, that could ever be relevant. Display rules will apply these facets, sort orders, and splits.

Retrieved from `{% assign settings_theme_id = "settings-" | append: theme.id %}{% assign settings = shop.metafields.esafilters[settings_theme_id] | default: shop.metafields.esafilters.settings %}{% if settings %}{{ settings | json }}{% else %}{}{% endif %}`

#### rule (object|string?)

The contextual display rule. Specifies the facet group to use, the sort order, the split, and the query filters to default to, as well as any pins.

Retrieved by default using

```
{% if collection %}{% assign settings_theme_id = "settings-" | append: theme.id %}{% assign settings = collection.metafields.esafilters[settings_theme_id] | default: collection.metafields.esafilters.settings %}{% endif %}{% unless settings %}{% assign settings_theme_id = "settings-" | append: theme.id %}{% assign settings = shop.metafields.esafilters[settings_theme_id] | default: shop.metafields.esafilters.settings %}{% if settings %}{{ settings.general_rule_id }}{% else %}null{% endif %}{% endif %}
````

By default, can be overridden by the URL, either by the query parameter `rule`, or by specifying as part of the path. Below
are examples of URLs which would override the rule specified here:

* https://yoursite.myshopify.com/collections/my-collection-handle?rule=my-rule-handle
* https://yoursite.myshopify.com/collections/my-collection-handle/my-rule-handle
* https://yoursite.myshopify.com/search?rule=my-rule-handle
* https://yoursite.myshopify.com/search/my-rule-handle

Can either be a JSON object in the case of a global rule override, or can be a simple refernece by ID to one of the global resultsets in `settings`.

#### productContainer (DOMElement|string)

The container that should display products. Should be passed in as a string.

#### productRenderer (DOMElement function(string)?)

A function that returns the DOM element for a product in the productContainer. Takes an object that represents a Shopify product, and returns a DOM element.

The default product renderer that is used if one is not supplied is listed below.

%{DefaultProductRenderer}


#### noInitialProductRender (bool?)

When true is passed, for the initial call, products are not loaded onto the page; this is only done for subsequent calls to filtration, pagination, and the like. Only valid if no search is occurring.
This can be useful if merchandising orders are very important and are updated very frequently, and the merchant wants changes to be "instant".

#### forceInitialQuery (bool?)

When true is passed, will *always* make a query upon page load. Shouldn't be used, unless there's a good reason for it.

#### paginationType (string?)

The type of pagination to use. Values are as follows:

* `null`: Uses no pagination out of the box.
* `"load"`: Adds a button into paginationConatiners (or the productContainer, if no pagination containers are specified), that loads more products on click.
* `"infinite"`: Upon reaching the bottom of the page, more products are loaded.
* `"paged"`: Fills the paginationContainers with a set of page numbers, and a next and prev button.

#### paginationContainers (DOMElement[] | string[])

The list of containers in which to use pagination. Only valid for `paged` and `load` modes. Should be generally retrieved by `document.querySelectorAll` and passed in.

#### sortDropdown (DOMElement | string?)

The select box that contains your sort orders. Should be generally retrieved by `document.querySelector` and passed in.

#### searchBars (DOMElement[] | string[]?)

The search bars. By default, this is hooked into any input that has the name 'q'. Should be generally retrieved by `document.querySelectorAll` and passed in.

#### searchBarOptions (object?)

Options fed to the search bars. Takes a hash, details below.

##### maxProducts (int: 5)

Determines how many products are retrieved by the search bar for instant search. Default is 5.

##### showAll (bool: false)

Determines if a "show all" button should be added to the search results. Default is false.

##### alwaysDropdown (bool: false)

If set to true, a search dropdown will always pop out regardless of what page the user is on.

If set to false, a search dropdown won't pop out on collection or search pages; instead, searches will refind the existing resultset already being shown. This is the default.

##### deferHookUntilLoad (bool: false)

Defers hooking the search bar until the page is fully loaded. Default false.

#### facetPane (FanePane?)

The facet pane to use. If unspecified, will use the default spot pane. This is where you'd pass in a custom pane (see the pane section for details), if you wanted to use one.

#### facetPaneContainer (DOMElement | string?)

The facet pane container to place the pane into. Should be something like an empty div, or larger container that can have elements placed into it. If not specified, no pane is initialized.

#### facetPaneContainerSelectorPosition (string: "beforeend")

Where the facet pane should be placed relative to the container. Default is "beforeend". Uses the enum of values present on insertAdjacentElement.

#### facetPaneOptions (object?)

A hash contains that can conatain the following attributes.

##### classes (string[]?)

An array of classes to add to the pane as it's created.

##### facetClasses (string[]?)

An array of classes to add to the facet divs as they're created.

##### facetValueClasses (string[]?)

An array of classes to add to the facet value divs as they're created.

##### singleActive (bool: false)

Ensures that when a user tries to open a facet, all other facets are closed. Useful in horizontal-style facet panes.

##### breadcrumbs (bool: true)

A boolean, determines whether breadcrumbs are used.

##### includeSorting (bool: false)

A boolean; determines whether or not to include the sort orders as "facets" in the pane. Will not produce breadcrumbs, nor will allow more than one to be selected at any one time.

##### omitHiddenFacetValues

A boolean, default false. In a situation where a facet would be hidden, we don't even construct the facet value elemnt. Can help with performance on very large facet sets.

#### noSearchResultsContainer (DOMElement?)

A container that should be shown or hidden if not results are found/not found. Can be passed a function which will be called instead.

#### resultsStatusContainer (DOMElement?)

A container to contain the status of the current page of results (i.e. start - end of count products for "search")

## FAQ

### How do I access Spot's objects from javascript, in order to create a custom widget?

If you are using `SpotDefault.init`, the best way to do this, is to simply add an event listener on `document` for the `SpotInit` event. Here's an example of making a pagiantion widget:

```html
  <div id='pagination'></div>
  <script type='text/javascript'>
    document.addEventListener("SpotInit", (e) => {
      document.querySelector('#pagination').appendChild(e.detail.spotDefault.pagedPaginationHelper({ count: totalProductCount }).element);
    });
  </script>
```

### How do I make the faceting pane instantiated be horizontal?

This can be achieved entirely with CSS styling, like the following, which will lay out your facet headings to take the entire container, and allow the facet value listing to pop down into its own box that takes up the entire
width of the paneContainer.

```css
  .pane.horizontal .facet.active .facet-values {
    position: absolute;
    background: #fff;
    z-index: 1000;
    left: 0;
    padding: 16px;
    width: 100%;
    text-align: left;
  }
  .pane.horizontal .facet.active .facet-values .facet-value {
    margin-right: 24px;
    margin-bottom: 4px;
    display: inline-block;
  }
  .pane.horizontal .facets {
    display: flex;
    margin: 12px 0;
    position: relative;
  }
  .pane.horizontal .facet-title {
    margin-top: 0;
  }
  .pane.horizontal .facets .facet {flex-grow: 1;
    margin: 0 4px;
  }
```

**/
/** %+{SpotDefaultInit} **/

window.SpotDefault.isReady = function() {
  return document.readyState === "complete" || document.readyState === "loaded" || document.readyState === "interactive";
}
window.SpotDefault.ready = function(done, condition, interval, timeout) {
  if (!condition) {
    if (window.SpotDefault.isReady())
      done();
    else
      document.addEventListener('DOMContentLoaded', done);
  } else {
    if (condition()) {
      done();
    } else {
      var recurring;
      var iterations = 0;
      var iterationTarget = Math.ceil((timeout || 0) / (interval || 100));
      recurring = setInterval(function() {
        if (condition() || (timeout && (iterations++ > iterationTarget))) {
          clearInterval(recurring);
          done();
        }
      }, interval || 100);
    }
  }
};
window.SpotDefault.init = function(options) {
  if (!options)
    options = {};
  options = Object.assign({}, options.settings, options);
  // ================= STEP 1 =========================
  // Instantiating the APIs and tossing approriate settings on there.
  var spotAPI = new window.SpotAPI(options.clusters);
  var deferred = spotAPI.Deferred();

  if (!options['clusters'])
    throw "Requires cluster listing from {{ shop.metafields.esafilters.clusters | json }}.";
  var spotDOM = new window.SpotDOM(spotAPI, options);
  // If we want to use default HTML/pagination implementations, we do this. For this example, we'll use it.
  var spotEvent = options['event'] || new window.SpotEvent(spotAPI);
  var spotDefault = new window.SpotDefault(spotDOM, spotEvent, options["language"]);
  spotDefault.options = options;

  // This is usually a singleton, but not always.
  window.spotAPI = spotAPI;
  window.spotDOM = spotDOM;
  window.spotDefault = spotDefault;

  if (options['init'])
    options['init'](spotAPI, spotDOM, spotDefault, options);
  document.dispatchEvent(new CustomEvent('SpotBegin', { detail: { spotAPI: spotAPI, spotDOM: spotDOM, spotDefault: spotDefault, options: options } }));
  spotDefault.setupEvents(options['eventSink'], options['customerId'], options['orderId'], options['cart']);

  if (!options.params)
    options.params = spotDOM.getUrlVars();
  spotDefault.setupContext(options);
  spotDefault.ready(function() {
    spotDOM.beginQuery(function() { document.body.classList.add(spotDefault.options.loadingClass || "spot-loading"); }).endQuery(function() { document.body.classList.remove(spotDefault.options.loadingClass || "spot-loading"); });
    spotDOM.products(function(products) { 
      document.body.classList.toggle("spot-no-results", products && products.length == 0 && spotDOM.page() == 1); 
    }, true);
    spotDOM.shopifyMode(function(isShopifyMode) { document.body.classList.toggle('spot-shopify-mode', isShopifyMode);  }, true);
  });

  // Issue a query guard directive; ensure that within this block, only a single query is run at the end of the block.
  // This is used so that setup can happen, checkboxes can be ticked, without actually triggering unecessary filtration.
  spotDOM.queryGuard(function() {
    // Here, we set up all internal data structures for use.
    spotDefault.setupObjects(options);
    var defaultSortOrder = options.defaultSortOrder && spotDOM.getSortOrder(options.defaultSortOrder);
    if (defaultSortOrder)
      spotDOM.defaultSort(defaultSortOrder);
    var sortOrder = options.sortOrder && spotDOM.getSortOrder(options.sortOrder);
    if (sortOrder)
      spotDOM.sort(sortOrder);
    if (options.merge)
      spotDOM.merge(options.merge);
    if (options.split)
      spotDOM.split(options.split);
    if (options.conditions)
      spotDOM.addConditions(options.conditions);
    if (options.pins)
      spotDOM.pins(options.pins);
    if (options.antipins)
      spotDOM.antipins(options.antipins);
    if (options.boosts)
      spotDOM.boosts(options.boosts);

    // If we can use the inital product set, use it.
    if (spotDOM.shopifyMode() || (spotDOM.queryCount() == 0 && spotDefault.canUseInitialProducts() && spotDOM.conditions().length == 0 && !options['forceInitialQuery'])) {
      if (options['count'])
        spotDOM.count(options.count);
      if (options['products'])
        spotDOM.products(options['products']);
    }

    // Everything below here is DOM related.
    spotDefault.setupSortOrderDropdown(options.sortDropdown || 'select[name="sort_by"]');
    if (options.collection || spotDOM.isSearchPage() || spotDOM.isCollectionPage()) {
      var currentPage = spotDOM.page();
      // ================= STEP 2 =========================
      // Choosing how to render products onto a page when we get them.
      if (options['productContainer']) {
        spotDefault.setupProductRender(options['productContainer'], options['productRenderer'], !options['noInitialProductRender'] || options['forceInitialQuery']);
        if (options['bannerRenderer'])
          spotDefault.bannerRenderer = options.bannerRenderer;
      }
      // ================= STEP 3 =========================
      if (options['facetPaneContainer'] || options['paneContainer']) {
        // Getting a pane HTML element, and attaching it to your page.
        window.spotFacetPane = options['pane'] || options['facetPane'] || new spotDefault.FacetPane(options['paneOptions'] || options['facetPaneOptions']);
        // Legacy.
        window.spotPane = window.spotFacetPane;
        var showMobileFlyout = spotDefault.createElement("<button class='facet-pane-mobile-show btn button'>" + spotDefault.templateLocale("filter") + "</button>");
        showMobileFlyout.addEventListener('click', function() { window.spotPane.paneElement.classList.add('expanded'); });
        spotDefault.resolveQuerySelector((options['facetPaneContainer'] || options['paneContainer']), function(element) {
          if (element) {
            element.insertAdjacentElement(options['facetPaneContainerSelectorPosition'] || options['paneContainerSelectorPosition'] || "beforeend", showMobileFlyout);
            element.insertAdjacentElement(options['facetPaneContainerSelectorPosition'] || options['paneContainerSelectorPosition'] || "beforeend", window.spotFacetPane.paneElement);
          }
        });
        if (window.spotFacetPane.breadcrumbPane) {
          spotDefault.resolveQuerySelector((options['facetPaneOptions'] ? options['facetPaneOptions']['breadcrumbs'] : null) || (options['facetPaneContainer'] || options['paneContainer']), function(element) {
            if (element) {
              spotDefault.resolveQuerySelector(window.spotFacetPane.breadcrumbPane.breadcrumbsElement, function(breadcrumbsElement) {
                element.insertAdjacentElement("afterbegin", breadcrumbsElement);
              });
            }
          });
        }
      }
      // ================= STEP 5 =========================
      // Here's an easy way to write pagination to the page when a query changes/is made for the first time.
      if (options['paginationType'] !== "none") {
        spotDefault.setupPagination(options['paginationType'] !== undefined ? options['paginationType'] : "infinite", options['paginationContainers'], options['productContainer'], options['currentCount'], Object.assign({  topOfPage: options['topOfPage'] || function() {
          var top = 0;
          // If we're not loaded by this point, will just return 0.
          if (options['productContainer']) {
            spotDefault.resolveQuerySelector(options['productContainer'], function(container) {
              top = container.getBoundingClientRect().top;
            });
          }
          return top;
        }, noBackfillHeight: options['noBackfillHeight'], triggerHeight: options['scrollTriggerHeight'] }, (options.paginationOptions || {})));
      }
      // ================= STEP 7 =========================
      // If we have a spellcheck that occurs, display it in the appropriate DOM element.
      if (options['spellcheckContainers']) {
        spotDOM.spellCheck(function(spellCheck){
          options['spellcheckContainers'].forEach(function(spellcheckContainer) {
            spotDefault.resolveQuerySelector(spellcheckContainer, function(spellcheckContainer) {
              spotDefault.toggleElement(spellcheckContainer, spellCheck);
              if (spellCheck)
                spellcheckContainer.innerHTML = "<p class='corrected'>" + spotDefault.templateLocale("showing-results-for") + " <a href='/search?q=" + encodeURIComponent(spellCheck.corrected) + "'>" + spotAPI.map(spellCheck.words, function(e) { if (e.corrected != e.original) { return "<b>" + e.corrected + "</b>"; } return e.corrected; }).join(" ") + "</a>.</p>";
            });
          });
        });
      }
      // Ensure that we update the query string whenever a query is made.
      spotDOM.query(function() { spotDefault.updateQueryString(); });
      window.addEventListener('popstate', function() { spotDOM.parseQueryString(); });
      spotDefault.setupShopifyModeFallback(options["products"], options["all_tags"]);
      if (spotDOM.isSearchPage()) {
        spotDefault.loaded(function() {
          if (spotDefault.relatedProduct()) {
            window.document.title = spotDefault.templateLocale("related-title", { product: spotDOM.getProductTitle(spotDefault.relatedProduct().product) });
          } else {
            window.document.title = spotDefault.templateLocale(spotDOM.products() && spotDOM.products().length > 0 ? "search-title" : "search-empty-title", { count: spotDOM.count(), originalSearch: spotDOM.search(), search: (spotDOM.spellCheck() ? spotDOM.spellCheck().corrected : spotDOM.search()) });
          }
        });
        spotDefault.addRecentSearch(spotDOM.search());
      }
      // Minor polish details.
      if (options['noSearchResultsContainer']) {
        spotDOM.query(function(products, count) {
          spotDefault.resolveQuerySelector(options['noSearchResultsContainer'], function(container) {
            spotDefault.toggleElement(container, spotDOM.page() == 1 && spotDOM.search() && count === 0);
          });
        });
      }
      if (options['resultsStatusContainer']) {
        const updateStatusContainer = function(products, count, _options) {
          var start = spotDefault.paginationType() && spotDefault.paginationType() != "infinite" && spotDefault.paginationType() != "load" ? (spotDOM.page() - 1) * spotDOM.paginate() + 1 :  ((spotDefault.paginationOffset() || 1) - 1) * spotDOM.paginate() + 1;
          var end = Math.min(spotDOM.page() * spotDOM.paginate(), count);
          spotDefault.resolveQuerySelector(options['resultsStatusContainer'], function(container) {
            if (container)
              container.innerText = spotDefault.templateLocale(spotDOM.search() ? "results-status-search" : "results-status", { start: start, end: end, count: count, originalSearch: spotDOM.search(), search: (_options.spellCheck ? _options.spellCheck.corrected : spotDOM.search()) });
          });
        };
        spotDOM.query(updateStatusContainer);
        spotDefault.paginationOffset(function() {
          updateStatusContainer(null, spotDOM.count(), { spellCheck: spotDOM.spellCheck() });
        });
      }
    } else if (spotDOM.isProductPage()) {
      var handle = spotDOM.getProductPage();
      var duplicate = spotDefault.recentProducts().filter(function(e) { return e.handle == handle })[0];
      if (duplicate)
        spotDefault.removeRecentProduct(duplicate);
      if (spotDefault.recentProducts().length > (options['maxRecentProducts'] || 7))
        spotDefault.removeRecentProduct(spotDefault.recentProducts()[0]);
      spotDefault.addRecentProduct({ handle: handle, product: options.product });
    }

    // ================= OPTIONAL STEP 1+2 =========================
    // Deal with searching; in this case, let's hook up the search bar, if we're on a collections page to search the actual collection.
    // If we're on the search page, it'll simply search as normal.
    spotDefault.setupSearchBars(
      options['searchBars'] || (options['searchBars'] === undefined ? ['input[name="q"]'] : []),
      (options['searchBarOptions'] && options['searchBarOptions']['alwaysDropdown'] == false) && (spotDOM.isSearchPage() || spotDOM.isCollectionPage()),
      (options['searchBarBackfill'] != null ? options['searchBarBackfill'] : false),
      options['searchBarOptions'],
      options['searchBarOptions'] && options['searchBarOptions']['deferHookUntilLoad']
    );
    if (options['product'] && options['recommendationsPaneContainer']) {
      // =================== OPTIONAL STEP 3 =========================
      // Set up our recommendations pane, ready to receive recommendations, and then set up our actual recommendations.
      window.spotRecommendationsPane = options['recommendationsPane'] || new spotDefault.RecommendationsPane(options['recommendationsPaneOptions'], options['recommendationsPaneRenderer'] || options['productRenderer']);
      spotDOM.addRecommendation(function(recommendation) {
        window.spotRecommendationsPane.addRecommendation(recommendation);
      }, true).removeRecommendation(function(recommendation) {
        window.spotRecommendationsPane.removeRecommendation(recommendation);
      });
      spotDefault.resolveQuerySelector(options['recommendationsPaneContainer'], function(container) {
        if (container) {
          if ((options['recommendationsPaneContainerSelectorPosition'] || 'replace') == 'replace') {
            container.innerHTML = '';
            container.appendChild(window.spotRecommendationsPane.paneElement);
          } else
            container.insertAdjacentElement(options['recommendationsPaneContainerSelectorPosition'], window.spotRecommendationsPane.paneElement);
        }
      });
    }

    spotDefault.setupActiveRules(options.rule);
    spotDefault.activeRules(function(rules) {
      var rule = rules[0] || {};
      // If there are reasons to trigger an initial query, trigger one.
      if ((spotDOM.isCollectionPage() || spotDOM.isSearchPage()) && (spotDefault.needsInitialQuery() || (rule.conditions && rule.conditions.length > 0) || spotDOM.personalization() || options['forceInitialQuery'])) {
        spotDefault.ready(function() { document.body.classList.add('spot-initial-loading'); });
        spotDOM.query().done(function() {
          spotDefault.loaded(true);
          spotDefault.ready(function() {
            document.body.classList.remove('spot-initial-loading');
            document.body.classList.add('spot-loaded');
          });
        });
      } else {
        spotDefault.loaded(true);
        spotDefault.ready(function() { document.body.classList.add('spot-loaded'); });
      }
      spotDefault.ready(function() {
        Array.from(document.body.classList).filter(function(klass) { return /^spot\-rule\-/.test(klass) }).forEach(function(klass) { document.body.classList.remove(klass); });
        rules.filter(function(r) { return r.handle; }).forEach(function(r) { document.body.classList.add("spot-rule-" + r.handle); });
      });
    }, true);
    if (options['freeformContext'] == null || options['freeformContext'])
      spotDefault.setupFreeformContext(options['freeformContext'], spotDefault.context());
    // Ensure that whenever something changes about the page (page change, collection change, etc..), we force a refresh.
    if (!options['suppressQueryChange'])
      spotDefault.setupQueryOnChange();
    deferred.resolve(spotAPI, spotDOM, spotDefault);
    document.dispatchEvent(new CustomEvent('SpotInit', { detail: { spotAPI: spotAPI, spotDOM: spotDOM, spotDefault: spotDefault } }));
    spotDefault.ready(function() {
      document.dispatchEvent(new CustomEvent('SpotReady', { detail: { spotAPI: spotAPI, spotDOM: spotDOM, spotDefault: spotDefault } }));
    });
  });
  return deferred;
};
window.spotLoaded = window.spotLoaded || (function() {
  var event = new CustomEvent('SpotLoaded', { detail: { } });
  document.dispatchEvent(event);
  return true;
})();
/** %-{SpotDefaultInit} **/

/**

## Custom Implementation Checklist

This is probably where you want to start if you're looking to hit the ground runnning if you're looking to do a custom implemetnation. Before you dive in, you should choose how to structure your Spot code.
Normally, the standard way to do this is to stick all of your code in a snippet, and include that snippet just before the closing body tag of your `theme.liquid` file.
Inside this snippet, you'll write your javascript as single function block, and then have that block run only after document ready. This is the suggested method
of inclusion, but there is no hard requirement, you may call it how you like.

Adding the code below will throw together a default implementation of Spot onto the site. When you implement Spot, the following issues are what you should be tackling, roughly, in order.
The sample code illustrates how each of these is handled; there are corresponding step indicators in the sample code.

### Checklist

1. Instantitate a copy of `SpotAPI`, `SpotDOM` and `SpotDefault`, with the appropriate credentials, by calling `new` on each in order. Ensure that you point Spot to your results container, sort dropdown, and pagination container.
2. Provide a function to `SpotDOM` which takes a JSON representation of the store's products using `.products`, and renders that onto your collections/search results page.
3. Determine where, and how you're going to attach the faceting pane to your collections/search page, and do so by creating it with `new spotDefault.Pane()`.
4. Instantiate what facets you're going to want to show, and register them with `SpotDOM`.
5. Implement how you're going to paginate your results (with infinite scrolling, or with page numbers), generally by hooking the `.count` scalar.
6. Determine how, if at all, you're going to let the user change the sort order, and populate/set the list of sort orders using the array adders, or `spotDOM.initDefaultSortOptions()`.
7. Add in polishing elements. A generic list of things to look out for is below.
  * After each query, make sure the query string is up to date.
  * If you're using spelling correction (and you should most of the time; `.autoCorrect(true)`), consider displaying the result of what the user's search query was corrected to, through the `SpotDOM.spellCheck` hook.
  * Make the filtering/faceting "snappy", by ensure that a new query runs after most filtering changes. (See the sample implementation below).
  * Make sure that when you link any product, that if you're not specifying `allVariants(true)`, that you link to the first relevant variant, as a direct link to the product page may link to a variant the user isn't looking at.
  * When implementing a quick search bar, if you're doing search-as-you-go, ensure that you have some mechanism to either throttle the user's queries, so the user's screend doesn't thrash around (you can use `SpotDefault.keyupListener`). If this is desired behavior, then you'll need to make sure instead that you handle queries in the order in which they were made, not the order which they return. If you do not, when a user stops typing, they could have results for something that wasn't their last search. For example, if a user has typed "shi", and then "shirt", and "shirt" returns before "shi" (likely because shi contains lots more product data then shirt), when the user stops typing, they'll see results for "shi", instead of "shirt", which isn't what the user wants.
  * If you want to only show things that are in stock, simply add `.available(true)`, to your default query.
8. Style everything.

### Optional Steps

1. Test with `.shopifyMode(true)` (see [here](#shopifymode) for details), and seeing if the system still works satisfactorily with Spot disabled.
2. Determine if/how you're going to have the search bar of the site interact with your collections page, and tying in a quick-search box, with search-as-you-go functionality.
3. Ensure that you've hooked up Spot to display your product recommendations.

Your implementation for all of this should ideally be no more than a few hundred lines of javascript.

You can see an example of this implementation in the normal initialization function, called by the Spot section. See above.

%{SpotDefaultInit}

**/


/***

Spot Endpoint Documentation
===========================

## Overview

This SDK serves as layer of abstraction infront of an endpoint.

To contact Spot directly, all you need to do is send HTTP(S) requests to a particular server. Exactly what servers hold your shop's data is listed in the shop-based metafield that has a namespace of `esafilters` and
a key of `clusters`. This metafield contains an array of at least one hostname. Requests made to the Spot endpoint are load-balanced behind these hostnames.

Spot requests can be served plain, but is encouraged to pass `Accept-Encoding: deflate` as a header in order to take advantage of Spot's zipping capability to make responses smaller, and to take advantage of more aggressive caching.

## Endpoint

Spot queries can be either `POST`s or `GET`s. In a `GET`, Spot will accept the query body in the `q` parameter, or as the body of a `POST` request. Spot supports a maximum of ~8kb in terms of header size, so any apps that have the potential
to submit long queries should be using `POST`, rather than `GET`.

The following query parameters are applicable to both `GET` and `POST` requests:

* `hostname`: Can be used to specify the .myshopify.com hostname of the shop you're looking to query. If not supplied, is inferred from the `Referrer` header.

## Query Structure

Queries are submitted in JSON (RFC8259). Each query consists of a dictionary with exactly two keys, `query`, and `attributes`.

### Query

An array. Contains all expressions that are to be AND'd together. Query expressions come in two flavours, compound or simple.

#### Simple Expressions

Take the form of a dictionary that specifies the field to be filtered as a key, an operator, and the desired operand to be checked against. The operator is optional, and can be omitted for simple equality. If the operator is present,
it forms an additional dictionary object to be passed as the sole value of field key.

    {"inventory_quantity":{">":0}}

    {"price":{"<=":10}}

    {"title":"Exactly This Product Title"}
    {"title":{"==":"Exactly This Product Title"}}

    {"tag":"red"}
    {"tag":{"==":"red"}}

    {"option1":{"!=":"Large"}}

Some fields are *specified* fields, which means that they require additional specification to determine what field you're talking about. This is done by recursively nesting dictionaries, like so:

    {"option":{"Color":{"==":"Red"}}}

    {"product-metafield":{"mynamespace":{"mykey":{">":0}}}}

    {"variant-metafield":{"mynamespace":{"mykey":{">":0}}}}

#### Compound Expressions

These expressions can contain other expressions. There are a number of these.

##### or

OR's together the various sub-expressions.

    {"or":[{"option":{"Color":"Red"}},{"product_type":"Shirt"}]}

##### and

AND's together the various sub-expressions. Implied in the opening array, but useful if ANDs and ORs are nested.

    {"and":[{"price":{"<":20},{"price":{">":10}]}

##### facets

Specifies which of your filters apply to your facets. Enables the use of disjuctive facets.

    {"facets":[{"or":[]},{"or":[]},{"or":[{"option":{"Color":"Red"}}]}]}

##### all

Specifies that only products that have *all* variants present as a result of the subquery should be returned.

    {"all":[{"option":{"Color":"Red"}}]}

##### not

Specifies that only products that do not match the subquery sould be returned.

    {"not":[{"option":{"Color":"Red"}}]}

##### any

Reverts the behaviour of `all` back to the default `any` inside an `all` subexpression.

#### Query Fields

There are many of these. This list is *non-exhaustive*. It includes every relevant property on Product or Variant, as they are named in Shopify's REST api.

##### Product Fields

* `product_type`
* `vendor`
* `title`
* `handle`
* `collection`
* `product-metafield` (qualified; *can be treated as numeric*)

##### Variant Fields

###### Numeric Fields

* `inventory_quantity`
* `price`

###### String Fields

* `available` (Definition is exactly as in Shopify's liquid reference; this should be used over `inventory_quantity > 0` to determine availability of a product).
* `option1`
* `option2`
* `option3`
* `option` (qualified)
* `variant-metafield` (qualified; *can be treated as numeric*)

#### Query Operators

Query operators come in two flavours, numeric operators, or string operators.

##### String Operators

* `==` : Equality.
* `!=` : Inequality.
* `^` : Starts With
* `in` : Checks if within a specified array.
* `not_in`: Checks if not within a specified array.

##### Numeric Operators

* `<`
* `>`
* `>=`
* `<=`
* `==`
* `!=`

### Attributes

Attributes are meta-information about the query that affect the query, and the results returned. The following is a non-exhaustive list of all attributes.

#### count (boolean, default: false)

Determines whether or not Spot should return the total amount of products in the resultset. Can result in longer query times if enabled.

#### countBehavior (string, default: 'approximate')

[See here](#countbehavior).

#### options (array, default: [])

Provides the list of facets you'd like to facet on. Facets are specified as in the Query Fields section. If you'd like to specify a list of particular facets of a given type (say, a list of tags instead of all tags on the store), you can specify this in the form of an array.

    {"options":["product_type","vendor",{"option":"Color"},{"option":"Size"},{"tag":["sale","merch","spring","summer","fall","winter"]}]

The order of these options is relevant, as it will affect your `facets` compound specifier if you use one.

In addition to simple specifiers, one can be more descriptive and group values together into single facets.

    {"options":[{"tag":["sale","merch",{"name":"seasonal","value":["spring","summer","fall","winter"]}]]}}

#### optionBehavior (string, default: 'approximate')

[See here](#optionbehavior).

#### optionDisjunctive (bool, default: true)

Determine whether your facets are disjuntive or conjunctive. [See the entry on disjucntive facet counts for details](#disjunctive-facet-counts).

#### search (string, default: null)

A free-form search string to search through the entire object; all query fields, as well as the body_html. Intelligently performs stemming based on locale. If not empty string or null, can be used with the `search` sort order,
as well as with the `autoCorrect` and `spellCheck` attributes.

#### collection (string, default: null)

A string representing a collection handle, for which this query should operate in the context of. Will only show products relevant to the desired collection, and will fill if not set the `sort` as being whatever the collection's
default is. If a collection does not exist, a warning will be generated, but a full resultset for the entire store will be returned. Also allows for the `manual` sort order, if applicable.

#### sort (string/object, default: null)

A string or object representing the order you'd like to sort the resultset by. In addition to custom sort orders, we support all Shopify sort orders, in all the myriad ways they're spelled, and defined.

If a string, the following strings, in addition to any named sort orders specified in the Spot backend are accepted:

* `created`
* `created-asc`
* `created-desc`
* `title`
* `title-asc`
* `title-desc`
* `alpha`
* `alpha-asc`
* `alpha-desc`
* `bestselling` (If order-reading is enabled in the Spot backend)
* `manual` (If sorting through a collection).
* `search` (If searching, orders by search relevancy).
* `price`
* `price-asc`
* `price-desc`
* Identifier for any custom sort order you've defined.

If an object, you can specify a sort direction (asc/desc), followed the field name.

    {"asc":"price"}

    {"desc":"title"}

In addition, you can also specify complex sort orders that you've defined on the Spot back-end this way.

    {"desc":{"product-metafield":{"mynamespace":"mykey"}}}

#### autoCorrect (boolean, default: false)

If true, will return a spellcheck in the response, based on the search string, as well as automatically correct the search to this spellcheck if there are no results with the initial search.

#### spellCheck (boolean, default: false)

If true, will return a spellcheck in the response, based on the search string.

#### rows (int, default: 12)

Specifies the amount of results to retrieve. Maximum of 100. (1000 is currently supported, but may be revoked depending on performance characteristics).

#### page (int, default: 1)

Specifies the page of results to retrieve.

#### fields (array[string], default: null)

Specifies the field list to retrieve. If not specified, will return the entire object. As an example:

    {"fields":["title","product_type","images"]}

#### split (string, default: "none")

Specifies the product split.

* `none`: Returns products as Shopify has them.
* `auto`: Returns the most appropriate split, given what you have set up in the Spot control panel, and your query.
* `<id>`: Returns the specifier split by identifier from the Spot control panel.

#### allVariants (bool/string, default: true)

[See here](#allvariants).

#### locale (string, default: shop default)

Specifies the locale to return products in if they're using Shopify's official translation system. Under construction; unstable.

#### currency (object, default: null)

Specifies the currency. Translates the incoming query to use prices converted by the specifies currenc. Under construction; unstable.

#### popularSearches (int, default: 0)

Specifies the amount of popular searches to return. Under construction; unstable.

## Examples

The following are sample full requests, and response pairs.

Simple request to grab "id" and "title" of two products.

    {"query":[],"attributes":{"fields":["id","title"],"rows":2}}

### Example 1

#### Request

    > GET /?q=%7B%22query%22%3A%5B%5D%2C%22attributes%22%3A%7B%22fields%22%3A%5B%22id%22%2C%22title%22%5D%2C%22rows%22%3A2%7D%7D%0A HTTP/1.1
    > Host: spot-cluster-ca-0.moddapps.com
    > User-Agent: curl/7.47.0
    > Accept: application/json
    > Accept-Encoding: deflate, gzip
    > Referer: https://spot-development.myshopify.com

#### Response

    < HTTP/1.1 200 OK
    < Content-Length: 135
    < Access-Control-Expose-Headers: X-Session-Id
    < Access-Control-Allow-Headers: X-Shopify-Shop-Domain,Content-Type,Accept,X-Requested-With,X-Session-Id
    < Access-Control-Allow-Credentials: true
    < Access-Control-Allow-Method: GET,POST
    < Access-Control-Allow-Origin: https://spot-development.myshopify.com
    < Content-Type: application/json; charset=UTF-8
    < X-Session-Id: 8b187da44afe7c04e1bc0d0d537e2abd
    < Content-Encoding: deflate
    < Date: Thu, 13 Aug 2020 01:33:10 GMT
    < Via: 1.1 google
    < Alt-Svc: clear
    {"products":[{"title":"Zoo York Zombie Skateboard 8\"","id":"4434463326319"},{"id":"4434463195247","title":"Zoo York Womens Unbreakable Chenille Cropped T-Shirt"}]}

### Example 2

Get the first page of 2 available products, and none of the irrelevant variants, that have the tag "language_en", and correspond to the search "hat", as well as have the Size "O/S". Get an exact count returned.

    {"query":[{"available":{"==":true}},{"tag":{"==":"language_en"}},{"option":{"Size":"O/S"}}],"attributes":{"count":true,"countBehavior":"exact","allVariants":false,"fields":["id","handle","title","product_type","vendor","tags","images","options","variants","metafields"],"rows":2,"page":1,"options":[],"optionDisjunctive":true,"search":"hat"}}

#### Request

    > GET /?q=%7B%22query%22%3A%5B%7B%22available%22%3A%7B%22%3D%3D%22%3Atrue%7D%7D%2C%7B%22tag%22%3A%7B%22%3D%3D%22%3A%22language_en%22%7D%7D%2C%7B%22option%22%3A%7B%22Size%22%3A%22O%2FS%22%7D%7D%5D%2C%22attributes%22%3A%7B%22count%22%3Atrue%2C%22countBehavior%22%3A%22exact%22%2C%22allVariants%22%3Afalse%2C%22fields%22%3A%5B%22id%22%2C%22handle%22%2C%22title%22%2C%22product_type%22%2C%22vendor%22%2C%22tags%22%2C%22images%22%2C%22options%22%2C%22variants%22%2C%22metafields%22%5D%2C%22rows%22%3A2%2C%22page%22%3A1%2C%22options%22%3A%5B%5D%2C%22optionDisjunctive%22%3Atrue%2C%22search%22%3A%22hat%22%7D%7D%0A HTTP/1.1
    > Host: spot-cluster-ca-0.moddapps.com
    > User-Agent: curl/7.47.0
    > Accept: application/json
    > Accept-Encoding: deflate, gzip
    > Referer: https://spot-development.myshopify.com

#### Response

    < HTTP/1.1 200 OK
    < Content-Length: 1642
    < Access-Control-Expose-Headers: X-Session-Id
    < Access-Control-Allow-Headers: X-Shopify-Shop-Domain,Content-Type,Accept,X-Requested-With,X-Session-Id
    < Access-Control-Allow-Credentials: true
    < Access-Control-Allow-Method: GET,POST
    < Access-Control-Allow-Origin: https://spot-development.myshopify.com
    < Content-Type: application/json; charset=UTF-8
    < X-Session-Id: b57219253b5961a953f6ed12567d462a
    < Content-Encoding: deflate
    < Date: Thu, 13 Aug 2020 01:43:37 GMT
    < Via: 1.1 google
    < Alt-Svc: clear

    {"products":[{"handle":"1648-18958383-zoo-york-mens-graphic-trucker-hat","images":[{"position":1,"updated_at":"2020-04-28T00:41:14-04:00","id":13932966969455,"alt":null,"height":2000,"width":1333,"product_id":4433605230703,"created_at":"2020-04-28T00:41:14-04:00","src":"https://cdn.shopify.com/s/files/1/0271/5952/7535/products/164818958383-69-0.jpg?v=1588048874","admin_graphql_api_id":"gid://shopify/ProductImage/13932966969455","variant_ids":[31478042198127]},{"updated_at":"2020-04-28T00:41:16-04:00","position":2,"id":13932967559279,"alt":null,"height":2000,"admin_graphql_api_id":"gid://shopify/ProductImage/13932967559279","variant_ids":[],"product_id":4433605230703,"created_at":"2020-04-28T00:41:16-04:00","width":1333,"src":"https://cdn.shopify.com/s/files/1/0271/5952/7535/products/164818958383-69-1.jpg?v=1588048876"},{"admin_graphql_api_id":"gid://shopify/ProductImage/13932967592047","variant_ids":[31478042230895],"width":1333,"product_id":4433605230703,"created_at":"2020-04-28T00:41:18-04:00","src":"https://cdn.shopify.com/s/files/1/0271/5952/7535/products/164818958383-40.jpg?v=1588048878","updated_at":"2020-04-28T00:41:18-04:00","position":3,"id":13932967592047,"alt":null,"height":2000},{"admin_graphql_api_id":"gid://shopify/ProductImage/13932967624815","variant_ids":[],"src":"https://cdn.shopify.com/s/files/1/0271/5952/7535/products/164818958383-40-1.jpg?v=1588048880","width":1333,"product_id":4433605230703,"created_at":"2020-04-28T00:41:20-04:00","id":13932967624815,"position":4,"updated_at":"2020-04-28T00:41:20-04:00","height":2000,"alt":null}],"metafields":[],"variants":[{"grams":0,"id":"31478042230895","position":2,"inventory_policy":"deny","admin_graphql_api_id":"gid://shopify/ProductVariant/31478042230895","image_id":13932967592047,"option3":null,"created_at":"2020-04-28T00:41:13-04:00","fulfillment_service":"manual","inventory_quantity":16,"barcode":"61953923","compare_at_price":"11.99","requires_shipping":true,"option2":"O/S","inventory_item_id":33129781821551,"option1":"Blue","updated_at":"2020-04-28T00:41:18-04:00","weight_unit":"lb","weight":0,"title":"Blue / O/S","metafields":[],"taxable":true,"sku":"1648-18958383-401010","product_id":4433605230703,"old_inventory_quantity":16,"price":"11.99","inventory_management":"shopify"}],"title":"Zoo York Mens Graphic Trucker Hat","vendor":"Zoo York","options":[{"position":1,"name":"Color","product_id":4433605230703,"id":5769550102639,"values":["Coral","Blue"]},{"values":["O/S"],"id":5769550135407,"position":2,"product_id":4433605230703,"name":"Size"}],"id":"4433605230703","tags":"brand amnesia, brand west49, gender mens, language_en, locale_ca, mar10, new, private label, promo 50off, promo bogo5, spring, style flamingo, style palm tree, subtype hats, type accessories","product_type":"Mens Hats"},{"metafields":[],"variants":[{"price":"11.99","inventory_management":"shopify","old_inventory_quantity":13,"product_id":4433517772911,"taxable":true,"sku":"2748-18958383-401010","metafields":[],"title":"Blue / O/S","weight_unit":"lb","weight":0,"option1":"Blue","updated_at":"2020-04-27T23:01:52-04:00","inventory_item_id":33129115254895,"requires_shipping":true,"option2":"O/S","fulfillment_service":"manual","inventory_quantity":13,"compare_at_price":"11.99","barcode":"61959516","option3":null,"created_at":"2020-04-27T23:01:50-04:00","admin_graphql_api_id":"gid://shopify/ProductVariant/31477430812783","image_id":13932622250095,"inventory_policy":"deny","id":"31477430812783","position":1,"grams":0},{"inventory_policy":"deny","grams":0,"position":2,"id":"31477430845551","compare_at_price":"11.99","barcode":"61957270","fulfillment_service":"manual","inventory_quantity":16,"option2":"O/S","requires_shipping":true,"image_id":13932622348399,"admin_graphql_api_id":"gid://shopify/ProductVariant/31477430845551","created_at":"2020-04-27T23:01:50-04:00","option3":null,"title":"Coral / O/S","weight":0,"weight_unit":"lb","metafields":[],"inventory_item_id":33129115287663,"updated_at":"2020-04-27T23:01:56-04:00","option1":"Coral","inventory_management":"shopify","price":"11.99","sku":"2748-18958383-691010","taxable":true,"old_inventory_quantity":16,"product_id":4433517772911}],"vendor":"Zoo York","title":"Zoo York Boys Graphic Trucker Hat","handle":"2748-18958383-zoo-york-boys-graphic-trucker-hat","images":[{"admin_graphql_api_id":"gid://shopify/ProductImage/13932622250095","variant_ids":[31477430812783],"src":"https://cdn.shopify.com/s/files/1/0271/5952/7535/products/274818958383-40-0.jpg?v=1588042912","width":1333,"product_id":4433517772911,"created_at":"2020-04-27T23:01:52-04:00","id":13932622250095,"updated_at":"2020-04-27T23:01:52-04:00","position":1,"height":2000,"alt":null},{"id":13932622315631,"updated_at":"2020-04-27T23:01:54-04:00","position":2,"alt":null,"height":2000,"variant_ids":[],"admin_graphql_api_id":"gid://shopify/ProductImage/13932622315631","src":"https://cdn.shopify.com/s/files/1/0271/5952/7535/products/274818958383-40-1.jpg?v=1588042914","width":1333,"product_id":4433517772911,"created_at":"2020-04-27T23:01:54-04:00"},{"id":13932622348399,"updated_at":"2020-04-27T23:01:56-04:00","position":3,"height":2000,"alt":null,"admin_graphql_api_id":"gid://shopify/ProductImage/13932622348399","variant_ids":[31477430845551],"src":"https://cdn.shopify.com/s/files/1/0271/5952/7535/products/274818958383-69.jpg?v=1588042916","width":1333,"created_at":"2020-04-27T23:01:56-04:00","product_id":4433517772911},{"height":2000,"alt":null,"position":4,"updated_at":"2020-04-27T23:01:58-04:00","id":13932622381167,"variant_ids":[],"admin_graphql_api_id":"gid://shopify/ProductImage/13932622381167","width":1333,"product_id":4433517772911,"created_at":"2020-04-27T23:01:58-04:00","src":"https://cdn.shopify.com/s/files/1/0271/5952/7535/products/274818958383-69-1.jpg?v=1588042918"}],"options":[{"values":["Blue","Coral"],"name":"Color","product_id":4433517772911,"position":1,"id":5769436135535},{"id":5769436168303,"position":2,"product_id":4433517772911,"name":"Size","values":["O/S"]}],"product_type":"Boys Hats","id":"4433517772911","tags":"brand amnesia, brand west49, gender boys, language_en, locale_ca, mar10, new, private label, promo 50off, promo bogo5, spring, style flamingo, style palm tree, subtype hats, type accessories"}],"count":22,"spellCheck":{"original":"hat","corrected":"hat","words":[{"original":"hat","corrected":"hat"}]}}

### Example 3

Ask for the first two products that are either "Baby Blue","Blue","Dark Blue","Denim Blue","Grey Blue","Light Blue","Light Denim Blue","Medium Blue","Medium Denim Blue","Midnight Blue","Ocean Blue","Royal Blue","Sky Blue","Navy","Turquoise","Aqua" or "Teal". Get an exact count, as well as a list of facets with their counts, that are "Color", "Size"," Price", english/french tags, as well as "subtype". "Color" groups together many colors into one, as does size. Price lists an array of ranges, english and french are two specific tags, and subtypes are another specific group of tags. All counts are approximate. Asking for the currency in Canadian dollars. Ask for whatever split is most appropriate to this query.

#### Request

    > POST / HTTP/1.1
    > Host: spot-cluster-ca-0.moddapps.com
    > User-Agent: curl/7.47.0
    > Accept: application/json
    > Accept-Encoding: deflate, gzip
    > Referer: https://spot-development.myshopify.com
    > Content-Length: 2921
    > Content-Type: application/json; charset=UTF-8

    {"query":[{"facets":[{"or":[{"or":[{"option":{"Color":{"==":"Baby Blue"}}},{"option":{"Color":{"==":"Blue"}}},{"option":{"Color":{"==":"Dark Blue"}}},{"option":{"Color":{"==":"Denim Blue"}}},{"option":{"Color":{"==":"Grey Blue"}}},{"option":{"Color":{"==":"Light Blue"}}},{"option":{"Color":{"==":"Light Denim Blue"}}},{"option":{"Color":{"==":"Medium Blue"}}},{"option":{"Color":{"==":"Medium Denim Blue"}}},{"option":{"Color":{"==":"Midnight Blue"}}},{"option":{"Color":{"==":"Ocean Blue"}}},{"option":{"Color":{"==":"Royal Blue"}}},{"option":{"Color":{"==":"Sky Blue"}}},{"option":{"Color":{"==":"Navy"}}},{"option":{"Color":{"==":"Turquoise"}}},{"option":{"Color":{"==":"Aqua"}}},{"option":{"Color":{"==":"Teal"}}}]}]},{"or":[]},{"or":[]},{"or":[]},{"or":[]}]}],"attributes":{"count":true,"countBehavior":"approximate","allVariants":false,"rows":2,"page":1,"collection":"all","options":[{"option":{"Color":[{"name":"Black","value":["Black","Black With White","Black with White","Black with white","Pure Black","Solid Black"]},{"name":"Blue","value":["Baby Blue","Blue","Dark Blue","Denim Blue","Grey Blue","Light Blue","Light Denim Blue","Medium Blue","Medium Denim Blue","Midnight Blue","Ocean Blue","Royal Blue","Sky Blue","Navy","Turquoise","Aqua","Teal"]},{"name":"Brown","value":["Brown","Copper","Tan"]},{"name":"Beige","value":["Beige","Natural","Camel","Oatmeal","Sand"]},{"name":"Red","value":["Red","Burgundy","Wine"]},{"name":"Green","value":["Green","Dark Green","Hunter Green","Camouflage","Jade","Khaki","Sage","Neon Green"]},{"name":"Grey","value":["Dark Grey","Grey","Heather Grey","Light Grey","Charcoal","Silver"]},{"name":"Orange","value":["Coral","Dark Orange","Neon Orange","Orange","Peach"]},{"name":"Pink","value":["Dark Pink","Light Pink","Neon Pink","Pink","Dusty Rose","Rose"]},{"name":"Purple","value":["Lilac","Magenta","Dark Purple","Eggplant","Fuchsia","Purple"]},{"name":"Yellow","value":["Neon Yellow","Pale Yellow","Yellow","Mustard","Gold"]},{"name":"Plaid","value":["Plaid","Gingham"]},{"name":"Multi","value":["Assorted","Multi"]},{"name":"Neon","value":["Neon Orange","Neon Pink","Neon Yellow","Neon Green"]},{"name":"Camouflage","value":["Camouflage"]}]}},{"option":{"Size":[{"name":"One Size","value":["One Size","O/S"]},{"name":"S (7/8)","value":["S (7/8)","7/8"]},{"name":"XS (6)","value":["XS (6)"]},{"name":"M (10/12)","value":["M (10/12)","9/10","11/12"]},{"name":"L (14/16)","value":["L (14/16)","L (14)","15/16","13/14"]},{"name":"XL (16)","value":["XL (16)"]}]}},{"price":["-10","10-25","25-50","50-75","75-100","100+"]},{"tags":[{"name":"English","value":"language_en"},{"name":"French","value":"language_fr"}]},{"tags":["subtype joggers","subtype lanyards","subtype leggings","subtype long sleeves","subtype longboard","subtype longboards"]}],"optionBehavior":"approximate","optionDisjunctive":true,"split":"auto","currency":{"code":"CAD","rate":"1.0"},"locale":"en"}}

#### Response

    < HTTP/1.1 200 OK
    < Content-Length: 2182
    < Access-Control-Expose-Headers: X-Session-Id
    < Access-Control-Allow-Headers: X-Shopify-Shop-Domain,Content-Type,Accept,X-Requested-With,X-Session-Id
    < Access-Control-Allow-Credentials: true
    < Access-Control-Allow-Method: GET,POST
    < Access-Control-Allow-Origin: https://spot-development.myshopify.com
    < Content-Type: application/json; charset=UTF-8
    < X-Session-Id: 25506ca4fd234c8af63e1952be1f60ea
    < Content-Encoding: deflate
    < Date: Thu, 13 Aug 2020 01:58:20 GMT
    < Via: 1.1 google
    < Alt-Svc: clear

    {"products":[],"options":[{"product_id":4429644300399,"name":"Color","position":1,"values":["Teal"],"id":5764571103343},{"id":5764571136111,"values":["8"],"product_id":4429644300399,"position":2,"name":"Size"}]}],"options":[{"option":{"Color":{"Black":919,"Blue":456,"Brown":33,"Beige":69,"Red":194,"Green":344,"Grey":314,"Orange":131,"Pink":158,"Purple":149,"Yellow":132,"Plaid":30,"Multi":249,"Neon":48,"Camouflage":109}}},{"option":{"Size":{"One Size":82,"S (7/8)":88,"XS (6)":2,"M (10/12)":88,"L (14/16)":88,"XL (16)":96}}},{"price":{"-10":9,"10-25":153,"25-50":116,"50-75":28,"75-100":28,"100+":11}},{"tag":{"English":392}},{"tag":{"subtype joggers":11,"subtype lanyards":1,"subtype long sleeves":18,"subtype longboards":8}}],"count":456,"currency":{"code":"CAD","rate":1.000000}}

***/
